import React, { useRef, useMemo, useState, useEffect, useCallback } from 'react'
import dynamic from 'next/dynamic'
import { useRouter, NextRouter } from 'next/router'
import Head from 'next/head'
import { SessionProvider } from 'next-auth/react'
import { Analytics } from '@vercel/analytics/next'
import {
  Loader,
  Container,
  ActionIcon,
  ColorScheme,
  MantineProvider,
  ColorSchemeProvider,
} from '@mantine/core'
import { NotificationsProvider } from '@mantine/notifications'
import structuredClone from '@ungap/structured-clone'
import FadeIn from 'react-fade-in'
import '../app/components/styles/styles.css'
import useStyles from './_app.styles'
import { CookiesProvider, useCookies } from 'react-cookie'
// import * as Sentry from '@sentry/nextjs'
import {
  isIOS,
  isEdge,
  isOpera,
  isMacOs,
  isIOS13,
  isChrome,
  isIPad13,
  isTablet,
  isMobile,
  isSafari,
  isWindows,
  isFirefox,
  isAndroid,
  isDesktop,
  osVersion,
  isIPhone13,
  isEmbedded,
  isMobileOnly,
  isMobileSafari,
  isEdgeChromium,
  isSamsungBrowser,
} from 'react-device-detect'
import AudioPlayer from 'react-h5-audio-player'
import 'react-lazy-load-image-component/src/effects/blur.css'
import useScrollRestoration from '../app/components/hooks/useScrollRestoration'
import { useDebounce } from '@uidotdev/usehooks'
import stringify from 'json-stringify-safe'
import { IconHeart, IconPlaylistAdd } from '@tabler/icons'
import {
  inter,
  uuidv4,
  cureCDN,
  hexToRgba,
  cureLiterals,
  simpleDecrypt,
  decodeUnicode,
  netDateToDate,
  dateToNetDate,
  isSamePlaylist,
  formatDuration,
  toTrimmedString,
  fetchWithTimeout,
  CURE_CDN_MP3_EXT,
  CURE_CDN_JPG_EXT,
  isNullOrUndefined,
  CURE_CDN_AVIF_EXT,
  isNullOrEmptyString,
  formatSongNameSmall,
  getOptimizedMediaUrl,
  isNotNullOrUndefined,
  isResolvedArweaveUrl,
  ONLY_UNIQUE_FUNCTION,
  checkHasOfflineTracks,
  isNotNullOrEmptyString,
  toTrimmedStringUpperCase,
  toTrimmedStringLowerCase,
  TO_TRIMMED_STRING_FUNCTION,
  ITALY_ISO_3166_ALPHA_2_CODE,
  SPAIN_ISO_3166_ALPHA_2_CODE,
  MALTA_ISO_3166_ALPHA_2_CODE,
  CYPRUS_ISO_3166_ALPHA_2_CODE,
  CANADA_ISO_3166_ALPHA_2_CODE,
  FRANCE_ISO_3166_ALPHA_2_CODE,
  GREECE_ISO_3166_ALPHA_2_CODE,
  LATVIA_ISO_3166_ALPHA_2_CODE,
  MONACO_ISO_3166_ALPHA_2_CODE,
  KOSOVO_ISO_3166_ALPHA_2_CODE,
  BRITAIN_ISO_3166_ALPHA_2_CODE,
  AUSTRIA_ISO_3166_ALPHA_2_CODE,
  BELGIUM_ISO_3166_ALPHA_2_CODE,
  GERMANY_ISO_3166_ALPHA_2_CODE,
  ESTONIA_ISO_3166_ALPHA_2_CODE,
  FINLAND_ISO_3166_ALPHA_2_CODE,
  CROATIA_ISO_3166_ALPHA_2_CODE,
  IRELAND_ISO_3166_ALPHA_2_CODE,
  ANDORRA_ISO_3166_ALPHA_2_CODE,
  PORTUGAL_ISO_3166_ALPHA_2_CODE,
  SLOVENIA_ISO_3166_ALPHA_2_CODE,
  SLOVAKIA_ISO_3166_ALPHA_2_CODE,
  LITHUANIA_ISO_3166_ALPHA_2_CODE,
  MONTENEGRO_ISO_3166_ALPHA_2_CODE,
  SAN_MARINO_ISO_3166_ALPHA_2_CODE,
  LUXEMBOURG_ISO_3166_ALPHA_2_CODE,
  FORMAT_SONG_NAME_BIGGER_FUNCTION,
  getSubscriptionButtonCurrencyText,
  NETHERLANDS_ISO_3166_ALPHA_2_CODE,
  SOUTH_KOREA_ISO_3166_ALPHA_2_CODE,
  VATICAN_CITY_ISO_3166_ALPHA_2_CODE,
  UNITED_STATES_ISO_3166_ALPHA_2_CODE,
  IS_NOT_NULL_OR_EMPTY_STRING_FUNCTION,
  EURO_CURRENCY_ISO_3166_ALPHA_2_COUNTRY_CODES,
} from '../app/utilities/misc'
import {
  HOST,
  IS_QA,
  USE_CDN,
  IS_LOCAL,
  BLOG_HOST,
  DEBUG_MODE,
  APP_DOMAIN,
  G2_PROFILE,
  MEDIUM_BLOG,
  USE_CHATBOT,
  FAV_ICON_16,
  FAV_ICON_32,
  FAV_ICON_96,
  THEME_COLOR,
  FAV_ICON_194,
  SOFTWARE_SKU,
  IS_PRODUCTION,
  COOKIE_CONFIG,
  CONTACT_PHONE,
  PRODUCTION_HOST,
  ARWEAVE_GATEWAY,
  SAAS_HUB_PROFILE,
  AUDIO_SILENT_SRC,
  IPIFY_SERVICE_URL,
  MS_APP_TILE_COLOR,
  SLASH_DOT_PROFILE,
  SHOW_CONTACT_PHONE,
  HARDWARE_DEVICE_SKU,
  STUDIO_TESTING_MODE,
  DISABLE_RIGHT_CLICK,
  APPLE_TOUCH_76_ICON,
  APPLE_TOUCH_72_ICON,
  APPLE_TOUCH_60_ICON,
  APPLE_TOUCH_57_ICON,
  FORCE_PRACTICAL_POD,
  APPLE_TOUCH_180_ICON,
  APPLE_TOUCH_152_ICON,
  APPLE_TOUCH_144_ICON,
  APPLE_TOUCH_120_ICON,
  APPLE_TOUCH_114_ICON,
  SOURCE_FORGE_PROFILE,
  GOOGLE_TRENDS_ID_URL,
  FAV_ICON_CDN_OR_LOCAL,
  SVG_ICON_CDN_OR_LOCAL,
  GOOGLE_TAG_MANAGER_ID,
  JUSTIA_TRADEMARK_OWNER,
  ANDROID_CHROME_72_ICON,
  ANDROID_CHROME_48_ICON,
  ANDROID_CHROME_36_ICON,
  USE_GOOGLE_TAG_MANAGER,
  HOME_PAGE_DISPLAY_NAME,
  FORCE_NOT_PRACTICAL_POD,
  STREAMY3_GITHUB_PROFILE,
  ANDROID_CHROME_512_ICON,
  ANDROID_CHROME_384_ICON,
  ANDROID_CHROME_256_ICON,
  ANDROID_CHROME_192_ICON,
  ANDROID_CHROME_144_ICON,
  ANDROID_CHROME_128_ICON,
  ERROR_PAGE_DISPLAY_NAME,
  LOGO_LARGE_DARK_PNG_CDN,
  LOGO_SMALL_DARK_PNG_CDN,
  STUDIO_PAGE_DISPLAY_NAME,
  MS_TILE_144_CDN_OR_LOCAL,
  SEARCH_PAGE_DISPLAY_NAME,
  USE_ENV_PROTECTION_MODAL,
  SOFTWARE_SUGGEST_PROFILE,
  ISSUE_WIRE_PRESS_RELEASE,
  LOGO_LARGE_DARK_AVIF_CDN,
  LOGO_LARGE_LIGHT_PNG_CDN,
  LOGO_SMALL_DARK_AVIF_CDN,
  LOGO_SMALL_LIGHT_PNG_CDN,
  MIN_PRICE_ROCKBOT_STR_ALT,
  CONTACT_PAGE_DISPLAY_NAME,
  LOGO_LARGE_LIGHT_AVIF_CDN,
  LOGO_SMALL_LIGHT_AVIF_CDN,
  EVALUATE_INVITATION_DELAY,
  STRIPE_PRODUCT_ID_HARDWARE,
  ENV_PROTECTION_COOKIE_NAME,
  INVITATION_ACCEPTED_STATUS,
  PLAYLIST_PAGE_DISPLAY_NAME,
  MIN_PRICE_JUKEBOXY_STR_ALT,
  ISO_4217_CURRENCY_CODE_EURO,
  INVITATION_CANCELLED_STATUS,
  FAVORITES_PAGE_DISPLAY_NAME,
  APPLE_TOUCH_STARTUP_IMAGE_1,
  APPLE_TOUCH_STARTUP_IMAGE_2,
  APPLE_TOUCH_STARTUP_IMAGE_3,
  APPLE_TOUCH_STARTUP_IMAGE_4,
  APPLE_TOUCH_STARTUP_IMAGE_5,
  APPLE_TOUCH_STARTUP_IMAGE_6,
  APPLE_TOUCH_STARTUP_IMAGE_7,
  APPLE_TOUCH_STARTUP_IMAGE_8,
  APPLE_TOUCH_STARTUP_IMAGE_9,
  PRACTICAL_STREAM_CDN_ADDRESS,
  APPLE_TOUCH_STARTUP_IMAGE_10,
  APPLE_TOUCH_STARTUP_IMAGE_11,
  APPLE_TOUCH_STARTUP_IMAGE_12,
  APPLE_TOUCH_STARTUP_IMAGE_13,
  APPLE_TOUCH_STARTUP_IMAGE_14,
  APPLE_TOUCH_STARTUP_IMAGE_15,
  APPLE_TOUCH_STARTUP_IMAGE_16,
  APPLE_TOUCH_STARTUP_IMAGE_17,
  APPLE_TOUCH_STARTUP_IMAGE_18,
  APPLE_TOUCH_STARTUP_IMAGE_19,
  APPLE_TOUCH_STARTUP_IMAGE_20,
  APPLE_TOUCH_STARTUP_IMAGE_21,
  APPLE_TOUCH_STARTUP_IMAGE_22,
  APPLE_TOUCH_STARTUP_IMAGE_23,
  APPLE_TOUCH_STARTUP_IMAGE_24,
  APPLE_TOUCH_STARTUP_IMAGE_25,
  APPLE_TOUCH_STARTUP_IMAGE_26,
  APPLE_TOUCH_STARTUP_IMAGE_27,
  APPLE_TOUCH_STARTUP_IMAGE_28,
  APPLE_TOUCH_STARTUP_IMAGE_29,
  APPLE_TOUCH_STARTUP_IMAGE_30,
  APPLE_TOUCH_STARTUP_IMAGE_31,
  MIN_PRICE_MOOD_MEDIA_STR_ALT,
  MAX_PRICE_MOOD_MEDIA_STR_ALT,
  MIN_PRICE_CLOUDCOVER_STR_ALT,
  APPLE_TOUCH_STARTUP_IMAGE_32,
  APPLE_TOUCH_STARTUP_IMAGE_33,
  APPLE_TOUCH_STARTUP_IMAGE_34,
  DEBUG_MONITOR_SCHEDULE_TIMERS,
  FOUR_O_FOUR_PAGE_DISPLAY_NAME,
  SELECTED_LANGUAGE_COOKIE_NAME,
  STREAMY3_ALL_NAICS_FOR_JSON_LD,
  FIVE_HUNDRED_PAGE_DISPLAY_NAME,
  SAFARI_PINNED_TAB_CDN_OR_LOCAL,
  DEFAULT_SUBSCRIPTION_PRICE_CAD,
  DEFAULT_SUBSCRIPTION_PRICE_USD,
  ACCOUNT_SETTINGS_REDIRECT_UUID,
  TRACE_APP_VERSION_RELOAD_CHECK,
  STREAMY3_OFFICIAL_ADDRESS_CITY,
  STREAMY3_OFFICIAL_BUSINESS_NAME,
  STREAMY3_INFORMAL_BUSINESS_NAME,
  USE_APP_MINIMUM_VERSION_RELOADS,
  // USE_SENTRY_APP_MONITORING_QA,
  DEFAULT_SUBSCRIPTION_PRICE_EURO,
  // DEBUG_MONITOR_SCHEDULE_TIMERS,
  STREAMY3_OFFICIAL_ADDRESS_LINE_1,
  COUCH_HOMEPAGE_SMALL_SQUARE_AVIF,
  ISO_4217_CURRENCY_CODE_US_DOLLAR,
  DEFAULT_SUBSCRIPTION_PRICE_POUND,
  AVATAR_BACKGROUND_IMAGE_DARK_PNG,
  AVATAR_BACKGROUND_IMAGE_LIGHT_PNG,
  AVATAR_BACKGROUND_IMAGE_DARK_AVIF,
  SAFARI_PINNED_TAB_MASK_ICON_COLOR,
  AVATAR_BACKGROUND_IMAGE_LIGHT_AVIF,
  STREAMY3_OFFICIAL_ADDRESS_ZIP_CODE,
  ACCOUNT_SETTINGS_PAGE_DISPLAY_NAME,
  USE_PRACTICAL_POD_REGULAR_RESTARTS,
  // USE_SENTRY_APP_MONITORING_LOCAL,
  ISO_4217_CURRENCY_CODE_BRITISH_POUND,
  STREAMY3_OFFICIAL_ADDRESS_STATE_CODE,
  ISO_4217_CURRENCY_CODE_CANADA_DOLLAR,
  JSON_LD_PRODUCT_LOGO_1500_X_1500_AVIF,
  FORCE_NO_PLAYLIST_EDITS_PRACTICAL_POD,
  MIN_PRICE_SOUNDTRACK_YOUR_BRAND_STR_ALT,
  // USE_SENTRY_APP_MONITORING_PRODUCTION,
  PRESETS_AND_STATIONS_ADMIN_WEB2_USER_ID,
  DEFAULT_SUBSCRIPTION_PRICE_USD_HARDWARE,
  DEFAULT_SUBSCRIPTION_PRICE_CAD_HARDWARE,
  DEFAULT_SUBSCRIPTION_PRICE_EURO_HARDWARE,
  PRIMARY_IMAGE_PLACEHOLDER_LOCAL_DARK_PNG,
  DEFAULT_SUBSCRIPTION_PRICE_POUND_HARDWARE,
  PRIMARY_IMAGE_PLACEHOLDER_LOCAL_LIGHT_PNG,
  PRIMARY_IMAGE_PLACEHOLDER_LOCAL_DARK_AVIF,
  PRIMARY_IMAGE_PLACEHOLDER_LOCAL_LIGHT_AVIF,
  POST_NEW_APP_VERSION_RELOAD_QUERYSTRING_FIELD,
  SECONDS_TO_PING_FOR_OFFLINE_OR_NEW_APP_VERSION,
  POST_NEW_APP_VERSION_RELOAD_QUERYSTRING_FIELD_VALUE,
  MAXIMUM_MINUTES_TO_NOT_RELOAD_AFTER_APP_NEEDS_RELOAD,
  JSON_LD_PRODUCT_ICON_OF_GENERIC_PROVIDER_1500_X_1500_AVIF,
  SECONDS_TO_PING_FOR_OFFLINE_OR_NEW_APP_VERSION_MOBILE_ONLY,
  GLOBAL_RAW_ENTRIES_ARTISTS_AND_GENRECONTRACTS_FETCH_AND_RESPONSE_CACHE_REVALIDATE_MINUTES,
} from '../app/utilities/config'
import { GET_CONTRACTS_FOR_GENRES_CALLSTRING } from '../app/utilities/genres'
import {
  shuffleSongs,
  getAllBulkLicensedEntries,
  mergeSongsWithSuppliedEntries,
} from '../app/utilities/entries'
import {
  saveUser,
  getCharges,
  getInvitation,
  saveInvitation,
  getSubscriptions,
  isUserSubscribed,
  getStripeCurrency,
  GET_USER_CALLSTRING,
  getSubscriptionUsers,
  isUserMaybeSubscribed,
  unsubscribeBusinessUser,
  isActiveStripeSubscription,
  isDeactivationScheduledYear,
  USER_SCHEDULED_FOR_DELETION_SUBSCRIBED_STR,
} from '../app/utilities/account'
import {
  toast,
  toastError,
  clearToasts,
  toastSticky,
  declineInvitation,
  toastWithSubscribeToday,
} from '../app/utilities/notification'
import {
  OG_TYPE,
  isKorean,
  isFrench,
  isSpanish,
  isEnglish,
  translate,
  BRAND_NAME,
  PRODUCT_NAME,
  OG_SITE_NAME,
  HOME_PS_LOGO,
  TITLE_TAG_400,
  TITLE_TAG_404,
  TITLE_TAG_500,
  OG_IMAGE_WIDTH,
  OG_IMAGE_HEIGHT,
  isValidLanguage,
  JSON_LD_SITE_NAME,
  ROCKBOT_PRODUCT_NAME,
  getNavigatorLanguage,
  LANGUAGE_CODE_KOREAN,
  LANGUAGE_CODE_FRENCH,
  JUKEBOXY_PRODUCT_NAME,
  LANGUAGE_CODE_SPANISH,
  LANGUAGE_CODE_ENGLISH,
  TITLE_TAG_HOME_FRENCH,
  TITLE_TAG_HOME_KOREAN,
  TITLE_TAG_HOME_ENGLISH,
  TITLE_TAG_HOME_SPANISH,
  TITLE_TAG_ERROR_FRENCH,
  TITLE_TAG_ERROR_KOREAN,
  MOOD_MEDIA_PRODUCT_NAME,
  TITLE_TAG_ERROR_ENGLISH,
  TITLE_TAG_ERROR_SPANISH,
  TITLE_TAG_SEARCH_FRENCH,
  TITLE_TAG_SEARCH_KOREAN,
  TITLE_TAG_STUDIO_FRENCH,
  TITLE_TAG_STUDIO_KOREAN,
  CLOUD_COVER_PRODUCT_NAME,
  TITLE_TAG_SEARCH_ENGLISH,
  TITLE_TAG_DEFAULT_FRENCH,
  TITLE_TAG_DEFAULT_KOREAN,
  TITLE_TAG_SEARCH_SPANISH,
  TITLE_TAG_CONTACT_FRENCH,
  TITLE_TAG_CONTACT_KOREAN,
  TITLE_TAG_STUDIO_ENGLISH,
  TITLE_TAG_STUDIO_SPANISH,
  TITLE_TAG_DEFAULT_ENGLISH,
  TITLE_TAG_DEFAULT_SPANISH,
  TITLE_TAG_CONTACT_ENGLISH,
  TITLE_TAG_CONTACT_SPANISH,
  TITLE_TAG_SETTINGS_FRENCH,
  TITLE_TAG_SETTINGS_KOREAN,
  TITLE_TAG_SETTINGS_ENGLISH,
  TITLE_TAG_SETTINGS_SPANISH,
  TITLE_TAG_PLAYLISTS_FRENCH,
  TITLE_TAG_PLAYLISTS_KOREAN,
  TITLE_TAG_FAVORITES_FRENCH,
  TITLE_TAG_FAVORITES_KOREAN,
  TITLE_TAG_HOME_FRENCH_USER,
  TITLE_TAG_HOME_KOREAN_USER,
  TITLE_TAG_PLAYLISTS_ENGLISH,
  TITLE_TAG_PLAYLISTS_SPANISH,
  TITLE_TAG_FAVORITES_ENGLISH,
  TITLE_TAG_FAVORITES_SPANISH,
  TITLE_TAG_HOME_ENGLISH_USER,
  TITLE_TAG_HOME_SPANISH_USER,
  JSON_LD_HAS_OFFER_CATALOG_NAME_EN,
  JSON_LD_HAS_OFFER_CATALOG_NAME_ES,
  JSON_LD_HAS_OFFER_CATALOG_NAME_FR,
  JSON_LD_HAS_OFFER_CATALOG_NAME_KO,
  SOUNDTRACK_YOUR_BRAND_PRODUCT_NAME,
  JSON_LD_SOFTWARE_ONLY_OFFER_DESCRIPTION_ES,
  JSON_LD_SOFTWARE_ONLY_OFFER_DESCRIPTION_FR,
  JSON_LD_SOFTWARE_ONLY_OFFER_DESCRIPTION_KO,
  JSON_LD_SOFTWARE_ONLY_OFFER_DESCRIPTION_DEFAULT,
  JSON_LD_PRODUCT_GENERIC_DESCRIPTION_FOR_COMPETITORS,
  JSON_LD_PRODUCT_GENERIC_DESCRIPTION_FOR_COMPETITORS_ES,
  JSON_LD_PRODUCT_GENERIC_DESCRIPTION_FOR_COMPETITORS_FR,
  JSON_LD_PRODUCT_GENERIC_DESCRIPTION_FOR_COMPETITORS_KO,
  JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_DESCRIPTION,
  JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_DESCRIPTION_ES,
  JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_DESCRIPTION_FR,
  JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_DESCRIPTION_KO,
  JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_FURTHER_DESCRIPTION,
  JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_FURTHER_DESCRIPTION_ES,
  JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_FURTHER_DESCRIPTION_FR,
  JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_FURTHER_DESCRIPTION_KO,
} from '../app/utilities/language'
import {
  canPlaylistAndFave,
  mergeFavoritesWithSongs,
  GET_FAVORITES_CALLSTRING,
  UPSERT_OR_DELETE_FAVORITE_CALLSTRING,
  MERGE_FAVORITES_WITH_SONGS_FUNCTION_STRING,
} from '../app/utilities/favorite'
import { isPresetSample, postPlaylistEntries } from '../app/utilities/playlist'
import {
  INSERT_STREAM_CALLSTRING,
  NO_IP_ADDRESS_PLACEHOLDER,
  NO_LOCAL_IP_ADDRESS_PLACEHOLDER,
  REQUIRE_IP_ADDRESS_TO_LOG_STREAM,
} from '../app/utilities/stream'
import { callKioskExtension } from '../app/utilities/kiosk'
import { GET_ARTISTS_CALLSTRING } from '../app/utilities/artists'
import { useReverseDebounce } from '../app/components/hooks/useReverseDebounce'
import {
  COVER_ART_GENRE_THUMB_BOX_SHADOW_DARK,
  COVER_ART_GENRE_THUMB_BOX_SHADOW_LIGHT,
  COVER_ART_GENRE_THUMB_BOX_SHADOW_PRACTICAL_POD,
} from '../app/utilities/styles'
import {
  TEST_EMBEDDED,
  ACCEPTABLE_SHIPPING_COUNTRIES_ISO3166_FOR_JSON_LD,
  ACCEPTABLE_SOFTWARE_ONLY_REFUND_COUNTRIES_ISO3166_FOR_JSON_LD_1,
  ACCEPTABLE_SOFTWARE_ONLY_REFUND_COUNTRIES_ISO3166_FOR_JSON_LD_2,
  SHIPPING_DESTINATIONS_SOFTWARE_ONLY_REFUND_COUNTRIES_ISO3166_FOR_JSON_LD_1,
  SHIPPING_DESTINATIONS_SOFTWARE_ONLY_REFUND_COUNTRIES_ISO3166_FOR_JSON_LD_2,
} from './api/utils/config'
import { getHowToStepDescriptor } from './api/utils/ai'
import { getBusinessUserCredit } from '../app/utilities/ai'
import {
  SYSTEM_RED_COLOR,
  SYSTEM_WHITE_COLOR,
  SYSTEM_BLACK_COLOR,
  DARK_MODE_ACTION_COLOR,
  LIGHT_MODE_ACTION_COLOR,
  DARK_MODE_BLUE_TINGED_BLACK,
  ZERO_POINT_SIX_FIVE_OPACITY,
  SYSTEM_DARK_GRAY_COLOR_TINGED,
  ZERO_POINT_EIGHT_FIVE_OPACITY,
  LIGHT_MODE_ACCENT_TERTIARY_COLOR,
  DARK_MODE_ACCENT_SECONDARY_COLOR,
} from '../app/utilities/theme'
import { useTelemetryPing } from '../app/components/hooks/useTelemetryPing'

const Link: React.ComponentType<any> = dynamic(() => import('next/link'), {
  ssr: true,
})
const CopyErrorModal: React.ComponentType<any> = dynamic(
  () => import('../app/components/controls/CopyErrorModal'),
  {
    ssr: false,
  },
)
const UnsubscribedModal: React.ComponentType<any> = dynamic(
  () => import('../app/components/controls/UnsubscribedModal'),
  {
    ssr: false,
  },
)
const OfflineModal: React.ComponentType<any> = dynamic(
  () => import('../app/components/controls/OfflineModal'),
  {
    ssr: false,
  },
)
const AddToPlaylistModal: React.ComponentType<any> = dynamic(
  () => import('../app/components/controls/AddToPlaylistModal'),
  {
    ssr: false,
  },
)
const InstallPwaOverlay: React.ComponentType<any> = dynamic(
  () => import('../app/components/controls/InstallPwaOverlay'),
  {
    ssr: true,
  },
)
const UnsupportedDeviceOrBrowser: React.ComponentType<any> = dynamic(
  () => import('../app/components/controls/UnsupportedDeviceOrBrowser'),
  {
    ssr: true,
  },
)
const PlaylistPlayingOverlay: React.ComponentType<any> = dynamic(
  () => import('../app/components/controls/PlaylistPlayingOverlay'),
  {
    ssr: false,
  },
)
const SubscribePitchModal: React.ComponentType<any> = dynamic(
  () => import('../app/components/controls/SubscribePitchModal'),
  {
    ssr: false,
  },
)
const MaskModal: React.ComponentType<any> = dynamic(
  () => import('../app/components/controls/MaskModal'),
  {
    ssr: false,
  },
)
const EnvironmentProtectionModal: React.ComponentType<any> = dynamic(
  () => import('../app/components/controls/EnvironmentProtectionModal'),
  {
    ssr: false,
  },
)
const Layout: React.ComponentType<any> = dynamic(
  () => import('../app/components/layouts/Layout'),
  {
    ssr: true,
  },
)
const SelectLanguageModal: React.ComponentType<any> = dynamic(
  () => import('../app/components/controls/SelectLanguageModal'),
  {
    ssr: false,
  },
)
const ApplePwaHelpModal: React.ComponentType<any> = dynamic(
  () => import('../app/components/controls/ApplePwaHelpModal'),
  {
    ssr: false,
  },
)
const AppleMacPwaHelpModal: React.ComponentType<any> = dynamic(
  () => import('../app/components/controls/AppleMacPwaHelpModal'),
  {
    ssr: false,
  },
)
const ScheduledPlaylistsEnabledModal: React.ComponentType<any> = dynamic(
  () => import('../app/components/controls/ScheduledPlaylistsEnabledModal'),
  {
    ssr: false,
  },
)
const StreamingInTwoOrMoreLocationsModal: React.ComponentType<any> = dynamic(
  () => import('../app/components/controls/StreamingInTwoOrMoreLocationsModal'),
  {
    ssr: false,
  },
)
const DeviceIsAboutToRestartModal: React.ComponentType<any> = dynamic(
  () => import('../app/components/controls/DeviceIsAboutToRestartModal'),
  {
    ssr: false,
  },
)
const AppNeedsToReloadModal: React.ComponentType<any> = dynamic(
  () => import('../app/components/controls/AppNeedsToReloadModal'),
  {
    ssr: false,
  },
)
const LoginToAcceptInvitationModal: React.ComponentType<any> = dynamic(
  () => import('../app/components/controls/LoginToAcceptInvitationModal'),
  {
    ssr: false,
  },
)
const ConfirmInvitationAcceptanceModal: React.ComponentType<any> = dynamic(
  () => import('../app/components/controls/ConfirmInvitationAcceptanceModal'),
  {
    ssr: false,
  },
)
const SubscribedUserCantAcceptInviteModal: React.ComponentType<any> = dynamic(
  () =>
    import('../app/components/controls/SubscribedUserCantAcceptInviteModal'),
  {
    ssr: false,
  },
)
const OnAnotherAccountCantAcceptInviteModal: React.ComponentType<any> = dynamic(
  () =>
    import('../app/components/controls/OnAnotherAccountCantAcceptInviteModal'),
  {
    ssr: false,
  },
)
const OverrideExceptionDateModal: React.ComponentType<any> = dynamic(
  () => import('../app/components/controls/OverrideExceptionDateModal'),
  {
    ssr: false,
  },
)
const MustAcceptInvitationWithSameEmailModal: React.ComponentType<any> =
  dynamic(
    () =>
      import('../app/components/controls/MustAcceptInvitationWithSameEmailModal'),
    {
      ssr: false,
    },
  )
const InvitationWasAlreadyAcceptedModal: React.ComponentType<any> = dynamic(
  () => import('../app/components/controls/InvitationWasAlreadyAcceptedModal'),
  {
    ssr: false,
  },
)
const InvitationWasCancelledModal: React.ComponentType<any> = dynamic(
  () => import('../app/components/controls/InvitationWasCancelledModal'),
  {
    ssr: false,
  },
)
const ScheduleMonitor: React.ComponentType<any> = dynamic(
  () => import('../app/components/controls/ScheduleMonitor'),
  {
    ssr: false,
  },
)
const _App_UserLoggedIn: React.ComponentType<any> = dynamic(
  () => import('../app/components/splits/_App_UserLoggedIn'),
  {
    ssr: false,
  },
)
const CoverArtImage: React.ComponentType<any> = dynamic(
  () => import('../app/components/controls/CoverArtImage'),
  {
    ssr: true,
  },
)

const APPLE_STORE_LISTED: boolean = false
const DELAY_TO_GET_IP_ADDRESS_MS: number = 1
const DELAY_TO_GET_LOCAL_IP_ADDRESS_MS: number = 2
const TIMEOUT_TO_PRELOAD_AUDIO_METADATA: number = 20000
const SENTRY_COMPONENT_NAME: string = '_app'
// const SENTRY_THROTTLED_TIMEOUT_MILLISECONDS: number = 15000
const BAR_PLACES_PRESET_PLAYLIST_ID: number = IS_PRODUCTION ? 35 : 118
const CAFE_PLACES_PRESET_PLAYLIST_ID: number = IS_PRODUCTION ? 36 : 119
const SPA_PLACES_PRESET_PLAYLIST_ID: number = IS_PRODUCTION ? 37 : 120
const PARTY_PLACES_PRESET_PLAYLIST_ID: number = IS_PRODUCTION ? 38 : 121
const SHOP_PLACES_PRESET_PLAYLIST_ID: number = IS_PRODUCTION ? 39 : 122
const CHILL_PLACES_PRESET_PLAYLIST_ID: number = IS_PRODUCTION ? 40 : 123
const PUMP_PLACES_PRESET_PLAYLIST_ID: number = IS_PRODUCTION ? 41 : 124
const LUXURY_PLACES_PRESET_PLAYLIST_ID: number = IS_PRODUCTION ? 42 : 125
const RESTRICT_PRACTICAL_POD_USERS_FROM_ACCESSING_APP_OTHERWISE: boolean = false
const USE_VERCEL_ANALYTICS: boolean = IS_PRODUCTION
const DO_YANDEX_VERIFICATION: boolean = true
const MAX_GAS_ITERATIONS: number = 20
const RETRY_GET_ALL_ENTRIES_MS: number = 1000
const MAXIMUM_RETRIES_GET_ALL_ENTRIES: number = 4
const RETRY_GET_CONTRACT_GENRES_MS: number = 1000
const MAXIMUM_RETRIES_GET_CONTRACT_GENRES: number = 4
const RETRY_GET_ARTISTS_MS: number = 1000
const MAXIMUM_RETRIES_ARTISTS: number = 4
const RETRY_GET_FAVORITES_MS: number = 1000
const MAXIMUM_RETRIES_FAVORITES: number = 4
const darkColors: any = [
  '#d5d7e0',
  '#acaebf',
  '#8c8fa3',
  SYSTEM_DARK_GRAY_COLOR_TINGED,
  '#4d4f66',
  '#34354a',
  DARK_MODE_BLUE_TINGED_BLACK,
  SYSTEM_BLACK_COLOR,
  SYSTEM_BLACK_COLOR,
  SYSTEM_BLACK_COLOR,
]
const Player_i18nAriaLabels: any = {
  player: PRODUCT_NAME,
  progressControl: 'Audio Progress Control',
  volumeControl: 'Volume Control',
  play: 'Play',
  pause: 'Pause',
  rewind: 'Rewind',
  forward: 'Forward',
  previous: 'Previous',
  next: 'Skip',
  loop: 'Disable Loop',
  loopOff: 'Enable Loop',
  volume: 'Mute',
  volumeMute: 'Unmute',
}

export default function App(props: any) {
  const {
    Component,
    pageProps: { ...pageProps },
  } = props
  const pageComponentDisplayName: any = Component?.displayName
  const router: NextRouter = useRouter()
  const hasInvitationToken: boolean = pageProps?.hasInvitationToken == true
  const currentRouterPath: any = router.pathname
  const [
    hasSetFirst_Song_Master_Favorited,
    setHasSetFirst_Song_Master_Favorited,
  ] = useState<boolean>(false)
  const [isMacOsSafariLimitedRender, setIsMacOsSafariLimitedRender] =
    useState<boolean>(false)
  const [isMacOsSafariLessThanVersion15, setIsMacOsSafariLessThanVersion15] =
    useState<boolean>(false)
  if (typeof navigator !== 'undefined')
    useEffect(() => {
      if (isSafari && isMacOs) {
        try {
          const userAgent = navigator.userAgent
          const match = userAgent.match(/Version\/(\d+\.\d+)/)
          if (match && match[1]) {
            setIsMacOsSafariLimitedRender(parseFloat(match[1]) < 16.5)
            setIsMacOsSafariLessThanVersion15(parseFloat(match[1]) < 15)
          }
        } catch (e) {
          if (DEBUG_MODE) console.log(e)
        }
      }
    }, [isMacOs, isSafari])
  const [
    hardwareUserNotLoggedIntoPracticalPod,
    setHardwareUserNotLoggedIntoPracticalPod,
  ] = useState<boolean>(false)
  const triggerMonitorSchedule = useRef<any>(null)
  const [triggerMonitorSchedule_State, setTriggerMonitorSchedule_State] =
    useState<any>(null)
  // const triggerResumePlayOnPracticalPodPowerButtonClick = useRef<any>(null)
  // const [
  //   triggerResumePlayOnPracticalPodPowerButtonClick_State,
  //   setTriggerResumePlayOnPracticalPodPowerButtonClick_State,
  // ] = useState<any>(null)
  useEffect(() => {
    try {
      if (DEBUG_MONITOR_SCHEDULE_TIMERS) console.log('ur-1')
      setTriggerMonitorSchedule_State(triggerMonitorSchedule?.current)
    } catch (e) {
      if (DEBUG_MONITOR_SCHEDULE_TIMERS) console.log('mg-1')
      if (DEBUG_MODE) console.log(e)
    }
  }, [triggerMonitorSchedule?.current])
  function triggerMonitorScheduleFn() {
    try {
      triggerMonitorSchedule.current = uuidv4()
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
    }
  }
  const forceRedrawSearch = useRef<boolean>(false)
  const [addedRightClickHandler, setAddedRightClickHandler] =
    useState<boolean>(false)
  const [retryCountGetAllEntries, setRetryCountGetAllEntries] =
    useState<number>(0)
  const [retryCountGetContractGenres, setRetryCountGetContractGenres] =
    useState<number>(0)
  const [offlineModalOpened, setOfflineModalOpened] = useState<boolean>(false)
  const [wasPlayingBeforeWentOffline, setWasPlayingBeforeWentOffline] =
    useState<boolean>(false)
  const [openedReturnsModal, setOpenedReturnsModal] = useState<boolean>(false)
  const [retryCountGetArtists, setRetryCountGetArtists] = useState<number>(0)
  const [retryCountGetFavorites, setRetryCountGetFavorites] =
    useState<number>(0)
  const [prevSongExecuteTimeoutId, setPrevSongExecuteTimeoutId] =
    useState<NodeJS.Timeout | null>(null)
  const [
    triggerClearPrevSongExecuteTimeoutId,
    setTriggerClearPrevSongExecuteTimeoutId,
  ] = useState<boolean>(false)
  const forceGetPresets = useRef<boolean>(false)
  const [loginEmail, setLoginEmail] = useState<any>(null)
  useEffect(() => {
    try {
      if (pageComponentDisplayName != ACCOUNT_SETTINGS_PAGE_DISPLAY_NAME)
        setLoginEmail(null)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
    }
  }, [pageComponentDisplayName])
  const [triggerSignIn, setTriggerSignIn] = useState<boolean>(false)
  const [favorites, setFavorites] = useState<any>(null)
  const [dismissedInstallPwaOverlay, setDismissedInstallPwaOverlay] =
    useState<boolean>(false)
  const [avifCompatible, setAvifCompatible] = useState<boolean>(true)
  useEffect(() => {
    try {
      const checkAvif = () => {
        return new Promise((resolve) => {
          const img = new Image()
          img.onload = () => {
            const isDecoded = img.naturalWidth > 0
            resolve(isDecoded)
          }
          img.onerror = () => resolve(false)
          img.src =
            'data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAAB0AAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAIAAAACAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQ0MAAAAABNjb2xybmNseAACAAIAAYAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACVtZGF0EgAKCBgANogQEAwgMg8f8D///8WfhwB8+ErK42A='
        })
      }
      checkAvif().then((supported: any) => {
        setAvifCompatible(supported)
      })
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
    }
  }, [])
  const [fadingInPwaInstallOverlay, setFadingInPwaInstallOverlay] =
    useState<boolean>(true)
  const [triggerPlaylistOverlayRefresh, setTriggerPlaylistOverlayRefresh] =
    useState<boolean>(false)
  const [isRunningPwa, setisRunningPwa] = useState<boolean>(false)
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false)
  const lastPause = useRef<Date | null>(null)
  const lastScheduledPlaylistPlayed = useRef<Date | null>(null)
  const lastScheduledPlaylistPlayed_One = useRef<Date | null>(null)
  const lastScheduledPlaylistPlayed_Two = useRef<Date | null>(null)
  const [processStreamTimeoutId_1, setProcessStreamTimeoutId_1] =
    useState<NodeJS.Timeout | null>(null)
  const [processStreamTimeoutId_2, setProcessStreamTimeoutId_2] =
    useState<NodeJS.Timeout | null>(null)
  const [hasInteracted, setHasInteracted] = useState(false)
  const [hasGotPresets, setHasGotPresets] = useState<boolean>(false)
  const [
    playlistToRefreshWithoutReloading,
    setPlaylistToRefreshWithoutReloading,
  ] = useState<any>(null)
  const [hasSetInitialMetadata, setHasSetInitialMetadata] =
    useState<boolean>(false)
  useEffect(() => {
    try {
      let tid: NodeJS.Timeout | null = setTimeout(() => {
        setHasSetInitialMetadata(true)
        try {
          if (tid != null) {
            clearTimeout(tid)
            tid = null
          }
        } catch {}
      }, 200)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }, [])
  const rhapSkipButtonElem1 = useRef<any>(null)
  const rhapSkipButtonElem2 = useRef<any>(null)
  const rhapTotalTimeElem = useRef<any>(null)
  const rhapProgressIndicatorElem = useRef<any>(null)
  const checkNotificationsIntervalId = useRef<any>(null)
  const setCheckNotificationsIntervalId = useCallback((val: any) => {
    try {
      checkNotificationsIntervalId.current = val
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
    }
  }, [])
  // const [sentryWasInit, setSentryWasInit] = useState<boolean>(false)
  // const [sentryThrottled, setSentryThrottled] = useState<boolean>(false)
  const [canShowAudioPlayer, setCanShowAudioPlayer] = useState<boolean>(false)
  const [
    streamingInTwoOrMoreLocationsModalOpened,
    setStreamingInTwoOrMoreLocationsModalOpened,
  ] = useState<boolean>(false)
  // useEffect(() => {
  //   try {
  //     if (
  //       !sentryWasInit &&
  //       ((USE_SENTRY_APP_MONITORING_PRODUCTION && IS_PRODUCTION) ||
  //         (USE_SENTRY_APP_MONITORING_QA && IS_QA) ||
  //         (USE_SENTRY_APP_MONITORING_LOCAL && IS_LOCAL))
  //     ) {
  //       setSentryWasInit(true)
  //       //   Sentry.init({
  //       //     dsn: 'https://a3d5a64545a613bc7c5264a0f4e6b361@o4508608786923520.ingest.us.sentry.io/4508608790593536',
  //       //     tracesSampleRate: 1.0,
  //       //     environment: IS_PRODUCTION ? 'production' : IS_QA ? 'qa' : 'local',
  //       //   })
  //     }
  //   } catch (e) {
  //     if (DEBUG_MODE) console.log(e)
  //   }
  // }, [])
  const logSentryError = useCallback((e: any, componentName: string) => {
    try {
      if (DEBUG_MODE) console.log(e)
      if (DEBUG_MODE) console.log(componentName)
      // if (sentryWasInit && !sentryThrottled)
      //   Sentry.captureMessage(toTrimmedString(e), {
      //     level: 'error',
      //     extra: {
      //       componentName: componentName,
      //       handled: true,
      //     },
      //   })
    } catch (er) {
      if (DEBUG_MODE) console.log(er)
      // setSentryThrottled(true)
      // let tina: NodeJS.Timeout | null = setTimeout(() => {
      //   setSentryThrottled(false)
      //   try {
      //     if (tina != null) {
      //       clearTimeout(tina)
      //       tina = null
      //     }
      //   } catch {}
      // }, SENTRY_THROTTLED_TIMEOUT_MILLISECONDS)
    }
  }, [])
  const [
    afterClearAllNotificationsClicked,
    setAfterClearAllNotificationsClicked,
  ] = useState<boolean>(false)
  const [hoverImage, setHoverImage] = useState<boolean>(false)
  const [isPracticalPod, setIsPracticalPod] = useState<boolean>(false)
  useEffect(() => {
    if (!IS_PRODUCTION || isPracticalPod || !USE_GOOGLE_TAG_MANAGER) return
    try {
      const scheduleIdleWork =
        window.requestIdleCallback || ((cb) => setTimeout(cb, 1))
      scheduleIdleWork(() => {
        const script = document.createElement('script')
        script.async = true
        script.src = `https://www.googletagmanager.com/gtm.js?id=${GOOGLE_TAG_MANAGER_ID}`
        window.dataLayer = window.dataLayer || []
        window.gtag = function () {
          window.dataLayer.push(arguments)
        }
        window.gtag('js', new Date())
        document.head.appendChild(script)
      })
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
    }
  }, [isPracticalPod, IS_PRODUCTION])
  const [hasDeterminedIsPracticalPod, setHasDeterminedIsPracticalPod] =
    useState<boolean>(false)
  // useEffect(() => {
  //   let intervalId: NodeJS.Timeout | null = null
  //   try {
  //     if (hasDeterminedIsPracticalPod && isPracticalPod) {
  //       intervalId = setInterval(() => {
  //         try {
  //           const nextUuid = uuidv4()
  //           triggerResumePlayOnPracticalPodPowerButtonClick.current = nextUuid
  //           setTriggerResumePlayOnPracticalPodPowerButtonClick_State(nextUuid)
  //         } catch (e) {
  //           if (DEBUG_MODE) console.log(e)
  //         }
  //       }, 1000)
  //     }
  //   } catch (e) {
  //     logSentryError(e, SENTRY_COMPONENT_NAME)
  //     if (DEBUG_MODE) console.log(e)
  //   }
  //   return () => {
  //     if (intervalId != null) clearInterval(intervalId)
  //   }
  // }, [isPracticalPod, hasDeterminedIsPracticalPod])
  const KEEP_TAB_AWAKE_AND_MONITOR_SCHEDULE_INTERVAL_MILLISECONDS_STR: string =
    '15000'
  const [triggerUpdateAppStartupDate, setTriggerUpdateAppStartupDate] =
    useState<boolean>(false)
  const [softReloading, setSoftReloading] = useState<boolean>(false)
  const softReload = useCallback(() => {
    try {
      try {
        setTriggerUpdateAppStartupDate(!triggerUpdateAppStartupDate)
        clearInterval(checkNotificationsIntervalId?.current)
        checkNotificationsIntervalId.current = null
      } catch (z: any) {
        if (DEBUG_MODE) console.log(z)
        logSentryError(z, SENTRY_COMPONENT_NAME)
      }
      setSoftReloading(true)
      let timeoutId: NodeJS.Timeout | null = null
      timeoutId = setTimeout(() => {
        setSoftReloading(false)
        try {
          if (timeoutId != null) {
            clearTimeout(timeoutId)
            timeoutId = null
          }
        } catch {}
      }, 500)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
    }
  }, [triggerUpdateAppStartupDate, checkNotificationsIntervalId?.current])
  const chatBotReadyToRender = useRef<boolean>(false)
  if (typeof window !== 'undefined')
    useEffect(() => {
      let timeoutId: NodeJS.Timeout | null = null
      try {
        timeoutId = setTimeout(() => {
          try {
            chatBotReadyToRender.current = true
          } catch (ze: any) {
            if (DEBUG_MODE) console.log(ze)
            logSentryError(ze, SENTRY_COMPONENT_NAME)
          }
        }, 500)
      } catch (e) {
        if (DEBUG_MODE) console.log(e)
        logSentryError(e, SENTRY_COMPONENT_NAME)
      }
      return () => {
        try {
          if (timeoutId != null) {
            clearTimeout(timeoutId)
            timeoutId = null
          }
        } catch {}
      }
    }, [])
  useEffect(() => {
    ;(function hardResetMagicCache() {
      try {
        const COMPLETED_FLAG = 'magic_migration_v33_reset_v1'
        if (typeof window === 'undefined' || !window.localStorage) return
        if (!localStorage.getItem(COMPLETED_FLAG)) {
          console.warn(
            'Legacy Magic session state detected. Executing hard cache eviction...',
          )
          const legacyKeys = [
            'magic_auth_tokens',
            'magic_user_metadata',
            'magic_iframe_state',
            'localforage',
          ]
          legacyKeys.forEach((k) => {
            localStorage.removeItem(k)
            sessionStorage.removeItem(k)
          })
          Object.keys(localStorage).forEach((key) => {
            if (key.includes('mg_') || key.includes('magic')) {
              localStorage.removeItem(key)
            }
          })
          if (
            window.indexedDB &&
            typeof window.indexedDB.deleteDatabase === 'function'
          ) {
            window.indexedDB.deleteDatabase('magic_iframe_db')
            window.indexedDB.deleteDatabase('localforage')

            const knownDBs = ['KeyValuePairs', 'magic-sdk', 'magic_storage']
            knownDBs.forEach((db) => window.indexedDB?.deleteDatabase(db))
          }
          if (typeof document !== 'undefined') {
            const cookies = document.cookie.split(';')
            for (let i = 0; i < cookies.length; i++) {
              const cookie = cookies[i]
              const eqPos = cookie.indexOf('=')
              const name =
                eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim()

              if (name.includes('mg_') || name.includes('magic')) {
                document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`
                const domainParts = window.location.hostname.split('.')
                const rootDomain = domainParts.slice(-2).join('.')
                document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${rootDomain};`
              }
            }
          }
          localStorage.setItem(COMPLETED_FLAG, 'true')
          console.log(
            'Cache eviction complete. Environment matches incognito mode.',
          )
          window.location.reload()
        }
      } catch (error) {
        console.error('Critical error during Magic cache eviction:', error)
      }
    })()
  }, [])

  const [openFooterItem, setOpenFooterItem] = useState<string | null>(null)
  const [hasShownOpenFooterItem, setHasShownOpenFooterItem] =
    useState<boolean>(false)
  useEffect(() => {
    try {
      if (openFooterItem == null && !hasShownOpenFooterItem) {
        const newVal: string = toTrimmedString(pageProps?.openFooterItem)
        setOpenFooterItem(newVal)
        if (isNullOrEmptyString(newVal)) setHasShownOpenFooterItem(true)
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }, [pageProps?.openFooterItem])
  const [
    confirmInvitationAcceptanceModalJustClosed,
    setConfirmInvitationAcceptanceModalJustClosed,
  ] = useState<boolean>(false)
  const [hasSetToggleLoopHandler, setHasSetToggleLoopHandler] =
    useState<boolean>(false)
  const [
    triggerRefreshSettingsPageUsersAndInvitations,
    setTriggerRefreshSettingsPageUsersAndInvitations,
  ] = useState<boolean | null>(null)
  const isApple: boolean =
    isIOS || isMacOs || isIPhone13 || isIPad13 || isMobileSafari || isSafari
  const [getRemainingSongsTrigger, setGetRemainingSongsTrigger] = useState<
    boolean | null
  >(null)
  const [firstSongSetBound, setFirstSongSetBound] = useState<boolean>(false)
  const [accumSongs, setAccumSongs] = useState<any>([])
  const [iterations, setIterations] = useState<number>(0)
  const [allEns, setAllEns] = useState<any>([])
  const ct = useRef<any>(null)
  const ga = useRef<any>(null)
  const [insertStreamTimeoutId, setInsertStreamTimeoutId] =
    useState<NodeJS.Timeout | null>(null)
  const [
    triggerClearInsertStreamTimeoutId,
    setTriggerClearInsertStreamTimeoutId,
  ] = useState<boolean>(false)
  const [
    signalReenableLoginFromInvitationControls,
    setSignalReenableLoginFromInvitationControls,
  ] = useState<boolean | null>(false)
  const [initialOrAllSongsTrigger, getInitialOrAllSongsTrigger] =
    useState<boolean>(false)
  const [masterSchedule, setMasterSchedule] = useState<any>(null)
  useEffect(() => {
    try {
      if (signalReenableLoginFromInvitationControls != null)
        setTriggerSignIn(false)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
    }
  }, [signalReenableLoginFromInvitationControls])
  const [triggerGetMasterSchedule, setTriggerGetMasterSchedule] =
    useState<boolean>(false)
  const triggerGetMasterScheduleRef = useRef<boolean>(false)
  useEffect(() => {
    try {
      triggerGetMasterScheduleRef.current = triggerGetMasterSchedule
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
    }
  }, [triggerGetMasterSchedule])
  const [triggerRebindSchedule, setTriggerRebindSchedule] = useState<
    boolean | null
  >(null)
  const [
    scheduledPlaylistScheduled_Begin,
    setScheduledPlaylistScheduled_Begin,
  ] = useState<string | null>(null)
  const [scheduledPlaylistScheduled_End, setScheduledPlaylistScheduled_End] =
    useState<string | null>(null)
  const [signalRefreshNotifications, setSignalRefreshNotifications] =
    useState<boolean>(false)
  const [deviceRestartModalAutoClosed, setDeviceRestartModalAutoClosed] =
    useState<boolean>(false)
  const [signalRefreshUser, setSignalRefreshUser] = useState<boolean>(false)
  const [scheduledPlaylistId, setScheduledPlaylistId] = useState<any>(null)
  const [refreshPlaylistPlayingOverlay, setRefreshPlaylistPlayingOverlay] =
    useState<boolean>(false)
  const [isPlayingPlaylistScheduled, setIsPlayingPlaylistScheduled] =
    useState<boolean>(false)
  const [isPlayingPlaylistExceptionDate, setIsPlayingPlaylistExceptionDate] =
    useState<boolean>(false)
  const [
    isPlayingPlaylistExceptionDateHoliday,
    setIsPlayingPlaylistExceptionDateHoliday,
  ] = useState<boolean>(false)
  const hasAddedRouterEventHandlers = useRef<boolean>(false)
  const [pingLoopStarted, setPingLoopStarted] = useState<boolean>(false)
  const [masterPlaylists, setMasterPlaylists] = useState<any>([])
  const [presetSamples, setPresetSamples] = useState<any>([])
  const [masterPlaylistsAndPresets, setMasterPlaylistsAndPresets] =
    useState<any>([])
  const [streamCredit_TokenId, setStreamCredit_TokenId] = useState<any>(null)
  const [streamCredit_ContractAddress, setStreamCredit_ContractAddress] =
    useState<any>(null)
  const [streamCredit_IPAddress, setStreamCredit_IPAddress] =
    useState<any>(null)
  const [streamCredit_LocalIPAddress, setStreamCredit_LocalIPAddress] =
    useState<any>(null)
  const [streamCredit_StreamStart, setStreamCredit_StreamStart] =
    useState<any>(null)
  const [streamCredit_StreamEnd, setStreamCredit_StreamEnd] =
    useState<any>(null)
  const [streamCredit_Time, setStreamCredit_Time] = useState<any>(null)
  const streamObject: any = useMemo(() => {
    return isNotNullOrEmptyString(streamCredit_IPAddress) &&
      isNotNullOrEmptyString(streamCredit_ContractAddress) &&
      isNotNullOrEmptyString(streamCredit_TokenId) &&
      isNotNullOrEmptyString(streamCredit_StreamStart) &&
      isNotNullOrEmptyString(streamCredit_StreamEnd) &&
      isNotNullOrEmptyString(streamCredit_Time)
      ? {
          tokenId: streamCredit_TokenId,
          contractAddress: streamCredit_ContractAddress,
          ipAddress: streamCredit_IPAddress,
          localIPAddress: isNullOrEmptyString(streamCredit_LocalIPAddress)
            ? NO_LOCAL_IP_ADDRESS_PLACEHOLDER
            : streamCredit_LocalIPAddress,
          streamStart: streamCredit_StreamStart,
          streamEnd: streamCredit_StreamEnd,
          time: streamCredit_Time,
        }
      : null
  }, [
    streamCredit_ContractAddress,
    streamCredit_LocalIPAddress,
    streamCredit_StreamStart,
    streamCredit_StreamEnd,
    streamCredit_IPAddress,
    streamCredit_TokenId,
    streamCredit_Time,
  ])
  const signalRefreshMasterPlaylistsRef = useRef<boolean>(false)
  const debouncedStreamObject: any = useDebounce(streamObject, 100)
  const [signalCheckClearIntervals, setSignalCheckClearIntervals] = useState<
    boolean | null
  >(false)
  const [userDeviceRestartTime, setUserDeviceRestartTime] = useState<any>(null)
  const [userAppReloadTime, setUserAppReloadTime] = useState<any>(null)
  const appStartupDate: Date = useMemo(() => {
    return new Date()
  }, [triggerUpdateAppStartupDate])
  const [signalRefreshMasterPlaylists, setSignalRefreshMasterPlaylists] =
    useState<boolean>(false)
  useEffect(() => {
    try {
      signalRefreshMasterPlaylistsRef.current = signalRefreshMasterPlaylists
    } catch {}
  }, [signalRefreshMasterPlaylists])
  const signalRefreshMasterPlaylistsDebounced: boolean = useDebounce(
    signalRefreshMasterPlaylists,
    550,
  )
  const [masterScheduleWasNull, setMasterScheduleWasNull] =
    useState<boolean>(false)
  const [selectLanguageModalOpened, setSelectLanguageModalOpened] =
    useState<boolean>(false)
  const [playingPlayingOverlayOpened, setPlayingPlayingOverlayOpened] =
    useState<boolean>(false)
  const [inIframe, setInIframe] = useState<boolean>(false)
  if (typeof window !== 'undefined')
    useEffect(() => {
      try {
        setInIframe(window.self !== window.top)
      } catch (e) {
        if (DEBUG_MODE) console.log(e)
        logSentryError(e, SENTRY_COMPONENT_NAME)
      }
    }, [window])
  const dontLoad: boolean = isNullOrUndefined(pageComponentDisplayName)
  const [triggerGetFavorites, setTriggerGetFavorites] = useState<any>(null)
  const [lastLogin, setLastLogin] = useState<Date | null>(null)
  const [playing, setPlaying] = useState<boolean>(false)
  // const [
  //   wasPlayingLessThanFiveSecondsAgo,
  //   setWasPlayingLessThanFiveSecondsAgo,
  // ] = useState<boolean>(false)
  // useEffect(() => {
  //   let timeoutId: NodeJS.Timeout | null = null
  //   try {
  //     if (playing) setWasPlayingLessThanFiveSecondsAgo(true)
  //     else
  //       timeoutId = setTimeout(
  //         () => setWasPlayingLessThanFiveSecondsAgo(false),
  //         5000,
  //       )
  //   } catch (e) {
  //     if (DEBUG_MODE) console.log(e)
  //   }
  //   return () => {
  //     try {
  //       if (timeoutId != null) clearTimeout(timeoutId)
  //     } catch {}
  //   }
  // }, [playing])
  const playingRef = useRef<boolean>(false)
  useEffect(() => {
    playingRef.current = playing
  }, [playing])
  const [unsubscribedModalOpened, setUnsubscribedModalOpened] =
    useState<boolean>(false)
  const [applePwaHelpModalOpened, setApplePwaHelpModalOpened] =
    useState<boolean>(false)
  const [appleMacPwaHelpModalOpened, setAppleMacPwaHelpModalOpened] =
    useState<boolean>(false)
  const { classes } = useStyles()
  const [preloadAudioMetadata, setPreloadAudioMetadata] =
    useState<boolean>(false)
  useEffect(() => {
    let tid: NodeJS.Timeout | null = setTimeout(() => {
      setPreloadAudioMetadata(true)
      try {
        if (tid != null) {
          clearTimeout(tid)
          tid = null
        }
      } catch {}
    }, TIMEOUT_TO_PRELOAD_AUDIO_METADATA)
  }, [])
  const [
    keepTabAwakeDOMManipulationSwitch,
    setKeepTabAwakeDOMManipulationSwitch,
  ] = useState<boolean>(false)
  const [
    keepTabAwakeAndMonitorScheduleLoopStarted,
    setKeepTabAwakeAndMonitorScheduleLoopStarted,
  ] = useState<boolean>(false)
  function keepTabAwakeAndMonitorScheduleLoopMessageReceived() {
    try {
      if (DEBUG_MONITOR_SCHEDULE_TIMERS) console.log('ktalmr')
      setTriggerMonitorSchedule_State(uuidv4())
      setKeepTabAwakeDOMManipulationSwitch(Math.random() < 0.5)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }
  function beginKeepTabAwakeAndMonitorScheduleLoop() {
    try {
      const fnStr = [
        'function tick() { postMessage(1); } setInterval(tick, ' +
          KEEP_TAB_AWAKE_AND_MONITOR_SCHEDULE_INTERVAL_MILLISECONDS_STR +
          ');',
      ]
      const jsBlob = new Blob(fnStr, { type: 'text/javascript' })
      const workerURL = URL.createObjectURL(jsBlob)
      const intervalWorker = new Worker(workerURL)
      intervalWorker.onmessage =
        keepTabAwakeAndMonitorScheduleLoopMessageReceived
      intervalWorker.onmessageerror = (ev: any) => {
        try {
          if (DEBUG_MODE) console.log(ev)
          logSentryError('worker.onmessageerror - 14', SENTRY_COMPONENT_NAME)
        } catch (zae: any) {
          if (DEBUG_MODE) console.log(zae)
          logSentryError(zae, SENTRY_COMPONENT_NAME)
        }
      }
      intervalWorker.onerror = (ev: any) => {
        try {
          if (DEBUG_MODE) console.log(ev)
        } catch (zae: any) {
          if (DEBUG_MODE) console.log(zae)
        }
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }
  const consecutiveIsOnlineFalse = useRef<number>(0)
  useEffect(() => {
    try {
      if (!hasDeterminedIsPracticalPod) return
      if (!keepTabAwakeAndMonitorScheduleLoopStarted && preloadAudioMetadata) {
        setKeepTabAwakeAndMonitorScheduleLoopStarted(true)
        beginKeepTabAwakeAndMonitorScheduleLoop()
      }
      if (!pingLoopStarted) {
        setPingLoopStarted(true)
        async function runCheck() {
          let networkUp: boolean = true
          const checkNetworkStatus = (await import('is-online')).default
          networkUp = await checkNetworkStatus()
          const consecNetworkDown: number = !networkUp
            ? consecutiveIsOnlineFalse?.current + 1
            : 0
          consecutiveIsOnlineFalse.current = consecNetworkDown
          if (playingRef?.current && consecNetworkDown > 1) pauseSong()
          setIsOnline(networkUp)
        }
        setInterval(
          runCheck,
          (!isMobileOnly
            ? SECONDS_TO_PING_FOR_OFFLINE_OR_NEW_APP_VERSION
            : SECONDS_TO_PING_FOR_OFFLINE_OR_NEW_APP_VERSION_MOBILE_ONLY) *
            1000,
        )
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }, [
    isMobileOnly,
    isPracticalPod,
    playingRef?.current,
    preloadAudioMetadata,
    hasDeterminedIsPracticalPod,
  ])
  const [colorScheme, setColorScheme] = useState<ColorScheme>('dark')
  const dark: boolean = isMobileOnly || colorScheme === 'dark'
  const [navigating, setNavigating] = useState<any>(null)
  const [quickSearchOpened, setQuickSearchOpened] = useState<boolean>(false)
  const [secondCanShowAudioPlayer, setSecondCanShowAudioPlayer] =
    useState<boolean>(false)
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null
    try {
      setSecondCanShowAudioPlayer(true)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
    }
    return () => {
      try {
        if (timeoutId != null) clearTimeout(timeoutId)
      } catch {}
    }
  }, [])
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null
    try {
      setCanShowAudioPlayer(true)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
    }
    return () => {
      try {
        if (timeoutId != null) clearTimeout(timeoutId)
      } catch {}
    }
  }, [])
  const [useEuro, setUseEuro] = useState<boolean>(false)
  const [useBritishPound, setUseBritishPound] = useState<boolean>(false)
  const [useCanadianDollar, setUseCanadianDollar] = useState<boolean>(false)
  const [user, setUser] = useState<any>(null)
  const [notifications, setNotifications] = useState<any>([])
  const [addedWakeLockHandler, setAddedWakeLockHandler] =
    useState<boolean>(false)
  const [songContractAddress, setSongContractAddress] = useState<any>('')
  const [songTokenId, setSongTokenId] = useState<any>('')
  const toggleColorScheme = useCallback(
    (value?: ColorScheme) =>
      setColorScheme(
        isMobileOnly
          ? 'dark'
          : value || (colorScheme === 'dark' ? 'light' : 'dark'),
      ),
    [isMobileOnly, colorScheme],
  )
  const boxShadowCoverArtGenreThumb: string = isPracticalPod
    ? COVER_ART_GENRE_THUMB_BOX_SHADOW_PRACTICAL_POD
    : dark
      ? COVER_ART_GENRE_THUMB_BOX_SHADOW_DARK
      : COVER_ART_GENRE_THUMB_BOX_SHADOW_LIGHT
  const [songs_Master, setSongs_Master] = useState<any>([])
  const [songs_Master_Original, setSongs_Master_Original] = useState<any>([])
  const [song_Master, setSong_Master] = useState<any>(null)
  const [favoritesPageFavorites, setFavoritesPageFavorites] = useState<any>([])
  const [gotFavorites, setGotFavorites] = useState<boolean>(false)
  const gotFavoritesDebounced = useDebounce(gotFavorites, 1000)
  const [
    isSubscribePassOrSubscribePassReloaded,
    setIsSubscribePassOrSubscribePassReloaded,
  ] = useState<boolean>(false)
  const [isInvitationPass, setIsInvitationPass] = useState<boolean>(false)
  const [
    hasPausedForCurrentTimeWithinOneMinuteAfterEndOfSchedule,
    setHasPausedForCurrentTimeWithinOneMinuteAfterEndOfSchedule,
  ] = useState<boolean>(false)
  const [
    hasPausedForCurrentTimeWithinOneMinuteAfterEndOfSchedule_One,
    setHasPausedForCurrentTimeWithinOneMinuteAfterEndOfSchedule_One,
  ] = useState<boolean>(false)
  const [
    hasPausedForCurrentTimeWithinOneMinuteAfterEndOfSchedule_Two,
    setHasPausedForCurrentTimeWithinOneMinuteAfterEndOfSchedule_Two,
  ] = useState<boolean>(false)
  const turnOnHasPausedForCurrentTimeWithinOneMinuteAfterEndOfSchedule =
    useCallback(() => {
      setHasPausedForCurrentTimeWithinOneMinuteAfterEndOfSchedule(true)
      let tela: NodeJS.Timeout | null = setTimeout(() => {
        setHasPausedForCurrentTimeWithinOneMinuteAfterEndOfSchedule(false)
        try {
          if (tela != null) {
            clearTimeout(tela)
            tela = null
          }
        } catch {}
      }, 64000)
    }, [])
  const turnOnHasPausedForCurrentTimeWithinOneMinuteAfterEndOfSchedule_One =
    useCallback(() => {
      setHasPausedForCurrentTimeWithinOneMinuteAfterEndOfSchedule_One(true)
      let tela: NodeJS.Timeout | null = setTimeout(() => {
        setHasPausedForCurrentTimeWithinOneMinuteAfterEndOfSchedule_One(false)
        try {
          if (tela != null) {
            clearTimeout(tela)
            tela = null
          }
        } catch {}
      }, 64000)
    }, [])
  const turnOnHasPausedForCurrentTimeWithinOneMinuteAfterEndOfSchedule_Two =
    useCallback(() => {
      setHasPausedForCurrentTimeWithinOneMinuteAfterEndOfSchedule_Two(true)
      let tela: NodeJS.Timeout | null = setTimeout(() => {
        setHasPausedForCurrentTimeWithinOneMinuteAfterEndOfSchedule_Two(false)
        try {
          if (tela != null) {
            clearTimeout(tela)
            tela = null
          }
        } catch {}
      }, 64000)
    }, [])
  const hasAScheduledPlaylistSoForceInteractionWithTheDocument =
    useRef<boolean>(false)
  const [
    hasShownScheduledPlaylistsEnabledModal,
    setHasShownScheduledPlaylistsEnabledModal,
  ] = useState<boolean>(false)
  const [
    scheduledPlaylistsEnabledModalOpened,
    setScheduledPlaylistsEnabledModalOpened,
  ] = useState<boolean>(false)
  const [
    deviceIsAboutToRestartModalOpened,
    setDeviceIsAboutToRestartModalOpened,
  ] = useState<boolean>(false)
  const [appNeedsToReloadModalOpened, setAppNeedsToReloadModalOpened] =
    useState<boolean>(false)
  const [
    isAuthenticatedUserAndSubscribed,
    setIsAuthenticatedUserAndSubscribed,
  ] = useState<boolean>(false)
  const [
    isAuthenticatedUserAndMaybeSubscribed,
    setIsAuthenticatedUserAndMaybeSubscribed,
  ] = useState<boolean>(false)
  const [
    loginToAcceptInvitationModalOpened,
    setLoginToAcceptInvitationModalOpened,
  ] = useState<boolean>(false)
  const [pendingInvitationEmail, setPendingInvitationEmail] =
    useState<any>(null)
  const [pendingInvitation, setPendingInvitation] = useState<any>(null)
  const [
    confirmInvitationAcceptanceModalOpened,
    setConfirmInvitationAcceptanceModalOpened,
  ] = useState<boolean>(false)
  const [
    subscribedUserCantAcceptInviteModalOpened,
    setSubscribedUserCantAcceptInviteModalOpened,
  ] = useState<boolean>(false)
  const [
    overrideExceptionDateModalOpened,
    setOverrideExceptionDateModalOpened,
  ] = useState<boolean>(false)
  const [
    onAnotherAccountCantAcceptInviteModalOpened,
    setOnAnotherAccountCantAcceptInviteModalOpened,
  ] = useState<boolean>(false)
  const [
    hasDecidedWhichInvitationDialogueToUse,
    setHasDecidedWhichInvitationDialogueToUse,
  ] = useState<boolean>(false)
  const [evaluateInvitationDelayElapsed, setEvaluateInvitationDelayElapsed] =
    useState<boolean>(false)
  const [
    mustAcceptInvitationWithSameEmailModalOpened,
    setMustAcceptInvitationWithSameEmailModalOpened,
  ] = useState<boolean>(false)
  const [
    invitationWasAlreadyAcceptedModalOpened,
    setInvitationWasAlreadyAcceptedModalOpened,
  ] = useState<boolean>(false)
  const [
    invitationWasCancelledModalOpened,
    setInvitationWasCancelledModalOpened,
  ] = useState<boolean>(false)
  const [chromeDeviceDataResponse, setChromeDeviceDataResponse] =
    useState<any>(null)
  const [nonSubscribedPlayCountLimit, setNonSubscribedPlayCountLimit] =
    useState<number>(60)
  useEffect(() => {
    setNonSubscribedPlayCountLimit(
      hardwareUserNotLoggedIntoPracticalPod ? 1 : isPracticalPod ? 10 : 30,
    )
  }, [isPracticalPod, hardwareUserNotLoggedIntoPracticalPod])
  const [hasUsedNonSubscribedPlayLimit, setHasUsedNonSubscribedPlayLimit] =
    useState<boolean>(false)
  const [nonSubscribedPlayCount, setNonSubscribedPlayCount] =
    useState<number>(0)
  const [primaryImagePlaceholder, setPrimaryImagePlaceholder] =
    useState<string>('')
  useEffect(() => {
    setPrimaryImagePlaceholder(
      avifCompatible === true
        ? dark
          ? PRIMARY_IMAGE_PLACEHOLDER_LOCAL_DARK_AVIF
          : PRIMARY_IMAGE_PLACEHOLDER_LOCAL_LIGHT_AVIF
        : dark
          ? PRIMARY_IMAGE_PLACEHOLDER_LOCAL_DARK_PNG
          : PRIMARY_IMAGE_PLACEHOLDER_LOCAL_LIGHT_PNG,
    )
  }, [dark, avifCompatible])
  const [isOnline, setIsOnline] = useState<boolean>(true)
  const barPreset_Sample = useRef<any>(null)
  const caffePreset_Sample = useRef<any>(null)
  const spaPreset_Sample = useRef<any>(null)
  const partyPreset_Sample = useRef<any>(null)
  const shopPreset_Sample = useRef<any>(null)
  const chillPreset_Sample = useRef<any>(null)
  const pumpPreset_Sample = useRef<any>(null)
  const luxuryPreset_Sample = useRef<any>(null)
  useEffect(() => {
    try {
      if (presetSamples?.length > 7) {
        barPreset_Sample.current = presetSamples?.find(
          (ps: any) => ps?.playlist_id == BAR_PLACES_PRESET_PLAYLIST_ID,
        )
        caffePreset_Sample.current = presetSamples?.find(
          (ps: any) => ps?.playlist_id == CAFE_PLACES_PRESET_PLAYLIST_ID,
        )
        spaPreset_Sample.current = presetSamples?.find(
          (ps: any) => ps?.playlist_id == SPA_PLACES_PRESET_PLAYLIST_ID,
        )
        presetSamples?.find(
          (ps: any) => ps?.playlist_id == PARTY_PLACES_PRESET_PLAYLIST_ID,
        )
        partyPreset_Sample.current = presetSamples?.find(
          (ps: any) => ps?.playlist_id == PARTY_PLACES_PRESET_PLAYLIST_ID,
        )
        shopPreset_Sample.current = presetSamples?.find(
          (ps: any) => ps?.playlist_id == SHOP_PLACES_PRESET_PLAYLIST_ID,
        )
        chillPreset_Sample.current = presetSamples?.find(
          (ps: any) => ps?.playlist_id == CHILL_PLACES_PRESET_PLAYLIST_ID,
        )
        pumpPreset_Sample.current = presetSamples?.find(
          (ps: any) => ps?.playlist_id == PUMP_PLACES_PRESET_PLAYLIST_ID,
        )
        luxuryPreset_Sample.current = presetSamples?.find(
          (ps: any) => ps?.playlist_id == LUXURY_PLACES_PRESET_PLAYLIST_ID,
        )
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }, [presetSamples?.length])
  function setSong_Master_Unfavorited() {
    try {
      song_Master.favorited = false
      setSong_Master(song_Master)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
    }
  }
  const [toastedSongPlaysLeft, setToastedSongPlaysLeft] =
    useState<boolean>(false)
  useEffect(() => {
    try {
      if (isOnline && song_Master != null) {
        if (!isAuthenticatedUserAndSubscribed) {
          const newCount: number = nonSubscribedPlayCount + 1
          const playsRemaining: number =
            nonSubscribedPlayCountLimit - newCount + 1
          const shouldToastOnMobile = isMobileOnly ? newCount % 5 === 0 : true
          if (!hasUsedNonSubscribedPlayLimit) {
            setNonSubscribedPlayCount(newCount)
            if (
              !toastedSongPlaysLeft &&
              newCount > 1 &&
              (!isMobileOnly || shouldToastOnMobile) &&
              newCount <= nonSubscribedPlayCountLimit &&
              (document.visibilityState === 'visible' ||
                newCount == nonSubscribedPlayCountLimit)
            ) {
              setToastedSongPlaysLeft(true)
              setTimeout(() => setToastedSongPlaysLeft(false), 5000)
              if (
                newCount == nonSubscribedPlayCountLimit &&
                limitedSubscriptionPitchRender &&
                !dark
              ) {
              } else if (
                pageComponentDisplayName != ACCOUNT_SETTINGS_PAGE_DISPLAY_NAME
              )
                toastWithSubscribeToday(
                  translate(
                    `You have ${playsRemaining.toString()} plays left in this session.`,
                    `Quedan ${playsRemaining.toString()} canciones por tocar en esta sesión.`,
                    `Il reste ${playsRemaining.toString()} chansons à jouer dans cette session.`,
                    `이 세션에는 ${playsRemaining.toString()}개의 재생이 남았습니다.`,
                    selectedLanguage,
                  ),
                  dark,
                  translate(
                    'Subscribe for unlimited music.',
                    'Suscríbete para música ilimitada.',
                    'Abonnez-vous pour de la musique illimitée.',
                    '무제한 음악 구독하기',
                    selectedLanguage,
                  ),
                  () => {
                    try {
                      navigateRoute('/settings')
                    } catch (en: any) {
                      if (DEBUG_MODE) console.log(en)
                    }
                  },
                  selectedLanguage,
                  width,
                )
              else
                toast(
                  translate(
                    `${playsRemaining.toString()} plays left. Log in to enjoy unlimited music.`,
                    `Solo quedan ${playsRemaining.toString()} reproducciones. Regístrate para música ilimitada.`,
                    `Plus que ${playsRemaining.toString()} écoutes. Connectez-vous pour musique en illimité.`,
                    `최대 ${playsRemaining.toString()} 재생할 수 있습니다. 구독하시면 음악을 무제한으로 즐기실 수 .`,
                    selectedLanguage,
                  ),
                  'neutral',
                )
            }
          }
          if (newCount > nonSubscribedPlayCountLimit) {
            setHasUsedNonSubscribedPlayLimit(true)
            toggleSubscribePitchModalOpened(true, false, false)
          }
        } else {
          if (!hardwareUserNotLoggedIntoPracticalPod) {
            setHasUsedNonSubscribedPlayLimit(false)
            toggleSubscribePitchModalOpened(false, false, false)
          } else setHasUsedNonSubscribedPlayLimit(true)
        }
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }, [isOnline, isMobileOnly, song_Master])
  const [songIdx_Master, setSongIdx_Master] = useState<any>(-1)
  const [songUrl_Master, setSongUrl_Master] = useState<any>('')
  const [songName_Master, setSongName_Master] = useState<any>('')
  const [songGenres_Master, setSongGenres_Master] = useState<any>([])
  const [songName_Master_Small, setSongName_Master_Small] = useState<any>('')
  const [song_StreamCount_Master, setSong_StreamCount_Master] = useState<any>(0)
  const [song_StreamStart_Master, setSong_StreamStart_Master] =
    useState<any>(null)
  const [
    songCoverImageLocalOrExternalURL_Master,
    setSongCoverImageLocalOrExternalURL_Master,
  ] = useState<any>('')
  const [lastPlayedDate, setLastPlayedDate] = useState<Date | null>(null)
  const [lastTeedUpDate, setLastTeedUpDate] = useState<Date | null>(null)
  const [lastPlayedDate_One, setLastPlayedDate_One] = useState<Date | null>(
    null,
  )
  const [lastTeedUpDate_One, setLastTeedUpDate_One] = useState<Date | null>(
    null,
  )
  const [lastPlayedDate_Two, setLastPlayedDate_Two] = useState<Date | null>(
    null,
  )
  const [lastTeedUpDate_Two, setLastTeedUpDate_Two] = useState<Date | null>(
    null,
  )
  const [localIPAddress, setLocalIPAddress] = useState<any>(null)
  const [ipAddress, setIPAddress] = useState<any>(null)
  const [ipAddressCountryCode, setIpAddressCountryCode] = useState<any>(null)
  const [gotSelectedLanguageFromCookies, setGotSelectedLanguageFromCookies] =
    useState<boolean>(false)
  const [cookies, setCookie] = useCookies<any>(['practical.stream'])
  const [selectedLanguage, setSelectedLanguage] = useState<string>(
    isNotNullOrEmptyString(pageProps?.initialLang) &&
      (isEnglish(pageProps?.initialLang) ||
        isFrench(pageProps?.initialLang) ||
        isKorean(pageProps?.initialLang) ||
        isSpanish(pageProps?.initialLang))
      ? toTrimmedStringLowerCase(pageProps?.initialLang)
      : LANGUAGE_CODE_ENGLISH,
  )
  const toggleSelectedLanguage = useCallback(
    async (selLang: any) => {
      try {
        const lang: string = isNotNullOrEmptyString(selLang)
          ? selLang
          : LANGUAGE_CODE_ENGLISH
        setSelectedLanguage(lang)
        setCookie(SELECTED_LANGUAGE_COOKIE_NAME, lang, COOKIE_CONFIG)
        if (user) {
          let cloneUser: any = structuredClone(user)
          cloneUser.defaultLanguage =
            lang.indexOf(LANGUAGE_CODE_SPANISH) == 0
              ? LANGUAGE_CODE_SPANISH
              : lang.indexOf(LANGUAGE_CODE_FRENCH) == 0
                ? LANGUAGE_CODE_FRENCH
                : lang.indexOf(LANGUAGE_CODE_KOREAN) == 0
                  ? LANGUAGE_CODE_KOREAN
                  : LANGUAGE_CODE_ENGLISH
          const ret: boolean = await saveUser(cloneUser)
          if (!ret)
            logSentryError('Failed to save user - 6.', SENTRY_COMPONENT_NAME)
        }
      } catch (e) {
        if (DEBUG_MODE) console.log(e)
        logSentryError(e, SENTRY_COMPONENT_NAME)
      }
    },
    [user],
  )
  const [firstNavigatedComponent, setFirstNavigatedComponent] =
    useState<any>('')
  const [web2UserIdLoggedIn, setWeb2UserIdLoggedIn] = useState<boolean | null>(
    null,
  )
  const [wholeSongSetRetrieved, setWholeSongSetRetrieved] =
    useState<boolean>(false)
  const [wholeSongSetBound, setWholeSongSetBound] = useState<boolean>(false)
  useEffect(() => {
    try {
      if (
        signalCheckClearIntervals != null &&
        isNotNullOrEmptyString(checkNotificationsIntervalId?.current)
      ) {
        clearInterval(checkNotificationsIntervalId?.current)
        setSignalCheckClearIntervals(null)
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
    }
  }, [signalCheckClearIntervals, web2UserIdLoggedIn])
  const [hideTimepickers, setHideTimepickers] = useState<boolean>(false)
  const [timePickerScript, setTimepickerScript] = useState<any>(null)
  useEffect(() => {
    setHideTimepickers(true)
    let tid: NodeJS.Timeout | null = setTimeout(() => {
      setTimepickerScript(
        web2UserIdLoggedIn == true &&
          (isNotNullOrEmptyString(firstNavigatedComponent) ||
            pageComponentDisplayName == ACCOUNT_SETTINGS_PAGE_DISPLAY_NAME ||
            pageComponentDisplayName == PLAYLIST_PAGE_DISPLAY_NAME) ? (
          <link
            rel="stylesheet"
            type="text/css"
            href={
              HOST +
              '/styles/timepicker-' +
              (dark ? 'dark' : 'light') +
              '.css?v=14'
            }
          />
        ) : (
          <></>
        ),
      )
      let tid2: NodeJS.Timeout | null = setTimeout(() => {
        setHideTimepickers(false)
        try {
          if (tid2 != null) {
            clearTimeout(tid2)
            tid2 = null
          }
        } catch {}
        try {
          if (tid != null) {
            clearTimeout(tid)
            tid = null
          }
        } catch {}
      }, 0)
    }, 0)
  }, [
    dark,
    web2UserIdLoggedIn,
    pageComponentDisplayName,
    firstNavigatedComponent,
  ])
  useEffect(() => {
    try {
      if (!gotSelectedLanguageFromCookies) {
        setGotSelectedLanguageFromCookies(true)
        let selLang: string = LANGUAGE_CODE_ENGLISH
        const initLang: string = toTrimmedString(pageProps?.initialLang)
        let forceEnglish: boolean = false
        try {
          if (isNotNullOrEmptyString(window?.location?.href)) {
            const removedHost = window.location.href
              .toString()
              .trim()
              .toLowerCase()
              .replace(HOST + '/', '')
            if (
              removedHost == LANGUAGE_CODE_KOREAN ||
              removedHost.startsWith(LANGUAGE_CODE_KOREAN + '/')
            )
              selLang = LANGUAGE_CODE_KOREAN
            else if (
              removedHost == LANGUAGE_CODE_FRENCH ||
              removedHost.startsWith(LANGUAGE_CODE_FRENCH + '/')
            )
              selLang = LANGUAGE_CODE_FRENCH
            else if (
              removedHost == LANGUAGE_CODE_SPANISH ||
              removedHost.startsWith(LANGUAGE_CODE_SPANISH + '/')
            )
              selLang = LANGUAGE_CODE_SPANISH
            else if (
              removedHost == LANGUAGE_CODE_ENGLISH ||
              removedHost.startsWith(LANGUAGE_CODE_ENGLISH + '/')
            )
              forceEnglish = true
          }
        } catch (e) {}
        if (selLang == LANGUAGE_CODE_ENGLISH && !forceEnglish) {
          if (
            IS_PRODUCTION &&
            isNotNullOrEmptyString(cookies?.p3_dsr) &&
            isValidLanguage(cookies?.p3_dsr)
          )
            selLang = cookies?.p3_dsr
          else if (
            (IS_QA || IS_LOCAL) &&
            isNotNullOrEmptyString(cookies?.p3_dEe) &&
            isValidLanguage(cookies?.p3_dEe)
          )
            selLang = cookies?.p3_dEe
          else if (
            isFrench(initLang) ||
            isSpanish(initLang) ||
            isKorean(initLang) ||
            isEnglish(initLang)
          )
            selLang = initLang
          else {
            const navLang: string = getNavigatorLanguage()
            if (isNotNullOrEmptyString(navLang) && isValidLanguage(navLang))
              selLang = navLang
          }
        }
        if (selLang?.length > 2 && selLang[2] == '-')
          selLang = selLang?.substring(0, 2)
        toggleSelectedLanguage(selLang)
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }, [])
  const majorVersion: number = parseInt(osVersion, 10)
  const isOldIOS: boolean = isIOS && majorVersion < 13
  const [
    canUseSelectedLanguageForTitleTag,
    setCanUseSelectedLanguageForTitleTag,
  ] = useState<boolean>(false)
  useEffect(() => {
    try {
      let tid: NodeJS.Timeout | null = setTimeout(() => {
        setCanUseSelectedLanguageForTitleTag(true)
        try {
          if (tid != null) {
            clearTimeout(tid)
            tid = null
          }
        } catch {}
      }, 250)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }, [])
  const STUDIO_PAGE_TITLE_TEXT: string =
    !canUseSelectedLanguageForTitleTag &&
    isNotNullOrEmptyString(pageProps?.initialTitle)
      ? pageProps?.initialTitle
      : translate(
          TITLE_TAG_STUDIO_ENGLISH,
          TITLE_TAG_STUDIO_SPANISH,
          TITLE_TAG_STUDIO_FRENCH,
          TITLE_TAG_STUDIO_KOREAN,
          selectedLanguage,
        )
  const HOME_PAGE_TITLE_TEXT: string =
    !canUseSelectedLanguageForTitleTag &&
    isNotNullOrEmptyString(pageProps?.initialTitle)
      ? pageProps?.initialTitle
      : pageProps?.uab != true
        ? translate(
            TITLE_TAG_HOME_ENGLISH_USER,
            TITLE_TAG_HOME_SPANISH_USER,
            TITLE_TAG_HOME_FRENCH_USER,
            TITLE_TAG_HOME_KOREAN_USER,
            selectedLanguage,
          )
        : translate(
            TITLE_TAG_HOME_ENGLISH,
            TITLE_TAG_HOME_SPANISH,
            TITLE_TAG_HOME_FRENCH,
            TITLE_TAG_HOME_KOREAN,
            selectedLanguage,
          )
  const SEARCH_PAGE_TITLE_TEXT: string =
    !canUseSelectedLanguageForTitleTag &&
    isNotNullOrEmptyString(pageProps?.initialTitle)
      ? pageProps?.initialTitle
      : translate(
          TITLE_TAG_SEARCH_ENGLISH,
          TITLE_TAG_SEARCH_SPANISH,
          TITLE_TAG_SEARCH_FRENCH,
          TITLE_TAG_SEARCH_KOREAN,
          selectedLanguage,
        )
  const FAVORITES_PAGE_TITLE_TEXT: string =
    !canUseSelectedLanguageForTitleTag &&
    isNotNullOrEmptyString(pageProps?.initialTitle)
      ? pageProps?.initialTitle
      : translate(
          TITLE_TAG_FAVORITES_ENGLISH,
          TITLE_TAG_FAVORITES_SPANISH,
          TITLE_TAG_FAVORITES_FRENCH,
          TITLE_TAG_FAVORITES_KOREAN,
          selectedLanguage,
        )
  const PLAYLIST_PAGE_TITLE_TEXT: string =
    !canUseSelectedLanguageForTitleTag &&
    isNotNullOrEmptyString(pageProps?.initialTitle)
      ? pageProps?.initialTitle
      : translate(
          TITLE_TAG_PLAYLISTS_ENGLISH,
          TITLE_TAG_PLAYLISTS_SPANISH,
          TITLE_TAG_PLAYLISTS_FRENCH,
          TITLE_TAG_PLAYLISTS_KOREAN,
          selectedLanguage,
        )
  const CONTACT_PAGE_TITLE_TEXT: string =
    !canUseSelectedLanguageForTitleTag &&
    isNotNullOrEmptyString(pageProps?.initialTitle)
      ? pageProps?.initialTitle
      : translate(
          TITLE_TAG_CONTACT_ENGLISH,
          TITLE_TAG_CONTACT_SPANISH,
          TITLE_TAG_CONTACT_FRENCH,
          TITLE_TAG_CONTACT_KOREAN,
          selectedLanguage,
        )
  const SETTINGS_TITLE_TEXT: string =
    !canUseSelectedLanguageForTitleTag &&
    isNotNullOrEmptyString(pageProps?.initialTitle)
      ? pageProps?.initialTitle
      : translate(
          TITLE_TAG_SETTINGS_ENGLISH,
          TITLE_TAG_SETTINGS_SPANISH,
          TITLE_TAG_SETTINGS_FRENCH,
          TITLE_TAG_SETTINGS_KOREAN,
          selectedLanguage,
        )
  const ERROR_PAGE_TITLE_TEXT: string =
    !canUseSelectedLanguageForTitleTag &&
    isNotNullOrEmptyString(pageProps?.initialTitle)
      ? pageProps?.initialTitle
      : translate(
          TITLE_TAG_ERROR_ENGLISH,
          TITLE_TAG_ERROR_SPANISH,
          TITLE_TAG_ERROR_FRENCH,
          TITLE_TAG_ERROR_KOREAN,
          selectedLanguage,
        )
  const player = useRef()
  const [song_Selected, setSong_Selected] = useState<any>(null)
  const isMacSafariDesktopOrTablet: boolean =
    (isDesktop || isTablet) &&
    !isMobileOnly &&
    (isMacOs || isIOS) &&
    (isSafari || isMobileSafari)
  const isMobileOnlySafariIOS: boolean =
    isMobileOnly && isIOS && (isSafari || isMobileSafari)
  const isPwaInstallableApple: boolean =
    isMacSafariDesktopOrTablet || isMobileOnlySafariIOS
  const titleText: string = useMemo(() => {
    return !canUseSelectedLanguageForTitleTag &&
      isNotNullOrEmptyString(pageProps?.initialTitle)
      ? pageProps?.initialTitle
      : pageComponentDisplayName == FIVE_HUNDRED_PAGE_DISPLAY_NAME
        ? TITLE_TAG_404
        : pageComponentDisplayName == FOUR_O_FOUR_PAGE_DISPLAY_NAME
          ? TITLE_TAG_500
          : pageComponentDisplayName == ERROR_PAGE_DISPLAY_NAME
            ? TITLE_TAG_400
            : hasSetInitialMetadata && isMobileOnly
              ? translate(
                  TITLE_TAG_DEFAULT_ENGLISH,
                  TITLE_TAG_DEFAULT_SPANISH,
                  TITLE_TAG_DEFAULT_FRENCH,
                  TITLE_TAG_DEFAULT_KOREAN,
                  selectedLanguage,
                )
              : pageComponentDisplayName == STUDIO_PAGE_DISPLAY_NAME
                ? STUDIO_PAGE_TITLE_TEXT
                : pageComponentDisplayName == SEARCH_PAGE_DISPLAY_NAME
                  ? SEARCH_PAGE_TITLE_TEXT
                  : pageComponentDisplayName ==
                      ACCOUNT_SETTINGS_PAGE_DISPLAY_NAME
                    ? SETTINGS_TITLE_TEXT
                    : pageComponentDisplayName == FAVORITES_PAGE_DISPLAY_NAME
                      ? FAVORITES_PAGE_TITLE_TEXT
                      : pageComponentDisplayName == PLAYLIST_PAGE_DISPLAY_NAME
                        ? PLAYLIST_PAGE_TITLE_TEXT
                        : pageComponentDisplayName == CONTACT_PAGE_DISPLAY_NAME
                          ? CONTACT_PAGE_TITLE_TEXT
                          : pageComponentDisplayName ==
                              FOUR_O_FOUR_PAGE_DISPLAY_NAME
                            ? ERROR_PAGE_TITLE_TEXT
                            : pageComponentDisplayName ==
                                ERROR_PAGE_DISPLAY_NAME
                              ? ERROR_PAGE_TITLE_TEXT
                              : pageComponentDisplayName ==
                                  FIVE_HUNDRED_PAGE_DISPLAY_NAME
                                ? ERROR_PAGE_TITLE_TEXT
                                : pageComponentDisplayName ==
                                    STUDIO_PAGE_DISPLAY_NAME
                                  ? STUDIO_PAGE_TITLE_TEXT
                                  : pageComponentDisplayName ==
                                      HOME_PAGE_DISPLAY_NAME
                                    ? HOME_PAGE_TITLE_TEXT
                                    : HOME_PAGE_TITLE_TEXT
  }, [
    isMobileOnly,
    selectedLanguage,
    SETTINGS_TITLE_TEXT,
    HOME_PAGE_TITLE_TEXT,
    ERROR_PAGE_TITLE_TEXT,
    hasSetInitialMetadata,
    STUDIO_PAGE_TITLE_TEXT,
    SEARCH_PAGE_TITLE_TEXT,
    CONTACT_PAGE_TITLE_TEXT,
    pageComponentDisplayName,
    PLAYLIST_PAGE_TITLE_TEXT,
    FAVORITES_PAGE_TITLE_TEXT,
  ])
  const [rerenderForIOSOrientation, setRerenderforIOSOrientation] =
    useState<boolean>(false)
  const [portrait, setPortrait] = useState<boolean | null>(null)
  const [loading, setLoading] = useState<any>(true)
  const [subscribed, setSubscribed] = useState<any>(null)
  const [subscriptionEnd, setSubscriptionEnd] = useState<any>(null)
  const [subscriptionElapsed, setSubscriptionElapsed] = useState<boolean>(false)
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)
  const web2UserIdLoggedInDebounced: boolean | null = useDebounce(
    web2UserIdLoggedIn,
    500,
  )
  const [pwaInstallShown, setPwaInstallShown] = useState<any>('unset')
  const [pwaDeferredInstallEvent, setPwaDeferredInstallEvent] =
    useState<any>(null)
  const [acceptedPwaInstallation, setAcceptedPwaInstallation] =
    useState<boolean>(false)
  const [web2UserSessionId, setWeb2UserSessionId] = useState<any>(false)
  const TELEMETRY_INTERVAL_MS: number = 30000
  useTelemetryPing({
    web2UserId: web2UserSessionId,
    adminWeb2UserId: user?.adminWeb2UserId,
    isPlaying: playing,
    currentTrack: songName_Master,
    intervalMs: TELEMETRY_INTERVAL_MS,
  })
  const [web2UserSessionEmail, setWeb2UserSessionEmail] = useState<any>(false)
  const [userCanMakePlaylists, setUserCanMakePlaylists] =
    useState<boolean>(false)
  const [userCanSetSchedule, setUserCanSetSchedule] = useState<boolean>(false)
  const [userCanPlayPresets, setUserCanPlayPresets] = useState<boolean>(false)
  const [userCanPlayStations, setUserCanPlayStations] = useState<boolean>(false)
  const isBusinessUserSubscribed: boolean = isUserSubscribed(user)
  useEffect(() => {
    try {
      setUserSubscribed(
        (user === null || user === undefined) && web2UserIdLoggedIn === true
          ? 'unknown'
          : web2UserIdLoggedIn === true && isBusinessUserSubscribed
            ? 'true'
            : 'false',
      )
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }, [user, web2UserIdLoggedIn, isBusinessUserSubscribed])
  const [userSubscribed, setUserSubscribed] = useState<any>('unknown')
  const [canFavoriteAndPlaylist, setCanFavoriteAndPlaylist] = useState<boolean>(
    canPlaylistAndFave(web2UserIdLoggedIn == true, userSubscribed == 'true'),
  )
  const [openToPlaylistScheduleTab, setOpenToPlaylistScheduleTab] =
    useState<boolean>(false)
  const [
    userLastDateWhenOverrodeExceptionDate,
    setUserLastDateWhenOverrodeExceptionDate,
  ] = useState<Date | null>(null)
  useEffect(() => {
    try {
      setCanFavoriteAndPlaylist(
        web2UserIdLoggedIn === true &&
          canPlaylistAndFave(true, userSubscribed == 'true'),
      )
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }, [userSubscribed, web2UserIdLoggedIn])
  const [stripeCustomerId, setStripeCustomerId] = useState<string | null>(null)
  useEffect(() => {
    if (web2UserIdLoggedIn == false) {
      setScheduledPlaylistId(null)
      setIsPlayingPlaylistScheduled(false)
      setIsPlayingPlaylistExceptionDate(false)
      setIsPlayingPlaylistExceptionDateHoliday(false)
      setScheduledPlaylistScheduled_End(null)
      setScheduledPlaylistScheduled_Begin(null)
    }
  }, [web2UserIdLoggedIn])
  const [stripeCharges, setStripeCharges] = useState<any>([])
  const [stripeSubscriptions, setStripeSubscriptions] = useState<any>([])
  const [stripeActiveSeats, setStripeActiveSeats] = useState<number>(0)
  const [stripeActiveHardwareSeats, setStripeActiveHardwareSeats] =
    useState<number>(0)
  const [
    hasResolvedStripeActiveSoftwareOrHardwareSeats,
    setHasResolvedStripeActiveSoftwareOrHardwareSeats,
  ] = useState<boolean>(false)
  const [subscriptionExpiring, setSubscriptionExpiring] =
    useState<boolean>(false)
  const [cancelStripeSubscriptionId, setCancelStripeSubscriptionId] =
    useState<any>(null)
  const [cancelSubscriptionIsHardware, setCancelSubscriptionIsHardware] =
    useState<any>(null)
  const [cancelSubscriptionQuantity, setCancelSubscriptionQuantity] =
    useState<number>(1)
  const [
    modifySubscriptionOriginalQuantity,
    setModifySubscriptionOriginalQuantity,
  ] = useState<any>(-1)
  const [modifySubscriptionIsHardware, setModifySubscriptionIsHardware] =
    useState<boolean>(false)
  const [modifyStripeSubscriptionId, setModifyStripeSubscriptionId] =
    useState<any>(null)
  const [hasFilteredNoSongsArtists, setHasFilteredNoSongsArtists] =
    useState<boolean>(false)
  const throttlePrevSongClick = useRef<boolean>(false)
  const [subscribePitchModalOpened, setSubscribePitchModalOpened] =
    useState<boolean>(false)
  const [addToPlaylistModalOpened, setAddToPlaylistModalOpened] =
    useState<boolean>(false)
  const [
    hasAddedPwaInstallationModeEventHandler,
    setHasAddedPwaInstalltionModeEventHandler,
  ] = useState<boolean>(false)
  const [masterArtists, setMasterArtists] = useState<any>([])
  const [contractGenres, setContractGenres]: any = useState<any>([])
  const allSongsArtistsGenreContractsLoaded =
    wholeSongSetBound && masterArtists?.length > 0
  const [getPlaylistEntries, setGetPlaylistEntries] = useState<boolean>(false)
  const [
    hasResolvedEnvironmentProtectionModal,
    setHasResolvedEnvironmentProtectionModal,
  ] = useState<boolean>(false)
  const [
    environmentProtectionModalOpened,
    setEnvironmentProtectionModalOpened,
  ] = useState<boolean>(false)
  const [muted, setMuted] = useState<boolean>(false)
  const [loop, setLoop] = useState<boolean>(false)
  const [changingSongs_Master, setChangingSongs_Master] =
    useState<boolean>(false)
  const debouncedPlaying: boolean = useDebounce(
    playing,
    changingSongs_Master ? 350 : 175,
  )
  const reverseDebouncedPlaying: any = useReverseDebounce(playing, 2250)
  const debouncedPlayingLong: any = useDebounce(playing, 1000)
  const [playlistSong_LowIdx, setPlaylistSong_LowIdx] = useState<any>(0)
  const [playlistSong_HighIdx, setPlaylistSong_HighIdx] = useState<any>(0)
  const [
    changingSongs_Master_PlaylistDelay,
    setChangingSongs_Master_PlaylistDelay,
  ] = useState<boolean>(false)
  const [height, setHeight] = useState<any>(0)
  const [width, setWidth] = useState<any>(0)
  const isSmallSubscriptionPitchRender: boolean =
    height > width
      ? isNotNullOrUndefined(width) && width < 500
      : isNotNullOrUndefined(height) && height < 500
  isMobileOnly && isSmallSubscriptionPitchRender
  const TEST_MOBILE_ONLY_SUBSCRIPTION_PITCH: boolean = false
  const limitedSubscriptionPitchRender: boolean =
    (isMobileOnly || TEST_MOBILE_ONLY_SUBSCRIPTION_PITCH) &&
    (height > width
      ? isNotNullOrUndefined(width) && width < 500
      : isNotNullOrUndefined(height) && height < 500)
  const isViewportTooSmall: boolean | null = useMemo(() => {
    return width == null || width == 0 ? null : width < 240
  }, [width])
  const largeDisplay: boolean = useMemo(() => {
    return width > 992
  }, [width])
  useScrollRestoration(router)
  const [moveToPlaylistEditId, setMoveToPlaylistEditId] = useState<any>(null)
  const [navigatorLockResolver, setNavigatorLockResolver] = useState<any>(null)
  const [navigatorLockKey, setNavigatorLockKey] = useState<any>(null)
  const [loadedData, setLoadedData] = useState<boolean>(true)
  const [playlistsChanged, setPlaylistsChanged] = useState<boolean | null>(null)
  const audioControlsTempDisabled = useRef<boolean>(false)
  const [rerenderCarousel, setRerenderCarousel] = useState<boolean | null>(null)
  const [lastClickedPlaylistSongs, setLastClickedPlaylistSongs] = useState<any>(
    [],
  )
  const [lastClickedPlaylistName, setLastClickedPlaylistName] = useState<
    string | null
  >(null)
  const [lastClickedPlaylistId, setLastClickedPlaylistId] = useState<
    number | null
  >(-1)
  const [copyErrorModalText, setCopyErrorModalText] = useState<any>('')
  const [copyErrorModalOpened, setCopyErrorModalOpened] =
    useState<boolean>(false)
  const [clipboardCopied, setClipboardCopied] = useState<boolean>(false)
  const [copyKey, setCopyKey] = useState<any>(null)
  const previousRoute = useRef<any>(null)
  const currentRoute = useRef<any>(null)
  const canShowCoverArtInPlayer: boolean = useMemo(() => {
    return (
      (!isPracticalPod || width > 1300) &&
      isNotNullOrEmptyString(songCoverImageLocalOrExternalURL_Master) &&
      largeDisplay
    )
  }, [largeDisplay, isPracticalPod, songCoverImageLocalOrExternalURL_Master])
  const canShowPlaylistPlayingOverlay: boolean = useMemo(() => {
    return (
      web2UserIdLoggedIn == true &&
      isOnline &&
      !hardwareUserNotLoggedIntoPracticalPod &&
      pageComponentDisplayName != FOUR_O_FOUR_PAGE_DISPLAY_NAME &&
      pageComponentDisplayName != FIVE_HUNDRED_PAGE_DISPLAY_NAME &&
      pageComponentDisplayName != ERROR_PAGE_DISPLAY_NAME &&
      (!environmentProtectionModalOpened || !USE_ENV_PROTECTION_MODAL) &&
      userSubscribed == 'true' &&
      playingPlayingOverlayOpened &&
      masterSchedule?.enabled == true &&
      masterPlaylistsAndPresets?.length > 0 &&
      width >= 280 &&
      height >= 355 &&
      isSamePlaylist(
        songs_Master,
        lastClickedPlaylistSongs,
        lastClickedPlaylistId,
        lastClickedPlaylistId,
      )
    )
  }, [
    width,
    height,
    isOnline,
    songs_Master,
    userSubscribed,
    web2UserIdLoggedIn,
    lastClickedPlaylistId,
    pageComponentDisplayName,
    masterSchedule?.enabled,
    lastClickedPlaylistSongs,
    USE_ENV_PROTECTION_MODAL,
    playingPlayingOverlayOpened,
    environmentProtectionModalOpened,
    masterPlaylistsAndPresets?.length,
    hardwareUserNotLoggedIntoPracticalPod,
  ])
  const canShowInstallPwaOverlay: boolean = useMemo(() => {
    return (
      (isNotNullOrEmptyString(firstNavigatedComponent) || !isMobileOnly) &&
      width < 2560 &&
      height > 400 &&
      !inIframe &&
      !dismissedInstallPwaOverlay &&
      pageComponentDisplayName != FOUR_O_FOUR_PAGE_DISPLAY_NAME &&
      pageComponentDisplayName != ERROR_PAGE_DISPLAY_NAME &&
      pageComponentDisplayName != FIVE_HUNDRED_PAGE_DISPLAY_NAME &&
      !environmentProtectionModalOpened &&
      !isViewportTooSmall &&
      !acceptedPwaInstallation &&
      !playingPlayingOverlayOpened &&
      cookies &&
      !isRunningPwa &&
      !isFullscreen &&
      !isPracticalPod &&
      ((!IS_PRODUCTION &&
        (!cookies?.p3_Ois || cookies?.p3_Ois?.toString().trim() != 'true')) ||
        (IS_PRODUCTION &&
          (!cookies?.p3_zeB ||
            cookies?.p3_zeB?.toString().trim() != 'true'))) &&
      (isPwaInstallableApple || pwaDeferredInstallEvent)
    )
  }, [
    isIOS,
    width,
    height,
    cookies,
    inIframe,
    isDesktop,
    isMobileOnly,
    isRunningPwa,
    isFullscreen,
    IS_PRODUCTION,
    isPracticalPod,
    cookies?.p3_Ois,
    cookies?.p3_zeB,
    isViewportTooSmall,
    isPwaInstallableApple,
    firstNavigatedComponent,
    pwaDeferredInstallEvent,
    acceptedPwaInstallation,
    pageComponentDisplayName,
    dismissedInstallPwaOverlay,
    playingPlayingOverlayOpened,
    environmentProtectionModalOpened,
  ])
  const chatBotEmbed: JSX.Element = useMemo(() => {
    return USE_CHATBOT &&
      isNotNullOrEmptyString(firstNavigatedComponent) &&
      !isPracticalPod &&
      !canShowInstallPwaOverlay &&
      web2UserIdLoggedIn != true &&
      (isNullOrEmptyString(selectedLanguage) || isEnglish(selectedLanguage)) &&
      chatBotReadyToRender?.current ? (
      <script src="https://cdn.jotfor.ms/agent/embedjs/019784bedde17316a04715cc1ffd1b88bf0c/embed.js?skipWelcome=1&maximizable=1"></script>
    ) : (
      <></>
    )
  }, [
    isPracticalPod,
    selectedLanguage,
    web2UserIdLoggedIn,
    firstNavigatedComponent,
    canShowInstallPwaOverlay,
    chatBotReadyToRender?.current,
  ])
  useEffect(() => {
    try {
      if (isNotNullOrEmptyString(firstNavigatedComponent)) {
        let wrapperEmbedChatbot: any = document.querySelector(
          '.embedded-agent-container',
        )
        if (wrapperEmbedChatbot) {
          if (
            canShowInstallPwaOverlay ||
            quickSearchOpened ||
            isEnglish(selectedLanguage) != true
          ) {
            try {
              wrapperEmbedChatbot.classList.remove('showInitial')
              wrapperEmbedChatbot.classList.add('displayNone')
            } catch {}
          } else {
            try {
              wrapperEmbedChatbot.classList.remove('displayNone')
              wrapperEmbedChatbot.classList.add('showInitial')
            } catch {}
          }
        }
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }, [selectedLanguage, quickSearchOpened, canShowInstallPwaOverlay])
  const useStaticMarquee: boolean =
    (isEdge && isMobileOnly) ||
    (isIOS && IS_PRODUCTION) ||
    isPracticalPod ||
    width > 2165
  const marqueeSpangClass: string = useMemo(() => {
    return useStaticMarquee ? classes.marqueeSpangStatic : 'marquee-spang'
  }, [useStaticMarquee])
  const marqueeClass: string = useMemo(() => {
    return (
      (isMobileOnly && width < 992
        ? classes.animatedSongName_Mobile
        : pwaInstallShown
          ? classes.animatedSongName_PwaInstall_Shown
          : classes.animatedSongName) + ' marquee'
    )
  }, [isIOS, width, isMobileOnly, pwaInstallShown])
  const noPlaylistButton: boolean =
    (isPracticalPod && FORCE_NO_PLAYLIST_EDITS_PRACTICAL_POD) ||
    (web2UserIdLoggedIn == true &&
      user?.canMakePlaylists == false &&
      isNotNullOrEmptyString(user?.adminWeb2UserId))
  const marqueeTransform: string = useMemo(() => {
    return noPlaylistButton
      ? width < 355
        ? 'scale(1.24, 1) translate(-13px, 0px)'
        : width < 400
          ? 'scale(1.1, 1) translate(-15px, 0px)'
          : width < 475
            ? 'scale(1.1, 1) translate(-15px, 0px)'
            : width < 530
              ? 'scale(1.04, 1) translate(-20px, 0px)'
              : width < 650
                ? 'scale(1.02, 1) translate(-20px, 0px)'
                : width < 710
                  ? 'scale(1.08, 1) translate(-10px, 0px)'
                  : width < 875
                    ? 'scale(1.02, 1) translate(-15px, 0px)'
                    : width < 895
                      ? 'scale(1.05, 1) translate(-10px, 0px)'
                      : width < 910
                        ? 'scale(1.025, 1) translate(-21px, 0px)'
                        : width < 993
                          ? 'scale(1.05, 1) translate(-12px, 0px)'
                          : width < 1050
                            ? 'scale(1.025, 1) translate(-12px, 0px)'
                            : width < 1200
                              ? 'scale(1.1, 1) translate(-12px, 0px)'
                              : width < 1400
                                ? 'scale(1.065, 1) translate(-20px, 0px)'
                                : width < 1450
                                  ? 'scale(1.12, 1) translate(-8px, 0px)'
                                  : width < 1525
                                    ? 'scale(1.12, 1) translate(-8px, 0px)'
                                    : width < 1750
                                      ? 'scale(1.025, 1) translate(-15px, 0px)'
                                      : width < 1975
                                        ? 'scale(1.025, 1) translate(-15px, 0px)'
                                        : 'scale(0.95, 1) translate(-40px, 0px)'
      : 'initial'
  }, [width, noPlaylistButton])
  const animateCharacterVisibility: any = useMemo(() => {
    return !canShowAudioPlayer ||
      rerenderForIOSOrientation ||
      dontLoad ||
      isViewportTooSmall
      ? 'hidden'
      : 'visible'
  }, [
    dontLoad,
    song_Master,
    songName_Master,
    canShowAudioPlayer,
    isViewportTooSmall,
    songName_Master_Small,
    rerenderForIOSOrientation,
  ])
  const audioWrapperVisibility: boolean = useMemo(() => {
    return isMobile || (canShowAudioPlayer && song_Master != null)
  }, [isMobile, song_Master, canShowAudioPlayer])
  const width_gt_1695: boolean = useMemo(() => {
    return width > 1695
  }, [width])
  const width_gte_1400: boolean = useMemo(() => {
    return width >= 1400
  }, [width])
  const width_gte_1335: boolean = useMemo(() => {
    return width >= 1335
  }, [width])
  const width_gt_1300: boolean = useMemo(() => {
    return width > 1300
  }, [width])
  const width_gt_800: boolean = useMemo(() => {
    return width > 800
  }, [width])
  const width_lt_641: boolean = useMemo(() => {
    return width < 641
  }, [width])
  const width_lt_1199: boolean = useMemo(() => {
    return width < 1199
  }, [width])
  const audioWrapperWidth: string = useMemo(() => {
    return width > 1695
      ? '64%'
      : width_gt_1695
        ? '63.2%'
        : width_gte_1400
          ? '55%'
          : width_gte_1335
            ? '55%'
            : width_gt_1300
              ? '54%'
              : width_gt_800
                ? '96%'
                : width_lt_641
                  ? '100%'
                  : width_lt_1199
                    ? '90%'
                    : '100%'
  }, [
    width_lt_641,
    width_gt_800,
    width_gt_1695,
    width_gt_1300,
    width_lt_1199,
    width_gte_1400,
    width_gte_1335,
  ])
  const audioPlayerWidth: string = useMemo(() => {
    return isPracticalPod && width > 992 && width < 1301
      ? 'calc(-342px + 100vw)'
      : !pwaInstallShown
        ? width > 1400
          ? '85%'
          : width > 1300
            ? '80%'
            : width > 1199
              ? '62%'
              : width > 992
                ? '52%'
                : width > 641
                  ? '90.75%'
                  : width < 641
                    ? '100%'
                    : '95%'
        : width > 2100
          ? '92%'
          : width > 2000
            ? '89%'
            : width > 1900
              ? '86%'
              : width > 1800
                ? '83%'
                : width > 1685
                  ? '78.5%'
                  : width > 1590
                    ? '85%'
                    : width > 1520
                      ? '80%'
                      : width > 1480
                        ? '78%'
                        : width > 1400
                          ? '79%'
                          : width > 1350
                            ? '76%'
                            : width > 1300
                              ? '73%'
                              : width > 1199
                                ? '62%'
                                : width > 992
                                  ? '52%'
                                  : width > 641
                                    ? '90.75%'
                                    : width < 641
                                      ? '100%'
                                      : '95%'
  }, [width, pwaInstallShown, isPracticalPod])
  const audioWrapperBackgroundColor: string = useMemo(() => {
    return largeDisplay
      ? 'transparent'
      : dark
        ? hexToRgba(SYSTEM_WHITE_COLOR, 0.8) + ' !important'
        : hexToRgba(SYSTEM_BLACK_COLOR, 0.5) + ' !important'
  }, [dark, largeDisplay])
  const audioWrapperLeft: number = useMemo(() => {
    return largeDisplay ? 200 : 0
  }, [largeDisplay])
  const renderedSongNameMaster: string = useMemo(() => {
    return cureLiterals(songName_Master)
  }, [songName_Master])
  const renderedSongNameMasterSmall: string = useMemo(() => {
    return cureLiterals(songName_Master_Small)
  }, [songName_Master_Small])
  const showAppNeedsToReloadModal: boolean = useMemo(() => {
    return (
      USE_APP_MINIMUM_VERSION_RELOADS &&
      appNeedsToReloadModalOpened &&
      !deviceIsAboutToRestartModalOpened &&
      !isPracticalPod &&
      isOnline &&
      (!environmentProtectionModalOpened || !USE_ENV_PROTECTION_MODAL)
    )
  }, [
    isOnline,
    isPracticalPod,
    USE_ENV_PROTECTION_MODAL,
    appNeedsToReloadModalOpened,
    USE_APP_MINIMUM_VERSION_RELOADS,
    environmentProtectionModalOpened,
    deviceIsAboutToRestartModalOpened,
  ])
  const showFavoritesLoader: boolean = useMemo(() => {
    return (
      !gotFavorites &&
      web2UserIdLoggedIn == true &&
      canFavoriteAndPlaylist &&
      isNotNullOrEmptyString(user?.web2UserId) &&
      isNotNullOrEmptyString(song_Master?.contract_address) &&
      isNotNullOrEmptyString(song_Master?.token_id)
    )
  }, [
    gotFavorites,
    user?.web2UserId,
    web2UserIdLoggedIn,
    song_Master?.token_id,
    canFavoriteAndPlaylist,
    song_Master?.contract_address,
  ])
  const favoritesButtonCursor: string = useMemo(() => {
    return web2UserIdLoggedIn == true &&
      !gotFavorites &&
      canFavoriteAndPlaylist &&
      isNotNullOrEmptyString(user?.web2UserId) &&
      isNotNullOrEmptyString(song_Master?.contract_address)
      ? 'default'
      : 'pointer'
  }, [
    gotFavorites,
    user?.web2UserId,
    web2UserIdLoggedIn,
    canFavoriteAndPlaylist,
    song_Master?.contract_address,
  ])
  const favoritesButtonVisibility: any = useMemo(() => {
    return rerenderForIOSOrientation
      ? 'hidden'
      : dontLoad || isViewportTooSmall
        ? 'hidden'
        : 'visible'
  }, [
    song_Master,
    songName_Master,
    isViewportTooSmall,
    rerenderForIOSOrientation,
  ])
  const favoritesButtonAriaLabel: string = useMemo(() => {
    return translate(
      'Favorite This Song',
      'Favorita esta canción',
      'Favoriser cette chanson',
      '이 노래를 즐겨찾기에 추가',
      selectedLanguage,
    )
  }, [selectedLanguage])
  const buttonPlayerPlaylistVisibility: any = useMemo(() => {
    return rerenderForIOSOrientation
      ? 'hidden'
      : dontLoad || isViewportTooSmall
        ? 'hidden'
        : 'visible'
  }, [
    dontLoad,
    song_Master,
    songName_Master,
    isViewportTooSmall,
    rerenderForIOSOrientation,
  ])
  const buttonPlayerPlaylistAriaLabel: string = useMemo(() => {
    return translate(
      'Add This Song to a Playlist',
      'Agregar esta canción a una lista de reproducción',
      'Ajouter cette chanson à une playlist',
      '이 노래를 플레이리스트에 추가하세요',
      selectedLanguage,
    )
  }, [selectedLanguage])
  const homepageLogoContainerVisibility: any = useMemo(() => {
    return !canShowAudioPlayer || width == null ? 'hidden' : 'visible'
  }, [song_Master, canShowAudioPlayer])
  const homepageLogoImageSource: string = useMemo(() => {
    return avifCompatible == true
      ? largeDisplay
        ? dark
          ? LOGO_LARGE_DARK_AVIF_CDN
          : LOGO_LARGE_LIGHT_AVIF_CDN
        : dark
          ? LOGO_SMALL_DARK_AVIF_CDN
          : LOGO_SMALL_LIGHT_AVIF_CDN
      : largeDisplay
        ? dark
          ? LOGO_LARGE_DARK_PNG_CDN
          : LOGO_LARGE_LIGHT_PNG_CDN
        : dark
          ? LOGO_SMALL_DARK_PNG_CDN
          : LOGO_SMALL_LIGHT_PNG_CDN
  }, [dark, largeDisplay, avifCompatible])
  const showCopyErrorModal: boolean = useMemo(() => {
    return (
      isOnline &&
      copyErrorModalOpened &&
      !showAppNeedsToReloadModal &&
      (!environmentProtectionModalOpened || !USE_ENV_PROTECTION_MODAL)
    )
  }, [
    isOnline,
    copyErrorModalOpened,
    USE_ENV_PROTECTION_MODAL,
    showAppNeedsToReloadModal,
    environmentProtectionModalOpened,
  ])
  const showMaskModal: boolean = useMemo(() => {
    return (
      pageComponentDisplayName != FOUR_O_FOUR_PAGE_DISPLAY_NAME &&
      pageComponentDisplayName != FIVE_HUNDRED_PAGE_DISPLAY_NAME &&
      pageComponentDisplayName != ERROR_PAGE_DISPLAY_NAME &&
      ((IS_QA &&
        !hasResolvedEnvironmentProtectionModal &&
        !isInvitationPass &&
        !isSubscribePassOrSubscribePassReloaded) ||
        (isOnline &&
          rerenderCarousel == true &&
          (!environmentProtectionModalOpened || !USE_ENV_PROTECTION_MODAL)))
    )
  }, [
    IS_QA,
    isOnline,
    rerenderCarousel,
    pageComponentDisplayName,
    USE_ENV_PROTECTION_MODAL,
    environmentProtectionModalOpened,
    hasResolvedEnvironmentProtectionModal,
  ])
  const showSelectLanguageModal: boolean = useMemo(() => {
    return (
      selectLanguageModalOpened &&
      !showAppNeedsToReloadModal &&
      (!environmentProtectionModalOpened || !USE_ENV_PROTECTION_MODAL) &&
      isOnline
    )
  }, [
    isOnline,
    USE_ENV_PROTECTION_MODAL,
    selectLanguageModalOpened,
    showAppNeedsToReloadModal,
    environmentProtectionModalOpened,
  ])
  const showAddToPlaylistModal: boolean = useMemo(() => {
    return (
      addToPlaylistModalOpened &&
      !showAppNeedsToReloadModal &&
      isNotNullOrUndefined(user) &&
      isOnline &&
      (!environmentProtectionModalOpened || !USE_ENV_PROTECTION_MODAL)
    )
  }, [
    user,
    isOnline,
    USE_ENV_PROTECTION_MODAL,
    addToPlaylistModalOpened,
    showAppNeedsToReloadModal,
    environmentProtectionModalOpened,
  ])
  const showUnsubscribeModal: boolean = useMemo(() => {
    return (
      unsubscribedModalOpened &&
      !showAppNeedsToReloadModal &&
      isOnline &&
      (!environmentProtectionModalOpened || !USE_ENV_PROTECTION_MODAL)
    )
  }, [
    isOnline,
    unsubscribedModalOpened,
    USE_ENV_PROTECTION_MODAL,
    showAppNeedsToReloadModal,
    environmentProtectionModalOpened,
  ])
  const showEnvironmentProtectionModal: boolean = useMemo(() => {
    return (
      environmentProtectionModalOpened && isOnline && USE_ENV_PROTECTION_MODAL
    )
  }, [isOnline, USE_ENV_PROTECTION_MODAL, environmentProtectionModalOpened])
  const showApplePwaHelpModal: boolean = useMemo(() => {
    return (
      applePwaHelpModalOpened &&
      isOnline &&
      isApple &&
      !environmentProtectionModalOpened &&
      !showAppNeedsToReloadModal
    )
  }, [
    isApple,
    isOnline,
    applePwaHelpModalOpened,
    showAppNeedsToReloadModal,
    environmentProtectionModalOpened,
  ])
  const showAppleMacPwaHelpModal: boolean = useMemo(() => {
    return (
      appleMacPwaHelpModalOpened &&
      !showAppNeedsToReloadModal &&
      !environmentProtectionModalOpened &&
      isMacOs &&
      isOnline
    )
  }, [
    isMacOs,
    isOnline,
    showAppNeedsToReloadModal,
    appleMacPwaHelpModalOpened,
    environmentProtectionModalOpened,
  ])
  const isAnInvitationModalOpened: boolean = useMemo(() => {
    return (
      !isPracticalPod &&
      !showAppNeedsToReloadModal &&
      (invitationWasCancelledModalOpened ||
        invitationWasAlreadyAcceptedModalOpened ||
        mustAcceptInvitationWithSameEmailModalOpened ||
        hasDecidedWhichInvitationDialogueToUse ||
        onAnotherAccountCantAcceptInviteModalOpened ||
        subscribedUserCantAcceptInviteModalOpened ||
        confirmInvitationAcceptanceModalOpened ||
        loginToAcceptInvitationModalOpened)
    )
  }, [
    isPracticalPod,
    showAppNeedsToReloadModal,
    invitationWasCancelledModalOpened,
    loginToAcceptInvitationModalOpened,
    hasDecidedWhichInvitationDialogueToUse,
    confirmInvitationAcceptanceModalOpened,
    invitationWasAlreadyAcceptedModalOpened,
    subscribedUserCantAcceptInviteModalOpened,
    onAnotherAccountCantAcceptInviteModalOpened,
    mustAcceptInvitationWithSameEmailModalOpened,
  ])
  const showStreamingInTwoOrMoreLocationsModal: boolean = useMemo(() => {
    return (
      web2UserIdLoggedIn == true &&
      streamingInTwoOrMoreLocationsModalOpened &&
      isOnline &&
      pageComponentDisplayName != FOUR_O_FOUR_PAGE_DISPLAY_NAME &&
      pageComponentDisplayName != FIVE_HUNDRED_PAGE_DISPLAY_NAME &&
      pageComponentDisplayName != ERROR_PAGE_DISPLAY_NAME &&
      !deviceIsAboutToRestartModalOpened &&
      !showAppNeedsToReloadModal &&
      isBusinessUserSubscribed &&
      (!environmentProtectionModalOpened || !USE_ENV_PROTECTION_MODAL) &&
      !isViewportTooSmall
    )
  }, [
    isOnline,
    isViewportTooSmall,
    web2UserIdLoggedIn,
    isBusinessUserSubscribed,
    pageComponentDisplayName,
    USE_ENV_PROTECTION_MODAL,
    showAppNeedsToReloadModal,
    environmentProtectionModalOpened,
    deviceIsAboutToRestartModalOpened,
    streamingInTwoOrMoreLocationsModalOpened,
  ])
  const showScheduledPlaylistsEnabledModal: boolean = useMemo(() => {
    return (
      scheduledPlaylistsEnabledModalOpened &&
      masterSchedule?.enabled == true &&
      isOnline &&
      !hasInteracted &&
      !isPracticalPod &&
      !hardwareUserNotLoggedIntoPracticalPod &&
      pageComponentDisplayName != FOUR_O_FOUR_PAGE_DISPLAY_NAME &&
      pageComponentDisplayName != FIVE_HUNDRED_PAGE_DISPLAY_NAME &&
      pageComponentDisplayName != ERROR_PAGE_DISPLAY_NAME &&
      !deviceIsAboutToRestartModalOpened &&
      !showAppNeedsToReloadModal &&
      !isAnInvitationModalOpened &&
      (!environmentProtectionModalOpened || !USE_ENV_PROTECTION_MODAL) &&
      !isViewportTooSmall
    )
  }, [
    isOnline,
    hasInteracted,
    isPracticalPod,
    isViewportTooSmall,
    masterSchedule?.enabled,
    USE_ENV_PROTECTION_MODAL,
    pageComponentDisplayName,
    isAnInvitationModalOpened,
    showAppNeedsToReloadModal,
    environmentProtectionModalOpened,
    deviceIsAboutToRestartModalOpened,
    scheduledPlaylistsEnabledModalOpened,
    hardwareUserNotLoggedIntoPracticalPod,
  ])
  useEffect(() => {
    if (!showScheduledPlaylistsEnabledModal) triggerMonitorScheduleFn()
  }, [showScheduledPlaylistsEnabledModal])
  const showInvitationWasCancelledModal: boolean = useMemo(() => {
    return (
      invitationWasCancelledModalOpened &&
      !showAppNeedsToReloadModal &&
      isOnline &&
      !isPracticalPod &&
      (!environmentProtectionModalOpened || !USE_ENV_PROTECTION_MODAL) &&
      !isViewportTooSmall
    )
  }, [
    isOnline,
    isPracticalPod,
    isViewportTooSmall,
    USE_ENV_PROTECTION_MODAL,
    showAppNeedsToReloadModal,
    environmentProtectionModalOpened,
    invitationWasCancelledModalOpened,
  ])
  const showInvitationWasAlreadyAcceptedModal: boolean = useMemo(() => {
    return (
      invitationWasAlreadyAcceptedModalOpened &&
      !showAppNeedsToReloadModal &&
      isOnline &&
      !isPracticalPod &&
      (!environmentProtectionModalOpened || !USE_ENV_PROTECTION_MODAL) &&
      !isViewportTooSmall
    )
  }, [
    isOnline,
    isPracticalPod,
    isViewportTooSmall,
    showAppNeedsToReloadModal,
    environmentProtectionModalOpened,
    invitationWasAlreadyAcceptedModalOpened,
  ])
  const showMustAcceptInvitationWithSameEmailModal: boolean = useMemo(() => {
    return (
      mustAcceptInvitationWithSameEmailModalOpened &&
      !showAppNeedsToReloadModal &&
      isOnline &&
      !isPracticalPod &&
      (!environmentProtectionModalOpened || !USE_ENV_PROTECTION_MODAL) &&
      !isViewportTooSmall
    )
  }, [
    isOnline,
    isPracticalPod,
    isViewportTooSmall,
    USE_ENV_PROTECTION_MODAL,
    showAppNeedsToReloadModal,
    environmentProtectionModalOpened,
    mustAcceptInvitationWithSameEmailModalOpened,
  ])
  const showOnAnotherAccountCantAcceptInviteModal: boolean = useMemo(() => {
    return (
      onAnotherAccountCantAcceptInviteModalOpened &&
      !showAppNeedsToReloadModal &&
      isOnline &&
      !isPracticalPod &&
      (!environmentProtectionModalOpened || !USE_ENV_PROTECTION_MODAL) &&
      !isViewportTooSmall
    )
  }, [
    isOnline,
    isPracticalPod,
    isViewportTooSmall,
    USE_ENV_PROTECTION_MODAL,
    showAppNeedsToReloadModal,
    environmentProtectionModalOpened,
    onAnotherAccountCantAcceptInviteModalOpened,
  ])
  const showSubscribedUserCantAcceptInviteModal: boolean = useMemo(() => {
    return (
      subscribedUserCantAcceptInviteModalOpened &&
      !showAppNeedsToReloadModal &&
      isOnline &&
      !isPracticalPod &&
      (!environmentProtectionModalOpened || !USE_ENV_PROTECTION_MODAL) &&
      !isViewportTooSmall
    )
  }, [
    isOnline,
    isPracticalPod,
    isViewportTooSmall,
    USE_ENV_PROTECTION_MODAL,
    showAppNeedsToReloadModal,
    environmentProtectionModalOpened,
    subscribedUserCantAcceptInviteModalOpened,
  ])
  const showOverrideExceptionDateModal: boolean = useMemo(() => {
    return (
      overrideExceptionDateModalOpened &&
      !showAppNeedsToReloadModal &&
      isOnline &&
      (!environmentProtectionModalOpened || !USE_ENV_PROTECTION_MODAL) &&
      !isViewportTooSmall
    )
  }, [
    isOnline,
    isViewportTooSmall,
    USE_ENV_PROTECTION_MODAL,
    showAppNeedsToReloadModal,
    overrideExceptionDateModalOpened,
    environmentProtectionModalOpened,
  ])
  const showConfirmInvitationAcceptanceModal: boolean = useMemo(() => {
    return (
      confirmInvitationAcceptanceModalOpened &&
      !showAppNeedsToReloadModal &&
      isOnline &&
      !isPracticalPod &&
      (!environmentProtectionModalOpened || !USE_ENV_PROTECTION_MODAL) &&
      !isViewportTooSmall
    )
  }, [
    isOnline,
    isPracticalPod,
    isViewportTooSmall,
    USE_ENV_PROTECTION_MODAL,
    showAppNeedsToReloadModal,
    environmentProtectionModalOpened,
    confirmInvitationAcceptanceModalOpened,
  ])
  const showLoginToAcceptInvitationModal: boolean = useMemo(() => {
    return (
      loginToAcceptInvitationModalOpened &&
      !showAppNeedsToReloadModal &&
      isOnline &&
      !isPracticalPod &&
      (!environmentProtectionModalOpened || !USE_ENV_PROTECTION_MODAL) &&
      !isViewportTooSmall
    )
  }, [
    isOnline,
    isPracticalPod,
    isViewportTooSmall,
    USE_ENV_PROTECTION_MODAL,
    showAppNeedsToReloadModal,
    environmentProtectionModalOpened,
    loginToAcceptInvitationModalOpened,
  ])
  const anInvitationModalsOpened: boolean = useMemo(() => {
    return (
      showInvitationWasCancelledModal ||
      showLoginToAcceptInvitationModal ||
      showConfirmInvitationAcceptanceModal ||
      showInvitationWasAlreadyAcceptedModal ||
      showSubscribedUserCantAcceptInviteModal ||
      showOnAnotherAccountCantAcceptInviteModal ||
      showMustAcceptInvitationWithSameEmailModal
    )
  }, [
    showInvitationWasCancelledModal,
    showLoginToAcceptInvitationModal,
    showConfirmInvitationAcceptanceModal,
    showInvitationWasAlreadyAcceptedModal,
    showSubscribedUserCantAcceptInviteModal,
    showOnAnotherAccountCantAcceptInviteModal,
    showMustAcceptInvitationWithSameEmailModal,
  ])
  const showDeviceIsAboutToRestartModal: boolean = useMemo(() => {
    return (
      isPracticalPod &&
      USE_PRACTICAL_POD_REGULAR_RESTARTS &&
      deviceIsAboutToRestartModalOpened &&
      isOnline &&
      (!environmentProtectionModalOpened || !USE_ENV_PROTECTION_MODAL) &&
      !isViewportTooSmall
    )
  }, [
    isOnline,
    isViewportTooSmall,
    USE_ENV_PROTECTION_MODAL,
    environmentProtectionModalOpened,
    deviceIsAboutToRestartModalOpened,
    USE_PRACTICAL_POD_REGULAR_RESTARTS,
  ])
  const hideComponentContentForInvitationModal: boolean = useMemo(() => {
    return (
      isOnline &&
      !isPracticalPod &&
      !isViewportTooSmall &&
      (!environmentProtectionModalOpened || !USE_ENV_PROTECTION_MODAL) &&
      (loginToAcceptInvitationModalOpened ||
        confirmInvitationAcceptanceModalOpened ||
        subscribedUserCantAcceptInviteModalOpened ||
        onAnotherAccountCantAcceptInviteModalOpened ||
        mustAcceptInvitationWithSameEmailModalOpened ||
        invitationWasAlreadyAcceptedModalOpened ||
        invitationWasCancelledModalOpened)
    )
  }, [
    isOnline,
    isPracticalPod,
    isViewportTooSmall,
    environmentProtectionModalOpened,
    invitationWasCancelledModalOpened,
    loginToAcceptInvitationModalOpened,
    confirmInvitationAcceptanceModalOpened,
    invitationWasAlreadyAcceptedModalOpened,
    subscribedUserCantAcceptInviteModalOpened,
    onAnotherAccountCantAcceptInviteModalOpened,
    mustAcceptInvitationWithSameEmailModalOpened,
  ])
  const showSubscribePitchModal: boolean = useMemo(() => {
    return (
      subscribePitchModalOpened &&
      isOnline &&
      !isViewportTooSmall &&
      !showAppNeedsToReloadModal &&
      (!environmentProtectionModalOpened || !USE_ENV_PROTECTION_MODAL)
    )
  }, [
    isOnline,
    isViewportTooSmall,
    USE_ENV_PROTECTION_MODAL,
    subscribePitchModalOpened,
    showAppNeedsToReloadModal,
    environmentProtectionModalOpened,
  ])
  const showBufferRegion: boolean = useMemo(() => {
    return (
      !isViewportTooSmall &&
      pageComponentDisplayName != FOUR_O_FOUR_PAGE_DISPLAY_NAME &&
      pageComponentDisplayName != FIVE_HUNDRED_PAGE_DISPLAY_NAME &&
      pageComponentDisplayName != ERROR_PAGE_DISPLAY_NAME &&
      isNotNullOrEmptyString(songName_Master) &&
      song_Master
    )
  }, [
    song_Master,
    songName_Master,
    isViewportTooSmall,
    pageComponentDisplayName,
  ])
  const subscriptionPrice: any = useMemo(() => {
    return useEuro
      ? DEFAULT_SUBSCRIPTION_PRICE_EURO
      : useCanadianDollar
        ? DEFAULT_SUBSCRIPTION_PRICE_CAD
        : useBritishPound
          ? DEFAULT_SUBSCRIPTION_PRICE_POUND
          : DEFAULT_SUBSCRIPTION_PRICE_USD
  }, [
    useEuro,
    useBritishPound,
    useCanadianDollar,
    DEFAULT_SUBSCRIPTION_PRICE_USD,
    DEFAULT_SUBSCRIPTION_PRICE_CAD,
    DEFAULT_SUBSCRIPTION_PRICE_EURO,
    DEFAULT_SUBSCRIPTION_PRICE_POUND,
  ])
  const subscriptionPrice_Hardware: any = useMemo(() => {
    return useEuro
      ? DEFAULT_SUBSCRIPTION_PRICE_EURO_HARDWARE
      : useCanadianDollar
        ? DEFAULT_SUBSCRIPTION_PRICE_CAD_HARDWARE
        : useBritishPound
          ? DEFAULT_SUBSCRIPTION_PRICE_POUND_HARDWARE
          : DEFAULT_SUBSCRIPTION_PRICE_USD_HARDWARE
  }, [
    useEuro,
    useBritishPound,
    useCanadianDollar,
    DEFAULT_SUBSCRIPTION_PRICE_USD_HARDWARE,
    DEFAULT_SUBSCRIPTION_PRICE_CAD_HARDWARE,
    DEFAULT_SUBSCRIPTION_PRICE_EURO_HARDWARE,
    DEFAULT_SUBSCRIPTION_PRICE_POUND_HARDWARE,
  ])
  const stripeOrVercelHeaderBasedCurrency: any = useMemo(() => {
    return useEuro
      ? ISO_4217_CURRENCY_CODE_EURO
      : useBritishPound
        ? ISO_4217_CURRENCY_CODE_BRITISH_POUND
        : useCanadianDollar
          ? ISO_4217_CURRENCY_CODE_CANADA_DOLLAR
          : ISO_4217_CURRENCY_CODE_US_DOLLAR
  }, [useEuro, useBritishPound, useCanadianDollar])
  const subscriptionButtonCurrencyText: string = useMemo(() => {
    return getSubscriptionButtonCurrencyText(
      stripeOrVercelHeaderBasedCurrency,
      subscriptionPrice,
      DEFAULT_SUBSCRIPTION_PRICE_USD,
      selectedLanguage,
      false,
    )
  }, [selectedLanguage, stripeOrVercelHeaderBasedCurrency, subscriptionPrice])
  const subscriptionButtonCurrencyText_Hardware_No_Suffix_Zeroes: string =
    useMemo(() => {
      let hwPrice: string = getSubscriptionButtonCurrencyText(
        stripeOrVercelHeaderBasedCurrency,
        subscriptionPrice_Hardware,
        DEFAULT_SUBSCRIPTION_PRICE_USD_HARDWARE,
        selectedLanguage,
        false,
      )
      hwPrice = isNotNullOrEmptyString(hwPrice)
        ? hwPrice.replace('.00', '')
        : toTrimmedString(hwPrice)
      return hwPrice
    }, [
      selectedLanguage,
      subscriptionPrice_Hardware,
      stripeOrVercelHeaderBasedCurrency,
    ])
  const reactPlayerStyles: string = useMemo(() => {
    return (
      `.rhap_container { visibility: ${
        secondCanShowAudioPlayer || (isMobile && width < 641)
          ? 'visible'
          : 'hidden'
      } !important; }` +
      `#rhap_current-time { color: ` +
      (dark
        ? `${DARK_MODE_ACCENT_SECONDARY_COLOR}`
        : `${LIGHT_MODE_ACCENT_TERTIARY_COLOR}`) +
      ` !important; } .rhap_total-time { color: ` +
      (dark
        ? `${DARK_MODE_ACCENT_SECONDARY_COLOR}`
        : `${LIGHT_MODE_ACCENT_TERTIARY_COLOR}`) +
      ` !important; } ` +
      `.react-player__play-icon { transform: ` +
      (width < 300
        ? `scale(1,1); `
        : width < 350
          ? `scale(1.1,1.1); `
          : width < 450
            ? `scale(1.2,1.2); `
            : `scale(1.5,1.5); `) +
      (dark
        ? `border-color: transparent transparent transparent ${DARK_MODE_ACTION_COLOR} !important; `
        : `border-color: transparent transparent transparent ${LIGHT_MODE_ACTION_COLOR} !important; `) +
      `} ` +
      `.react-player__preview {border: ` +
      (dark ? `1px solid transparent; ` : `1px solid transparent; `) +
      `border-radius: 15px; ` +
      `&:hover { opacity: ` +
      `.75;` +
      `} ` +
      `} ` +
      `.react-player__shadow { background: ` +
      (dark
        ? `radial-gradient(rgba(255, 255, 128, 0.3), ${hexToRgba(SYSTEM_BLACK_COLOR, 0)} 35%) !important; `
        : `radial-gradient(rgba(255, 16, 240, 0.3), ${hexToRgba(SYSTEM_BLACK_COLOR, 0)} 35%) !important; `) +
      `height: 100% !important; ` +
      `width: 100% !important; ` +
      `transform: scale(1.85,1.85); ` +
      `border-radius: 64px; ` +
      `} ` +
      `.videoWrapper div { display: flex; ` +
      `align-items: center;  ` +
      `justify-content: center; ` +
      `}`
    )
  }, [dark, width, isMobile, secondCanShowAudioPlayer])
  const [
    hasCheckedIfNeedsReloadForMinimumAppVersion,
    setHasCheckedIfNeedsReloadForMinimumAppVersion,
  ] = useState(false)
  const minutesElapsedToReload = useRef<number>(0)
  const [minutesRemainingToReload, setMinutesRemainingToReload] =
    useState<number>(MAXIMUM_MINUTES_TO_NOT_RELOAD_AFTER_APP_NEEDS_RELOAD)

  function checkIfNeedsReloadForMinimumAppVersionMessageReceived(event: any) {
    try {
      if (TRACE_APP_VERSION_RELOAD_CHECK) console.log('AVR-3')
      const checkResult: boolean = event?.data == false ? false : true
      if (checkResult) {
        if (TRACE_APP_VERSION_RELOAD_CHECK) console.log('AVR-4')
        setAppNeedsToReloadModalOpened(true)
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null
    try {
      if (showAppNeedsToReloadModal) {
        intervalId = setInterval(() => {
          if (
            minutesElapsedToReload?.current ==
            MAXIMUM_MINUTES_TO_NOT_RELOAD_AFTER_APP_NEEDS_RELOAD
          )
            reloadAppForNewVersion()
          else {
            const minsElapsed = minutesElapsedToReload.current
            minutesElapsedToReload.current = minsElapsed + 1
            setMinutesRemainingToReload(
              MAXIMUM_MINUTES_TO_NOT_RELOAD_AFTER_APP_NEEDS_RELOAD -
                minsElapsed -
                1,
            )
          }
        }, 60000)
      }
    } catch (e) {
      logSentryError(e, SENTRY_COMPONENT_NAME)
      if (DEBUG_MODE) console.log(e)
    }
    return () => {
      if (intervalId != null) clearInterval(intervalId)
    }
  }, [showAppNeedsToReloadModal])

  useEffect(() => {
    try {
      if (
        web2UserIdLoggedIn &&
        !IS_PRODUCTION &&
        !hasCheckedIfNeedsReloadForMinimumAppVersion
      ) {
        if (TRACE_APP_VERSION_RELOAD_CHECK) console.log('AVR-1')
        setHasCheckedIfNeedsReloadForMinimumAppVersion(true)
        let tid: NodeJS.Timeout | null = setTimeout(async () => {
          if (TRACE_APP_VERSION_RELOAD_CHECK) console.log('AVR-2')
          const { checkIfNeedsReloadForMinimumAppVersion } =
            await import('../app/utilities/app')
          checkIfNeedsReloadForMinimumAppVersion(
            checkIfNeedsReloadForMinimumAppVersionMessageReceived,
          )
          try {
            if (tid != null) {
              clearTimeout(tid)
              tid = null
            }
          } catch {}
        }, 1)
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }, [web2UserIdLoggedIn])

  useEffect(() => {
    if (!isNullOrEmptyString(firstNavigatedComponent)) return
    try {
      const observer = new MutationObserver((mutations, obs) => {
        if (DEBUG_MODE) console.log(mutations)
        const cookieTho = document.getElementsByClassName('cookie-though')
        if (cookieTho?.length > 0) {
          try {
            cookieTho[0].ariaHidden = 'true'
            //@ts-ignore
            cookieTho[0].tabIndex = -1
          } catch {}
          obs.disconnect()
        }
      })
      observer.observe(document.body, {
        childList: true,
        subtree: true,
      })
      return () => observer.disconnect()
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
    }
  }, [firstNavigatedComponent])

  useEffect(() => {
    try {
      if (!rhapSkipButtonElem1?.current || !rhapSkipButtonElem2?.current) {
        const elems: any = document.getElementsByClassName('rhap_skip-button')
        rhapSkipButtonElem1.current = elems[0]
        rhapSkipButtonElem2.current = elems[1]
      }
      if (rhapSkipButtonElem1?.current && rhapSkipButtonElem2?.current) {
        rhapSkipButtonElem1.current.style.display = 'block'
        rhapSkipButtonElem2.current.style.display = 'block'
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }, [pageComponentDisplayName])

  const notifyIsAuthenticatedChanged = useCallback(
    (
      userIsLoggedIn: boolean,
      userSessionId: any,
      userSessionEmail: any,
      lastLogin: any,
    ) => {
      try {
        setWeb2UserIdLoggedIn(userIsLoggedIn)
        setWeb2UserSessionId(userSessionId)
        setWeb2UserSessionEmail(userSessionEmail)
        setIsAuthenticated(userIsLoggedIn)
        setLastLogin(lastLogin)
      } catch (e) {
        if (DEBUG_MODE) console.log(e)
        logSentryError(e, SENTRY_COMPONENT_NAME)
      }
    },
    [],
  )

  useEffect(() => {
    try {
      if (document?.body?.style)
        document.body.style.setProperty(
          'overflow-y',
          !song_Master || isNullOrEmptyString(songName_Master) ? 'hidden' : '',
          'important',
        )
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }, [song_Master, songName_Master])

  function reloadAppForNewVersion() {
    try {
      const url: URL = new URL(window.location.href)
      const params = url.searchParams
      for (const key of Array.from(params.keys())) {
        params.delete(key)
      }
      url.searchParams.set(
        POST_NEW_APP_VERSION_RELOAD_QUERYSTRING_FIELD,
        POST_NEW_APP_VERSION_RELOAD_QUERYSTRING_FIELD_VALUE,
      )
      window.location.href = url.href
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }
  try {
    if (!window?.onpopstate)
      window.onpopstate = (event: any) => {
        if (isNotNullOrEmptyString(event?.state?.faux)) {
        }
      }
  } catch (e) {
    if (DEBUG_MODE) console.log(e)
    logSentryError(e, SENTRY_COMPONENT_NAME)
  }
  async function toggleScheduledPlaylistsEnabledModalOpened(opened: boolean) {
    try {
      if (!opened) {
        setUserLastDateWhenOverrodeExceptionDate(null)
        setLastPlayedDate(null)
        setLastPlayedDate_One(null)
        setLastPlayedDate_Two(null)
        setLastTeedUpDate(null)
        setLastTeedUpDate_One(null)
        setLastTeedUpDate_Two(null)
        if (DEBUG_MONITOR_SCHEDULE_TIMERS) console.log('su-2')
        triggerMonitorSchedule.current = uuidv4()
        setScheduledPlaylistsEnabledModalOpened(false)
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }
  const setLastPlayedDate_Conditional = useCallback((val: any) => {
    try {
      setLastPlayedDate(val)
    } catch {}
  }, [])
  const setLastTeedUpDate_Conditional = useCallback((val: any) => {
    try {
      setLastTeedUpDate(val)
    } catch {}
  }, [])
  const setLastPlayedDate_Conditional_One = useCallback((val: any) => {
    try {
      setLastPlayedDate_One(val)
    } catch {}
  }, [])
  const setLastTeedUpDate_Conditional_One = useCallback((val: any) => {
    try {
      setLastTeedUpDate_One(val)
    } catch {}
  }, [])
  const setLastPlayedDate_Conditional_Two = useCallback((val: any) => {
    try {
      setLastPlayedDate_Two(val)
    } catch {}
  }, [])
  const setLastTeedUpDate_Conditional_Two = useCallback((val: any) => {
    try {
      setLastTeedUpDate_Two(val)
    } catch {}
  }, [])
  try {
    if (
      !navigatorLockResolver &&
      isNullOrEmptyString(navigatorLockKey) &&
      navigator?.locks?.request
    ) {
      const navLockKey: any = uuidv4()
      setNavigatorLockKey(navLockKey)
      const promise = new Promise((res: any) => {
        setNavigatorLockResolver(res)
      })
      navigator?.locks?.request(
        isNotNullOrEmptyString(navLockKey) ? navLockKey : 's3',
        { mode: 'shared' },
        () => {
          return promise
        },
      )
    }
  } catch (e) {
    if (DEBUG_MODE) console.log(e)
    logSentryError(e, SENTRY_COMPONENT_NAME)
  }

  function setPwaInstallationMode(e: any) {
    try {
      if (e) {
        e.preventDefault()
        setPwaDeferredInstallEvent(e)
        setPwaInstallShown('shown')
      } else {
        setPwaDeferredInstallEvent(null)
        setPwaInstallShown('unshown')
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }

  useEffect(() => {
    try {
      if (!hasAddedPwaInstallationModeEventHandler) {
        window.addEventListener('beforeinstallprompt', setPwaInstallationMode)
        setHasAddedPwaInstalltionModeEventHandler(true)
      }
    } catch (er) {
      if (DEBUG_MODE) console.log(er)
      logSentryError(er, SENTRY_COMPONENT_NAME)
    }
  }, [hasAddedPwaInstallationModeEventHandler])

  if (typeof window !== 'undefined' && typeof navigator !== 'undefined')
    useEffect(() => {
      let handler: any = null
      try {
        if (navigator.userActivation?.hasBeenActive) {
          setHasInteracted(true)
          return
        }
        handler = () => setHasInteracted(true)
        window.addEventListener('pointerdown', handler, {
          once: true,
          passive: true,
        })
        window.addEventListener('keydown', handler, {
          once: true,
          passive: true,
        })
      } catch (e) {
        if (DEBUG_MODE) console.log(e)
      }
      return () => {
        if (isNotNullOrUndefined(handler)) {
          window.removeEventListener('pointerdown', handler)
          window.removeEventListener('keydown', handler)
        }
      }
    }, [navigator?.userActivation?.hasBeenActive])

  useEffect(() => {
    try {
      if (hasAddedPwaInstallationModeEventHandler) {
        window.removeEventListener(
          'beforeinstallprompt',
          setPwaInstallationMode,
        )
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }, [pwaInstallShown])

  useEffect(() => {
    try {
      if (DISABLE_RIGHT_CLICK && !addedRightClickHandler) {
        setAddedRightClickHandler(true)
        document.addEventListener('contextmenu', (e: any) => {
          let nodeName: any = toTrimmedStringUpperCase(e?.target?.nodeName)
          if (nodeName == '' || (nodeName != 'INPUT' && nodeName != 'TEXTAREA'))
            e.preventDefault()
        })
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }, [addedRightClickHandler])

  useEffect(() => {
    let timeoutId2: NodeJS.Timeout | null = null
    let timeoutId3: NodeJS.Timeout | null = null
    if (
      pageComponentDisplayName == SEARCH_PAGE_DISPLAY_NAME ||
      pageComponentDisplayName == ACCOUNT_SETTINGS_PAGE_DISPLAY_NAME ||
      pageComponentDisplayName == FAVORITES_PAGE_DISPLAY_NAME ||
      pageComponentDisplayName == PLAYLIST_PAGE_DISPLAY_NAME ||
      pageComponentDisplayName == CONTACT_PAGE_DISPLAY_NAME ||
      pageComponentDisplayName == STUDIO_PAGE_DISPLAY_NAME ||
      pageComponentDisplayName == HOME_PAGE_DISPLAY_NAME
    ) {
      if (isNotNullOrEmptyString(pageProps?.initialCountryCode))
        configureByCountryCode(pageProps?.initialCountryCode)
      if (isNullOrEmptyString(ipAddress)) {
        if (isNotNullOrEmptyString(pageProps?.initialIp))
          setIPAddress(pageProps?.initialIp)
        else
          timeoutId2 = setTimeout(
            () => getIPAddress(),
            DELAY_TO_GET_IP_ADDRESS_MS,
          )
      }
      timeoutId3 = setTimeout(
        () => getLocalIPAddress(),
        DELAY_TO_GET_LOCAL_IP_ADDRESS_MS,
      )
    }
    return () => {
      try {
        if (timeoutId2 != null) clearTimeout(timeoutId2)
        if (timeoutId3 != null) clearTimeout(timeoutId3)
      } catch {}
    }
  }, [firstSongSetBound, canShowAudioPlayer])

  useEffect(() => {
    try {
      setLoading(false)
      if (navigator && 'serviceWorker' in navigator) {
        if (!navigator?.serviceWorker?.controller)
          navigator.serviceWorker.register('/practical-stream-sw.js', {
            scope: './',
          })
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }, [])

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null
    try {
      if (
        pageComponentDisplayName == SEARCH_PAGE_DISPLAY_NAME &&
        (!contractGenres || contractGenres?.length == 0)
      )
        timeoutId = setTimeout(
          async () => {
            try {
              loadAllContractGenres()
            } catch (e) {
              if (DEBUG_MODE) console.log(e)
              logSentryError(e, SENTRY_COMPONENT_NAME)
            }
          },
          retryCountGetContractGenres == 0
            ? 1
            : RETRY_GET_CONTRACT_GENRES_MS * retryCountGetContractGenres,
        )
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
    return () => {
      try {
        if (timeoutId != null) clearTimeout(timeoutId)
      } catch {}
    }
  }, [retryCountGetContractGenres, router?.asPath])

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null
    try {
      if (!hasFilteredNoSongsArtists) {
        if (!masterArtists || masterArtists?.length == 0)
          timeoutId = setTimeout(
            () => {
              try {
                loadAllArtists()
              } catch (e) {
                if (DEBUG_MODE) console.log(e)
                logSentryError(e, SENTRY_COMPONENT_NAME)
              }
            },
            retryCountGetArtists < 1
              ? 1
              : retryCountGetArtists * RETRY_GET_ARTISTS_MS,
          )
        else if (
          allSongsArtistsGenreContractsLoaded &&
          songs_Master?.length > 0 &&
          masterArtists?.length > 0
        ) {
          setHasFilteredNoSongsArtists(true)
          const ma: any = masterArtists.filter((a: any) =>
            songs_Master.some((sm: any) => sm?.artist == a?.name),
          )
          setMasterArtists(ma)
        }
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
    return () => {
      try {
        if (timeoutId != null) clearTimeout(timeoutId)
      } catch {}
    }
  }, [
    songs_Master,
    masterArtists,
    retryCountGetArtists,
    hasFilteredNoSongsArtists,
    allSongsArtistsGenreContractsLoaded,
  ])

  if (typeof window !== 'undefined')
    useEffect(() => {
      try {
        if (isNotNullOrEmptyString(window?.location?.search)) {
          let tid: NodeJS.Timeout | null = setTimeout(() => {
            setEvaluateInvitationDelayElapsed(true)
            try {
              if (tid != null) {
                clearTimeout(tid)
                tid = null
              }
            } catch {}
          }, EVALUATE_INVITATION_DELAY)
        }
      } catch (e) {
        if (DEBUG_MODE) console.log(e)
        logSentryError(e, SENTRY_COMPONENT_NAME)
      }
    }, [window?.location?.search])

  useEffect(() => {
    try {
      if (
        (web2UserIdLoggedIn == true ||
          isNotNullOrEmptyString(pendingInvitation?.email)) &&
        !isPracticalPod &&
        evaluateInvitationDelayElapsed &&
        !hasDecidedWhichInvitationDialogueToUse &&
        pendingInvitation &&
        hasResolvedStripeActiveSoftwareOrHardwareSeats &&
        (!web2UserIdLoggedIn || isNotNullOrUndefined(user)) &&
        pageComponentDisplayName == ACCOUNT_SETTINGS_PAGE_DISPLAY_NAME
      ) {
        let userSubscribedBenchmark = new Date('1/1/2000')
        let userSubscribedDate: Date | null = isNotNullOrEmptyString(
          user?.subscribed,
        )
          ? netDateToDate(user?.subscribed)
          : null
        if (pendingInvitation?.status == INVITATION_CANCELLED_STATUS) {
          setInvitationWasCancelledModalOpened(true)
          setHasDecidedWhichInvitationDialogueToUse(true)
        } else if (pendingInvitation?.status == INVITATION_ACCEPTED_STATUS) {
          setInvitationWasAlreadyAcceptedModalOpened(true)
          setHasDecidedWhichInvitationDialogueToUse(true)
        } else if (web2UserIdLoggedIn != true) {
          setPendingInvitationEmail(pendingInvitation?.email)
          setLoginToAcceptInvitationModalOpened(true)
          setHasDecidedWhichInvitationDialogueToUse(true)
        } else if (isNullOrEmptyString(web2UserSessionEmail)) {
          setPendingInvitation(null)
          setPendingInvitationEmail(null)
          setHasDecidedWhichInvitationDialogueToUse(true)
          let tiza: NodeJS.Timeout | null = setTimeout(() => {
            toastError(selectedLanguage)
            try {
              if (tiza != null) {
                clearTimeout(tiza)
                tiza = null
              }
            } catch {}
          }, 2000)
          const errMsg: string =
            'web2UserSessionEmail was missing during invitation acceptance.'
          if (DEBUG_MODE) console.log(errMsg)
          logSentryError(errMsg, SENTRY_COMPONENT_NAME)
        } else if (
          toTrimmedStringLowerCase(web2UserSessionEmail) !=
          toTrimmedStringLowerCase(pendingInvitation?.email)
        ) {
          setHasDecidedWhichInvitationDialogueToUse(true)
          setMustAcceptInvitationWithSameEmailModalOpened(true)
        } else if (
          userSubscribedDate != null &&
          userSubscribedDate > userSubscribedBenchmark
        ) {
          setHasDecidedWhichInvitationDialogueToUse(true)
          setSubscribedUserCantAcceptInviteModalOpened(true)
        } else if (
          isNotNullOrEmptyString(user?.adminWeb2UserId) &&
          toTrimmedString(user?.adminWeb2UserId) !=
            pendingInvitation?.adminWeb2UserId
        ) {
          setHasDecidedWhichInvitationDialogueToUse(true)
          setOnAnotherAccountCantAcceptInviteModalOpened(true)
        } else {
          setConfirmInvitationAcceptanceModalOpened(true)
          setHasDecidedWhichInvitationDialogueToUse(true)
        }
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }, [
    user,
    isPracticalPod,
    userSubscribed,
    pendingInvitation,
    web2UserSessionId,
    web2UserIdLoggedIn,
    web2UserSessionEmail,
    evaluateInvitationDelayElapsed,
    hasResolvedStripeActiveSoftwareOrHardwareSeats,
  ])

  useEffect(() => {
    try {
      if (isNotNullOrUndefined(user) && isSubscribePassOrSubscribePassReloaded)
        setIsSubscribePassOrSubscribePassReloaded(false)
      else if (isSubscribePassOrSubscribePassReloaded) {
        let tiza: NodeJS.Timeout | null = setTimeout(() => {
          setIsSubscribePassOrSubscribePassReloaded(false)
          if (IS_QA) setHasResolvedEnvironmentProtectionModal(true)
          try {
            if (tiza != null) {
              clearTimeout(tiza)
              tiza = null
            }
          } catch {}
        }, 2000)
      }
      if (isNotNullOrUndefined(user) && isInvitationPass)
        setIsInvitationPass(false)
      else if (isInvitationPass) {
        let tiza: NodeJS.Timeout | null = setTimeout(() => {
          setIsInvitationPass(false)
          if (IS_QA) setHasResolvedEnvironmentProtectionModal(true)
          try {
            if (tiza != null) {
              clearTimeout(tiza)
              tiza = null
            }
          } catch {}
        }, 2000)
      }
    } catch (e) {}
  }, [user, isInvitationPass, isSubscribePassOrSubscribePassReloaded])

  const [hasInvitationQs, setHasInvitationQs] = useState<boolean | null>(null)

  if (typeof history !== 'undefined' && typeof window !== 'undefined') {
    useEffect(() => {
      setTimeout(async () => {
        try {
          const queryString = (await import('query-string')).default
          let frmLocationSearch: any = ''
          if (isNotNullOrEmptyString(location?.search))
            frmLocationSearch = location?.search
              ?.toString()
              .trim()
              .replace(/\?d=0/gi, '')
              .replace(/\&d=0/gi, '')
          const parsedQueryString: any = queryString.parse(frmLocationSearch)
          setHasInvitationQs(isNotNullOrEmptyString(parsedQueryString?.g))
          if (
            parsedQueryString?.q &&
            parsedQueryString?.q?.toString().trim() ==
              ACCOUNT_SETTINGS_REDIRECT_UUID
          ) {
            window.location.href =
              '/settings' +
              (isNotNullOrEmptyString(parsedQueryString?.dd) &&
              parsedQueryString?.dd?.toString().trim() == '0'
                ? '?dd=0'
                : '?dd=1') +
              (parsedQueryString?.rz &&
              parsedQueryString?.rz?.toString().trim() == '1'
                ? '&bz=1'
                : '')
          }
          if (isNotNullOrEmptyString(parsedQueryString?.dd)) {
            try {
              toggleColorScheme(
                parsedQueryString?.dd?.toString().trim() == '0'
                  ? 'light'
                  : 'dark',
              )
              if (
                parsedQueryString?.bz &&
                parsedQueryString?.bz?.toString().trim() == '1'
              ) {
                toastSticky(
                  translate(
                    'You logged out of all devices.',
                    'Has cerrado sesión en todos los dispositivos.',
                    'Vous vous êtes déconnecté de tous les appareils.',
                    '모든 기기에서 로그아웃되었습니다.',
                    isNotNullOrEmptyString(pageProps?.initialLang)
                      ? pageProps?.initLang
                      : selectedLanguage,
                  ),
                  'success',
                )
              }
              setSignalCheckClearIntervals(!signalCheckClearIntervals)
            } catch (e) {
              if (DEBUG_MODE) console.log(e)
              logSentryError(e, SENTRY_COMPONENT_NAME)
            }
          }
          if (isNotNullOrEmptyString(parsedQueryString?.w))
            setUnsubscribedModalOpened(true)
          else if (isNotNullOrEmptyString(parsedQueryString?.gg)) {
            if (DEBUG_MODE) console.log(parsedQueryString?.gg)
          } else if (isNotNullOrEmptyString(parsedQueryString?.s)) {
            const subscribePass: boolean =
              toTrimmedString(parsedQueryString?.s) == '1'
            const subscribePassReloaded: boolean =
              toTrimmedString(parsedQueryString?.s) == '2'
            setIsSubscribePassOrSubscribePassReloaded(
              subscribePass || subscribePassReloaded,
            )
            if (subscribePassReloaded)
              toast(
                translate(
                  'Success.',
                  'Éxito',
                  'Succès',
                  '성공',
                  selectedLanguage,
                ),
                'success',
              )
            else if (subscribePass)
              window.location.href = HOST + '/account-settings?s=2'
          } else if (isNotNullOrEmptyString(parsedQueryString?.g)) {
            const invitationToken: any = toTrimmedString(parsedQueryString?.g)
            if (isNotNullOrEmptyString(invitationToken)) {
              if (
                pageComponentDisplayName != ACCOUNT_SETTINGS_PAGE_DISPLAY_NAME
              )
                await navigateRouteWithInvitationToken(
                  '/settings',
                  invitationToken,
                )
              setIsInvitationPass(true)
              const invitation: any = await getInvitation(invitationToken)
              setPendingInvitation(invitation)
            }
          } else setPendingInvitation(null)
        } catch (e) {
          if (DEBUG_MODE) console.log(e)
          logSentryError(e, SENTRY_COMPONENT_NAME)
        }
      }, 0)
    }, [window?.location?.search])
    window.onmessage = function (e) {
      if (e?.data == 'light') {
        toggleColorScheme('light')
      } else if (e?.data == 'dark') {
        toggleColorScheme('dark')
      } else if (e?.data == 'stopParentPlay') {
        try {
          let timz: NodeJS.Timeout | null = setTimeout(() => {
            try {
              pauseSong()
            } catch (ez: any) {
              if (DEBUG_MODE) console.log(ez)
              logSentryError(ez, SENTRY_COMPONENT_NAME)
            } finally {
              try {
                if (timz != null) {
                  clearTimeout(timz)
                  timz = null
                }
              } catch {}
            }
          }, 10)
        } catch (er: any) {
          if (DEBUG_MODE) console.log(er)
          logSentryError(er, SENTRY_COMPONENT_NAME)
        }
      }
    }
  }

  useEffect(() => {
    try {
      let tz: NodeJS.Timeout | null = setTimeout(async () => {
        try {
          const requestDeviceDataResponse = await callKioskExtension(
            'getClientDeviceData',
          )
          setChromeDeviceDataResponse(requestDeviceDataResponse)
        } catch (ee: any) {
          if (DEBUG_MODE) console.log(ee)
          logSentryError(ee, SENTRY_COMPONENT_NAME)
        } finally {
          try {
            if (tz != null) {
              clearTimeout(tz)
              tz = null
            }
          } catch {}
        }
      })
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }, [])

  useEffect(() => {
    try {
      setIsPracticalPod(
        !FORCE_NOT_PRACTICAL_POD &&
          (FORCE_PRACTICAL_POD || chromeDeviceDataResponse != null),
      )
      setHasDeterminedIsPracticalPod(true)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }, [chromeDeviceDataResponse])

  const broadcastDimensions = useCallback(
    (h: any, w: any, isPwa: boolean, fullScreen: boolean) => {
      try {
        setHeight(h)
        setWidth(w)
        setisRunningPwa(isPwa)
        setIsFullscreen(fullScreen)
      } catch (e) {
        if (DEBUG_MODE) console.log(e)
        logSentryError(e, SENTRY_COMPONENT_NAME)
      }
    },
    [],
  )

  const clearAllNotificationsClicked = useCallback(() => {
    try {
      setNotifications([])
      setAfterClearAllNotificationsClicked(true)
      let trz: NodeJS.Timeout | null = setTimeout(() => {
        setAfterClearAllNotificationsClicked(false)
        try {
          if (trz != null) {
            clearTimeout(trz)
            trz = null
          }
        } catch {}
      }, 7000)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }, [])

  useEffect(() => {
    try {
      if (allEns?.length > 0 && accumSongs?.length > 0) {
        mergeSongsWithSuppliedEntries(
          accumSongs,
          allEns,
          avifCompatible == true,
          allEns?.length > accumSongs?.length,
          isPracticalPod,
        )
          .then(async (mergedSongs: any) => {
            try {
              bindInitialData(
                shuffleSongs(mergedSongs?.length > 0 ? mergedSongs : []),
              )
            } catch (za: any) {
              if (DEBUG_MODE) console.log(za)
              logSentryError(za, SENTRY_COMPONENT_NAME)
            }
          })
          .catch((ez: any) => {
            if (DEBUG_MODE) console.log(ez)
            logSentryError(ez, SENTRY_COMPONENT_NAME)
          })
      }
    } catch {}
  }, [allEns, accumSongs])

  function getRemainingSongs() {
    try {
      let cursorResp = null
      setIterations(iterations + 1)
      if (iterations < MAX_GAS_ITERATIONS) {
        const fnStr: string[] = [
          `self.addEventListener('message', async (event) => {
                  if (event.data != undefined && event.data != null && event.data.toString().trim() != '') {
                      const opts = {
                        method: 'GET',
                        headers: {
                          'ga': '1',
                        },
                        next: { revalidate: ${(
                          GLOBAL_RAW_ENTRIES_ARTISTS_AND_GENRECONTRACTS_FETCH_AND_RESPONSE_CACHE_REVALIDATE_MINUTES *
                          60
                        ).toString()},
                        tags: ['${uuidv4()}'], }, 
                      }
                      await fetch('${HOST}' + '/api/ps-gc/gas?ga=1', opts).then(async (ret) => {
                        if (${DEBUG_MODE}) console.log(ret)
                        let resp = await ret?.json()
                        let jsonString = JSON.stringify(resp);
                        let encoder = new TextEncoder();
                        let arrayBuffer = encoder.encode(jsonString).buffer;
                        resp = undefined
                        encoder = undefined
                        jsonString = undefined
                        self.postMessage({status: ret?.status, respData: arrayBuffer})
                      })
                      .catch((e) => {
                        if (${DEBUG_MODE}) console.log(e)
                      })
                  } else self.postMessage({status: -1, respData: null})
            })`,
        ]
        const jsBlob: Blob = new Blob(fnStr, { type: 'text/javascript' })
        const workerURL: string = URL.createObjectURL(jsBlob)
        const worker: Worker = new Worker(workerURL)
        worker.postMessage(true)
        worker.onmessage = (event: MessageEvent<any>) => {
          try {
            try {
              cursorResp = JSON.parse(
                new TextDecoder().decode(event?.data?.respData as ArrayBuffer),
              )
            } catch (ze: any) {
              if (DEBUG_MODE) console.log(ze)
              logSentryError(ze, SENTRY_COMPONENT_NAME)
              cursorResp = null
            }
            if (
              !cursorResp?.success ||
              isNullOrUndefined(cursorResp?.songsData) ||
              cursorResp?.songsData?.length < 1
            ) {
              let trz: NodeJS.Timeout | null = setTimeout(() => {
                setGetRemainingSongsTrigger(!getRemainingSongsTrigger)
                try {
                  if (trz != null) {
                    clearTimeout(trz)
                    trz = null
                  }
                } catch {}
              }, 200)
              cursorResp = undefined
            } else {
              let acSongs = accumSongs
              let newSongs: any =
                acSongs?.length > 0
                  ? acSongs.concat(
                      cursorResp?.songsData?.length > 0
                        ? cursorResp?.songsData
                        : [],
                    )
                  : cursorResp?.songsData
              if (getRemainingSongsTrigger == null) {
                setAccumSongs(newSongs)
                setGetRemainingSongsTrigger(true)
              } else {
                setWholeSongSetRetrieved(true)
                setAccumSongs(newSongs)
              }
            }
          } catch (eza: any) {
            if (DEBUG_MODE) console.log(eza)
            logSentryError(eza, SENTRY_COMPONENT_NAME)
          }
        }
        worker.onmessageerror = (ev: any) => {
          try {
            if (DEBUG_MODE) console.log(ev)
            logSentryError('worker.onmessageerror - 1', SENTRY_COMPONENT_NAME)
          } catch (zae: any) {
            if (DEBUG_MODE) console.log(zae)
            logSentryError(zae, SENTRY_COMPONENT_NAME)
          } finally {
            worker.terminate()
          }
        }
        worker.onerror = (ev: any) => {
          try {
            if (DEBUG_MODE) console.log(ev)
            logSentryError('worker.onerror - 1', SENTRY_COMPONENT_NAME)
          } catch (zae: any) {
            if (DEBUG_MODE) console.log(zae)
            logSentryError(zae, SENTRY_COMPONENT_NAME)
          } finally {
            worker.terminate()
          }
        }
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
    }
  }

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null
    try {
      if (getRemainingSongsTrigger != null) {
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(() => {
            getRemainingSongs()
          })
        } else
          timeoutId = setTimeout(
            () => {
              if (isSafari || isMobileSafari) getRemainingSongs()
              else setGetRemainingSongsTrigger(!getRemainingSongsTrigger)
            },
            isSafari || isMobileSafari ? 1 : 1000,
          )
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
    return () => {
      try {
        if (timeoutId != null) {
          clearTimeout(timeoutId)
          timeoutId = null
        }
      } catch {}
    }
  }, [isSafari, isMobileSafari, avifCompatible, getRemainingSongsTrigger])

  function notifySongStillLoading() {
    try {
      toast(
        translate(
          'Wait for the song to load.',
          'Espera a que se cargue la canción.',
          'Veuillez patienter pendant le chargement de la chanson.',
          '노래가 로드될 때까지 기다려 주세요.',
          selectedLanguage,
        ),
        'neutral',
      )
      //@ts-ignore
      player.current.audio.current.stop()
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null
    try {
      if (retryCountGetAllEntries < MAXIMUM_RETRIES_GET_ALL_ENTRIES) {
        timeoutId = setTimeout(async () => {
          try {
            let allEntries: any = await getAllBulkLicensedEntries()
            if (allEntries?.length > 0) setAllEns(allEntries)
            else {
              let timeoutId2: NodeJS.Timeout | null = null
              timeoutId2 = setTimeout(
                () => {
                  try {
                    setRetryCountGetAllEntries(retryCountGetAllEntries + 1)
                  } catch {
                  } finally {
                    try {
                      if (timeoutId2 != null) {
                        clearTimeout(timeoutId2)
                        timeoutId2 = null
                      }
                    } catch {}
                  }
                },
                retryCountGetAllEntries < 1
                  ? RETRY_GET_ALL_ENTRIES_MS
                  : retryCountGetAllEntries * RETRY_GET_ALL_ENTRIES_MS,
              )
            }
          } catch {
          } finally {
            try {
              if (timeoutId != null) {
                clearTimeout(timeoutId)
                timeoutId = null
              }
            } catch {}
          }
        }, 1)
      }
    } catch {}
  }, [retryCountGetAllEntries])

  useEffect(() => {
    if (
      web2UserIdLoggedIn != null &&
      !firstSongSetBound &&
      !wholeSongSetRetrieved
    ) {
      setTimeout(() => {
        try {
          const fnStr: string[] = [
            `self.addEventListener('message', async (event) => {
            const opts = {
              method: 'GET',
              headers: {
                'ga': ${web2UserIdLoggedIn ? '1' : '0'}
              },
              next: { revalidate: ${(
                GLOBAL_RAW_ENTRIES_ARTISTS_AND_GENRECONTRACTS_FETCH_AND_RESPONSE_CACHE_REVALIDATE_MINUTES *
                60
              ).toString()}, tags: ['gas'] }, 
            }
            await fetch('${HOST}' + '/api/ps-gc/gas?ga=${
              web2UserIdLoggedIn || ga?.current ? '1' : '0'
            }', opts).then(async (ret) => {
              if (${DEBUG_MODE}) console.log(ret)
              let resp = await ret?.json()
              let jsonString = JSON.stringify(resp);
              let encoder = new TextEncoder();
              let arrayBuffer = encoder.encode(jsonString).buffer;
              resp = undefined
              encoder = undefined
              jsonString = undefined
              self.postMessage({status: ret?.status, respData: arrayBuffer})
            })
            .catch((e) => {
              if (${DEBUG_MODE}) console.log(e)
              self.postMessage({status: -1, respData: null})
            })
          })`,
          ]
          const jsBlob: Blob = new Blob(fnStr, { type: 'text/javascript' })
          const workerURL: string = URL.createObjectURL(jsBlob)
          const worker: Worker = new Worker(workerURL)
          worker.postMessage('')
          worker.onmessage = async (event: MessageEvent<any>) => {
            try {
              let resp: any = JSON.parse(
                new TextDecoder().decode(event?.data?.respData as ArrayBuffer),
              )
              if (
                !resp?.success ||
                isNullOrUndefined(resp?.songsData) ||
                resp?.songsData?.length < 1
              ) {
                resp = undefined
                let tiza: NodeJS.Timeout | null = setTimeout(() => {
                  getInitialOrAllSongsTrigger(!initialOrAllSongsTrigger)
                  try {
                    if (tiza != null) {
                      clearTimeout(tiza)
                      tiza = null
                    }
                  } catch {}
                }, 200)
                worker.terminate()
              } else {
                let newSongs = resp?.songsData
                ct.current = resp?.ct
                ga.current = resp?.ga
                if (resp?.ga && !web2UserIdLoggedIn) {
                  setAccumSongs(newSongs)
                  setGetRemainingSongsTrigger(!getRemainingSongsTrigger)
                } else {
                  setWholeSongSetRetrieved(true)
                  setAccumSongs(newSongs)
                }
                worker.terminate()
              }
            } catch (ea: any) {
              if (DEBUG_MODE) console.log(ea)
              logSentryError(ea, SENTRY_COMPONENT_NAME)
              worker.terminate()
            }
          }
          worker.onmessageerror = (ev: any) => {
            try {
              if (DEBUG_MODE) console.log(ev)
              logSentryError('worker.onmessageerror - 5', SENTRY_COMPONENT_NAME)
            } catch (zae: any) {
              if (DEBUG_MODE) console.log(zae)
              logSentryError(zae, SENTRY_COMPONENT_NAME)
            } finally {
              worker.terminate()
            }
          }
          worker.onerror = (ev: any) => {
            try {
              if (DEBUG_MODE) console.log(ev)
              logSentryError('worker.onerror - 5', SENTRY_COMPONENT_NAME)
            } catch (zae: any) {
              if (DEBUG_MODE) console.log(zae)
              logSentryError(zae, SENTRY_COMPONENT_NAME)
            } finally {
              worker.terminate()
            }
          }
        } catch (e: any) {
          if (DEBUG_MODE) console.log(e)
          logSentryError(e, SENTRY_COMPONENT_NAME)
        }
      }, 1)
    }
  }, [web2UserIdLoggedIn, initialOrAllSongsTrigger])

  const postPlaylistSongEntries = useCallback(
    async (playlistId: any, newEntries: any) => {
      return new Promise<boolean>(async (resolve) => {
        try {
          let respData: any = await postPlaylistEntries(
            playlistId,
            newEntries,
            web2UserSessionId,
          )
          resolve(respData?.success == true)
        } catch (e) {
          if (DEBUG_MODE) console.log(e)
          logSentryError(e, SENTRY_COMPONENT_NAME)
          resolve(false)
        }
      })
    },
    [web2UserSessionId],
  )

  const synchronizePlaydeckWithPlaylistChange = useCallback(
    async (newEntries: any, originalEntries: any, playlist_id: any) => {
      try {
        if (
          newEntries?.length > 0 &&
          isSamePlaylist(
            songs_Master,
            originalEntries,
            lastClickedPlaylistId,
            playlist_id,
          )
        ) {
          const pl: any =
            masterPlaylistsAndPresets?.length > 0 &&
            masterPlaylistsAndPresets.some(
              (p: any) => p?.playlist_id == playlist_id,
            )
              ? masterPlaylistsAndPresets.find(
                  (p: any) => p?.playlist_id == playlist_id,
                )
              : null
          if (pl != null) setPlaydeck(newEntries, true, false, null, pl?.name)
        }
      } catch (e) {
        if (DEBUG_MODE) console.log(e)
        logSentryError(e, SENTRY_COMPONENT_NAME)
      }
    },
    [songs_Master, lastClickedPlaylistId, masterPlaylistsAndPresets],
  )

  const removeSongFromPlaylist = useCallback(
    async (song: any, entries: any, playlist_id: any) => {
      try {
        if (entries?.length > 0) {
          let cloneEntries: any = entries
          let targetIdx: number = -1
          cloneEntries?.map((en: any, i: any) => {
            if (
              en?.contract_address == song?.contract_address &&
              en?.token_id == song?.token_id &&
              isNotNullOrEmptyString(en?.contract_address) &&
              isNotNullOrEmptyString(en?.token_id)
            )
              targetIdx = i
          })
          if (targetIdx > -1) {
            let isPlaylistInPlaydeck = isSamePlaylist(
              songs_Master,
              entries,
              lastClickedPlaylistId,
              playlist_id,
            )
            cloneEntries.splice(targetIdx, 1)
            const res: any = await postPlaylistEntries(
              playlist_id,
              cloneEntries,
              web2UserSessionId,
            )
            if (isPlaylistInPlaydeck) {
              const pl: any =
                masterPlaylistsAndPresets?.length > 0 &&
                masterPlaylistsAndPresets.some(
                  (p: any) => p?.playlist_id == playlist_id,
                )
                  ? masterPlaylistsAndPresets.find(
                      (p: any) => p?.playlist_id == playlist_id,
                    )
                  : null
              if (pl != null)
                setPlaydeck(cloneEntries, true, false, null, pl?.name)
            }
            if (!res?.success)
              logSentryError('Failed to remove song.', SENTRY_COMPONENT_NAME)
          }
        }
      } catch (e) {
        if (DEBUG_MODE) console.log(e)
        logSentryError(e, SENTRY_COMPONENT_NAME)
      }
    },
    [
      songs_Master,
      web2UserSessionId,
      lastClickedPlaylistId,
      masterPlaylistsAndPresets,
    ],
  )

  async function bindInitialData(licensedAndPublishedSongs: any) {
    try {
      if (!firstSongSetBound) {
        setFirstSongSetBound(true)
        if (navigating && song_Master) {
          setSongIdx_Master(0)
          licensedAndPublishedSongs.unshift(song_Master)
        }
        if (!navigating) setChangingSongs_Master(true)
        let selIdx: any = licensedAndPublishedSongs?.length - 1
        const nextSong: any = licensedAndPublishedSongs[selIdx]
        setSong_Master(nextSong)
        setSongCoverImageLocalOrExternalURL_Master(nextSong?.frontImageCD)
        setSongContractAddress(nextSong?.contract_address)
        setSongTokenId(nextSong?.token_id)
        bindSong(nextSong, selIdx, true, false)
      }
      if (wholeSongSetRetrieved)
        setSongs_Master_Original(licensedAndPublishedSongs)
      if (!navigating && !firstSongSetBound)
        setTimeout(() => setChangingSongs_Master(false), 200)
      loadSongs(
        licensedAndPublishedSongs,
        licensedAndPublishedSongs?.length - 1,
        false,
        true,
        true,
        null,
      )
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }

  const resumeSong = useCallback(async (restartIfAtEnd: boolean) => {
    try {
      //@ts-ignore
      let currTime: any =
        restartIfAtEnd &&
        //@ts-ignore
        player.current.audio.current.currentTime < //@ts-ignore
          player.current.audio.current.currentTime - 10 //@ts-ignore
          ? 0
          : //@ts-ignore
            player.current.audio.current.currentTime //@ts-ignore
      //@ts-ignore
      if (player?.current?.audio?.current) {
        //@ts-ignore
        player.current.audio.current.currentTime = currTime ? currTime : 0
        //@ts-ignore
        let tis: NodeJS.Timeout | null = setTimeout(async () => {
          try {
            //@ts-ignore
            var rez: any = await player?.current?.audio?.current.play()
            if (rez !== undefined) {
              rez
                .then(function () {
                  if (DEBUG_MODE) console.log('Playback started.')
                })
                .catch(function (error: any) {
                  if (DEBUG_MODE) console.log(error)
                  logSentryError(error, SENTRY_COMPONENT_NAME)
                })
                .finally(() => {
                  try {
                    if (tis != null) {
                      clearTimeout(tis)
                      tis = null
                    }
                  } catch {}
                })
            } else {
              try {
                if (tis != null) {
                  clearTimeout(tis)
                  tis = null
                }
              } catch {}
            }
          } catch (za: any) {
            if (DEBUG_MODE) console.log(za)
            logSentryError(za, SENTRY_COMPONENT_NAME)
            try {
              if (tis != null) {
                clearTimeout(tis)
                tis = null
              }
            } catch {}
          }
        }, 200)
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }, [])

  async function tryPlayPlayer() {
    return new Promise<any>(async (resolve) => {
      //@ts-ignore
      let isPlaying: boolean =
        //@ts-ignore
        player?.current?.audio?.current.currentTime > 0 &&
        //@ts-ignore
        !player?.current?.audio?.current.paused &&
        //@ts-ignore
        !player?.current?.audio?.current.ended &&
        //@ts-ignore
        player?.current?.audio?.current.readyState >
          //@ts-ignore
          player?.current?.audio?.current.HAVE_CURRENT_DATA

      if (!isPlaying) {
        try {
          //@ts-ignore
          await player?.current?.audio?.current
            .play()
            .then(() => {
              resolve(true)
            })
            .catch(function (z: any) {
              if (DEBUG_MODE) console.log(z)
              if (
                isNotNullOrEmptyString(z) &&
                z.toString().indexOf('NotAllowedError') > -1
              ) {
                pauseSong()
                setPlaying(false)
                if (
                  !hasShownScheduledPlaylistsEnabledModal &&
                  hasAScheduledPlaylistSoForceInteractionWithTheDocument.current &&
                  masterScheduleWasNull
                ) {
                  setHasShownScheduledPlaylistsEnabledModal(true)
                  if (
                    web2UserSessionId != PRESETS_AND_STATIONS_ADMIN_WEB2_USER_ID
                  )
                    setScheduledPlaylistsEnabledModalOpened(true)
                  hasAScheduledPlaylistSoForceInteractionWithTheDocument.current = false
                }
              } else {
                let tall: NodeJS.Timeout | null = setTimeout(async () => {
                  try {
                    //@ts-ignore
                    await player?.current?.audio?.current.play()
                  } catch (ea: any) {
                    if (
                      isNotNullOrEmptyString(z) &&
                      z.toString().indexOf('NotAllowedError') > -1
                    ) {
                      pauseSong()
                      if (
                        !hasShownScheduledPlaylistsEnabledModal &&
                        hasAScheduledPlaylistSoForceInteractionWithTheDocument?.current &&
                        masterScheduleWasNull
                      ) {
                        if (
                          web2UserSessionId !=
                          PRESETS_AND_STATIONS_ADMIN_WEB2_USER_ID
                        )
                          setScheduledPlaylistsEnabledModalOpened(true)
                        hasAScheduledPlaylistSoForceInteractionWithTheDocument.current = false
                      }
                    } else {
                      if (DEBUG_MODE) console.log(z)
                    }
                  } finally {
                    try {
                      if (tall != null) {
                        clearTimeout(tall)
                        tall = null
                      }
                    } catch {}
                  }
                }, 50)
              }
              resolve(false)
            })
            .finally(() => {
              let tidz: NodeJS.Timeout | null = setTimeout(() => {
                try {
                  audioControlsTempDisabled.current = false
                } catch (ez: any) {
                  if (DEBUG_MODE) console.log(ez)
                } finally {
                  try {
                    if (tidz != null) {
                      clearTimeout(tidz)
                      tidz = null
                    }
                  } catch {}
                }
              }, 2000)
              resolve(true)
            })
        } catch (e) {
          if (DEBUG_MODE) console.log(e)
          resolve(false)
        }
      } else resolve(true)
    })
  }

  async function loadLocallyAndPlay(
    externalUrl: any,
    fromInitialAppLoad: boolean,
  ) {
    return new Promise<any>(async (resolve) => {
      try {
        if (!fromInitialAppLoad) audioControlsTempDisabled.current = true
      } catch (e) {
        if (DEBUG_MODE) console.log(e)
        logSentryError(e, SENTRY_COMPONENT_NAME)
        audioControlsTempDisabled.current = false
      } finally {
        try {
          try {
            let audioUrl: any =
              externalUrl.toString().toLowerCase().trim().indexOf('https://') ==
              -1
                ? simpleDecrypt(externalUrl)
                : externalUrl
            playFromExternalUrl(audioUrl, fromInitialAppLoad, false)
            resolve(true)
          } catch (errr: any) {
            if (DEBUG_MODE) console.log(errr)
            logSentryError(errr, SENTRY_COMPONENT_NAME)
            resolve(false)
          } finally {
            let tam: NodeJS.Timeout | null = setTimeout(() => {
              try {
                audioControlsTempDisabled.current = false
              } catch (erz: any) {
                if (DEBUG_MODE) console.log(erz)
                logSentryError(erz, SENTRY_COMPONENT_NAME)
              } finally {
                try {
                  if (tam != null) {
                    clearTimeout(tam)
                    tam = null
                  }
                } catch {}
              }
            }, 500)
          }
        } catch (err) {
          if (DEBUG_MODE) console.log(err)
          logSentryError(err, SENTRY_COMPONENT_NAME)
          resolve(false)
        }
      }
    })
  }

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null
    if (web2UserIdLoggedIn === true) {
      try {
        timeoutId = setTimeout(async () => {
          try {
            if (isNotNullOrUndefined(favorites) && wholeSongSetBound) {
              workerMergeFavoritesWithSongs(
                favorites?.length > 0 ? favorites : [],
              )
            }
          } catch (e) {
            if (DEBUG_MODE) console.log(e)
            logSentryError(e, SENTRY_COMPONENT_NAME)
          }
        }, 10)
      } catch (ea: any) {
        if (DEBUG_MODE) console.log(ea)
        logSentryError(ea, SENTRY_COMPONENT_NAME)
      }
    } else if (web2UserIdLoggedIn === false) setGotFavorites(true)
    return () => {
      try {
        if (timeoutId != null) clearTimeout(timeoutId)
      } catch {}
    }
  }, [favorites, wholeSongSetBound, web2UserIdLoggedIn])

  function workerMergeFavoritesWithSongs(faves: any[]) {
    try {
      const fnStr: string[] = MERGE_FAVORITES_WITH_SONGS_FUNCTION_STRING
      const jsBlob = new Blob(fnStr, { type: 'text/javascript' })
      const workerURL = URL.createObjectURL(jsBlob)
      const worker = new Worker(workerURL)
      worker.postMessage({
        songs: songs_Master,
        favorites: faves,
      })
      worker.onmessage = async (event: MessageEvent<any>) => {
        let respData: any = null
        try {
          respData = JSON.parse(
            new TextDecoder().decode(event?.data?.respData as ArrayBuffer),
          )
          if (respData?.success) {
            if (respData?.songs?.length > 0) {
              setSongs_Master(respData?.songs)
              setFavoritesPageFavorites(
                respData?.favorites?.length > 0 ? respData?.favorites : [],
              )
            }
            if (!hasSetFirst_Song_Master_Favorited) {
              let rez = respData?.songs?.filter(
                (s: any) =>
                  s?.contract_address == song_Master?.contract_address &&
                  s?.token_id == song_Master?.token_id,
              )
              if (rez?.length > 0) {
                let cln = structuredClone(song_Master)
                cln.favorited = rez[0].favorited
                setSong_Master(cln)
              }
              setHasSetFirst_Song_Master_Favorited(true)
            }
            setGotFavorites(true)
          } else setGotFavorites(true)
        } catch (ee: any) {
          setGotFavorites(true)
          if (DEBUG_MODE) console.log(ee)
          logSentryError(ee, SENTRY_COMPONENT_NAME)
        } finally {
          respData = undefined
          worker.terminate()
        }
      }
      worker.onmessageerror = (ev: any) => {
        try {
          setGotFavorites(true)
          if (DEBUG_MODE) console.log(ev)
          logSentryError('worker.onmessageerror - 6', SENTRY_COMPONENT_NAME)
        } catch (zae: any) {
          if (DEBUG_MODE) console.log(zae)
          logSentryError(zae, SENTRY_COMPONENT_NAME)
        } finally {
          worker.terminate()
        }
      }
      worker.onerror = (ev: any) => {
        try {
          setGotFavorites(true)
          if (DEBUG_MODE) console.log(ev)
          logSentryError('worker.onerror - 6', SENTRY_COMPONENT_NAME)
        } catch (zae: any) {
          if (DEBUG_MODE) console.log(zae)
          logSentryError(zae, SENTRY_COMPONENT_NAME)
        } finally {
          worker.terminate()
        }
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }

  useEffect(() => {
    try {
      if (wholeSongSetBound) {
        if (canFavoriteAndPlaylist) {
          setTimeout(
            () => {
              const jsBlob: Blob = new Blob(GET_FAVORITES_CALLSTRING, {
                type: 'text/javascript',
              })
              const workerURL: string = URL.createObjectURL(jsBlob)
              const worker: Worker = new Worker(workerURL)
              worker.postMessage(web2UserSessionId)
              worker.onmessage = (event: MessageEvent<any>) => {
                if (event?.data?.length > 0) setFavorites(event?.data)
                else if (retryCountGetFavorites < MAXIMUM_RETRIES_FAVORITES)
                  setRetryCountGetFavorites(retryCountGetFavorites + 1)
                worker.terminate()
              }
              worker.onmessageerror = (ev: any) => {
                try {
                  if (DEBUG_MODE) console.log(ev)
                  logSentryError(
                    'worker.onmessageerror - 7',
                    SENTRY_COMPONENT_NAME,
                  )
                } catch (zae: any) {
                  if (DEBUG_MODE) console.log(zae)
                  logSentryError(zae, SENTRY_COMPONENT_NAME)
                } finally {
                  worker.terminate()
                }
              }
              worker.onerror = (ev: any) => {
                try {
                  if (DEBUG_MODE) console.log(ev)
                  logSentryError('worker.onerror - 7', SENTRY_COMPONENT_NAME)
                } catch (zae: any) {
                  if (DEBUG_MODE) console.log(zae)
                  logSentryError(zae, SENTRY_COMPONENT_NAME)
                } finally {
                  worker.terminate()
                }
              }
            },
            retryCountGetFavorites < 1 || gotFavorites
              ? 1
              : retryCountGetFavorites * RETRY_GET_FAVORITES_MS,
          )
        } else if (favorites != null) setFavorites([])
      }
    } catch (e: any) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }, [
    wholeSongSetBound,
    triggerGetFavorites,
    retryCountGetFavorites,
    canFavoriteAndPlaylist,
  ])

  useEffect(() => {
    try {
      if (
        web2UserIdLoggedIn === true &&
        pageComponentDisplayName != FOUR_O_FOUR_PAGE_DISPLAY_NAME &&
        pageComponentDisplayName != FIVE_HUNDRED_PAGE_DISPLAY_NAME &&
        pageComponentDisplayName != ERROR_PAGE_DISPLAY_NAME
      ) {
        setMasterScheduleWasNull(masterSchedule == null)
        const fnStr: string[] = [
          `self.addEventListener('message', async (event) => {
            if (event.data != undefined && event.data != null && event.data.toString().trim() != '') {
              const opts = {
                method: 'POST',
                body: JSON.stringify({
                  createdByWeb2UserId: event.data,
                }),
              }
              await fetch('${HOST}/api/p.stream-sv/ls/gls', opts)
                .then(async (x) => {
                  const ret = await x.json()
                  self.postMessage(ret?.locationSchedule)
                })
                .catch((e) => {
                  if (${DEBUG_MODE}) console.log(e)
                  self.postMessage(null)
                })
            } else self.postMessage(null)
          })`,
        ]
        const jsBlob: Blob = new Blob(fnStr, { type: 'text/javascript' })
        const workerURL: string = URL.createObjectURL(jsBlob)
        const worker: Worker = new Worker(workerURL)
        if (
          canFavoriteAndPlaylist &&
          isNotNullOrEmptyString(web2UserSessionId)
        ) {
          worker.postMessage(web2UserSessionId)
          worker.onmessage = (event: MessageEvent<any>) => {
            setMasterSchedule(event?.data)
            setTriggerRebindSchedule(!triggerRebindSchedule)
            worker.terminate()
          }
          worker.onmessageerror = (ev: any) => {
            try {
              if (DEBUG_MODE) console.log(ev)
              logSentryError('worker.onmessageerror - 8', SENTRY_COMPONENT_NAME)
            } catch (zae: any) {
              if (DEBUG_MODE) console.log(zae)
              logSentryError(zae, SENTRY_COMPONENT_NAME)
            } finally {
              worker.terminate()
            }
          }
          worker.onerror = (ev: any) => {
            try {
              if (DEBUG_MODE) console.log(ev)
              logSentryError('worker.onerror - 8', SENTRY_COMPONENT_NAME)
            } catch (zae: any) {
              if (DEBUG_MODE) console.log(zae)
              logSentryError(zae, SENTRY_COMPONENT_NAME)
            } finally {
              worker.terminate()
            }
          }
        } else {
          setMasterSchedule(null)
          worker.terminate()
        }
      } else setMasterSchedule(null)
    } catch (e: any) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }, [web2UserSessionId, canFavoriteAndPlaylist, triggerGetMasterSchedule])

  const playScheduledPlaylist = useCallback(
    async (
      currentlyScheduledPlaylistOrPreset: any,
      alsoPlay: boolean,
      timeSlot: number,
    ) => {
      try {
        if (
          debouncedPlaying &&
          isSamePlaylist(
            songs_Master,
            currentlyScheduledPlaylistOrPreset?.entries,
            lastClickedPlaylistId,
            currentlyScheduledPlaylistOrPreset?.playlist_id,
          )
        ) {
          if (DEBUG_MONITOR_SCHEDULE_TIMERS) console.log('a-b-4')
        } else {
          if (alsoPlay) {
            await pauseSong(true)
          }
          let onlySongs: any = []
          let entriesCopy: any = JSON.parse(
            stringify(currentlyScheduledPlaylistOrPreset?.entries, null, 2),
          )
          entriesCopy = entriesCopy.sort((a: any, b: any) =>
            a.sortOrder > b.sortOrder ? 1 : b.sortOrder > a.sortOrder ? -1 : 0,
          )
          entriesCopy.map((e: any, i: any) => {
            e.sortOrder = i + 1
          })
          entriesCopy.map((en: any) => {
            onlySongs.push(en)
          })
          if (alsoPlay) {
            if (timeSlot == 0) lastScheduledPlaylistPlayed.current = new Date()
            else if (timeSlot == 1)
              lastScheduledPlaylistPlayed_One.current = new Date()
            else lastScheduledPlaylistPlayed_Two.current = new Date()
          }
          if (currentlyScheduledPlaylistOrPreset?.shuffleOnPlay == true)
            onlySongs = shuffleSongs(onlySongs)
          setLastClickedPlaylistId(
            currentlyScheduledPlaylistOrPreset?.playlist_id,
          )
          setPlaydeck(
            onlySongs,
            true,
            alsoPlay,
            null,
            currentlyScheduledPlaylistOrPreset?.name,
          )
        }
        setRefreshPlaylistPlayingOverlay(!refreshPlaylistPlayingOverlay)
      } catch (e) {
        if (DEBUG_MODE) console.log(e)
        logSentryError(e, SENTRY_COMPONENT_NAME)
      }
    },
    [
      songs_Master,
      debouncedPlaying,
      lastClickedPlaylistId,
      hasUsedNonSubscribedPlayLimit,
      songs_Master_Original?.length,
      refreshPlaylistPlayingOverlay,
      isAuthenticatedUserAndSubscribed,
    ],
  )

  const performRestart = useCallback(() => {
    try {
      if (!deviceRestartModalAutoClosed)
        setDeviceIsAboutToRestartModalOpened(true)
      setTimeout(() => {
        try {
          callKioskExtension('callRestart')
        } catch (ezae) {
          if (DEBUG_MODE) console.log(ezae)
          logSentryError(ezae, SENTRY_COMPONENT_NAME)
        }
      }, 60000)
      setTimeout(() => {
        try {
          setDeviceRestartModalAutoClosed(true)
          setDeviceIsAboutToRestartModalOpened(false)
          setTimeout(() => {
            setDeviceRestartModalAutoClosed(false)
          }, 60000)
        } catch (ezae) {
          if (DEBUG_MODE) console.log(ezae)
          logSentryError(ezae, SENTRY_COMPONENT_NAME)
        }
      }, 80000)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }, [deviceRestartModalAutoClosed])

  const wasPausedLessThanOneMinuteAgo = useCallback(() => {
    let ret: boolean = false
    try {
      if (lastPause?.current != null && !scheduledPlaylistsEnabledModalOpened) {
        const now: Date = new Date()
        const timeDifference: number =
          now.getTime() - lastPause.current.getTime()
        const oneMinuteInMilliseconds: number = 60 * 1000
        ret = timeDifference < oneMinuteInMilliseconds
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
    return ret
  }, [lastPause?.current, scheduledPlaylistsEnabledModalOpened])

  const wasScheduledPlaylistPlayedLessThanOneMinuteAgo = useCallback(() => {
    let ret: boolean = false
    try {
      if (lastScheduledPlaylistPlayed?.current != null) {
        const now: Date = new Date()
        const timeDifference: number =
          now.getTime() - lastScheduledPlaylistPlayed.current.getTime()
        const oneMinuteInMilliseconds: number = 60 * 1000
        ret = timeDifference < oneMinuteInMilliseconds
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
    return ret
  }, [
    lastScheduledPlaylistPlayed?.current,
    scheduledPlaylistsEnabledModalOpened,
  ])

  const wasScheduledPlaylistPlayedLessThanOneMinuteAgo_One = useCallback(() => {
    let ret: boolean = false
    try {
      if (lastScheduledPlaylistPlayed_One?.current != null) {
        const now: Date = new Date()
        const timeDifference: number =
          now.getTime() - lastScheduledPlaylistPlayed_One.current.getTime()
        const oneMinuteInMilliseconds: number = 60 * 1000
        ret = timeDifference < oneMinuteInMilliseconds
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
    return ret
  }, [
    scheduledPlaylistsEnabledModalOpened,
    lastScheduledPlaylistPlayed_One?.current,
  ])

  const [hasOfflineTracks, setHasOfflineTracks] = useState<boolean>(false)

  useEffect(() => {
    if (web2UserIdLoggedIn) {
      async function checkInitialCache() {
        const available = await checkHasOfflineTracks()
        if (available) setHasOfflineTracks(true)
      }
      checkInitialCache()
    }
  }, [web2UserIdLoggedIn])

  const wasScheduledPlaylistPlayedLessThanOneMinuteAgo_Two = useCallback(() => {
    let ret: boolean = false
    try {
      if (lastScheduledPlaylistPlayed_Two?.current != null) {
        const now: Date = new Date()
        const timeDifference: number =
          now.getTime() - lastScheduledPlaylistPlayed_Two.current.getTime()
        const oneMinuteInMilliseconds: number = 60 * 1000
        ret = timeDifference < oneMinuteInMilliseconds
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
    return ret
  }, [
    scheduledPlaylistsEnabledModalOpened,
    lastScheduledPlaylistPlayed_Two?.current,
  ])

  useEffect(() => {
    try {
      if (!web2UserIdLoggedIn) return
      if (hasInteracted || isPracticalPod) {
        let tid: NodeJS.Timeout | null = setTimeout(() => {
          try {
            triggerMonitorSchedule.current = uuidv4()
            if (tid != null) {
              clearTimeout(tid)
              tid = null
            }
          } catch (ez: any) {
            if (DEBUG_MODE) console.log(ez)
          }
        }, 1000)
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
    }
  }, [hasInteracted, isPracticalPod, web2UserIdLoggedIn])

  const toggleFavorite = useCallback(
    async (
      contract_address: any,
      token_id: any,
      add: boolean,
      web2UserSessionId: any,
    ) => {
      return new Promise<boolean>(async (resolve) => {
        try {
          let cln = structuredClone(song_Master)
          cln.favorited = add
          setSong_Master(cln)
          const jsBlob: Blob = new Blob(UPSERT_OR_DELETE_FAVORITE_CALLSTRING, {
            type: 'text/javascript',
          })
          const workerURL: string = URL.createObjectURL(jsBlob)
          const worker: Worker = new Worker(workerURL)
          worker.postMessage({
            contract_address: contract_address,
            token_id: token_id,
            web2UserId: web2UserSessionId,
            addFavorite: add,
          })
          worker.onmessage = (event: MessageEvent<any>) => {
            if (event?.data) {
              setTriggerGetFavorites(!triggerGetFavorites)
              if (
                song_Master?.contract_address == contract_address &&
                song_Master.token_id == token_id
              ) {
                let cln = structuredClone(song_Master)
                cln.favorited = add
                setSong_Master(cln)
                setSongs_Master((prevSongs: any) => {
                  return prevSongs.map((prevSong: any) => {
                    if (
                      prevSong.contract_address === cln?.contract_address &&
                      prevSong?.token_id == cln?.token_id
                    ) {
                      return { ...prevSong, favorited: add }
                    }
                    return prevSong
                  })
                })
              }
            }
            return resolve(true)
          }
          worker.onmessageerror = (ev: any) => {
            try {
              if (DEBUG_MODE) console.log(ev)
              logSentryError('worker.onmessageerror - 9', SENTRY_COMPONENT_NAME)
              return resolve(false)
            } catch (zae: any) {
              if (DEBUG_MODE) console.log(zae)
              logSentryError(zae, SENTRY_COMPONENT_NAME)
              return resolve(false)
            }
          }
          worker.onerror = (ev: any) => {
            try {
              if (DEBUG_MODE) console.log(ev)
              logSentryError('worker.onerror - 9', SENTRY_COMPONENT_NAME)
              return resolve(false)
            } catch (zae: any) {
              if (DEBUG_MODE) console.log(zae)
              logSentryError(zae, SENTRY_COMPONENT_NAME)
              return resolve(false)
            }
          }
          return () => worker.terminate()
        } catch (e) {
          if (DEBUG_MODE) console.log(e)
          logSentryError(e, SENTRY_COMPONENT_NAME)
          return resolve(false)
        }
      })
    },
    [song_Master, triggerGetFavorites],
  )

  if (isIOS)
    useEffect(() => {
      try {
        const previousPortrait: boolean | null = portrait
        const isPortrait: boolean = height >= width
        setPortrait(isPortrait)
        if (previousPortrait != null && previousPortrait != isPortrait) {
          setRerenderforIOSOrientation(true)
          let tid: NodeJS.Timeout | null = setTimeout(() => {
            setRerenderforIOSOrientation(false)
            try {
              if (tid != null) {
                clearTimeout(tid)
                tid = null
              }
            } catch {}
          }, 100)
        }
      } catch (er: any) {
        if (DEBUG_MODE) console.log(er)
        logSentryError(er, SENTRY_COMPONENT_NAME)
      }
    }, [width, height])

  useEffect(() => {
    try {
      if (!web2UserIdLoggedIn) return
      const didSubscriptionElapse: boolean = !subscriptionEnd
        ? false
        : subscriptionEnd > new Date('1995-12-17T03:24:00') &&
          subscriptionEnd < new Date()
      setSubscriptionElapsed(didSubscriptionElapse)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }, [subscriptionEnd, web2UserIdLoggedIn])

  async function trytoSetAudioSrc(attemptCount: number, externalUrl: any) {
    return new Promise<any>(async (resolve) => {
      try {
        attemptCount = attemptCount + 1
        //@ts-ignore
        player.current.audio.current.src =
          toTrimmedString(externalUrl).indexOf('?') == -1
            ? externalUrl + '?067F9E41-1BBD-4B7A-A8BC-9F93A5B3FFFD=1'
            : externalUrl
        return resolve(true)
      } catch (z: any) {
        if (DEBUG_MODE) console.log(z)
        logSentryError(z, SENTRY_COMPONENT_NAME)
        if (attemptCount < 3) {
          let tid: NodeJS.Timeout | null = setTimeout(() => {
            try {
              trytoSetAudioSrc(attemptCount, externalUrl)
            } catch (za: any) {
              if (DEBUG_MODE) console.log(za)
              logSentryError(za, SENTRY_COMPONENT_NAME)
            } finally {
              try {
                if (tid != null) {
                  clearTimeout(tid)
                  tid = null
                }
              } catch {}
            }
          }, 200)
        }
        return resolve(false)
      }
    })
  }

  function unmute() {
    try {
      //@ts-ignore
      const lastVol: any = player.current.audio.current.lastVolume
      // @ts-ignore
      player.current.audio.current.volume = isNotNullOrUndefined(lastVol)
        ? lastVol
        : 1
      //@ts-ignore
      player.current.audio.current.muted = false
      setMuted(false)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
    }
  }

  function playFromExternalUrl(
    externalUrl: any,
    fromInitialAppLoad: any,
    retry: boolean,
  ) {
    try {
      if (
        fromInitialAppLoad &&
        (isSafari ||
          isMobileSafari ||
          isIOS ||
          isIOS13 ||
          isApple ||
          isChrome ||
          isFirefox)
      ) {
        let tide: NodeJS.Timeout | null = setTimeout(async () => {
          try {
            //@ts-ignore
            const lastVol: any = player.current.audio.current.lastVolume
              ? //@ts-ignore
                player.current.audio.current.lastVolume
              : 1
            let tidc: NodeJS.Timeout | null = setTimeout(async () => {
              try {
                await trytoSetAudioSrc(1, externalUrl)
                  .then(() => {
                    if (isChrome) {
                      // @ts-ignore
                      player.current.audio.current.volume =
                        isNotNullOrUndefined(lastVol) ? lastVol : 1
                      //@ts-ignore
                      player.current.audio.current.muted = false
                      //@ts-ignore
                      player?.current?.audio?.current?.pause()
                    }
                  })
                  .catch((o: any) => {
                    if (DEBUG_MODE) console.log(o)
                    logSentryError(o, SENTRY_COMPONENT_NAME)
                  })
                  .finally(() => {
                    try {
                      if (tidc != null) {
                        clearTimeout(tidc)
                        tidc = null
                      }
                    } catch {}
                    try {
                      if (tide != null) {
                        clearTimeout(tide)
                        tide = null
                      }
                    } catch {}
                  })
              } catch (ess) {
                if (DEBUG_MODE) console.log(ess)
                logSentryError(ess, SENTRY_COMPONENT_NAME)
                try {
                  if (tidc != null) {
                    clearTimeout(tidc)
                    tidc = null
                  }
                } catch {}
                try {
                  if (tide != null) {
                    clearTimeout(tide)
                    tide = null
                  }
                } catch {}
              }
            }, 200)
          } catch (z: any) {
            if (DEBUG_MODE) console.log(z)
            logSentryError(z, SENTRY_COMPONENT_NAME)
            try {
              if (tide != null) {
                clearTimeout(tide)
                tide = null
              }
            } catch {}
          }
        }, 200)
      } else {
        try {
          if (
            isNullOrUndefined(rhapTotalTimeElem?.current) ||
            isNullOrUndefined(rhapProgressIndicatorElem?.current)
          ) {
            let r: any = document.getElementsByClassName('rhap_total-time')
            let o: any = document.getElementsByClassName(
              'rhap_progress-indicator',
            )
            if (r?.length > 0) rhapTotalTimeElem.current = r[0]
            if (o?.length > 0) rhapProgressIndicatorElem.current = o[0]
          }
          if (rhapTotalTimeElem?.current)
            rhapTotalTimeElem.current.style.visibility = 'hidden'
          if (rhapProgressIndicatorElem?.current)
            rhapProgressIndicatorElem.current.style.visibility = 'hidden'
        } catch (re: any) {
          if (DEBUG_MODE) console.log(re)
          logSentryError(re, SENTRY_COMPONENT_NAME)
        }
        //@ts-ignore
        player.current.audio.current.src =
          toTrimmedString(externalUrl).indexOf('?') == -1
            ? externalUrl + '?067F9E41-1BBD-4B7A-A8BC-9F93A5B3FFFD=1'
            : externalUrl
        let tida: NodeJS.Timeout | null = setTimeout(
          () => {
            try {
              if (rhapTotalTimeElem?.current)
                rhapTotalTimeElem.current.style.visibility = 'visible'
              if (rhapProgressIndicatorElem?.current)
                rhapProgressIndicatorElem.current.style.visibility = 'visible'
            } catch (ezad: any) {
              if (DEBUG_MODE) console.log(ezad)
              logSentryError(ezad, SENTRY_COMPONENT_NAME)
            } finally {
              try {
                if (tida != null) {
                  clearTimeout(tida)
                  tida = null
                }
              } catch {}
            }
          },
          song_Master != null ? 200 : 600,
        )
      }
      if (
        //@ts-ignore
        player?.current?.audio?.current &&
        !fromInitialAppLoad
      ) {
        let tid0: NodeJS.Timeout | null = setTimeout(async () => {
          try {
            if (undefined !== player?.current && null != player?.current) {
              //@ts-ignore
              await player.current.audio.current.load()
              await tryPlayPlayer()
            } else {
              let tidz: NodeJS.Timeout | null = setTimeout(async () => {
                try {
                  //@ts-ignore
                  await player.current.audio.current.load()
                  await tryPlayPlayer()
                } catch (exa: any) {
                  if (DEBUG_MODE) console.log(exa)
                  logSentryError(exa, SENTRY_COMPONENT_NAME)
                } finally {
                  try {
                    if (tidz != null) {
                      clearTimeout(tidz)
                      tidz = null
                    }
                  } catch {}
                }
              }, 500)
            }
          } catch (er: any) {
            if (DEBUG_MODE) console.log(er)
            logSentryError(er, SENTRY_COMPONENT_NAME)
          } finally {
            try {
              if (tid0 != null) {
                clearTimeout(tid0)
                tid0 = null
              }
            } catch {}
          }
        }, 10)
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
      if (!retry) {
        let tid0: NodeJS.Timeout | null = setTimeout(() => {
          playFromExternalUrl(externalUrl, fromInitialAppLoad, true)
          try {
            if (tid0 != null) {
              clearTimeout(tid0)
              tid0 = null
            }
          } catch {}
        }, 1000)
      }
    }
  }

  useEffect(() => {
    try {
      let tid: NodeJS.Timeout | null = setTimeout(async () => {
        try {
          if (
            isAuthenticatedUserAndMaybeSubscribed &&
            isNotNullOrEmptyString(user?.customer?.stripeCustomerId) &&
            isNullOrEmptyString(user?.adminWeb2UserId)
          ) {
            await refreshSubscriptionsAndCharges(
              user?.customer?.stripeCustomerId,
            )
          } else {
            setStripeCharges([])
            setStripeSubscriptions([])
            setStripeActiveSeats(0)
            setStripeActiveHardwareSeats(0)
            let tid0: NodeJS.Timeout | null = setTimeout(() => {
              setHasResolvedStripeActiveSoftwareOrHardwareSeats(true)
              try {
                if (tid0 != null) {
                  clearTimeout(tid0)
                  tid0 = null
                }
              } catch {}
            }, 200)
          }
        } catch (ez) {
          if (DEBUG_MODE) console.log(ez)
          logSentryError(ez, SENTRY_COMPONENT_NAME)
        } finally {
          try {
            if (tid != null) {
              clearTimeout(tid)
              tid = null
            }
          } catch {}
        }
      }, 10)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }, [user?.customer?.stripeCustomerId, isAuthenticatedUserAndMaybeSubscribed])

  useEffect(() => {
    if (!web2UserIdLoggedIn) {
      setIsAuthenticatedUserAndSubscribed(false)
      return
    }
    try {
      let tid: NodeJS.Timeout | null = setTimeout(async () => {
        try {
          setIsAuthenticatedUserAndSubscribed(
            isAuthenticated && isBusinessUserSubscribed,
          )
          setIsAuthenticatedUserAndMaybeSubscribed(isUserMaybeSubscribed(user))
        } catch (ez) {
          if (DEBUG_MODE) console.log(ez)
          logSentryError(ez, SENTRY_COMPONENT_NAME)
        } finally {
          try {
            if (tid != null) {
              clearTimeout(tid)
              tid = null
            }
          } catch {}
        }
      }, 10)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }, [user, web2UserIdLoggedIn, isAuthenticated, isBusinessUserSubscribed])

  const refreshSubscriptionsAndCharges = useCallback(
    async (stripeCustomerId: any) => {
      return new Promise<boolean>(async (resolve) => {
        try {
          let charges: any = []
          if (isNotNullOrEmptyString(stripeCustomerId)) {
            const rez: any = await getCharges(stripeCustomerId)
            if (rez?.success) charges = rez?.charges
            else
              logSentryError(
                'Failed to get charges - 1.',
                SENTRY_COMPONENT_NAME,
              )
          }
          setStripeCharges(charges?.length > 0 ? charges : [])
          let subscriptions: any = isNotNullOrEmptyString(stripeCustomerId)
            ? await getSubscriptions(stripeCustomerId)
            : {
                subscriptions: [],
              }
          if (subscriptions?.length > 0) {
            subscriptions = subscriptions.filter((su: any) => su?.quantity > 0)
          }
          let subscriptionUsersAndAdmin: any = null
          let subscriptionUsers_Software: any = []
          subscriptionUsersAndAdmin = await getSubscriptionUsers(
            isNotNullOrEmptyString(user?.adminWeb2UserId)
              ? user?.adminWeb2UserId
              : user?.web2UserId,
          )
          let hardwareUsersScheduledForDeletionCount: number =
            subscriptionUsersAndAdmin?.length > 0 &&
            subscriptionUsersAndAdmin?.some(
              (sua: any) =>
                isDeactivationScheduledYear(sua?.subscribed) == true ||
                sua?.subscribed == USER_SCHEDULED_FOR_DELETION_SUBSCRIBED_STR,
            )
              ? subscriptionUsersAndAdmin?.filter(
                  (sua: any) =>
                    isDeactivationScheduledYear(sua?.subscribed) == true ||
                    sua?.subscribed ==
                      USER_SCHEDULED_FOR_DELETION_SUBSCRIBED_STR,
                )?.length
              : 0

          if (subscriptions?.length > 0) {
            let today = new Date()
            let seats: number = 0
            let hardwareSeats: number = 0
            let baseDate: Date = new Date('1980-01-01T00:00:00Z')
            let longestInFutureSubscriptionEnd: Date = baseDate
            let currDate: any = null
            subscriptions.map((s: any) => {
              if (isActiveStripeSubscription(s?.status, s?.cancel_at)) {
                if (s?.current_period_end > -1) {
                  currDate = new Date(s?.current_period_end * 1000)
                  if (currDate > longestInFutureSubscriptionEnd)
                    longestInFutureSubscriptionEnd = currDate
                }
                if (currDate > today) {
                  if (
                    isNotNullOrEmptyString(s?.plan?.product) &&
                    toTrimmedString(s?.plan?.product) ==
                      STRIPE_PRODUCT_ID_HARDWARE
                  )
                    hardwareSeats =
                      hardwareSeats + (s?.quantity > 0 ? s?.quantity : 1)
                  else seats = seats + (s?.quantity > 0 ? s?.quantity : 1)
                }
              }
            })
            if (subscriptionUsersAndAdmin?.length > 0) {
              subscriptionUsers_Software = subscriptionUsersAndAdmin.filter(
                (su: any) =>
                  su?.isHardware != true &&
                  isNotNullOrEmptyString(su?.adminWeb2UserId),
              )
            }
            if (
              subscriptionUsers_Software?.length > 0 &&
              subscriptionUsers_Software?.length >= seats
            ) {
              unsubscribe_One_SoftwareOnly_BusinessUser_AndReloadIfCurrentUser(
                subscriptionUsers_Software,
              )
            } else if (
              longestInFutureSubscriptionEnd > baseDate &&
              isNotNullOrEmptyString(user?.subscriptionEnd)
            ) {
              let longestInFutureSubscriptionEnd_Day: number =
                longestInFutureSubscriptionEnd.getDate()
              let longestInFutureSubscriptionEnd_Month: number =
                longestInFutureSubscriptionEnd.getMonth()
              let longestInFutureSubscriptionEnd_Year: number =
                longestInFutureSubscriptionEnd.getFullYear()
              const longestInFutureSubscriptionEnd_Without_Time: Date =
                new Date(
                  longestInFutureSubscriptionEnd_Year,
                  longestInFutureSubscriptionEnd_Month,
                  longestInFutureSubscriptionEnd_Day,
                )
              let subscriptionEnd: any = netDateToDate(user?.subscriptionEnd)
              let subscriptionEnd_Day: number = subscriptionEnd.getDate()
              let subscriptionEnd_Month: number = subscriptionEnd.getMonth()
              let subscriptionEnd_Year: number = subscriptionEnd.getFullYear()
              const subscriptionEnd_Without_Time: Date = new Date(
                subscriptionEnd_Year,
                subscriptionEnd_Month,
                subscriptionEnd_Day,
              )
              if (
                longestInFutureSubscriptionEnd_Without_Time >
                subscriptionEnd_Without_Time
              ) {
                longestInFutureSubscriptionEnd_Without_Time.setDate(
                  longestInFutureSubscriptionEnd_Without_Time.getDate() + 1,
                )
                if (isNotNullOrEmptyString(web2UserSessionId)) {
                  let cloneUser: any = structuredClone(user)
                  cloneUser.subscriptionEnd = dateToNetDate(
                    longestInFutureSubscriptionEnd_Without_Time,
                  )
                  await saveUser(cloneUser).then(async (r: boolean) => {
                    try {
                      if (!r)
                        logSentryError(
                          'Failed to save user - 7.',
                          SENTRY_COMPONENT_NAME,
                        )
                      const jsBlob: Blob = new Blob(GET_USER_CALLSTRING, {
                        type: 'text/javascript',
                      })
                      const workerURL: string = URL.createObjectURL(jsBlob)
                      const worker: Worker = new Worker(workerURL)
                      try {
                        if (isNotNullOrEmptyString(web2UserSessionId)) {
                          worker.postMessage({
                            sessionUserId: web2UserSessionId,
                            email: web2UserSessionEmail,
                          })
                          worker.onmessage = (event: MessageEvent<any>) => {
                            try {
                              refreshUser(event?.data)
                            } catch (ez) {
                              if (DEBUG_MODE) console.log(ez)
                              logSentryError(ez, SENTRY_COMPONENT_NAME)
                            } finally {
                              worker.terminate()
                            }
                          }
                          worker.onmessageerror = (ev: any) => {
                            try {
                              if (DEBUG_MODE) console.log(ev)
                              logSentryError(
                                'worker.onmessageerror - 10',
                                SENTRY_COMPONENT_NAME,
                              )
                              worker.terminate()
                            } catch (zae: any) {
                              if (DEBUG_MODE) console.log(zae)
                              logSentryError(zae, SENTRY_COMPONENT_NAME)
                            }
                          }
                          worker.onerror = (ev: any) => {
                            try {
                              if (DEBUG_MODE) console.log(ev)
                              logSentryError(
                                'worker.onerror - 10',
                                SENTRY_COMPONENT_NAME,
                              )
                              worker.terminate()
                            } catch (zae: any) {
                              if (DEBUG_MODE) console.log(zae)
                              logSentryError(zae, SENTRY_COMPONENT_NAME)
                            }
                          }
                        }
                      } catch (ad: any) {
                        if (DEBUG_MODE) console.log(ad)
                        logSentryError(ad, SENTRY_COMPONENT_NAME)
                        worker.terminate()
                      }
                    } catch (e: any) {
                      if (DEBUG_MODE) console.log(e)
                      logSentryError(e, SENTRY_COMPONENT_NAME)
                    }
                  })
                }
              }
            }
            if (
              hardwareUsersScheduledForDeletionCount > 0 &&
              hardwareSeats > hardwareUsersScheduledForDeletionCount
            ) {
              hardwareSeats =
                hardwareSeats - hardwareUsersScheduledForDeletionCount
            }
            setStripeActiveSeats(seats)
            setStripeActiveHardwareSeats(hardwareSeats)
            let tid0: NodeJS.Timeout | null = setTimeout(() => {
              setHasResolvedStripeActiveSoftwareOrHardwareSeats(true)
              try {
                if (tid0 != null) {
                  clearTimeout(tid0)
                  tid0 = null
                }
              } catch {}
            }, 200)
          } else {
            setStripeActiveSeats(0)
            setStripeActiveHardwareSeats(0)
            let tid: NodeJS.Timeout | null = setTimeout(() => {
              setHasResolvedStripeActiveSoftwareOrHardwareSeats(true)
              try {
                if (tid != null) {
                  clearTimeout(tid)
                  tid = null
                }
              } catch {}
            }, 200)
          }
          setStripeSubscriptions(subscriptions?.length > 0 ? subscriptions : [])
          resolve(true)
        } catch (e) {
          if (DEBUG_MODE) console.log(e)
          logSentryError(e, SENTRY_COMPONENT_NAME)
          resolve(false)
        }
      })
    },
    [user?.web2UserId, user?.adminWeb2UserId, web2UserSessionId],
  )

  async function unsubscribe_One_SoftwareOnly_BusinessUser_AndReloadIfCurrentUser(
    subscriptionUsers_Software: any,
  ) {
    try {
      if (!subscriptionUsers_Software || subscriptionUsers_Software?.length < 1)
        return
      let remUser: any = null
      let secondRemUser: any = null
      let currDate: any = null
      let latestSubscribedDate: any = null
      try {
        subscriptionUsers_Software.map((s: any) => {
          if (isNotNullOrEmptyString(s?.subscribed)) {
            currDate = new Date(s?.subscribed)
            if (currDate > latestSubscribedDate) {
              latestSubscribedDate = currDate
              secondRemUser = remUser
              remUser = s
            }
          }
        })
      } catch (zz: any) {
        if (DEBUG_MODE) console.log(zz)
        logSentryError(zz, SENTRY_COMPONENT_NAME)
      }
      let maxSubscriptionEndUser_Software: any =
        remUser != null ? remUser : subscriptionUsers_Software[0]
      let secondMaxSubscriptionEndUser_Software: any =
        secondRemUser != null
          ? secondRemUser
          : subscriptionUsers_Software?.length > 2
            ? subscriptionUsers_Software[1]
            : null
      if (
        isNotNullOrEmptyString(
          maxSubscriptionEndUser_Software?.adminWeb2UserId,
        ) &&
        isNotNullOrEmptyString(maxSubscriptionEndUser_Software?.web2UserId)
      ) {
        await unsubscribeBusinessUser(
          maxSubscriptionEndUser_Software?.web2UserId,
        ).then(async (r2: any) => {
          if (DEBUG_MODE) console.log(r2)
          const queryString = (await import('query-string')).default
          let frmLocationSearch: any = ''
          if (isNotNullOrEmptyString(location?.search))
            frmLocationSearch = location?.search
              ?.toString()
              .trim()
              .replace(/\?d=0/gi, '')
              .replace(/\&d=0/gi, '')
          const parsedQueryString: any = queryString.parse(frmLocationSearch)
          if (
            maxSubscriptionEndUser_Software?.web2UserId == web2UserSessionId &&
            (!parsedQueryString?.eg ||
              parsedQueryString?.eg?.toString().trim() != '1')
          ) {
            if (r2) window.location.href = HOST + '?eg=1'
            else logSentryError('Unexpected return - 1.', SENTRY_COMPONENT_NAME)
          } else
            setTriggerRefreshSettingsPageUsersAndInvitations(
              !triggerRefreshSettingsPageUsersAndInvitations,
            )
        })
      } else if (
        isNotNullOrEmptyString(
          secondMaxSubscriptionEndUser_Software?.adminWeb2UserId,
        ) &&
        isNotNullOrEmptyString(
          secondMaxSubscriptionEndUser_Software?.web2UserId,
        )
      ) {
        await unsubscribeBusinessUser(
          secondMaxSubscriptionEndUser_Software?.web2UserId,
        ).then(async (r: any) => {
          if (DEBUG_MODE) console.log(r)
          const queryString = (await import('query-string')).default
          let frmLocationSearch: any = ''
          if (isNotNullOrEmptyString(location?.search))
            frmLocationSearch = location?.search
              ?.toString()
              .trim()
              .replace(/\?d=0/gi, '')
              .replace(/\&d=0/gi, '')
          const parsedQueryString: any = queryString.parse(frmLocationSearch)
          if (
            toTrimmedString(maxSubscriptionEndUser_Software?.web2UserId) ==
              toTrimmedString(web2UserSessionId) &&
            (!parsedQueryString?.eg ||
              parsedQueryString?.eg?.toString().trim() != '1')
          ) {
            if (r) window.location.href = HOST + '?eg=1'
            else logSentryError('Unexpected return - 2.', SENTRY_COMPONENT_NAME)
          } else
            setTriggerRefreshSettingsPageUsersAndInvitations(
              !triggerRefreshSettingsPageUsersAndInvitations,
            )
        })
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }

  async function bindSong(
    song: any,
    idx: any,
    fromInitialAppLoad: boolean,
    fromPlaylist: boolean,
  ) {
    try {
      if (
        canFavoriteAndPlaylist &&
        favorites?.length > 0 &&
        favorites.some(
          (f: any) =>
            f?.contractAddress == song?.contract_address &&
            f?.tokenId == song?.token_id,
        )
      )
        song.favorited = true
      setSong_Master(song)
      setSongCoverImageLocalOrExternalURL_Master(song?.frontImageCD)
      const artistName: string = toTrimmedString(
        song?.formattedArtistNameBigger,
      )
      const songName: any = toTrimmedString(song?.formattedSongNameBigger)
      const url: string = await getOptimizedMediaUrl(
        song?.animation_url,
        CURE_CDN_MP3_EXT,
      )
      setSongUrl_Master(url)
      setSongIdx_Master(idx)
      setSongGenres_Master(song?.genres)
      setSongName_Master(songName)
      setSongName_Master_Small(formatSongNameSmall(songName))
      if (
        !fromPlaylist &&
        playlistSong_LowIdx >= 0 &&
        playlistSong_HighIdx > 0
      ) {
        let playlistSongs: any = null
        try {
          playlistSongs =
            playlistSong_LowIdx == playlistSong_HighIdx
              ? [songs_Master[playlistSong_LowIdx]]
              : songs_Master.slice(
                  playlistSong_LowIdx,
                  playlistSong_HighIdx + 1,
                )
          if (
            playlistSongs?.length > 0 &&
            playlistSongs?.some((ps: any) => {
              return (
                isNotNullOrEmptyString(ps?.contract_address) &&
                isNotNullOrEmptyString(ps?.token_id) &&
                ps?.contract_address == song?.contract_address &&
                ps?.token_id == song?.token_id
              )
            }) == false
          ) {
            setPlaylistSong_LowIdx(0)
            setPlaylistSong_HighIdx(0)
          }
        } catch {
        } finally {
          playlistSongs = undefined
        }
      }
      await loadLocallyAndPlay(url, fromInitialAppLoad).then(async () => {
        let tid: NodeJS.Timeout | null = setTimeout(async () => {
          try {
            await upsertMediaSession(songName, artistName, song?.frontImage)
          } catch (eza: any) {
            if (DEBUG_MODE) console.log(eza)
            logSentryError(eza, SENTRY_COMPONENT_NAME)
          } finally {
            try {
              if (tid != null) {
                clearTimeout(tid)
                tid = null
              }
            } catch {}
          }
        }, 200)
      })
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }

  function pauseSongExecute() {
    try {
      //@ts-ignore
      if (player?.current?.audio?.current)
        //@ts-ignore
        player.current.audio.current.pause()
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }

  const pauseSong = useCallback(
    async (fadeOut: boolean = false, fastFade: boolean = false) => {
      return new Promise<any>((resolve) => {
        try {
          if (fadeOut)
            safeFadePause(fastFade, async () => {
              pauseSongExecute()
              lastPause.current = new Date()
              resolve(true)
            })
          else {
            pauseSongExecute()
            lastPause.current = new Date()
          }
        } catch (e) {
          if (DEBUG_MODE) console.log(e)
          logSentryError(e, SENTRY_COMPONENT_NAME)
        } finally {
          if (!fadeOut) resolve(true)
        }
      })
    },
    [],
  )

  const stopSong = useCallback(async () => {
    return new Promise<any>(() => {
      try {
        pauseSongExecute()
        //@ts-ignore
        player.current.audio.current.currentTime = 0
        lastPause.current = new Date()
      } catch (e) {
        if (DEBUG_MODE) console.log(e)
        logSentryError(e, SENTRY_COMPONENT_NAME)
      }
    })
  }, [])

  const getPlaylistIdx = useCallback(
    (next: any) => {
      let ret: any = -1
      try {
        const playlistBounds: boolean =
          pageComponentDisplayName == PLAYLIST_PAGE_DISPLAY_NAME &&
          playlistSong_LowIdx > -1 &&
          playlistSong_HighIdx > -1
        const playlistBounds_Low: any =
          playlistBounds && playlistSong_LowIdx == songIdx_Master
            ? next
              ? songIdx_Master
              : playlistSong_HighIdx + 1
            : songIdx_Master
        const playlistBounds_High: any =
          playlistBounds && playlistSong_HighIdx == songIdx_Master
            ? next
              ? playlistSong_LowIdx - 1
              : songIdx_Master
            : songIdx_Master
        ret =
          playlistBounds_Low != songIdx_Master
            ? playlistBounds_Low
            : playlistBounds_High != songIdx_Master
              ? playlistBounds_High
              : -1
      } catch (e) {
        if (DEBUG_MODE) console.log(e)
        logSentryError(e, SENTRY_COMPONENT_NAME)
      }
      return ret
    },
    [
      songIdx_Master,
      playlistSong_LowIdx,
      playlistSong_HighIdx,
      pageComponentDisplayName,
    ],
  )

  function safeFadePause(fastFade: boolean, callback?: () => void): void {
    //@ts-ignore
    const audio: HTMLAudioElement | null = player.current?.audio?.current
    if (!audio || audio.paused) {
      if (callback) callback()
      return
    }
    const intervalTime: number = 25
    const fadeDuration: number = fastFade ? 2000 : 5000
    const steps: number = fadeDuration / intervalTime
    const originalVolume: number = audio.volume
    const floorVolume: number = 0.001
    const stepAmount: number = (originalVolume - floorVolume) / steps
    let currentVolume: number = originalVolume
    const freezeSlider = (e: Event): void => e.stopPropagation()
    audio.addEventListener('volumechange', freezeSlider, true)
    const fadeDown = setInterval(() => {
      currentVolume -= stepAmount
      if (currentVolume <= floorVolume) {
        clearInterval(fadeDown)
        audio.volume = floorVolume
        setTimeout(() => {
          audio.pause()
          audio.volume = originalVolume
          audio.removeEventListener('volumechange', freezeSlider, true)
          if (callback) callback()
        }, 500)
      } else {
        audio.volume = currentVolume
      }
    }, intervalTime)
  }

  if (typeof window !== 'undefined')
    useEffect(() => {
      try {
        if (!hasSetToggleLoopHandler && isNotNullOrUndefined(player?.current)) {
          try {
            let tid: NodeJS.Timeout | null = setTimeout(() => {
              try {
                //@ts-ignore
                player.current.handleClickLoopButton = () => {
                  try {
                    //@ts-ignore
                    player.current.audio.current.loop =
                      //@ts-ignore
                      !player.current.audio.current.loop
                    //@ts-ignore
                    player?.current.forceUpdate()
                    //@ts-ignore
                    setLoop(player.current.audio.current.loop)
                  } catch (er: any) {
                    if (DEBUG_MODE) console.log(er)
                    logSentryError(er, SENTRY_COMPONENT_NAME)
                  }
                }
                //@ts-ignore
                player.current.handleClickVolumeButton = () => {
                  try {
                    //@ts-ignore
                    const lastVol: any = player.current.audio.current.lastVolume
                      ? //@ts-ignore
                        player.current.audio.current.lastVolume
                      : 1
                    //@ts-ignore
                    const vol: any = player.current.audio.current.volume
                      ? //@ts-ignore
                        player.current.audio.current.volume
                      : 1
                    //@ts-ignore
                    player.current.audio.current.muted =
                      //@ts-ignore
                      !player.current.audio.current.muted
                    //@ts-ignore
                    if (!player.current.audio.current.muted)
                      //@ts-ignore
                      player.current.audio.current.volume = lastVol
                    else {
                      //@ts-ignore
                      player.current.audio.current.volume = 0
                      //@ts-ignore
                      player.current.audio.current.lastVolume = vol
                    }
                    //@ts-ignore
                    player?.current.forceUpdate()
                    //@ts-ignore
                    setMuted(player.current.audio.current.muted)
                  } catch (er: any) {
                    if (DEBUG_MODE) console.log(er)
                    logSentryError(er, SENTRY_COMPONENT_NAME)
                  }
                }
              } catch (er: any) {
                if (DEBUG_MODE) console.log(er)
                logSentryError(er, SENTRY_COMPONENT_NAME)
              } finally {
                try {
                  if (tid != null) {
                    clearTimeout(tid)
                    tid = null
                  }
                } catch {}
              }
            }, 30)
          } catch (er: any) {
            if (DEBUG_MODE) console.log(er)
            logSentryError(er, SENTRY_COMPONENT_NAME)
          } finally {
            setHasSetToggleLoopHandler(true)
          }
        }
      } catch (e) {
        if (DEBUG_MODE) console.log(e)
        logSentryError(e, SENTRY_COMPONENT_NAME)
      }
      //@ts-ignore
    }, [player?.current, hasSetToggleLoopHandler])

  const endLoop = useCallback(() => {
    try {
      let clsLoop: any = document.getElementsByClassName('rhap_repeat-button')
      if (clsLoop?.length > 0) {
        let ariaLabel: any = clsLoop[0].getAttribute('aria-label')
        let isLooping: boolean = ariaLabel != 'Enable Loop'
        if (isLooping) {
          //ts-ignore
          clsLoop[0].click()
        }
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    } finally {
      setLoop(false)
    }
  }, [])

  async function nextSongExecute(specificIdxStart: any) {
    return new Promise<any>((resolve) => {
      let tid: NodeJS.Timeout | null = setTimeout(async () => {
        try {
          if (!audioControlsTempDisabled?.current) {
            endLoop()
            audioControlsTempDisabled.current = true
            let playlistIdx: any = getPlaylistIdx(true)
            let idxToUse: any =
              playlistIdx > 0
                ? playlistIdx
                : specificIdxStart != null
                  ? specificIdxStart
                  : songIdx_Master
            if (songs_Master && idxToUse < songs_Master?.length - 1) {
              let newIdx: any = idxToUse + 1
              let newSong: any = songs_Master[newIdx]
              setSongContractAddress(newSong?.contract_address)
              setSongTokenId(newSong?.token_id)
              bindSong(newSong, newIdx, false, false)
            } else if (songs_Master?.length > 0) {
              audioControlsTempDisabled.current = false
              setLastClickedPlaylistId(null)
              goToSong(-1)
            }
          }
        } catch (e) {
          if (DEBUG_MODE) console.log(e)
          logSentryError(e, SENTRY_COMPONENT_NAME)
          audioControlsTempDisabled.current = false
        } finally {
          try {
            if (tid != null) {
              clearTimeout(tid)
              tid = null
            }
          } catch {}
          resolve(true)
        }
      }, 10)
    })
  }

  const nextSong = useCallback(
    async (specificIdxStart: any) => {
      return new Promise<any>((resolve) => {
        try {
          if (
            songs_Master?.length > 0 &&
            !audioControlsTempDisabled?.current &&
            (!hasUsedNonSubscribedPlayLimit || isAuthenticatedUserAndSubscribed)
          ) {
            setChangingSongs_Master_PlaylistDelay(true)
            pauseSongExecute()
            nextSongExecute(specificIdxStart)
            let tid: NodeJS.Timeout | null = setTimeout(() => {
              setChangingSongs_Master_PlaylistDelay(false)
              try {
                if (tid != null) {
                  clearTimeout(tid)
                  tid = null
                }
              } catch {}
            }, 5000)
          } else if (hasUsedNonSubscribedPlayLimit)
            toggleSubscribePitchModalOpened(true, false, false)
        } catch (e) {
          if (DEBUG_MODE) console.log(e)
        } finally {
          resolve(true)
        }
      })
    },
    [
      favorites,
      song_Master,
      songIdx_Master,
      playlistSong_LowIdx,
      playlistSong_HighIdx,
      songs_Master?.length,
      canFavoriteAndPlaylist,
      pageComponentDisplayName,
      hasUsedNonSubscribedPlayLimit,
      throttlePrevSongClick?.current,
      isAuthenticatedUserAndSubscribed,
      isAuthenticatedUserAndSubscribed,
      audioControlsTempDisabled?.current,
      audioControlsTempDisabled?.current,
    ],
  )

  useEffect(() => {
    try {
      if (prevSongExecuteTimeoutId != null) {
        clearTimeout(prevSongExecuteTimeoutId)
        setPrevSongExecuteTimeoutId(null)
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }, [triggerClearPrevSongExecuteTimeoutId])

  function prevSongExecute(specificIdxStart: any) {
    return new Promise<any>((resolve) => {
      setPrevSongExecuteTimeoutId(
        setTimeout(async () => {
          try {
            if (!audioControlsTempDisabled?.current) {
              endLoop()
              audioControlsTempDisabled.current = true
              let playlistIdx: any = getPlaylistIdx(false)
              let idxToUse: any =
                playlistIdx > 0
                  ? playlistIdx
                  : specificIdxStart != null
                    ? specificIdxStart
                    : songIdx_Master
              if (songs_Master && idxToUse > 0 && songs_Master?.length > 1) {
                let newIdx: any = idxToUse - 1
                let newSong: any = songs_Master[newIdx]
                if (newIdx < songs_Master_Original?.length)
                  setLastClickedPlaylistId(null)
                setSongContractAddress(newSong?.contract_address)
                setSongTokenId(newSong?.token_id)
                bindSong(newSong, newIdx, false, false)
              } else if (songs_Master?.length > 0) {
                audioControlsTempDisabled.current = false
                goToSong(-2)
              }
            }
          } catch (e) {
            if (DEBUG_MODE) console.log(e)
            logSentryError(e, SENTRY_COMPONENT_NAME)
            audioControlsTempDisabled.current = false
          } finally {
            setTriggerClearPrevSongExecuteTimeoutId(
              !triggerClearPrevSongExecuteTimeoutId,
            )
            resolve(true)
          }
        }, 10),
      )
    })
  }

  const prevSong = useCallback(
    async (specificIdxStart: any) => {
      return new Promise<any>((resolve) => {
        try {
          if (
            songs_Master?.length > 0 &&
            (!hasUsedNonSubscribedPlayLimit || isAuthenticatedUserAndSubscribed)
          ) {
            let goPrev: boolean = true
            try {
              //@ts-ignore
              goPrev = //@ts-ignore
                //@ts-ignore
                player?.current?.audio?.current?.currentTime && //@ts-ignore
                //@ts-ignore
                player.current.audio.current.currentTime > 5 //@ts-ignore
            } catch (er) {
              if (DEBUG_MODE) console.log(er)
              logSentryError(er, SENTRY_COMPONENT_NAME)
            }
            if (!audioControlsTempDisabled?.current) {
              //@ts-ignore
              if (
                goPrev &&
                !throttlePrevSongClick?.current &&
                !specificIdxStart
              ) {
                pauseSongExecute()
                //@ts-ignore
                player.current.audio.current.currentTime = 0
                //@ts-ignore
                player?.current?.audio?.current.play()
                throttlePrevSongClick.current = true
                let tid: NodeJS.Timeout | null = setTimeout(() => {
                  try {
                    throttlePrevSongClick.current = false
                    if (tid != null) {
                      clearTimeout(tid)
                      tid = null
                    }
                  } catch {}
                }, 3000)
              } else {
                setChangingSongs_Master_PlaylistDelay(true)
                pauseSongExecute()
                prevSongExecute(specificIdxStart)
                let timeoutId: NodeJS.Timeout | null = setTimeout(() => {
                  try {
                    setChangingSongs_Master_PlaylistDelay(false)
                    try {
                      if (timeoutId != null) {
                        clearTimeout(timeoutId)
                        timeoutId = null
                      }
                    } catch {}
                  } catch {}
                }, 5000)
              }
            }
          } else if (hasUsedNonSubscribedPlayLimit)
            toggleSubscribePitchModalOpened(true, false, false)
        } catch (e) {
          if (DEBUG_MODE) console.log(e)
          logSentryError(e, SENTRY_COMPONENT_NAME)
        } finally {
          resolve(true)
        }
      })
    },
    [
      songs_Master?.length,
      hasUsedNonSubscribedPlayLimit,
      throttlePrevSongClick?.current,
      isAuthenticatedUserAndSubscribed,
      audioControlsTempDisabled?.current,
    ],
  )

  const nextSongRef = useRef(nextSong)
  useEffect(() => {
    nextSongRef.current = nextSong
  }, [nextSong])
  const prevSongRef = useRef(prevSong)
  useEffect(() => {
    prevSongRef.current = nextSong
  }, [prevSong])
  const resumeSongRef = useRef(resumeSong)
  const pauseSongRef = useRef(pauseSong)

  useEffect(() => {
    let handlers: any = []
    try {
      handlers = {
        play: async () => {
          try {
            navigator.mediaSession.playbackState = 'playing'
            await resumeSongRef.current(false)
          } catch (e) {
            if (DEBUG_MODE) console.log(e)
            navigator.mediaSession.playbackState = 'paused'
          }
        },
        pause: async () => {
          try {
            navigator.mediaSession.playbackState = 'paused'
            await pauseSongRef.current()
          } catch (e) {
            if (DEBUG_MODE) console.log(e)
          }
        },
        seekbackward: async (ev: any) => {
          try {
            const defaultSkipTime: number = 10
            const skipTime: any = ev?.seekOffset || defaultSkipTime
            const newTime: number = Math.max(
              //@ts-ignore
              player.current.audio.current.currentTime - skipTime,
              0,
            )
            //@ts-ignore
            player.current.audio.current.currentTime = newTime > 0 ? newTime : 0
          } catch (e) {
            if (DEBUG_MODE) console.log(e)
          }
        },
        seekforward: async (ev: any) => {
          try {
            const defaultSkipTime: number = 10
            const skipTime: any = ev.seekOffset || defaultSkipTime
            const newTime: number = Math.min(
              //@ts-ignore
              player.current.audio.current.currentTime + skipTime,
              //@ts-ignore
              player.current.audio.current.duration,
            )
            //@ts-ignore
            player.current.audio.current.currentTime = newTime
          } catch (e) {
            if (DEBUG_MODE) console.log(e)
          }
        },
        seekto: async (details: any) => {
          try {
            //@ts-ignore
            player.current.audio.current.currentTime =
              details?.seekTime > 0 ? details?.seekTime : 0
          } catch (e) {
            if (DEBUG_MODE) console.log(e)
          }
        },
        previoustrack: async () => {
          try {
            navigator.mediaSession.playbackState = 'playing'
            await prevSongRef.current(null)
          } catch (e) {
            if (DEBUG_MODE) console.log(e)
          }
        },
        nexttrack: async () => {
          try {
            navigator.mediaSession.playbackState = 'playing'
            await nextSongRef.current(null)
          } catch (e) {
            if (DEBUG_MODE) console.log(e)
            logSentryError(e, SENTRY_COMPONENT_NAME)
          }
        },
        stop: async () => {
          try {
            navigator.mediaSession.playbackState = 'paused'
            await pauseSongRef.current()
          } catch (e) {
            if (DEBUG_MODE) console.log(e)
            logSentryError(e, SENTRY_COMPONENT_NAME)
          }
        },
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
    }
    Object.keys(handlers).forEach((action: any) => {
      try {
        navigator.mediaSession.setActionHandler(
          action as MediaSessionAction,
          handlers[action],
        )
      } catch (error) {
        if (DEBUG_MODE) console.warn(`Action ${action} not supported`, error)
      }
    })
    return () => {
      try {
        Object.keys(handlers).forEach((action: any) => {
          navigator.mediaSession.setActionHandler(action, null)
        })
      } catch (e) {
        if (DEBUG_MODE) console.log(e)
      }
    }
  }, [])

  async function goToSong(idx: any) {
    try {
      if (idx == -1) idx = 0
      if (idx == -2) idx = songs_Master?.length - 1
      if (songs_Master && idx >= 0 && idx < songs_Master?.length) {
        const newSong: any = songs_Master[idx]
        setSongContractAddress(newSong?.contract_address)
        setSongTokenId(newSong?.token_id)
        bindSong(newSong, idx, false, false)
      } else audioControlsTempDisabled.current = false
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
      audioControlsTempDisabled.current = false
    }
  }

  const addAndPlaySong = useCallback(
    (
      contract_address: any,
      token_id: any,
      definitelyNotFromPlaydeck: boolean,
    ) => {
      try {
        const songFilt: any =
          songs_Master?.length > 0
            ? songs_Master?.filter(
                (s: any) =>
                  s?.contract_address == contract_address &&
                  s?.token_id == token_id,
              )
            : []
        let song: any = songFilt?.length > 0 ? songFilt[0] : null
        if (song) {
          if (
            !audioControlsTempDisabled?.current &&
            (!hasUsedNonSubscribedPlayLimit || isAuthenticatedUserAndSubscribed)
          ) {
            try {
              if (!audioControlsTempDisabled?.current) {
                endLoop()
                audioControlsTempDisabled.current = true
                let newIdx: any = -1
                if (songs_Master && song) {
                  if (!definitelyNotFromPlaydeck) {
                    for (let i = songs_Master?.length - 1; i > -1; i--) {
                      if (
                        songs_Master[i]?.contract_address ==
                          song?.contract_address &&
                        songs_Master[i]?.token_id == song?.token_id
                      ) {
                        newIdx = i
                        song = songs_Master[i]
                        break
                      }
                    }
                  }
                  if (!newIdx || newIdx == -1) {
                    newIdx = songs_Master.length
                    setSongs_Master([...songs_Master, song])
                  }
                  setSongContractAddress(song?.contract_address)
                  setSongTokenId(song?.token_id)
                  setChangingSongs_Master(true)
                  pauseSongExecute()
                  let tid: NodeJS.Timeout | null = setTimeout(() => {
                    setChangingSongs_Master(false)
                    try {
                      if (tid != null) {
                        clearTimeout(tid)
                        tid = null
                      }
                    } catch {}
                  }, 1000)
                  bindSong(song, newIdx, false, false)
                } else pauseSongExecute()
              } else pauseSongExecute()
            } catch (e) {
              if (DEBUG_MODE) console.log(e)
              logSentryError(e, SENTRY_COMPONENT_NAME)
              audioControlsTempDisabled.current = false
            }
          } else if (hasUsedNonSubscribedPlayLimit)
            toggleSubscribePitchModalOpened(true, false, false)
        }
      } catch (e) {
        if (DEBUG_MODE) console.log(e)
      }
    },
    [
      songs_Master,
      hasUsedNonSubscribedPlayLimit,
      isAuthenticatedUserAndSubscribed,
      audioControlsTempDisabled?.current,
    ],
  )

  const setPlaydeck = useCallback(
    (
      songs: any,
      isPlaylist: any,
      alsoPlay: boolean,
      idxToUseWithinSongsArg: any,
      playlistName: any,
    ) => {
      const curedPlaylistName: string | null = isNotNullOrEmptyString(
        playlistName,
      )
        ? toTrimmedString(decodeUnicode(playlistName))
        : null
      if (
        songs?.length > 0 &&
        isNotNullOrEmptyString(curedPlaylistName) &&
        isPlaylist
      ) {
        setLastClickedPlaylistSongs(songs)
        setLastClickedPlaylistName(curedPlaylistName)
        setPlayingPlayingOverlayOpened(true)
      }
      if (!hasUsedNonSubscribedPlayLimit || isAuthenticatedUserAndSubscribed) {
        try {
          let idx: any =
            songs_Master_Original?.length > 0
              ? songs?.length > 0
                ? songs_Master_Original?.length
                : songs_Master_Original?.length - 1
              : 0
          if (songs?.length > 0) {
            endLoop()
            if (!isPlaylist && idxToUseWithinSongsArg != null)
              setSongIdx_Master(idx + idxToUseWithinSongsArg)
            loadSongs(
              songs,
              !isPlaylist
                ? alsoPlay
                  ? idx
                  : idxToUseWithinSongsArg
                    ? idx + idxToUseWithinSongsArg
                    : idx
                : idx,
              isPlaylist,
              false,
              alsoPlay,
              idxToUseWithinSongsArg,
            )
            if (isPlaylist) setPlayingPlayingOverlayOpened(true)
          }
        } catch (e) {
          if (DEBUG_MODE) console.log(e)
          logSentryError(e, SENTRY_COMPONENT_NAME)
        }
      } else if (hasUsedNonSubscribedPlayLimit)
        toggleSubscribePitchModalOpened(true, false, false)
    },
    [
      hasUsedNonSubscribedPlayLimit,
      songs_Master_Original?.length,
      isAuthenticatedUserAndSubscribed,
    ],
  )

  async function loadSongs(
    songs: any | undefined,
    idx: any | undefined,
    isPlaylist: boolean | undefined,
    fromInitialAppLoad: boolean | undefined,
    startPlay: boolean | undefined,
    idxToUseWithinPlaylist: any | undefined,
  ) {
    try {
      setChangingSongs_Master(true)
      if (songs.length > 0) {
        let mergedSongs: any =
          songs_Master_Original?.length > 0
            ? [...songs_Master_Original, ...songs]
            : songs
        if (isPlaylist) {
          setPlaylistSong_LowIdx(idx)
          setPlaylistSong_HighIdx(mergedSongs?.length - 1)
        }
        if (favorites?.length > 0)
          mergedSongs = mergeFavoritesWithSongs(mergedSongs, favorites)
        setSongs_Master(mergedSongs)
        if (wholeSongSetRetrieved) setWholeSongSetBound(true)
        if (startPlay) {
          let timeId: NodeJS.Timeout | null = setTimeout(() => {
            setChangingSongs_Master(false)
            try {
              try {
                if (timeId != null) {
                  clearTimeout(timeId)
                  timeId = null
                }
              } catch {}
            } catch {}
          }, 100)
          if (!fromInitialAppLoad) {
            let selIdx: any =
              idx < mergedSongs?.length && idx > 0
                ? idxToUseWithinPlaylist == null
                  ? idx
                  : idx + idxToUseWithinPlaylist
                : 0
            const nextSong: any = mergedSongs[selIdx]
            setSong_Master(nextSong)
            setSongCoverImageLocalOrExternalURL_Master(nextSong?.frontImageCD)
            setSongContractAddress(nextSong?.contract_address)
            setSongTokenId(nextSong?.token_id)
            bindSong(
              nextSong,
              selIdx,
              fromInitialAppLoad ? true : false,
              isPlaylist ? true : false,
            )
          }
        }
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }

  const getLocalIPAddress = useCallback(async () => {
    try {
      let ip: any = await new Promise<any>((resolve, reject) => {
        try {
          const conn = new RTCPeerConnection()
          conn.createDataChannel('')
          conn.createOffer((offer) => conn.setLocalDescription(offer), reject)
          conn.onicecandidate = (ice) => {
            if (ice?.candidate?.candidate) {
              resolve(ice?.candidate?.candidate.split(' ')[4])
            } else resolve('')
            conn.close()
          }
        } catch (er: any) {
          if (DEBUG_MODE) console.log(er)
          logSentryError(er, SENTRY_COMPONENT_NAME)
          resolve('')
        }
      })
      const lip: any = toTrimmedString(ip)
      setLocalIPAddress(lip)
      return toTrimmedString(ip)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
      return ''
    }
  }, [])

  const getIPAddress = useCallback(async () => {
    try {
      let ip: any = null
      fetchWithTimeout(IPIFY_SERVICE_URL, { timeout: 2000 })
        .then(async (x: any) => {
          const bodyJson: any = await x.json()
          ip = isNotNullOrEmptyString(pageProps?.initialIp)
            ? pageProps?.initialIp
            : isNotNullOrEmptyString(bodyJson?.ip)
              ? bodyJson?.ip
              : ''
          setIPAddress(isNotNullOrEmptyString(ip) ? ip : null)
          const opts: any = {
            method: 'POST',
            body: JSON.stringify({
              ip: ip,
            }),
          }
          const rez: Response = await fetch(HOST + '/api/ps-gl/j', opts)
          const bodyJson2: any = await rez.json()
          ip = isNotNullOrEmptyString(pageProps?.initialIp)
            ? pageProps?.initialIp
            : isNotNullOrEmptyString(ip)
              ? ip
              : isNotNullOrEmptyString(bodyJson2?.IPv4)
                ? bodyJson2?.IPv4
                : ''
          setIPAddress(isNotNullOrEmptyString(ip) ? ip : null)
          configureByCountryCode(
            isNotNullOrEmptyString(pageProps?.initialCountryCode)
              ? pageProps?.initialCountryCode
              : bodyJson2?.country_code,
          )
        })
        .catch(async (ez: any) => {
          if (DEBUG_MODE) console.log(ez)
          logSentryError(ez, SENTRY_COMPONENT_NAME)
        })
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }, [pageProps?.initialCountryCode])

  function configureByCountryCode(cCode: any) {
    try {
      if (isNotNullOrEmptyString(cCode)) {
        const countryCode = toTrimmedStringUpperCase(cCode)
        setUseEuro(
          EURO_CURRENCY_ISO_3166_ALPHA_2_COUNTRY_CODES?.length > 0 &&
            EURO_CURRENCY_ISO_3166_ALPHA_2_COUNTRY_CODES?.some(
              (ec: any) => ec == countryCode,
            ),
        )
        setIpAddressCountryCode(
          isNotNullOrEmptyString(countryCode) ? countryCode : null,
        )
        setUseBritishPound(countryCode == 'GB')
        setUseCanadianDollar(countryCode == 'CA')
      } else {
        setUseEuro(false)
        setUseBritishPound(false)
        setUseCanadianDollar(false)
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
    }
  }

  useEffect(() => {
    try {
      if (streamCredit_Time == null) {
        if (processStreamTimeoutId_1 != null) {
          clearTimeout(processStreamTimeoutId_1)
          setProcessStreamTimeoutId_1(null)
        }
        if (processStreamTimeoutId_2 != null) {
          clearTimeout(processStreamTimeoutId_2)
          setProcessStreamTimeoutId_2(null)
        }
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }, [streamCredit_Time])

  async function processStream(
    time: any,
    streamStart: any,
    streamEnd: any,
    contractAddress: any,
    tokenId: any,
  ) {
    try {
      if (
        isNotNullOrEmptyString(ipAddress) ||
        !REQUIRE_IP_ADDRESS_TO_LOG_STREAM
      ) {
        setStreamCredit_TokenId(tokenId)
        setStreamCredit_ContractAddress(contractAddress)
        setStreamCredit_IPAddress(
          isNotNullOrEmptyString(ipAddress)
            ? ipAddress
            : NO_IP_ADDRESS_PLACEHOLDER,
        )
        setStreamCredit_LocalIPAddress(
          isNotNullOrEmptyString(localIPAddress)
            ? localIPAddress
            : NO_LOCAL_IP_ADDRESS_PLACEHOLDER,
        )
        setStreamCredit_StreamStart(streamStart)
        setStreamCredit_StreamEnd(streamEnd)
        setStreamCredit_Time(time)
        let timeoutId: NodeJS.Timeout | null = setTimeout(() => {
          setStreamCredit_TokenId(null)
          setStreamCredit_ContractAddress(null)
          setStreamCredit_StreamStart(null)
          setStreamCredit_StreamEnd(null)
          setStreamCredit_Time(null)
        }, 1000)
        setProcessStreamTimeoutId_1(timeoutId)
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }

  useEffect(() => {
    try {
      if (!web2UserIdLoggedIn) return
      if (
        RESTRICT_PRACTICAL_POD_USERS_FROM_ACCESSING_APP_OTHERWISE &&
        hasDeterminedIsPracticalPod &&
        !isPracticalPod &&
        user?.isHardware == true
      ) {
        setHardwareUserNotLoggedIntoPracticalPod(true)
        toggleSubscribePitchModalOpened(true, true, false)
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }, [
    isPracticalPod,
    user?.isHardware,
    web2UserIdLoggedIn,
    hasDeterminedIsPracticalPod,
  ])

  const refreshUser = useCallback(
    (u: any) => {
      try {
        if (
          isNotNullOrEmptyString(user?.userId) &&
          user?.userId != user?.userId
        )
          setTriggerGetFavorites(!triggerGetFavorites)
        setUserDeviceRestartTime(u?.deviceRestartTime)
        setUserAppReloadTime(u?.appReloadTime)
        setUser(u)
        setSubscribed(u?.subscribed ? new Date(u?.subscribed) : null)
        setSubscriptionEnd(
          u?.subscriptionEnd ? new Date(u?.subscriptionEnd) : null,
        )
        setStripeCustomerId(toTrimmedString(u?.customer?.stripeCustomerId))
      } catch (e) {
        if (DEBUG_MODE) console.log(e)
        logSentryError(e, SENTRY_COMPONENT_NAME)
      }
    },
    [
      user?.userId,
      user?.appReloadTime,
      triggerGetFavorites,
      user?.deviceRestartTime,
    ],
  )

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null
    if (isNullOrEmptyString(stripeCustomerId)) return
    try {
      timeoutId = setTimeout(async () => {
        const stripeCurrency: any = await getStripeCurrency(stripeCustomerId)
        if (isNotNullOrEmptyString(stripeCurrency)) {
          if (
            toTrimmedStringUpperCase(stripeCurrency) ==
            ISO_4217_CURRENCY_CODE_EURO
          ) {
            setUseEuro(true)
            setUseCanadianDollar(false)
            setUseBritishPound(false)
          } else if (
            toTrimmedStringUpperCase(stripeCurrency) ==
            ISO_4217_CURRENCY_CODE_CANADA_DOLLAR
          ) {
            setUseCanadianDollar(true)
            setUseEuro(false)
            setUseBritishPound(false)
          } else if (
            toTrimmedStringUpperCase(stripeCurrency) ==
            ISO_4217_CURRENCY_CODE_BRITISH_POUND
          ) {
            setUseBritishPound(true)
            setUseEuro(false)
            setUseCanadianDollar(false)
          } else {
            setUseBritishPound(false)
            setUseEuro(false)
            setUseCanadianDollar(false)
          }
        } else {
          if (isNotNullOrEmptyString(pageProps?.initialCountryCode))
            configureByCountryCode(pageProps?.initialCountryCode)
          else {
            setUseEuro(false)
            setUseBritishPound(false)
            setUseCanadianDollar(false)
          }
        }
      }, 1)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
    }
    return () => {
      try {
        if (timeoutId != null) clearTimeout(timeoutId)
      } catch {}
    }
  }, [stripeCustomerId])

  const toggleSubscribePitchModalOpened = useCallback(
    (
      opened: boolean,
      fromPlaylistOrFavoriteButtonClick: boolean,
      fromBrandStudioClick: boolean,
    ) => {
      try {
        let doOpenWithRerender: boolean = false
        if (
          opened &&
          fromPlaylistOrFavoriteButtonClick &&
          isNullOrEmptyString(firstNavigatedComponent)
        ) {
          const strToast: string = translate(
            'Subscribe for favorites and playlists.',
            'Suscríbete para añadir favoritos y crear listas de reproducción.',
            'Abonnez-vous pour ajouter des favoris et créer des listes de lecture.',
            '구독하시면 즐겨찾기를 추가하고 플레이리스트을 만들 수 있습니다.',
            selectedLanguage,
          )
          doOpenWithRerender = true
          clearToasts()
          if (dark || !limitedSubscriptionPitchRender) {
            if (!isMobile) toastSticky(strToast, 'neutral')
            else toast(strToast, 'neutral')
          }
        } else if (opened && fromBrandStudioClick) {
          const toastText: string = translate(
            'Please subscribe to use this feature.',
            'Suscríbete para usar esta función.',
            'Abonnez-vous pour utiliser cette fonctionnalité.',
            '이 기능을 사용하려면 구독하세요.',
            selectedLanguage,
          )
          clearToasts()
          if (dark || !limitedSubscriptionPitchRender) {
            if (!isMobileOnly) toastSticky(toastText, 'neutral')
            else toast(toastText, 'neutral')
          }
          doOpenWithRerender = true
        } else if (opened) {
          if (
            !isPracticalPod &&
            !fromPlaylistOrFavoriteButtonClick &&
            !hardwareUserNotLoggedIntoPracticalPod &&
            (dark || !limitedSubscriptionPitchRender)
          )
            toast(
              translate(
                'Subscribe for unlimited music.',
                'Suscríbete para música ilimitada.',
                'Abonnez-vous pour de la musique illimitée.',
                '무제한 음악 구독하기',
                selectedLanguage,
              ),
              'neutral',
            )
          doOpenWithRerender = true
        } else setSubscribePitchModalOpened(opened)
        if (doOpenWithRerender) {
          setQuickSearchOpened(false)
          try {
            document?.body?.scrollTo(0, window?.scrollY ? window?.scrollY : 0)
          } catch (er) {
            if (DEBUG_MODE) console.log(er)
            logSentryError(er, SENTRY_COMPONENT_NAME)
          }
          setRerenderCarousel(true)
          let tid1: NodeJS.Timeout | null = setTimeout(() => {
            setSubscribePitchModalOpened(true)
            try {
              if (tid1 != null) {
                clearTimeout(tid1)
                tid1 = null
              }
            } catch {}
          }, 250)
          let tid2: NodeJS.Timeout | null = setTimeout(() => {
            setSubscribePitchModalOpened(false)
            try {
              if (tid2 != null) {
                clearTimeout(tid2)
                tid2 = null
              }
            } catch {}
          }, 500)
          let tid3: NodeJS.Timeout | null = setTimeout(() => {
            setSubscribePitchModalOpened(true)
            try {
              if (tid3 != null) {
                clearTimeout(tid3)
                tid3 = null
              }
            } catch {}
          }, 750)
          let tid4: NodeJS.Timeout | null = setTimeout(() => {
            setRerenderCarousel(false)
            try {
              if (tid4 != null) {
                clearTimeout(tid4)
                tid4 = null
              }
            } catch {}
          }, 1250)
        }
      } catch (e) {
        if (DEBUG_MODE) console.log(e)
        logSentryError(e, SENTRY_COMPONENT_NAME)
      }
    },
    [
      dark,
      isMobileOnly,
      isPracticalPod,
      selectedLanguage,
      firstNavigatedComponent,
      limitedSubscriptionPitchRender,
      hardwareUserNotLoggedIntoPracticalPod,
    ],
  )

  const toggleAddToPlaylistModalOpened = useCallback(
    (opened: boolean, song: any) => {
      try {
        setGetPlaylistEntries(true)
        if (isNotNullOrUndefined(song)) setSong_Selected(structuredClone(song))
        setAddToPlaylistModalOpened(opened)
      } catch (e) {
        logSentryError(e, SENTRY_COMPONENT_NAME)
        if (DEBUG_MODE) console.log(e)
      }
    },
    [],
  )

  const navigateRoute = useCallback(
    async (routerPath: string) => {
      setNavigating(true)
      React.startTransition(() => {
        if (isNullOrEmptyString(firstNavigatedComponent)) {
          setFirstNavigatedComponent(pageComponentDisplayName)
        }
      })
      requestAnimationFrame(async () => {
        try {
          try {
            scrollTop('instant')
          } catch {}
          const deploymentId = toTrimmedString(
            process.env.NEXT_PUBLIC_VERCEL_DEPLOYMENT_ID,
          )
          await router.push(
            { pathname: routerPath, query: { dpl: deploymentId } },
            routerPath,
            { shallow: false },
          )
        } catch (e) {
          if (DEBUG_MODE) console.log(e)
          logSentryError(e, SENTRY_COMPONENT_NAME)
        }
      })
    },
    [pageComponentDisplayName, firstNavigatedComponent, router],
  )

  const navigateRouteWithInvitationToken = useCallback(
    async (routerPath: string, invitationToken: string) => {
      try {
        requestAnimationFrame(async () => {
          if (isNullOrEmptyString(firstNavigatedComponent))
            setFirstNavigatedComponent(pageComponentDisplayName)
          try {
            scrollTop('instant')
          } catch {}
          setNavigating(true)
          const deploymentId = toTrimmedString(
            process.env.NEXT_PUBLIC_VERCEL_DEPLOYMENT_ID,
          )
          router.push(
            {
              pathname: routerPath,
              query: { dpl: deploymentId, g: invitationToken },
            },
            routerPath,
            { shallow: false },
          )
        })
      } catch (e) {
        if (DEBUG_MODE) console.log(e)
        logSentryError(e, SENTRY_COMPONENT_NAME)
      }
    },
    [pageComponentDisplayName, firstNavigatedComponent],
  )

  if (typeof window !== 'undefined')
    useEffect(() => {
      if (!hasAddedRouterEventHandlers?.current) {
        router.events.on('routeChangeStart', (url: any) => {
          if (currentRoute?.current != url)
            previousRoute.current = currentRoute?.current
          currentRoute.current = url
        })
        router.events.on('routeChangeComplete', () => {
          if (!isPracticalPod) {
            let tid: NodeJS.Timeout | null = setTimeout(
              () => {
                setNavigating(false)
                try {
                  if (tid != null) {
                    clearTimeout(tid)
                    tid = null
                  }
                } catch {}
              },
              web2UserIdLoggedIn != true ? 500 : 250,
            )
          } else setNavigating(false)
        })
        hasAddedRouterEventHandlers.current = true
      }
    }, [])

  function loadAllContractGenres() {
    try {
      const jsBlob: Blob = new Blob(GET_CONTRACTS_FOR_GENRES_CALLSTRING, {
        type: 'text/javascript',
      })
      const workerURL: string = URL.createObjectURL(jsBlob)
      const worker: Worker = new Worker(workerURL)
      worker.postMessage({
        genreId: -1,
      })
      worker.onmessage = (event: MessageEvent<any>) => {
        if (event?.data?.length > 0) setContractGenres(event?.data)
        else if (
          retryCountGetContractGenres < MAXIMUM_RETRIES_GET_CONTRACT_GENRES
        )
          setRetryCountGetContractGenres(retryCountGetContractGenres + 1)
      }
      worker.onmessageerror = (ev: any) => {
        try {
          if (DEBUG_MODE) console.log(ev)
          logSentryError('worker.onmessageerror - 11', SENTRY_COMPONENT_NAME)
        } catch (zae: any) {
          if (DEBUG_MODE) console.log(zae)
          logSentryError(zae, SENTRY_COMPONENT_NAME)
        }
      }
      worker.onerror = (ev: any) => {
        try {
          if (DEBUG_MODE) console.log(ev)
          logSentryError('worker.onerror - 11', SENTRY_COMPONENT_NAME)
        } catch (zae: any) {
          if (DEBUG_MODE) console.log(zae)
          logSentryError(zae, SENTRY_COMPONENT_NAME)
        }
      }
      return () => worker.terminate()
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }

  function loadAllArtists() {
    try {
      const jsBlob: Blob = new Blob(GET_ARTISTS_CALLSTRING, {
        type: 'text/javascript',
      })
      const workerURL: string = URL.createObjectURL(jsBlob)
      const worker: Worker = new Worker(workerURL)
      worker.postMessage({
        artistId: -1,
      })
      worker.onmessage = (event: MessageEvent<any>) => {
        if (event?.data?.length > 0) {
          let newArtists: any = []
          let artist = null
          event?.data?.map((a: any) => {
            artist = structuredClone(a)
            artist.artistImage = isNotNullOrEmptyString(a?.artistImage)
              ? cureCDN(
                  (isResolvedArweaveUrl(a?.artistImage)
                    ? ''
                    : ARWEAVE_GATEWAY) + a.artistImage,
                  CURE_CDN_JPG_EXT,
                  true,
                )
              : ''
            artist.artistImageAvif = isNotNullOrEmptyString(a?.artistImageAvif)
              ? cureCDN(
                  (isResolvedArweaveUrl(a?.artistImageAvif)
                    ? ''
                    : ARWEAVE_GATEWAY) + a.artistImageAvif,
                  CURE_CDN_AVIF_EXT,
                  true,
                )
              : ''
            newArtists.push(artist)
          })
          setMasterArtists(newArtists)
        } else if (retryCountGetArtists < MAXIMUM_RETRIES_ARTISTS)
          setRetryCountGetArtists(retryCountGetArtists + 1)
      }
      worker.onmessageerror = (ev: any) => {
        try {
          if (DEBUG_MODE) console.log(ev)
          logSentryError('worker.onmessageerror - 12-a', SENTRY_COMPONENT_NAME)
        } catch (zae: any) {
          if (DEBUG_MODE) console.log(zae)
          logSentryError(zae, SENTRY_COMPONENT_NAME)
        }
      }
      worker.onerror = (ev: any) => {
        try {
          if (DEBUG_MODE) console.log(ev)
          logSentryError('worker.onerror - 12-a', SENTRY_COMPONENT_NAME)
        } catch (zae: any) {
          if (DEBUG_MODE) console.log(zae)
          logSentryError(zae, SENTRY_COMPONENT_NAME)
        }
      }
      return () => worker.terminate()
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }

  const share = useCallback(async (shareObj: any) => {
    try {
      if (navigator?.canShare && navigator?.canShare(shareObj)) {
        await navigator
          .share(shareObj)
          .then(() => {})
          .catch((error: any) => {
            if (DEBUG_MODE) console.log('Error sharing', error)
            logSentryError(error, SENTRY_COMPONENT_NAME)
          })
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }, [])

  const upsertMediaSession = useCallback(
    (songName: any, artistName: any, artwork: any) => {
      return new Promise<any>(async (resolve) => {
        try {
          if ('mediaSession' in navigator) {
            navigator.mediaSession.metadata = null
            navigator.mediaSession.metadata = new MediaMetadata({
              title: cureLiterals(toTrimmedString(songName)),
              artist: cureLiterals(toTrimmedString(artistName)),
              album: '',
              artwork: [
                {
                  src: toTrimmedString(artwork),
                },
              ],
            })
          }
        } catch (er) {
          if (DEBUG_MODE) console.log(er)
          logSentryError(er, SENTRY_COMPONENT_NAME)
        } finally {
          resolve(true)
        }
      })
    },
    [],
  )

  async function upsertMediaSessionState(state: any) {
    return new Promise<any>(async (resolve) => {
      try {
        if (navigator?.mediaSession)
          navigator.mediaSession.playbackState = state
      } catch (e) {
        if (DEBUG_MODE) console.log(e)
        logSentryError(e, SENTRY_COMPONENT_NAME)
      } finally {
        resolve(true)
      }
    })
  }

  useEffect(() => {
    try {
      if (isOnline) {
        setWasPlayingBeforeWentOffline(false)
        return
      }
      scrollTop('instant')
      setWasPlayingBeforeWentOffline(playing)
      setOfflineModalOpened(true)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }, [isOnline])

  const scrollTop = useCallback(async (behavior: any) => {
    try {
      document?.body?.scrollTo({
        top: 0,
        left: 0,
        behavior: behavior,
      })
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }, [])

  const smallLoaderOffset: string =
    'scale(.25,.25) translate(' +
    (width > 1020
      ? width - 1070
      : width > 900
        ? width - 870
        : width > 750
          ? width - 770
          : width > 720
            ? width - 710
            : width > 630
              ? width - 680
              : width > 510
                ? width - 520
                : width > 480
                  ? width - 550
                  : width > 450
                    ? width - 580
                    : width > 420
                      ? width - 610
                      : width > 390
                        ? width - 640
                        : width > 360
                          ? width - 670
                          : width > 330
                            ? width - 700
                            : width > 300
                              ? width - 735
                              : width > 270
                                ? width - 765
                                : width > 0
                                  ? width
                                  : 0
    ).toString() +
    'px, 5px)'

  function toggleEnvironmentProtectionModal() {
    try {
      try {
        setCookie(ENV_PROTECTION_COOKIE_NAME, 'true', COOKIE_CONFIG)
      } catch (a: any) {
        if (DEBUG_MODE) console.log(a)
        logSentryError(a, SENTRY_COMPONENT_NAME)
      }
      setEnvironmentProtectionModalOpened(false)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }

  useEffect(() => {
    try {
      if (!web2UserIdLoggedIn) return
      if (
        masterSchedule &&
        masterScheduleWasNull &&
        masterPlaylistsAndPresets &&
        !hasShownScheduledPlaylistsEnabledModal
      ) {
        if (
          masterPlaylistsAndPresets?.some(
            (pl: any) =>
              (masterSchedule?.saturday_Enabled &&
                pl.playlist_id ==
                  masterSchedule?.saturday_BusinessPlaylistId) ||
              (masterSchedule?.sunday_Enabled &&
                pl.playlist_id == masterSchedule?.sunday_BusinessPlaylistId) ||
              (masterSchedule?.monday_Enabled &&
                pl.playlist_id == masterSchedule?.monday_BusinessPlaylistId) ||
              (masterSchedule?.tuesday_Enabled &&
                pl.playlist_id == masterSchedule?.tuesday_BusinessPlaylistId) ||
              (masterSchedule?.wednesday_Enabled &&
                pl.playlist_id ==
                  masterSchedule?.wednesday_BusinessPlaylistId) ||
              (masterSchedule?.thursday_Enabled &&
                pl.playlist_id ==
                  masterSchedule?.thursday_BusinessPlaylistId) ||
              (masterSchedule?.friday_Enabled &&
                pl.playlist_id == masterSchedule?.friday_BusinessPlaylistId),
          )
        ) {
          hasAScheduledPlaylistSoForceInteractionWithTheDocument.current = true
          setHasShownScheduledPlaylistsEnabledModal(true)
          if (web2UserSessionId != PRESETS_AND_STATIONS_ADMIN_WEB2_USER_ID)
            setScheduledPlaylistsEnabledModalOpened(true)
        }
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }, [masterSchedule, web2UserIdLoggedIn, masterPlaylistsAndPresets])

  const reactToPlaylistsChange = useCallback(
    (
      addedToPlaylistId: any,
      getPresets: boolean,
      triggerPlaylistsChanged: boolean,
    ) => {
      if (addedToPlaylistId != null)
        setPlaylistToRefreshWithoutReloading(addedToPlaylistId)
      if (triggerPlaylistsChanged)
        setPlaylistsChanged(playlistsChanged == null ? true : !playlistsChanged)
      forceGetPresets.current = getPresets
      setSignalRefreshMasterPlaylists(!signalRefreshMasterPlaylistsRef.current)
      let tid: NodeJS.Timeout | null = setTimeout(() => {
        setPlaylistToRefreshWithoutReloading(null)
        setPlaylistsChanged(null)
        try {
          if (tid != null) {
            clearTimeout(tid)
            tid = null
          }
        } catch {}
      }, 200)
    },
    [playlistsChanged, signalRefreshMasterPlaylistsRef.current],
  )

  useEffect(() => {
    try {
      if (wholeSongSetBound && songs_Master?.length > 0) {
        if (isNotNullOrEmptyString(web2UserSessionId)) getMasterPlaylists()
        else {
          setMasterPlaylistsAndPresets([])
          setMasterPlaylists([])
        }
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }, [
    wholeSongSetBound,
    web2UserSessionId,
    songs_Master?.length,
    signalRefreshMasterPlaylistsDebounced,
  ])

  useEffect(() => {
    try {
      if (
        (pageComponentDisplayName == PLAYLIST_PAGE_DISPLAY_NAME ||
          pageComponentDisplayName == FAVORITES_PAGE_DISPLAY_NAME) &&
        !isPracticalPod &&
        (isNullOrUndefined(presetSamples) || presetSamples?.length < 1) &&
        wholeSongSetBound &&
        songs_Master?.length > 0
      )
        setTimeout(() => getPresetSamples(), 1)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }, [wholeSongSetBound, songs_Master?.length, pageComponentDisplayName])

  function getPresetSamples() {
    try {
      const fnStr: string[] = [
        TO_TRIMMED_STRING_FUNCTION +
          ONLY_UNIQUE_FUNCTION +
          IS_NOT_NULL_OR_EMPTY_STRING_FUNCTION +
          FORMAT_SONG_NAME_BIGGER_FUNCTION +
          `
  function mergePlaylistEntriesWithSongs(playlists, allSongs) {
    if (!playlists?.length || !allSongs?.length) return playlists || [];
    const uniqueSongs = allSongs.filter(onlyUnique);
    const songMap = new Map();
    for (const s of uniqueSongs) {
      if (isNotNullOrEmptyString(s?.contract_address) && isNotNullOrEmptyString(s?.token_id)) {
        songMap.set(\`\${s.contract_address}_\${s.token_id}\`, s);
      }
    }
    return playlists.map((pl) => {
      const clonedPl = { ...pl };
      if (pl?.entries?.length > 0) {
        const mergedEntries = [];
        for (const en of pl.entries) {
          const songMatch = songMap.get(\`\${en.contract_address}_\${en.token_id}\`);
          if (songMatch) {
            mergedEntries.push({
              animation_url: songMatch.animation_url,
              contract_address: en.contract_address,
              token_id: en.token_id,
              artist: songMatch.artist,
              duration: en.duration,
              formattedDuration: en.formattedDuration,
              genres: en.genres,
              frontImage: songMatch.frontImage,
              playlistId: en.playlistId,
              favorited: songMatch.favorited,
              sortOrder: en.sortOrder,
              name: formatSongNameBigger(toTrimmedString(songMatch.name)),
              formattedMintDate: songMatch.formattedMintDate,
              formattedSongNameSmallSpaceEscaped: songMatch.formattedSongNameSmallSpaceEscaped,
              formattedSongNameBig: songMatch.formattedSongNameBig,
              formattedSongNameBigger: songMatch.formattedSongNameBigger,
              formattedArtistNameBig: songMatch.formattedArtistNameBig,
              formattedArtistNameBigger: songMatch.formattedArtistNameBigger,
              keyOne: songMatch.keyOne,
              keyTwo: songMatch.keyTwo,
              keyThree: songMatch.keyThree,
            });
          }
        }
        clonedPl.entries = mergedEntries;
      }
      return clonedPl;
    });
  }
  self.addEventListener('message', async (event) => {
    const data = event?.data;
    if (!data?.songs_Master?.length) return;
    try {
      const opts = { method: 'GET', cache: 'force-cache' };
      const response = await fetch('${HOST}' + '/api/p.stream-sv/pl/gps', opts);
      const rez = await response.json();
      const playlists = rez?.playlists || [];
      if (playlists.length === 0) {
        self.postMessage({ dragPlaylists: null });
        return;
      }
      const preparedPlaylists = playlists.map(rp => {
        if (!rp.playlist_id) return rp;
        // Adding 'id' as a standard property is faster than defineProperties
        rp.id = toTrimmedString(rp.playlist_id);
        return rp;
      });
      const merged = mergePlaylistEntriesWithSongs(preparedPlaylists, data.songs_Master);
      self.postMessage({ dragPlaylists: merged });

    } catch (e) {
      if (${DEBUG_MODE}) console.error('Worker Error:', e);
      self.postMessage({ dragPlaylists: null });
    }
  });
  `,
      ]
      const jsBlob = new Blob(fnStr, { type: 'text/javascript' })
      const workerURL = URL.createObjectURL(jsBlob)
      const worker = new Worker(workerURL)
      worker.postMessage({
        songs_Master: songs_Master,
      })
      worker.onmessage = async (event: MessageEvent<any>) => {
        try {
          let dragPlaylists: any = event?.data?.dragPlaylists
          if (dragPlaylists?.length > 0) {
            dragPlaylists?.sort((a: any, b: any) => {
              const decodedNameA: string = isNotNullOrEmptyString(a?.name)
                ? decodeUnicode(a.name)
                : ''
              const decodedNameB: string = isNotNullOrEmptyString(b?.name)
                ? decodeUnicode(b.name)
                : ''
              const nameA: string = decodedNameA
                ? decodedNameA?.toUpperCase()
                : ''
              const nameB: string = decodedNameB
                ? decodedNameB?.toUpperCase()
                : ''
              if (nameA < nameB) return -1
            })
            let listValues: any[] = []
            dragPlaylists?.map((playlist: any) => {
              listValues.push({
                label: decodeUnicode(playlist?.name),
                value: playlist?.playlist_id,
                image: '',
              })
            })
            setPresetSamples(dragPlaylists)
          } else setPresetSamples([])
        } catch (ee: any) {
          if (DEBUG_MODE) console.log(ee)
          logSentryError(ee, SENTRY_COMPONENT_NAME)
        } finally {
          worker.terminate()
        }
      }
      worker.onmessageerror = (ev: any) => {
        try {
          if (DEBUG_MODE) console.log(ev)
          logSentryError('worker.onmessageerror - x.1', SENTRY_COMPONENT_NAME)
          worker.terminate()
        } catch (zae: any) {
          if (DEBUG_MODE) console.log(zae)
          logSentryError(zae, SENTRY_COMPONENT_NAME)
        }
      }
      worker.onerror = (ev: any) => {
        try {
          if (DEBUG_MODE) console.log(ev)
          logSentryError('worker.onerror - x.1', SENTRY_COMPONENT_NAME)
          worker.terminate()
        } catch (zae: any) {
          if (DEBUG_MODE) console.log(zae)
          logSentryError(zae, SENTRY_COMPONENT_NAME)
        }
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }

  function getPlaylists(web2UserId: string, dontGetEntries: boolean) {
    try {
      const fnStr: string[] = [
        TO_TRIMMED_STRING_FUNCTION +
          ONLY_UNIQUE_FUNCTION +
          IS_NOT_NULL_OR_EMPTY_STRING_FUNCTION +
          FORMAT_SONG_NAME_BIGGER_FUNCTION +
          `
        function mergePlaylistEntriesWithSongs(playlists, allSongs) {
          let ret = []
          let clonedPl = null
          try {
            if (playlists?.length > 0 && allSongs?.length > 0) {
              playlists.map((pl) => {
                clonedPl = structuredClone(pl)
                if (pl?.entries?.length > 0) {
                  clonedPl.entries = []
                  let uniqueSongs = []
                  if (allSongs?.length > 0)
                    uniqueSongs = allSongs?.filter(onlyUnique)
                  pl?.entries?.map((en) => {
                    let match = uniqueSongs?.filter(
                      (s) =>
                        s?.contract_address == en?.contract_address &&
                        s?.token_id == en?.token_id &&
                        isNotNullOrEmptyString(s?.contract_address) &&
                        isNotNullOrEmptyString(s?.token_id)
                    )
                    if (match?.length > 0) {
                      let songMatch = match[0]
                      clonedPl.entries.push({
                        animation_url: songMatch?.animation_url,
                        contract_address: en?.contract_address,
                        token_id: en?.token_id,
                        artist: songMatch?.artist,
                        duration: en?.duration,
                        formattedDuration: en?.formattedDuration,
                        genres: en?.genres,
                        frontImage: songMatch?.frontImage,
                        playlistId: en?.playlistId,
                        favorited: songMatch?.favorited,
                        sortOrder: en?.sortOrder,
                        name: formatSongNameBigger(
                          toTrimmedString(songMatch?.name)
                        ),
                        formattedMintDate: songMatch?.formattedMintDate,
                        formattedSongNameSmallSpaceEscaped: songMatch?.formattedSongNameSmallSpaceEscaped,
                        formattedSongNameBig: songMatch?.formattedSongNameBig,
                        formattedSongNameBigger: songMatch?.formattedSongNameBigger,
                        formattedArtistNameBig: songMatch?.formattedArtistNameBig,
                        formattedArtistNameBigger: songMatch?.formattedArtistNameBigger,
                        keyOne: songMatch?.keyOne,
                        keyTwo: songMatch?.keyTwo,
                        keyThree: songMatch?.keyThree,
                      })
                    } else if (${DEBUG_MONITOR_SCHEDULE_TIMERS}) console.log('tl-tl-2')
                  })
                }
                if (${DEBUG_MONITOR_SCHEDULE_TIMERS}) console.log(clonedPl)
                ret?.push(clonedPl)
              })
            }
          } catch (e) {
            if (${DEBUG_MONITOR_SCHEDULE_TIMERS}) console.log(e)
          }
          return ret
        }
        self.addEventListener('message', async (event) => {
          if (event?.data != undefined && 
              event?.data != null && 
              event?.data?.web2UserId != undefined &&
              event?.data?.web2UserId != null &&
              event?.data?.web2UserId.toString().trim() != '' &&
              event?.data?.songs_Master?.length > 0) {
              const songs_Master = event?.data?.songs_Master
              const opts = {
                method: 'POST',
                body: JSON.stringify({
                  web2UserId: event?.data?.web2UserId.toString().trim(),
                  dontGetEntries: event?.data?.dontGetEntries == true ? true : false,
                  dontGetPresets: ${
                    !hasGotPresets ||
                    forceGetPresets?.current ||
                    web2UserSessionId == PRESETS_AND_STATIONS_ADMIN_WEB2_USER_ID
                      ? 'false'
                      : 'true'
                  },
                }),
              }
              await fetch('${HOST}' + '/api/p.stream-sv/pl/gpls2', opts)
                .then(async (x) => {
                  const rez = await x.json()
                  let pl = rez?.playlists?.length > 0 ? rez?.playlists : []
                  let dragPlaylists = []
                  let dragPlaylists_WithPresets = []
                  let acc = { playlists: [] }
                  let accWithPresets = { playlists: [] }
                  let repl = null
                  let clonePlaylists = structuredClone(pl)
                  if (clonePlaylists?.length > 0) {
                    await Promise.all(
                      await clonePlaylists.map(async (rp) => {
                        if (rp?.playlist_id) {
                          repl = Object.defineProperties(rp, {
                            id: {
                              value: toTrimmedString(rp?.playlist_id),
                              writable: true,
                              enumerable: false,
                            },
                          })
                          if (rp &&
                              ${web2UserIdLoggedIn == true} &&
                              isNotNullOrEmptyString(rp?.web2UserId) &&
                              ${web2UserSessionId} == rp?.web2UserId) 
                              acc.playlists.push(repl)
                          accWithPresets.playlists.push(repl)
                        }
                      })
                    )
                    clonePlaylists = undefined
                    dragPlaylists = acc?.playlists
                    let cln = structuredClone(acc?.playlists)
                    let plz = mergePlaylistEntriesWithSongs(cln, songs_Master)
                    dragPlaylists_WithPresets = accWithPresets?.playlists
                    let cln_WithPresets = structuredClone(accWithPresets?.playlists)
                    let plz_WithPresets = mergePlaylistEntriesWithSongs(
                      cln_WithPresets,
                      songs_Master
                    )
                    dragPlaylists = plz
                    dragPlaylists_WithPresets = plz_WithPresets
                    cln = undefined
                    cln_WithPresets = undefined
                    self.postMessage({dragPlaylists: dragPlaylists, dragPlaylists_WithPresets: dragPlaylists_WithPresets})
                  } else self.postMessage({dragPlaylists: null, dragPlaylists_WithPresets: null})
                })
                .catch((y) => {
                  if (${DEBUG_MODE}) console.log(y)
                  self.postMessage({dragPlaylists: null, dragPlaylists_WithPresets: null})
                })
           }
        })`,
      ]
      const jsBlob = new Blob(fnStr, { type: 'text/javascript' })
      const workerURL = URL.createObjectURL(jsBlob)
      const worker = new Worker(workerURL)
      worker.postMessage({
        web2UserId: web2UserId,
        dontGetEntries: dontGetEntries,
        songs_Master: songs_Master,
      })
      worker.onmessage = async (event: MessageEvent<any>) => {
        try {
          let rez: any = event?.data
          let dragPlaylists = rez?.dragPlaylists
          let dragPlaylists_WithPresets = rez?.dragPlaylists_WithPresets
          if (dragPlaylists?.length > 0) {
            //@ts-ignore
            dragPlaylists?.sort((a: any, b: any) => {
              let decodedNameA: any = isNotNullOrEmptyString(a?.name)
                ? decodeUnicode(a.name)
                : ''
              let decodedNameB: any = isNotNullOrEmptyString(b?.name)
                ? decodeUnicode(b.name)
                : ''
              const nameA: any = decodedNameA ? decodedNameA?.toUpperCase() : ''
              const nameB: any = decodedNameB ? decodedNameB?.toUpperCase() : ''
              if (nameA < nameB) return -1
            })
            let listValues: any = []
            dragPlaylists?.map((playlist: any) => {
              listValues.push({
                label: decodeUnicode(playlist?.name),
                value: playlist?.playlist_id,
                image: '',
              })
            })
            setMasterPlaylists(dragPlaylists)
            let mpap: any = []
            if (masterPlaylistsAndPresets?.length > 0) {
              masterPlaylistsAndPresets.map((p: any) => {
                if (p?.web2UserId != web2UserSessionId) mpap.push(p)
                else {
                  let nva = dragPlaylists?.filter(
                    (n: any) => n?.playlist_id == p?.playlist_id,
                  )
                  if (nva?.length > 0) {
                    mpap.push(nva[0])
                  }
                }
              })
            }
            setMasterPlaylistsAndPresets(mpap)
          } else setMasterPlaylists([])
          if (
            (!hasGotPresets ||
              forceGetPresets.current ||
              web2UserSessionId == PRESETS_AND_STATIONS_ADMIN_WEB2_USER_ID) &&
            dragPlaylists_WithPresets?.length > 0
          ) {
            forceGetPresets.current = false
            let listValues: any = []
            dragPlaylists_WithPresets?.map((playlist: any) => {
              listValues.push({
                label: decodeUnicode(playlist?.name),
                value: playlist?.playlist_id,
                image: '',
              })
            })
            let nonPresetSamples = dragPlaylists_WithPresets?.filter(
              (p: any) => isPresetSample(p?.playlist_id) != true,
            )
            if (isNullOrUndefined(nonPresetSamples)) nonPresetSamples = []
            let presetSamples = dragPlaylists_WithPresets?.filter(
              (p: any) => isPresetSample(p?.playlist_id) == true,
            )
            if (isNullOrUndefined(presetSamples)) presetSamples = []
            if (nonPresetSamples?.length > 0)
              //@ts-ignore
              nonPresetSamples.sort((a: any, b: any) => {
                let decodedNameA: any = isNotNullOrEmptyString(a?.name)
                  ? decodeUnicode(a?.name)
                  : ''
                let decodedNameB: any = isNotNullOrEmptyString(b?.name)
                  ? decodeUnicode(b?.name)
                  : ''
                const nameA: any = decodedNameA
                  ? decodedNameA?.toUpperCase()
                  : ''
                const nameB: any = decodedNameB
                  ? decodedNameB?.toUpperCase()
                  : ''
                let num: number = nameA < nameB ? -1 : nameA > nameB ? 1 : 0
                if (nameA < nameB) return num
              })
            if (
              presetSamples?.length > 0 &&
              web2UserSessionId == PRESETS_AND_STATIONS_ADMIN_WEB2_USER_ID
            )
              //@ts-ignore
              presetSamples.sort((a: any, b: any) => {
                try {
                  let decodedNameA: any = isNotNullOrEmptyString(a?.name)
                    ? decodeUnicode(a?.name)
                    : ''
                  let decodedNameB: any = isNotNullOrEmptyString(b?.name)
                    ? decodeUnicode(b?.name)
                    : ''
                  const nameA: any = decodedNameA
                    ? decodedNameA?.toUpperCase()
                    : ''
                  const nameB: any = decodedNameB
                    ? decodedNameB?.toUpperCase()
                    : ''
                  let num: number = nameA < nameB ? -1 : nameA > nameB ? 1 : 0
                  if (nameA < nameB) return num
                } catch {
                  return 0
                }
              })
            dragPlaylists_WithPresets = [...nonPresetSamples, ...presetSamples]
            setMasterPlaylistsAndPresets(dragPlaylists_WithPresets)
            setHasGotPresets(true)
          }
          setTriggerPlaylistOverlayRefresh(!triggerPlaylistOverlayRefresh)
        } catch (ee: any) {
          if (DEBUG_MODE) console.log(ee)
          logSentryError(ee, SENTRY_COMPONENT_NAME)
        } finally {
          worker.terminate()
        }
      }
      worker.onmessageerror = (ev: any) => {
        try {
          if (DEBUG_MODE) console.log(ev)
          logSentryError('worker.onmessageerror - 13', SENTRY_COMPONENT_NAME)
          worker.terminate()
        } catch (zae: any) {
          if (DEBUG_MODE) console.log(zae)
          logSentryError(zae, SENTRY_COMPONENT_NAME)
        }
      }
      worker.onerror = (ev: any) => {
        try {
          if (DEBUG_MODE) console.log(ev)
          logSentryError('worker.onerror - 13', SENTRY_COMPONENT_NAME)
          worker.terminate()
        } catch (zae: any) {
          if (DEBUG_MODE) console.log(zae)
          logSentryError(zae, SENTRY_COMPONENT_NAME)
        }
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }

  async function getMasterPlaylists() {
    try {
      if (wholeSongSetBound) {
        if (isNotNullOrEmptyString(web2UserSessionId))
          getPlaylists(web2UserSessionId, false)
        else {
          setMasterPlaylists([])
          setMasterPlaylistsAndPresets([])
        }
      } else {
        setMasterPlaylists([])
        setMasterPlaylistsAndPresets([])
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }

  useEffect(() => {
    try {
      if (insertStreamTimeoutId != null) {
        clearTimeout(insertStreamTimeoutId)
        setInsertStreamTimeoutId(null)
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }, [triggerClearInsertStreamTimeoutId])

  useEffect(() => {
    try {
      if (isNotNullOrUndefined(debouncedStreamObject)) {
        setInsertStreamTimeoutId(
          setTimeout(async () => {
            const jsBlob: Blob = new Blob(INSERT_STREAM_CALLSTRING, {
              type: 'text/javascript',
            })
            const workerURL: string = URL.createObjectURL(jsBlob)
            const worker: Worker = new Worker(workerURL)
            worker.postMessage({
              walletAddress: null,
              tokenId: debouncedStreamObject?.tokenId,
              contractAddress: debouncedStreamObject?.contractAddress,
              ipAddress: debouncedStreamObject?.ipAddress,
              localIPAddress: debouncedStreamObject?.localIPAddress,
              streamStart: debouncedStreamObject?.streamStart,
              streamEnd: debouncedStreamObject?.streamEnd,
              time: debouncedStreamObject?.time,
              bz: isBusinessUserSubscribed,
              web2UserId: web2UserSessionId,
            })
            worker.onmessage = (event: MessageEvent<any>) => {
              try {
                if (
                  event?.data?.success == true &&
                  event?.data?.isitomlao == true &&
                  isBusinessUserSubscribed
                ) {
                  pauseSong()
                  setStreamingInTwoOrMoreLocationsModalOpened(true)
                }
                worker.terminate()
              } catch (e) {
                if (DEBUG_MODE) console.log(e)
                logSentryError(e, SENTRY_COMPONENT_NAME)
              } finally {
                setTriggerClearInsertStreamTimeoutId(
                  !triggerClearInsertStreamTimeoutId,
                )
              }
            }
            worker.onmessageerror = (ev: any) => {
              try {
                if (DEBUG_MODE) console.log(ev)
                logSentryError(
                  'worker.onmessageerror - 14',
                  SENTRY_COMPONENT_NAME,
                )
                worker.terminate()
              } catch (zae: any) {
                if (DEBUG_MODE) console.log(zae)
              } finally {
                setTriggerClearInsertStreamTimeoutId(
                  !triggerClearInsertStreamTimeoutId,
                )
              }
            }
            worker.onerror = (ev: any) => {
              try {
                if (DEBUG_MODE) console.log(ev)
                logSentryError('worker.onerror - 14', SENTRY_COMPONENT_NAME)
                worker.terminate()
              } catch (zae: any) {
                if (DEBUG_MODE) console.log(zae)
                logSentryError(zae, SENTRY_COMPONENT_NAME)
              } finally {
                setTriggerClearInsertStreamTimeoutId(
                  !triggerClearInsertStreamTimeoutId,
                )
              }
            }
          }, 10),
        )
      } else if (DEBUG_MODE) console.log(debouncedStreamObject)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }, [debouncedStreamObject, isBusinessUserSubscribed])

  async function loginToAcceptInvitationModalCallback(confirmed: any) {
    try {
      if (pendingInvitation) {
        if (
          confirmed &&
          isNotNullOrEmptyString(pendingInvitation?.email) &&
          !web2UserIdLoggedIn
        ) {
          setLoginEmail(pendingInvitation?.email)
          setIsInvitationPass(false)
          setTriggerSignIn(true)
        }
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }

  async function confirmInvitationAcceptanceCallback(confirmed: any) {
    try {
      if (
        web2UserIdLoggedIn == true &&
        isNotNullOrEmptyString(web2UserSessionId) &&
        isNotNullOrEmptyString(pendingInvitation?.email) &&
        toTrimmedStringLowerCase(pendingInvitation?.email) ==
          toTrimmedStringLowerCase(web2UserSessionEmail)
      ) {
        if (confirmed)
          await saveInvitation(
            pendingInvitation?.adminWeb2UserId,
            pendingInvitation?.invitationToken,
            INVITATION_ACCEPTED_STATUS,
            dateToNetDate(new Date()),
            web2UserSessionId,
            pendingInvitation?.email,
            null,
          )
            .then((r: any) => {
              if (r?.success == true) {
                window.location.href =
                  HOST + '/settings?uX=' + pendingInvitation?.invitationToken
              } else {
                if (r?.errorCode == 5)
                  toast(
                    translate(
                      'This invitation has been cancelled.',
                      'Esta invitación ha sido cancelada.',
                      'Cette invitation a été annulée.',
                      '이 초대는 취소되었습니다.',
                      selectedLanguage,
                    ),
                    'error',
                  )
                else toastError(selectedLanguage)
              }
            })
            .catch((ex: any) => {
              toastError(selectedLanguage)
              if (DEBUG_MODE) console.log(ex)
              logSentryError(ex, SENTRY_COMPONENT_NAME)
            })
        else declineInvitation(pendingInvitation)
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }

  const [userCreditsBalance, setUserCreditsBalance] = useState<number | null>(
    null,
  )
  const [creditsReady, setCreditsReady] = useState<boolean>(
    !pageProps.gotCredits,
  )
  const [creditsStripeConfirmationCode, setCreditsStripeConfirmationCode] =
    useState<string | null>(pageProps?.scc)

  function cleanRouterUrl() {
    try {
      const cleanUrl: string = router?.pathname
      router.replace(cleanUrl, undefined, { shallow: true })
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
    }
  }

  async function updateUserCredits() {
    try {
      const retVal: any = await getBusinessUserCredit(
        isNullOrEmptyString(user?.adminWeb2UserId)
          ? web2UserSessionId
          : user?.adminWeb2UserId,
        null,
      )
      if (retVal?.success && retVal?.businessUserCredit)
        setUserCreditsBalance(
          retVal?.businessUserCredit?.balance > -1
            ? retVal?.businessUserCredit?.balance
            : 0,
        )
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }

  const headTag: JSX.Element = useMemo(() => {
    return (
      <Head>
        <script
          data-partytown-config
          dangerouslySetInnerHTML={{
            __html: `
              partytown = {
                lib: "/_next/static/~partytown/",
                forward: ["gtag"]
              };
            `,
          }}
        />
        {width > 275 && height > 350 && chatBotEmbed}
        <style type="text/css">{reactPlayerStyles}</style>
        {pageComponentDisplayName == HOME_PAGE_DISPLAY_NAME &&
          navigating != true && (
            <style type="text/css">{`
                  #__next::before {
                  content: "";
                  position: absolute;
                  top: 0;
                  left: 0;
                  width: 100%;
                  height: var(--overlay-height);
                  z-index: -1;
                  background-image: ${
                    dark
                      ? avifCompatible
                        ? isMobileOnly
                          ? 'url("/images/F90CE7C0-9B76-40DC-9EE9-73FC16CF47C1.avif")'
                          : 'url("/images/64A9D722-26E1-414D-B662-58FF17AE57E9.avif")'
                        : isMobileOnly
                          ? 'url("/images/67ED0EE7-917C-479A-B809-096D8B39C7A8.png")'
                          : 'url("/images/64A9D722-26E1-414D-B662-58FF17AE57E9.png")'
                      : avifCompatible
                        ? isMobileOnly
                          ? 'url("/images/2FDBA16B-6D26-44C4-A051-534C85FFC674.avif")'
                          : 'url("/images/5E46F11A-462B-4DAE-9314-F8AB1CDB4730.avif")'
                        : isMobileOnly
                          ? 'url("/images/16408D0B-BD01-41E0-B88A-BCE3CA0EF2A3.png")'
                          : 'url("/images/5E46F11A-462B-4DAE-9314-F8AB1CDB4730.png")'
                  };
                  background-size: 100% 90%;
                  background-position: top center;
                  background-repeat: no-repeat;
                  opacity: ${navigating ? 0.7 : 1};
                  transition: opacity 0.4s ease-in-out;
                  pointer-events: none;
                }
                #__next {
                  position: relative;
                  min-height: 100dvh;
                  display: flow-root; 
                }
        `}</style>
          )}
        <title>{titleText}</title>
        {timePickerScript}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html:
              toTrimmedString(pageProps?.jsonLdType) == 'home'
                ? `{
                    "@context": "https://schema.org",
                     "@graph": [{
                                  "@type" : "WebSite",
                                  "@id": "${PRODUCTION_HOST}#website",
                                  "name" : "${JSON_LD_SITE_NAME}",
                                  "url" : "${PRODUCTION_HOST}",
                                  "publisher": {
                                    "@id": "${PRODUCTION_HOST}#organization"
                                  }
                                },
                                {
                                  "@type": ["Organization"],
                                  "@id": "${PRODUCTION_HOST}#organization",
                                  "name": "${STREAMY3_INFORMAL_BUSINESS_NAME}",
                                  "legalName": "${STREAMY3_OFFICIAL_BUSINESS_NAME}",
											            "naics": [${STREAMY3_ALL_NAICS_FOR_JSON_LD}],
                                  "description": "${toTrimmedString(
                                    pageProps?.jsonLdOrganizationDescription,
                                  )}",
                                  "sameAs": [
                                      "${BLOG_HOST}",
                                      "${ISSUE_WIRE_PRESS_RELEASE}",
                                      "${MEDIUM_BLOG}",
                                      "${SOURCE_FORGE_PROFILE}",
                                      "${SAAS_HUB_PROFILE}",
                                      "${SOFTWARE_SUGGEST_PROFILE}",
                                      "${G2_PROFILE}",
                                      "${JUSTIA_TRADEMARK_OWNER}",
                                      "${STREAMY3_GITHUB_PROFILE}",
                                      "${SLASH_DOT_PROFILE}",
                                      "${GOOGLE_TRENDS_ID_URL}"
                                  ],
                                  "address": {
                                    "@type": "PostalAddress",
                                    "streetAddress": "${STREAMY3_OFFICIAL_ADDRESS_LINE_1}",
                                    "addressLocality": "${STREAMY3_OFFICIAL_ADDRESS_CITY}",
                                    "addressRegion": "${STREAMY3_OFFICIAL_ADDRESS_STATE_CODE}",
                                    "postalCode": "${STREAMY3_OFFICIAL_ADDRESS_ZIP_CODE}",
                                    "addressCountry": "${UNITED_STATES_ISO_3166_ALPHA_2_CODE}"
                                  },
                                  "url": "${PRODUCTION_HOST}",
                                  "logo": "${HOME_PS_LOGO}",
                                  "contactPoint": [
                                    {
                                      "@type": "ContactPoint",
                                      "email": "${toTrimmedString(
                                        pageProps?.jsonLdOrganizationContactEmail,
                                      )}", ${
                                        SHOW_CONTACT_PHONE
                                          ? `"telephone": "${CONTACT_PHONE}",`
                                          : ''
                                      }
                                      "contactType": "customer service",
                                      "availableLanguage": ["${LANGUAGE_CODE_ENGLISH}", "${LANGUAGE_CODE_SPANISH}", "${LANGUAGE_CODE_FRENCH}", "${LANGUAGE_CODE_KOREAN}"]
                                    }
                                  ],
                                  "publishingPrinciples" : "${HOST}/responsible-ai",
                                  "areaServed": [
                                    { "@type": "Country", "name": "${UNITED_STATES_ISO_3166_ALPHA_2_CODE}" },
                                    { "@type": "Country", "name": "${CANADA_ISO_3166_ALPHA_2_CODE}" },
                                    { "@type": "Country", "name": "${BRITAIN_ISO_3166_ALPHA_2_CODE}" },
                                    { "@type": "Country", "name": "${AUSTRIA_ISO_3166_ALPHA_2_CODE}" },
                                    { "@type": "Country", "name": "${BELGIUM_ISO_3166_ALPHA_2_CODE}" },
                                    { "@type": "Country", "name": "${CYPRUS_ISO_3166_ALPHA_2_CODE}" },
                                    { "@type": "Country", "name": "${GERMANY_ISO_3166_ALPHA_2_CODE}" },
                                    { "@type": "Country", "name": "${ESTONIA_ISO_3166_ALPHA_2_CODE}" },
                                    { "@type": "Country", "name": "${FINLAND_ISO_3166_ALPHA_2_CODE}" },
                                    { "@type": "Country", "name": "${GREECE_ISO_3166_ALPHA_2_CODE}" },
                                    { "@type": "Country", "name": "${CROATIA_ISO_3166_ALPHA_2_CODE}" },
                                    { "@type": "Country", "name": "${IRELAND_ISO_3166_ALPHA_2_CODE}" },
                                    { "@type": "Country", "name": "${ITALY_ISO_3166_ALPHA_2_CODE}" },
                                    { "@type": "Country", "name": "${LUXEMBOURG_ISO_3166_ALPHA_2_CODE}" },
                                    { "@type": "Country", "name": "${LATVIA_ISO_3166_ALPHA_2_CODE}" },
                                    { "@type": "Country", "name": "${LITHUANIA_ISO_3166_ALPHA_2_CODE}" },
                                    { "@type": "Country", "name": "${MALTA_ISO_3166_ALPHA_2_CODE}" },
                                    { "@type": "Country", "name": "${NETHERLANDS_ISO_3166_ALPHA_2_CODE}" },
                                    { "@type": "Country", "name": "${PORTUGAL_ISO_3166_ALPHA_2_CODE}" },
                                    { "@type": "Country", "name": "${SLOVENIA_ISO_3166_ALPHA_2_CODE}" },
                                    { "@type": "Country", "name": "${SLOVAKIA_ISO_3166_ALPHA_2_CODE}" },
                                    { "@type": "Country", "name": "${ANDORRA_ISO_3166_ALPHA_2_CODE}" },
                                    { "@type": "Country", "name": "${MONACO_ISO_3166_ALPHA_2_CODE}" },
                                    { "@type": "Country", "name": "${SAN_MARINO_ISO_3166_ALPHA_2_CODE}" },
                                    { "@type": "Country", "name": "${VATICAN_CITY_ISO_3166_ALPHA_2_CODE}" },
                                    { "@type": "Country", "name": "${MONTENEGRO_ISO_3166_ALPHA_2_CODE}" },
                                    { "@type": "Country", "name": "${KOSOVO_ISO_3166_ALPHA_2_CODE}" },
                                    { "@type": "Country", "name": "${SPAIN_ISO_3166_ALPHA_2_CODE}" },
                                    { "@type": "Country", "name": "${FRANCE_ISO_3166_ALPHA_2_CODE}" },
                                    { "@type": "Country", "name": "${SOUTH_KOREA_ISO_3166_ALPHA_2_CODE}" }
                                  ]
                                },
                                {
                                  "@type": "Service",
                                  "@id": "${PRODUCTION_HOST}#service",
                                  "provider": { "@id": "${PRODUCTION_HOST}#organization" },
                                  "name": { "@value": "${PRODUCT_NAME}", "@language": "${LANGUAGE_CODE_ENGLISH}" },
                                  "hasOfferCatalog": {
                                    "@type": "OfferCatalog",
                                    "@id": "${PRODUCTION_HOST}#offercatalog",
                                    "name": "${translate(
                                      JSON_LD_HAS_OFFER_CATALOG_NAME_EN,
                                      JSON_LD_HAS_OFFER_CATALOG_NAME_ES,
                                      JSON_LD_HAS_OFFER_CATALOG_NAME_FR,
                                      JSON_LD_HAS_OFFER_CATALOG_NAME_KO,
                                      selectedLanguage,
                                    )}",
                                    "itemListElement": [ 
                                      { "@id": "${PRODUCTION_HOST}#softwaretier" },
                                      { "@id": "${PRODUCTION_HOST}#hardwaretier" }
                                    ]
                                  }
                                },
                                {
                                  "@type": ["WebApplication", "MobileApplication"],
                                  "@id": "${PRODUCTION_HOST}#app",
                                  "isPartOf": {
                                    "@id": "${PRODUCTION_HOST}#website"
                                  },
                                  "publisher": {
                                    "@id": "${PRODUCTION_HOST}#organization"
                                  },
                                  "name": "${JSON_LD_SITE_NAME}",
                                  "inLanguage": "${toTrimmedString(
                                    selectedLanguage,
                                  )}",
                                  "applicationCategory": ["MultimediaApplication", "BusinessApplication"],
                                  "operatingSystem": ["Android", "iOS", "Windows", "Web"],
                                  "browserRequirements": "Requires JavaScript. Requires HTML5.",
                                  "offers": {
                                    "@type": "Offer",
                                    "@id": "${PRODUCTION_HOST}#softwaretier",
                                    "sku": "${SOFTWARE_SKU}",
                                    "eligibleQuantity": {
                                      "@type": "QuantitativeValue",
                                      "minValue": "1",
                                      "unitCode": "C62" 
                                    },
                                    "priceSpecification": {
                                      "@type": "PriceSpecification",
                                      "minPrice": "${pageProps?.jsonLdSubscriptionCurrencyPrice}",
                                      "maxPrice": "${pageProps?.jsonLdSubscriptionCurrencyPrice}"
                                    },
                                    "seller": { "@id": "${PRODUCTION_HOST}#organization" },
                                    "price": "${toTrimmedString(
                                      pageProps?.jsonLdSubscriptionCurrencyPrice,
                                    )}",
                                    "priceCurrency": "${toTrimmedString(
                                      pageProps?.jsonLdCurrencyCaps,
                                    )}",
                                    "priceValidUntil": "${
                                      pageProps?.jsonLdSubscriptionCurrencyPriceValidUntil
                                    }",
                                    "availability": "https://schema.org/OnlineOnly",
                                    "hasMerchantReturnPolicy": [
                                      {
                                        "@type": "MerchantReturnPolicy",
                                        "@id": "${PRODUCTION_HOST}#policysoftware_A",
                                        "applicableCountry": [${ACCEPTABLE_SOFTWARE_ONLY_REFUND_COUNTRIES_ISO3166_FOR_JSON_LD_1}],
                                        "returnPolicyCountry": "${UNITED_STATES_ISO_3166_ALPHA_2_CODE}",
                                        "returnPolicyCategory": "https://schema.org/MerchantReturnNotPermitted",
                                        "url": "${PRODUCTION_HOST}/refunds"
                                      },
                                      {
                                        "@type": "MerchantReturnPolicy",
                                        "@id": "${PRODUCTION_HOST}#policysoftware_B",
                                        "applicableCountry": [${ACCEPTABLE_SOFTWARE_ONLY_REFUND_COUNTRIES_ISO3166_FOR_JSON_LD_2}],
                                        "returnPolicyCountry": "${UNITED_STATES_ISO_3166_ALPHA_2_CODE}",
                                        "returnPolicyCategory": "https://schema.org/MerchantReturnNotPermitted",
                                        "url": "${PRODUCTION_HOST}/refunds"
                                      }
                                    ],
                                    "shippingDetails": [
                                        {
                                          "@type": "OfferShippingDetails",
                                          "shippingRate": {
                                            "@type": "MonetaryAmount",
                                            "value": "0.00",
                                            "currency": "${toTrimmedString(
                                              pageProps?.jsonLdCurrencyCaps,
                                            )}"
                                          },
                                          "deliveryTime": {
                                            "@type": "ShippingDeliveryTime",
                                            "handlingTime": {
                                              "@type": "QuantitativeValue",
                                              "minValue": 0,
                                              "maxValue": 0,
                                              "unitCode": "DAY"
                                            },
                                            "transitTime": {
                                              "@type": "QuantitativeValue",
                                              "minValue": 0,
                                              "maxValue": 0,
                                              "unitCode": "DAY"
                                            }
                                          },
                                          "shippingDestination": [${SHIPPING_DESTINATIONS_SOFTWARE_ONLY_REFUND_COUNTRIES_ISO3166_FOR_JSON_LD_1}]
                                      },
                                      {
                                        "@type": "OfferShippingDetails",
                                        "shippingRate": {
                                          "@type": "MonetaryAmount",
                                          "value": "0.00",
                                          "currency": "${toTrimmedString(
                                            pageProps?.jsonLdCurrencyCaps,
                                          )}"
                                        },
                                        "deliveryTime": {
                                          "@type": "ShippingDeliveryTime",
                                          "handlingTime": {
                                            "@type": "QuantitativeValue",
                                            "minValue": 0,
                                            "maxValue": 0,
                                            "unitCode": "DAY"
                                          },
                                          "transitTime": {
                                            "@type": "QuantitativeValue",
                                            "minValue": 0,
                                            "maxValue": 0,
                                            "unitCode": "DAY"
                                          }
                                        },
                                        "shippingDestination": [${SHIPPING_DESTINATIONS_SOFTWARE_ONLY_REFUND_COUNTRIES_ISO3166_FOR_JSON_LD_2}]
                                      }
                                    ]
                                  },
                                  "downloadUrl": "${PRODUCTION_HOST}",
                                  "installUrl": "${PRODUCTION_HOST}",
                                  "url": "${PRODUCTION_HOST}",
                                  "about": {
                                    "@type": "Service",
                                    "name": { "@value": "${PRODUCT_NAME}", "@language": "${LANGUAGE_CODE_ENGLISH}" },
                                    "description": "${
                                      pageProps?.jsonLdApplicationAbout
                                    }"
                                  },
                                  "featureList": [
                                    "${translate('AI Brand Analysis', 'Análisis de marca mediante inteligencia artificial', "Analyse de marque par l'IA", '인공지능 브랜드 분석', selectedLanguage)}",
                                    "${translate('AI Generated Playlists', 'Listas de reproducción generada por IA', "Listes de lecture générée par l'IA", '인공지능이 생성한 플레이리스트', selectedLanguage)}",
                                    "${translate('Automated Scheduling', 'Programación automática', 'Programmes de lecture automatique', '자동 일정 관리', selectedLanguage)}",
                                    "${translate('Multi-zone Management', 'Gestión multizona', 'Gestion multizone', '다중 구역 관리', selectedLanguage)}",
                                    "${translate('Playlists', 'Listas de reproducción', 'Listes de lecture', '재생 목록', selectedLanguage)}",
                                    "${translate('Preset Playlists', 'Listas de reproducción preestablecidas', 'Listes de lecture prédéfinies', '사전 설정된 재생 목록', selectedLanguage)}",
                                    "${translate('Radios', 'Radios', 'Radios', '라디오', selectedLanguage)}",
                                    "${translate('Public Performance License', 'Licencia de actuación pública', 'Licence de représentation publique', '공개 공연 라이선스', selectedLanguage)}",
                                    "${translate('Huge Music Library', 'Enorme biblioteca de música', 'Vaste bibliothèque musicale', '방대한 음악 라이브러리', selectedLanguage)}"
                                  ],
                                  "alternateName": "${APP_DOMAIN}",
                                  "keywords": [${
                                    pageProps?.jsonLdSoftwareApplicationKeywords
                                  }]
                                },
                                {
                                  "@type": "ItemList",
                                  "name": "${translate(
                                    `Commercial Background Music Streaming Service Comparison 2026`,
                                    `Comparación de Servicios Comerciales de Transmisión de Música Ambiental en 2026`,
                                    `Comparaison des Services de Diffusion de Musique d'Ambiance Commerciaux en 2026`,
                                    `2026년 상업용 배경 음악 스트리밍 서비스 비교`,
                                    selectedLanguage,
                                  )}",
                                  "description": "${translate(
                                    `A side-by-side comparison of business background music providers based on monthly software costs.`,
                                    `Comparación de proveedores de música ambiental para negocios basada en los costos mensuales del software.`,
                                    `Comparaison des fournisseurs de musique d'ambiance pour les entreprises en fonction des coûts mensuels des logiciels.`,
                                    `월별 소프트웨어 비용을 기준으로 기업용 배경 음악 제공업체를 비교합니다.`,
                                    selectedLanguage,
                                  )}",
                                  "numberOfItems": 6,
                                  "itemListElement": [
                                    {
                                      "@type": "ListItem",
                                      "position": 1,
                                      "item": {
                                        "@type": "Product",
                                        "name": "${PRODUCT_NAME}",
                                        "description": "${translate(
                                          JSON_LD_SOFTWARE_ONLY_OFFER_DESCRIPTION_DEFAULT,
                                          JSON_LD_SOFTWARE_ONLY_OFFER_DESCRIPTION_ES,
                                          JSON_LD_SOFTWARE_ONLY_OFFER_DESCRIPTION_FR,
                                          JSON_LD_SOFTWARE_ONLY_OFFER_DESCRIPTION_KO,
                                          selectedLanguage,
                                        )}",
                                        "image": {
                                            "@type": "ImageObject",
                                            "url": "${JSON_LD_PRODUCT_LOGO_1500_X_1500_AVIF}",
                                            "contentUrl": "${JSON_LD_PRODUCT_LOGO_1500_X_1500_AVIF}",
                                            "width": "1500",
                                            "height": "1500",
                                            "caption": "${translate(
                                              'Icon for ' + PRODUCT_NAME,
                                              'Icono para ' + PRODUCT_NAME,
                                              'Icône pour ' + PRODUCT_NAME,
                                              PRODUCT_NAME + ' 아이콘',
                                              selectedLanguage,
                                            )}",
                                            "description": "${translate(
                                              'Logo of ' +
                                                PRODUCT_NAME +
                                                ', the commercial background music provider.',
                                              'Logotipo de ' +
                                                PRODUCT_NAME +
                                                ', proveedor de música ambiental para comercios.',
                                              'Logo de ' +
                                                PRODUCT_NAME +
                                                ", fournisseur de musique d'ambiance commerciale.",
                                              '상업용 배경 음악 제공업체인 ' +
                                                PRODUCT_NAME +
                                                '의 로고',
                                              selectedLanguage,
                                            )}",
                                            "encodingFormat": "image/avif"
                                        },
                                        "brand": { "@type": "Brand", "name": "${BRAND_NAME}" },
                                        "offers": [{ "@id": "${PRODUCTION_HOST}#softwaretier" },
                                          {
                                            "@type": "Offer",
                                            "@id": "${PRODUCTION_HOST}#hardwaretier",
                                            "sku": "${HARDWARE_DEVICE_SKU}",
                                            "eligibleQuantity": {
                                              "@type": "QuantitativeValue",
                                              "minValue": "1",
                                              "unitCode": "C62" 
                                            },
                                            "priceSpecification": {
                                              "@type": "PriceSpecification",
                                              "minPrice": "${pageProps?.jsonLdSubscriptionCurrencyPrice_Hardware}",
                                              "maxPrice": "${pageProps?.jsonLdSubscriptionCurrencyPrice_Hardware}"
                                            },
                                            "price": "${
                                              pageProps?.jsonLdSubscriptionCurrencyPrice_Hardware
                                            }",
                                            "priceCurrency": "${
                                              pageProps?.jsonLdCurrencyCaps
                                            }",
                                            "priceValidUntil": "${
                                              pageProps?.jsonLdPriceValidUntil
                                            }",
                                            "availability": "https://schema.org/InStock",
                                            "itemCondition": "http://schema.org/NewCondition",
                                            "seller": { "@id": "${PRODUCTION_HOST}#organization" },
                                            "hasMerchantReturnPolicy": { "@id": "${PRODUCTION_HOST}#policyreturns" },
                                            "shippingDetails": {
                                              "@type": "OfferShippingDetails",
                                              "shippingRate": {
                                                "@type": "MonetaryAmount",
                                                "value": "0",
                                                "currency": "${toTrimmedString(
                                                  pageProps?.jsonLdCurrencyCaps,
                                                )}"
                                              },
                                              "shippingDestination": {
                                                "@type": "DefinedRegion",
                                                "addressCountry": [
                                                ${ACCEPTABLE_SHIPPING_COUNTRIES_ISO3166_FOR_JSON_LD}]
                                              },
                                              "deliveryTime": {
                                                "@type": "ShippingDeliveryTime",
                                                "handlingTime": {
                                                  "@type": "QuantitativeValue",
                                                  "minValue": "0",
                                                  "maxValue": "1",
                                                  "unitCode": "d"
                                                },
                                                "transitTime": {
                                                  "@type": "QuantitativeValue",
                                                  "minValue": "7",
                                                  "maxValue": "10",
                                                  "unitCode": "d"
                                                },
                                                "businessDays": {
                                                  "@type": "OpeningHoursSpecification",
                                                  "dayOfWeek": [
                                                    "Monday",
                                                    "Tuesday",
                                                    "Wednesday",
                                                    "Thursday",
                                                    "Friday"
                                                  ]
                                                }
                                              }
                                            }
                                          }
                                        ]
                                      }
                                    },
                                    {
                                      "@type": "ListItem",
                                      "position": 2,
                                      "item": {
                                        "@type": "Product",
                                        "name": "${SOUNDTRACK_YOUR_BRAND_PRODUCT_NAME}",
                                        "description": "${translate(
                                          JSON_LD_PRODUCT_GENERIC_DESCRIPTION_FOR_COMPETITORS,
                                          JSON_LD_PRODUCT_GENERIC_DESCRIPTION_FOR_COMPETITORS_ES,
                                          JSON_LD_PRODUCT_GENERIC_DESCRIPTION_FOR_COMPETITORS_FR,
                                          JSON_LD_PRODUCT_GENERIC_DESCRIPTION_FOR_COMPETITORS_KO,
                                          selectedLanguage,
                                        )}",
                                        "image": {
                                            "@type": "ImageObject",
                                            "url": "${JSON_LD_PRODUCT_ICON_OF_GENERIC_PROVIDER_1500_X_1500_AVIF}",
                                            "contentUrl": "${JSON_LD_PRODUCT_ICON_OF_GENERIC_PROVIDER_1500_X_1500_AVIF}",
                                            "width": "1500",
                                            "height": "1500",
                                            "caption": "${translate(
                                              JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_DESCRIPTION,
                                              JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_DESCRIPTION_ES,
                                              JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_DESCRIPTION_FR,
                                              JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_DESCRIPTION_KO,
                                              selectedLanguage,
                                            )}",
                                            "description": "${translate(
                                              JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_FURTHER_DESCRIPTION,
                                              JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_FURTHER_DESCRIPTION_ES,
                                              JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_FURTHER_DESCRIPTION_FR,
                                              JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_FURTHER_DESCRIPTION_KO,
                                              selectedLanguage,
                                            )}",
                                            "encodingFormat": "image/avif"
                                        },
                                        "offers": {
                                          "@type": "Offer",
                                          "price": "${MIN_PRICE_SOUNDTRACK_YOUR_BRAND_STR_ALT}",
                                          "priceCurrency": "${ISO_4217_CURRENCY_CODE_US_DOLLAR}",
                                          "availability": "https://schema.org/InStock"
                                        }
                                      }
                                    },
                                    {
                                      "@type": "ListItem",
                                      "position": 3,
                                      "item": {
                                        "@type": "Product",
                                        "name": "${MOOD_MEDIA_PRODUCT_NAME}",
                                        "description": "${translate(
                                          JSON_LD_PRODUCT_GENERIC_DESCRIPTION_FOR_COMPETITORS,
                                          JSON_LD_PRODUCT_GENERIC_DESCRIPTION_FOR_COMPETITORS_ES,
                                          JSON_LD_PRODUCT_GENERIC_DESCRIPTION_FOR_COMPETITORS_FR,
                                          JSON_LD_PRODUCT_GENERIC_DESCRIPTION_FOR_COMPETITORS_KO,
                                          selectedLanguage,
                                        )}",
                                        "image": {
                                            "@type": "ImageObject",
                                            "url": "${JSON_LD_PRODUCT_ICON_OF_GENERIC_PROVIDER_1500_X_1500_AVIF}",
                                            "contentUrl": "${JSON_LD_PRODUCT_ICON_OF_GENERIC_PROVIDER_1500_X_1500_AVIF}",
                                            "width": "1500",
                                            "height": "1500",
                                            "caption": "${translate(
                                              JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_DESCRIPTION,
                                              JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_DESCRIPTION_ES,
                                              JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_DESCRIPTION_FR,
                                              JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_DESCRIPTION_KO,
                                              selectedLanguage,
                                            )}",
                                            "description": "${translate(
                                              JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_FURTHER_DESCRIPTION,
                                              JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_FURTHER_DESCRIPTION_ES,
                                              JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_FURTHER_DESCRIPTION_FR,
                                              JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_FURTHER_DESCRIPTION_KO,
                                              selectedLanguage,
                                            )}",
                                            "encodingFormat": "image/avif"
                                        },
                                        "offers": {
                                          "@type": "Offer",
                                          "price": "${MIN_PRICE_MOOD_MEDIA_STR_ALT}",
                                          "priceCurrency": "${ISO_4217_CURRENCY_CODE_US_DOLLAR}",
                                          "priceSpecification": {
                                            "@type": "PriceSpecification",
                                            "minPrice": "${MIN_PRICE_MOOD_MEDIA_STR_ALT}",
                                            "maxPrice": "${MAX_PRICE_MOOD_MEDIA_STR_ALT}"
                                          },
                                          "availability": "https://schema.org/InStock"
                                        }
                                      }
                                    },
                                    {
                                      "@type": "ListItem",
                                      "position": 4,
                                      "item": {
                                        "@type": "Product",
                                        "name": "${ROCKBOT_PRODUCT_NAME}",
                                        "description": "${translate(
                                          JSON_LD_PRODUCT_GENERIC_DESCRIPTION_FOR_COMPETITORS,
                                          JSON_LD_PRODUCT_GENERIC_DESCRIPTION_FOR_COMPETITORS_ES,
                                          JSON_LD_PRODUCT_GENERIC_DESCRIPTION_FOR_COMPETITORS_FR,
                                          JSON_LD_PRODUCT_GENERIC_DESCRIPTION_FOR_COMPETITORS_KO,
                                          selectedLanguage,
                                        )}",
                                        "image": {
                                            "@type": "ImageObject",
                                            "url": "${JSON_LD_PRODUCT_ICON_OF_GENERIC_PROVIDER_1500_X_1500_AVIF}",
                                            "contentUrl": "${JSON_LD_PRODUCT_ICON_OF_GENERIC_PROVIDER_1500_X_1500_AVIF}",
                                            "width": "1500",
                                            "height": "1500",
                                            "caption": "${translate(
                                              JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_DESCRIPTION,
                                              JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_DESCRIPTION_ES,
                                              JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_DESCRIPTION_FR,
                                              JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_DESCRIPTION_KO,
                                              selectedLanguage,
                                            )}",
                                            "description": "${translate(
                                              JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_FURTHER_DESCRIPTION,
                                              JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_FURTHER_DESCRIPTION_ES,
                                              JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_FURTHER_DESCRIPTION_FR,
                                              JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_FURTHER_DESCRIPTION_KO,
                                              selectedLanguage,
                                            )}",
                                            "encodingFormat": "image/avif"
                                        },
                                        "offers": {
                                          "@type": "Offer",
                                          "price": "${MIN_PRICE_ROCKBOT_STR_ALT}",
                                          "priceCurrency": "${ISO_4217_CURRENCY_CODE_US_DOLLAR}",
                                          "availability": "https://schema.org/InStock"
                                        }
                                      }
                                    },
                                    {
                                      "@type": "ListItem",
                                      "position": 5,
                                      "item": {
                                        "@type": "Product",
                                        "name": "${CLOUD_COVER_PRODUCT_NAME}",
                                        "description": "${translate(
                                          JSON_LD_PRODUCT_GENERIC_DESCRIPTION_FOR_COMPETITORS,
                                          JSON_LD_PRODUCT_GENERIC_DESCRIPTION_FOR_COMPETITORS_ES,
                                          JSON_LD_PRODUCT_GENERIC_DESCRIPTION_FOR_COMPETITORS_FR,
                                          JSON_LD_PRODUCT_GENERIC_DESCRIPTION_FOR_COMPETITORS_KO,
                                          selectedLanguage,
                                        )}",
                                        "image": {
                                            "@type": "ImageObject",
                                            "url": "${JSON_LD_PRODUCT_ICON_OF_GENERIC_PROVIDER_1500_X_1500_AVIF}",
                                            "contentUrl": "${JSON_LD_PRODUCT_ICON_OF_GENERIC_PROVIDER_1500_X_1500_AVIF}",
                                            "width": "1500",
                                            "height": "1500",
                                            "caption": "${translate(
                                              JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_DESCRIPTION,
                                              JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_DESCRIPTION_ES,
                                              JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_DESCRIPTION_FR,
                                              JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_DESCRIPTION_KO,
                                              selectedLanguage,
                                            )}",
                                            "description": "${translate(
                                              JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_FURTHER_DESCRIPTION,
                                              JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_FURTHER_DESCRIPTION_ES,
                                              JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_FURTHER_DESCRIPTION_FR,
                                              JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_FURTHER_DESCRIPTION_KO,
                                              selectedLanguage,
                                            )}",
                                            "encodingFormat": "image/avif"
                                        },
                                        "offers": {
                                          "@type": "Offer",
                                          "price": "${MIN_PRICE_CLOUDCOVER_STR_ALT}",
                                          "priceCurrency": "${ISO_4217_CURRENCY_CODE_US_DOLLAR}",
                                          "availability": "https://schema.org/InStock"
                                        }
                                      }
                                    },
                                    {
                                      "@type": "ListItem",
                                      "position": 6,
                                      "item": {
                                        "@type": "Product",
                                        "name": "${JUKEBOXY_PRODUCT_NAME}",
                                        "description": "${translate(
                                          JSON_LD_PRODUCT_GENERIC_DESCRIPTION_FOR_COMPETITORS,
                                          JSON_LD_PRODUCT_GENERIC_DESCRIPTION_FOR_COMPETITORS_ES,
                                          JSON_LD_PRODUCT_GENERIC_DESCRIPTION_FOR_COMPETITORS_FR,
                                          JSON_LD_PRODUCT_GENERIC_DESCRIPTION_FOR_COMPETITORS_KO,
                                          selectedLanguage,
                                        )}",
                                        "image": {
                                            "@type": "ImageObject",
                                            "url": "${JSON_LD_PRODUCT_ICON_OF_GENERIC_PROVIDER_1500_X_1500_AVIF}",
                                            "contentUrl": "${JSON_LD_PRODUCT_ICON_OF_GENERIC_PROVIDER_1500_X_1500_AVIF}",
                                            "width": "1500",
                                            "height": "1500",
                                            "caption": "${translate(
                                              JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_DESCRIPTION,
                                              JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_DESCRIPTION_ES,
                                              JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_DESCRIPTION_FR,
                                              JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_DESCRIPTION_KO,
                                              selectedLanguage,
                                            )}",
                                            "description": "${translate(
                                              JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_FURTHER_DESCRIPTION,
                                              JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_FURTHER_DESCRIPTION_ES,
                                              JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_FURTHER_DESCRIPTION_FR,
                                              JSON_LD_PRODUCT_IMAGE_ICON_GENERIC_COMPETITOR_FURTHER_DESCRIPTION_KO,
                                              selectedLanguage,
                                            )}",
                                            "encodingFormat": "image/avif"
                                        },
                                        "offers": {
                                          "@type": "Offer",
                                          "price": "${MIN_PRICE_JUKEBOXY_STR_ALT}",
                                          "priceCurrency": "${ISO_4217_CURRENCY_CODE_US_DOLLAR}",
                                          "availability": "https://schema.org/InStock"
                                        }
                                      }
                                    }
                                  ]
                                }
                    ]
                  }
                  `
                : toTrimmedString(pageProps?.jsonLdType) == 'contact'
                  ? `
                  {
                    "@context": "https://schema.org",
                    "@graph": [
                      {
                        "@type": "ContactPage",
                        "@id": "${translate(
                          PRODUCTION_HOST + '/contact',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_SPANISH +
                            '/contacto',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_FRENCH +
                            '/contact',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_KOREAN +
                            '/문의하기',
                          selectedLanguage,
                        )}#webpage",
                        "url": "${translate(
                          PRODUCTION_HOST + '/contact',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_SPANISH +
                            '/contacto',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_FRENCH +
                            '/contact',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_KOREAN +
                            '/문의하기',
                          selectedLanguage,
                        )}",
                        "name": "${translate(
                          `Contact ${PRODUCT_NAME} Support`,
                          `Aquí puede ponerse en contacto con el servicio de soporte técnico de ${PRODUCT_NAME}.`,
                          `Vous pouvez contacter ici le service d'assistance technique de ${PRODUCT_NAME}.`,
                          `여기에서 ${PRODUCT_NAME} 지원팀에 연락하실 수 있습니다.`,
                          selectedLanguage,
                        )}",
                        "description": "${translate(
                          `Get in touch with ${STREAMY3_INFORMAL_BUSINESS_NAME} for music licensing support and commercial streaming inquiries.`,
                          `Póngase en contacto con ${STREAMY3_INFORMAL_BUSINESS_NAME} para obtener asistencia sobre licencias musicales y consultas sobre transmisión comercial.`,
                          `Contactez ${STREAMY3_INFORMAL_BUSINESS_NAME} pour obtenir de l'aide concernant les licences musicales et les questions relatives à la diffusion en continu à des fins commerciales.`,
                          `음악 라이선스 및 상업적 목적의 스트리밍 관련 문의 사항은 ${STREAMY3_INFORMAL_BUSINESS_NAME}에 문의하십시오.`,
                          selectedLanguage,
                        )}",
                        "inLanguage": "${toTrimmedString(selectedLanguage)}",
                        "isPartOf": { "@id": "${PRODUCTION_HOST}#website" },
                        "breadcrumb": { "@id": "${translate(
                          PRODUCTION_HOST + '/contact',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_SPANISH +
                            '/contacto',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_FRENCH +
                            '/contact',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_KOREAN +
                            '/문의하기',
                          selectedLanguage,
                        )}#breadcrumb" },
                        "mainEntity": { "@id": "${PRODUCTION_HOST}#organization" } 
                      },
                      {
                        "@type": ["Organization"],
                        "@id": "${PRODUCTION_HOST}#organization",
                        "name": "${STREAMY3_INFORMAL_BUSINESS_NAME}",
                        "logo": "${HOME_PS_LOGO}",
                        "contactPoint": [
                          {
                              "@type": "ContactPoint",
                              "email": "${toTrimmedString(
                                pageProps?.jsonLdOrganizationContactEmail,
                              )}", ${
                                SHOW_CONTACT_PHONE
                                  ? `"telephone": "${CONTACT_PHONE}",`
                                  : ''
                              }
                              "contactType": "customer service",
                              "availableLanguage": ["${LANGUAGE_CODE_ENGLISH}", "${LANGUAGE_CODE_SPANISH}", "${LANGUAGE_CODE_FRENCH}", "${LANGUAGE_CODE_KOREAN}"]
                            }
                          ],
                          "address": {
                            "@type": "PostalAddress",
                            "streetAddress": "${STREAMY3_OFFICIAL_ADDRESS_LINE_1}",
                            "addressLocality": "${STREAMY3_OFFICIAL_ADDRESS_CITY}",
                            "addressRegion": "${STREAMY3_OFFICIAL_ADDRESS_STATE_CODE}",
                            "postalCode": "${STREAMY3_OFFICIAL_ADDRESS_ZIP_CODE}",
                            "addressCountry": "${UNITED_STATES_ISO_3166_ALPHA_2_CODE}"
                          }
                      },
                      {
                        "@type": "BreadcrumbList",
                        "@id": "${translate(
                          PRODUCTION_HOST + '/contact',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_SPANISH +
                            '/contacto',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_FRENCH +
                            '/contact',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_KOREAN +
                            '/문의하기',
                          selectedLanguage,
                        )}#breadcrumb",
                        "itemListElement": [
                          {
                            "@type": "ListItem",
                            "position": 1,
                            "name": "${translate(
                              'Home',
                              'Inicio',
                              'Accueil',
                              '홈페이지',
                              selectedLanguage,
                            )}",
                            "item": "${PRODUCTION_HOST}"
                          },
                          {
                            "@type": "ListItem",
                            "position": 2,
                            "name": "${translate(
                              'Contact Us',
                              'Contacta con Nosotras',
                              'Contactez-nous',
                              '문의하기',
                              selectedLanguage,
                            )}"
                          }
                        ]
                      }
                    ]
                  }
                  `
                  : toTrimmedString(pageProps?.jsonLdType) == 'playlist'
                    ? `{
                    "@context": "https://schema.org",
                    "@graph": [
                      {
                        "@type" : "WebSite",
                        "@id": "${PRODUCTION_HOST}#website",
                        "name" : "${JSON_LD_SITE_NAME}",
                        "url" : "${PRODUCTION_HOST}",
                        "publisher": {
                          "@id": "${PRODUCTION_HOST}#organization"
                        }
                      },
                      {
                        "@type": "WebPage",
                        "@id": "${translate(
                          PRODUCTION_HOST + '/playlist',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_SPANISH +
                            '/playlist',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_FRENCH +
                            '/playlist',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_KOREAN +
                            '/플레이리스트',
                          selectedLanguage,
                        )}#webpage",
                        "url": "${translate(
                          PRODUCTION_HOST + '/playlist',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_SPANISH +
                            '/playlist',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_FRENCH +
                            '/playlist',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_KOREAN +
                            '/플레이리스트',
                          selectedLanguage,
                        )}",
                        "inLanguage": "${toTrimmedString(selectedLanguage)}",
                        "name": "${translate(
                          `Your Playlists - ${PRODUCT_NAME}`,
                          `Tus Listas de Reproducción - ${PRODUCT_NAME}`,
                          `Vos Listes de Lecture - ${PRODUCT_NAME}`,
                          `내 재생 목록 - ${PRODUCT_NAME}`,
                          selectedLanguage,
                        )}",
                        "isPartOf": { "@id": "${PRODUCTION_HOST}#website" },
                        "breadcrumb": { "@id": "${translate(
                          PRODUCTION_HOST + '/playlist',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_SPANISH +
                            '/playlist',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_FRENCH +
                            '/playlist',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_KOREAN +
                            '/플레이리스트',
                          selectedLanguage,
                        )}#breadcrumb" }
                      },
                      {
                        "@type": "BreadcrumbList",
                        "@id": "${translate(
                          PRODUCTION_HOST + '/playlist',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_SPANISH +
                            '/playlist',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_FRENCH +
                            '/playlist',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_KOREAN +
                            '/플레이리스트',
                          selectedLanguage,
                        )}#breadcrumb",
                        "itemListElement": [
                          {
                            "@type": "ListItem",
                            "position": 1,
                            "name": "${translate(
                              'Home',
                              'Inicio',
                              'Accueil',
                              '홈페이지',
                              selectedLanguage,
                            )}",
                            "item": "${PRODUCTION_HOST}"
                          },
                          {
                            "@type": "ListItem",
                            "position": 2,
                            "name": "${translate(
                              'Playlists',
                              'Lista de Reproducción',
                              'Liste de Lecture',
                              '재생 목록',
                              selectedLanguage,
                            )}"
                          }
                        ]
                      },
                      {
                        "@type": "MusicPlaylist",
                        "@id": "${PRODUCTION_HOST}#barpresetplaylistsample",
                        "name": "${pageProps?.jsonLdBarPresetSampleName}",
                        "numTracks": ${
                          pageProps?.jsonLdPresetPlaylistsTrackCounts
                        },
                        "genre": "${pageProps?.jsonLdBarPresetSampleGenres}",
                        "description": "${
                          pageProps?.jsonLdBarPresetSampleDescription
                        }",
                        "accessMode": "auditory",
                        "isAccessibleForFree": true,
                        "educationalUse": "${
                          pageProps?.jsonLdPresetPlaylistsEducationalUse
                        }",
                        "publisher": { "@id": "${PRODUCTION_HOST}#organization" },
                        "track": {
                          "@type": "ItemList",
                          "numberOfItems": ${
                            pageProps?.jsonLdPresetPlaylistsTrackCounts
                          },
                          "itemListElement": [
                            {
                              "@type": "ListItem",
                              "position": 1,
                              "item": {
                                  "@type": "MusicRecording",
                                  "name": "${
                                    pageProps?.jsonLdBarPreset_Song1_Name
                                  }",
                                  "byArtist": {
                                    "@type": "MusicGroup",
                                    "name": "${PRODUCT_NAME}" 
                                  },
                                  "duration": "${
                                    pageProps?.jsonLdBarPreset_Song1_Duration
                                  }",
                                  "genre": "${
                                    pageProps?.jsonLdBarPreset_Song1_Genres
                                  }",
                                  "audio": {
                                    "@type": "AudioObject",
                                    "contentUrl": "${
                                      pageProps?.jsonLdBarPreset_Song1_ContentUrl
                                    }",
                                    "encodingFormat": "audio/mpeg"
                                  }
                              }
                            },
                            {
                              "@type": "ListItem",
                              "position": 2,
                              "item": {
                                  "@type": "MusicRecording",
                                  "name": "${
                                    pageProps?.jsonLdBarPreset_Song2_Name
                                  }",
                                  "byArtist": {
                                    "@type": "MusicGroup",
                                    "name": "${PRODUCT_NAME}" 
                                  },
                                  "duration": "${
                                    pageProps?.jsonLdBarPreset_Song2_Duration
                                  }",
                                  "genre": "${
                                    pageProps?.jsonLdBarPreset_Song2_Genres
                                  }",
                                  "audio": {
                                    "@type": "AudioObject",
                                    "contentUrl": "${
                                      pageProps?.jsonLdBarPreset_Song2_ContentUrl
                                    }",
                                    "encodingFormat": "audio/mpeg"
                                  }
                              }
                            },
                            {
                              "@type": "ListItem",
                              "position": 3,
                              "item": {
                                  "@type": "MusicRecording",
                                  "name": "${
                                    pageProps?.jsonLdBarPreset_Song3_Name
                                  }",
                                  "byArtist": {
                                    "@type": "MusicGroup",
                                    "name": "${PRODUCT_NAME}" 
                                  },
                                  "duration": "${
                                    pageProps?.jsonLdBarPreset_Song3_Duration
                                  }",
                                  "genre": "${
                                    pageProps?.jsonLdBarPreset_Song3_Genres
                                  }",
                                  "audio": {
                                    "@type": "AudioObject",
                                    "contentUrl": "${
                                      pageProps?.jsonLdBarPreset_Song3_ContentUrl
                                    }",
                                    "encodingFormat": "audio/mpeg"
                                  }
                              }
                            },
                            {
                              "@type": "ListItem",
                              "position": 4,
                              "item": {
                                  "@type": "MusicRecording",
                                  "name": "${
                                    pageProps?.jsonLdBarPreset_Song4_Name
                                  }",
                                  "byArtist": {
                                    "@type": "MusicGroup",
                                    "name": "${PRODUCT_NAME}" 
                                  },
                                  "duration": "${
                                    pageProps?.jsonLdBarPreset_Song4_Duration
                                  }",
                                  "genre": "${
                                    pageProps?.jsonLdBarPreset_Song4_Genres
                                  }",
                                  "audio": {
                                    "@type": "AudioObject",
                                    "contentUrl": "${
                                      pageProps?.jsonLdBarPreset_Song4_ContentUrl
                                    }",
                                    "encodingFormat": "audio/mpeg"
                                  }
                              }
                            },
                            {
                              "@type": "ListItem",
                              "position": 5,
                              "item": {
                                  "@type": "MusicRecording",
                                  "name": "${
                                    pageProps?.jsonLdBarPreset_Song5_Name
                                  }",
                                  "byArtist": {
                                    "@type": "MusicGroup",
                                    "name": "${PRODUCT_NAME}" 
                                  },
                                  "duration": "${
                                    pageProps?.jsonLdBarPreset_Song5_Duration
                                  }",
                                  "genre": "${
                                    pageProps?.jsonLdBarPreset_Song5_Genres
                                  }",
                                  "audio": {
                                    "@type": "AudioObject",
                                    "contentUrl": "${
                                      pageProps?.jsonLdBarPreset_Song5_ContentUrl
                                    }",
                                    "encodingFormat": "audio/mpeg"
                                  }
                              }
                            }
                          ]
                        }
                      },
                      {
                        "@type": "MusicPlaylist",
                        "@id": "${PRODUCTION_HOST}#cafepresetplaylistsample",
                        "name": "${pageProps?.jsonLdCafePresetSampleName}",
                        "numTracks": ${
                          pageProps?.jsonLdPresetPlaylistsTrackCounts
                        },
                        "genre": "${pageProps?.jsonLdCafePresetSampleGenres}",
                        "description": "${
                          pageProps?.jsonLdCafePresetSampleDescription
                        }",
                        "accessMode": "auditory",
                        "isAccessibleForFree": true,
                        "educationalUse": "${
                          pageProps?.jsonLdPresetPlaylistsEducationalUse
                        }",
                        "publisher": { "@id": "${PRODUCTION_HOST}#organization" },
                        "track": {
                          "@type": "ItemList",
                          "numberOfItems": ${
                            pageProps?.jsonLdPresetPlaylistsTrackCounts
                          },
                          "itemListElement": [
                            {
                              "@type": "ListItem",
                              "position": 1,
                              "item": {
                                  "@type": "MusicRecording",
                                  "name": "${
                                    pageProps?.jsonLdCafePreset_Song1_Name
                                  }",
                                  "byArtist": {
                                    "@type": "MusicGroup",
                                    "name": "${PRODUCT_NAME}" 
                                  },
                                  "duration": "${
                                    pageProps?.jsonLdCafePreset_Song1_Duration
                                  }",
                                  "genre": "${
                                    pageProps?.jsonLdCafePreset_Song1_Genres
                                  }",
                                  "audio": {
                                    "@type": "AudioObject",
                                    "contentUrl": "${
                                      pageProps?.jsonLdCafePreset_Song1_ContentUrl
                                    }",
                                    "encodingFormat": "audio/mpeg"
                                  }
                              }
                            },
                            {
                              "@type": "ListItem",
                              "position": 2,
                              "item": {
                                  "@type": "MusicRecording",
                                  "name": "${
                                    pageProps?.jsonLdCafePreset_Song2_Name
                                  }",
                                  "byArtist": {
                                    "@type": "MusicGroup",
                                    "name": "${PRODUCT_NAME}" 
                                  },
                                  "duration": "${
                                    pageProps?.jsonLdCafePreset_Song2_Duration
                                  }",
                                  "genre": "${
                                    pageProps?.jsonLdCafePreset_Song2_Genres
                                  }",
                                  "audio": {
                                    "@type": "AudioObject",
                                    "contentUrl": "${
                                      pageProps?.jsonLdCafePreset_Song2_ContentUrl
                                    }",
                                    "encodingFormat": "audio/mpeg"
                                  }
                              }
                            },
                            {
                              "@type": "ListItem",
                              "position": 3,
                              "item": {
                                  "@type": "MusicRecording",
                                  "name": "${
                                    pageProps?.jsonLdCafePreset_Song3_Name
                                  }",
                                  "byArtist": {
                                    "@type": "MusicGroup",
                                    "name": "${PRODUCT_NAME}" 
                                  },
                                  "duration": "${
                                    pageProps?.jsonLdCafePreset_Song3_Duration
                                  }",
                                  "genre": "${
                                    pageProps?.jsonLdCafePreset_Song3_Genres
                                  }",
                                  "audio": {
                                    "@type": "AudioObject",
                                    "contentUrl": "${
                                      pageProps?.jsonLdCafePreset_Song3_ContentUrl
                                    }",
                                    "encodingFormat": "audio/mpeg"
                                  }
                              }
                            },
                            {
                              "@type": "ListItem",
                              "position": 4,
                              "item": {
                                  "@type": "MusicRecording",
                                  "name": "${
                                    pageProps?.jsonLdCafePreset_Song4_Name
                                  }",
                                  "byArtist": {
                                    "@type": "MusicGroup",
                                    "name": "${PRODUCT_NAME}" 
                                  },
                                  "duration": "${
                                    pageProps?.jsonLdCafePreset_Song4_Duration
                                  }",
                                  "genre": "${
                                    pageProps?.jsonLdCafePreset_Song4_Genres
                                  }",
                                  "audio": {
                                    "@type": "AudioObject",
                                    "contentUrl": "${
                                      pageProps?.jsonLdCafePreset_Song4_ContentUrl
                                    }",
                                    "encodingFormat": "audio/mpeg"
                                  }
                              }
                            },
                            {
                              "@type": "ListItem",
                              "position": 5,
                              "item": {
                                  "@type": "MusicRecording",
                                  "name": "${
                                    pageProps?.jsonLdCafePreset_Song5_Name
                                  }",
                                  "byArtist": {
                                    "@type": "MusicGroup",
                                    "name": "${PRODUCT_NAME}" 
                                  },
                                  "duration": "${
                                    pageProps?.jsonLdCafePreset_Song5_Duration
                                  }",
                                  "genre": "${
                                    pageProps?.jsonLdCafePreset_Song5_Genres
                                  }",
                                  "audio": {
                                    "@type": "AudioObject",
                                    "contentUrl": "${
                                      pageProps?.jsonLdCafePreset_Song5_ContentUrl
                                    }",
                                    "encodingFormat": "audio/mpeg"
                                  }
                              }
                            }
                          ]
                        }
                      },
                      {
                        "@type": "MusicPlaylist",
                        "@id": "${PRODUCTION_HOST}#shopspresetplaylistsample",
                        "name": "${pageProps?.jsonLdShopsPresetSampleName}",
                        "numTracks": ${
                          pageProps?.jsonLdPresetPlaylistsTrackCounts
                        },
                        "genre": "${pageProps?.jsonLdShopsPresetSampleGenres}",
                        "description": "${
                          pageProps?.jsonLdShopsPresetSampleDescription
                        }",
                        "accessMode": "auditory",
                        "isAccessibleForFree": true,
                        "educationalUse": "${
                          pageProps?.jsonLdPresetPlaylistsEducationalUse
                        }",
                        "publisher": { "@id": "${PRODUCTION_HOST}#organization" },
                        "track": {
                          "@type": "ItemList",
                          "numberOfItems": ${
                            pageProps?.jsonLdPresetPlaylistsTrackCounts
                          },
                          "itemListElement": [
                            {
                              "@type": "ListItem",
                              "position": 1,
                              "item": {
                                  "@type": "MusicRecording",
                                  "name": "${
                                    pageProps?.jsonLdShopPreset_Song1_Name
                                  }",
                                  "byArtist": {
                                    "@type": "MusicGroup",
                                    "name": "${PRODUCT_NAME}" 
                                  },
                                  "duration": "${
                                    pageProps?.jsonLdShopPreset_Song1_Duration
                                  }",
                                  "genre": "${
                                    pageProps?.jsonLdShopPreset_Song1_Genres
                                  }",
                                  "audio": {
                                    "@type": "AudioObject",
                                    "contentUrl": "${
                                      pageProps?.jsonLdShopPreset_Song1_ContentUrl
                                    }",
                                    "encodingFormat": "audio/mpeg"
                                  }
                              }
                            },
                            {
                              "@type": "ListItem",
                              "position": 2,
                              "item": {
                                  "@type": "MusicRecording",
                                  "name": "${
                                    pageProps?.jsonLdShopPreset_Song2_Name
                                  }",
                                  "byArtist": {
                                    "@type": "MusicGroup",
                                    "name": "${PRODUCT_NAME}" 
                                  },
                                  "duration": "${
                                    pageProps?.jsonLdShopPreset_Song2_Duration
                                  }",
                                  "genre": "${
                                    pageProps?.jsonLdShopPreset_Song2_Genres
                                  }",
                                  "audio": {
                                    "@type": "AudioObject",
                                    "contentUrl": "${
                                      pageProps?.jsonLdShopPreset_Song2_ContentUrl
                                    }",
                                    "encodingFormat": "audio/mpeg"
                                  }
                              }
                            },
                            {
                              "@type": "ListItem",
                              "position": 3,
                              "item": {
                                  "@type": "MusicRecording",
                                  "name": "${
                                    pageProps?.jsonLdShopPreset_Song3_Name
                                  }",
                                  "byArtist": {
                                    "@type": "MusicGroup",
                                    "name": "${PRODUCT_NAME}" 
                                  },
                                  "duration": "${
                                    pageProps?.jsonLdShopPreset_Song3_Duration
                                  }",
                                  "genre": "${
                                    pageProps?.jsonLdShopPreset_Song3_Genres
                                  }",
                                  "audio": {
                                    "@type": "AudioObject",
                                    "contentUrl": "${
                                      pageProps?.jsonLdShopPreset_Song3_ContentUrl
                                    }",
                                    "encodingFormat": "audio/mpeg"
                                  }
                              }
                            },
                            {
                              "@type": "ListItem",
                              "position": 4,
                              "item": {
                                  "@type": "MusicRecording",
                                  "name": "${
                                    pageProps?.jsonLdShopPreset_Song4_Name
                                  }",
                                  "byArtist": {
                                    "@type": "MusicGroup",
                                    "name": "${PRODUCT_NAME}" 
                                  },
                                  "duration": "${
                                    pageProps?.jsonLdShopPreset_Song4_Duration
                                  }",
                                  "genre": "${
                                    pageProps?.jsonLdShopPreset_Song4_Genres
                                  }",
                                  "audio": {
                                    "@type": "AudioObject",
                                    "contentUrl": "${
                                      pageProps?.jsonLdShopPreset_Song4_ContentUrl
                                    }",
                                    "encodingFormat": "audio/mpeg"
                                  }
                              }
                            },
                            {
                              "@type": "ListItem",
                              "position": 5,
                              "item": {
                                  "@type": "MusicRecording",
                                  "name": "${
                                    pageProps?.jsonLdShopPreset_Song5_Name
                                  }",
                                  "byArtist": {
                                    "@type": "MusicGroup",
                                    "name": "${PRODUCT_NAME}" 
                                  },
                                  "duration": "${
                                    pageProps?.jsonLdShopPreset_Song5_Duration
                                  }",
                                  "genre": "${
                                    pageProps?.jsonLdShopPreset_Song5_Genres
                                  }",
                                  "audio": {
                                    "@type": "AudioObject",
                                    "contentUrl": "${
                                      pageProps?.jsonLdShopPreset_Song5_ContentUrl
                                    }",
                                    "encodingFormat": "audio/mpeg"
                                  }
                              }
                            }
                          ]
                        }
                      },
                      {
                        "@type": "MusicPlaylist",
                        "@id": "${PRODUCTION_HOST}#luxurypresetplaylistsample",
                        "name": "${pageProps?.jsonLdLuxuryPresetSampleName}",
                        "numTracks": ${
                          pageProps?.jsonLdPresetPlaylistsTrackCounts
                        },
                        "genre": "${pageProps?.jsonLdLuxuryPresetSampleGenres}",
                        "description": "${
                          pageProps?.jsonLdLuxuryPresetSampleDescription
                        }",
                        "accessMode": "auditory",
                        "isAccessibleForFree": true,
                        "educationalUse": "${
                          pageProps?.jsonLdPresetPlaylistsEducationalUse
                        }",
                        "publisher": { "@id": "${PRODUCTION_HOST}#organization" },
                        "track": {
                          "@type": "ItemList",
                          "numberOfItems": ${
                            pageProps?.jsonLdPresetPlaylistsTrackCounts
                          },
                          "itemListElement": [
                            {
                              "@type": "ListItem",
                              "position": 1,
                              "item": {
                                  "@type": "MusicRecording",
                                  "name": "${
                                    pageProps?.jsonLdLuxuryPreset_Song1_Name
                                  }",
                                  "byArtist": {
                                    "@type": "MusicGroup",
                                    "name": "${PRODUCT_NAME}" 
                                  },
                                  "duration": "${
                                    pageProps?.jsonLdLuxuryPreset_Song1_Duration
                                  }",
                                  "genre": "${
                                    pageProps?.jsonLdLuxuryPreset_Song1_Genres
                                  }",
                                  "audio": {
                                    "@type": "AudioObject",
                                    "contentUrl": "${
                                      pageProps?.jsonLdLuxuryPreset_Song1_ContentUrl
                                    }",
                                    "encodingFormat": "audio/mpeg"
                                  }
                              }
                            },
                            {
                              "@type": "ListItem",
                              "position": 2,
                              "item": {
                                  "@type": "MusicRecording",
                                  "name": "${
                                    pageProps?.jsonLdLuxuryPreset_Song2_Name
                                  }",
                                  "byArtist": {
                                    "@type": "MusicGroup",
                                    "name": "${PRODUCT_NAME}" 
                                  },
                                  "duration": "${
                                    pageProps?.jsonLdLuxuryPreset_Song2_Duration
                                  }",
                                  "genre": "${
                                    pageProps?.jsonLdLuxuryPreset_Song2_Genres
                                  }",
                                  "audio": {
                                    "@type": "AudioObject",
                                    "contentUrl": "${
                                      pageProps?.jsonLdLuxuryPreset_Song2_ContentUrl
                                    }",
                                    "encodingFormat": "audio/mpeg"
                                  }
                              }
                            },
                            {
                              "@type": "ListItem",
                              "position": 3,
                              "item": {
                                  "@type": "MusicRecording",
                                  "name": "${
                                    pageProps?.jsonLdLuxuryPreset_Song3_Name
                                  }",
                                  "byArtist": {
                                    "@type": "MusicGroup",
                                    "name": "${PRODUCT_NAME}" 
                                  },
                                  "duration": "${
                                    pageProps?.jsonLdLuxuryPreset_Song3_Duration
                                  }",
                                  "genre": "${
                                    pageProps?.jsonLdLuxuryPreset_Song3_Genres
                                  }",
                                  "audio": {
                                    "@type": "AudioObject",
                                    "contentUrl": "${
                                      pageProps?.jsonLdLuxuryPreset_Song3_ContentUrl
                                    }",
                                    "encodingFormat": "audio/mpeg"
                                  }
                              }
                            },
                            {
                              "@type": "ListItem",
                              "position": 4,
                              "item": {
                                  "@type": "MusicRecording",
                                  "name": "${
                                    pageProps?.jsonLdLuxuryPreset_Song4_Name
                                  }",
                                  "byArtist": {
                                    "@type": "MusicGroup",
                                    "name": "${PRODUCT_NAME}" 
                                  },
                                  "duration": "${
                                    pageProps?.jsonLdLuxuryPreset_Song4_Duration
                                  }",
                                  "genre": "${
                                    pageProps?.jsonLdLuxuryPreset_Song4_Genres
                                  }",
                                  "audio": {
                                    "@type": "AudioObject",
                                    "contentUrl": "${
                                      pageProps?.jsonLdLuxuryPreset_Song4_ContentUrl
                                    }",
                                    "encodingFormat": "audio/mpeg"
                                  }
                              }
                            },
                            {
                              "@type": "ListItem",
                              "position": 5,
                              "item": {
                                  "@type": "MusicRecording",
                                  "name": "${
                                    pageProps?.jsonLdLuxuryPreset_Song5_Name
                                  }",
                                  "byArtist": {
                                    "@type": "MusicGroup",
                                    "name": "${PRODUCT_NAME}" 
                                  },
                                  "duration": "${
                                    pageProps?.jsonLdLuxuryPreset_Song5_Duration
                                  }",
                                  "genre": "${
                                    pageProps?.jsonLdLuxuryPreset_Song5_Genres
                                  }",
                                  "audio": {
                                    "@type": "AudioObject",
                                    "contentUrl": "${
                                      pageProps?.jsonLdLuxuryPreset_Song5_ContentUrl
                                    }",
                                    "encodingFormat": "audio/mpeg"
                                  }
                              }
                            }
                          ]
                        }
                      },
                      {
                        "@type": "MusicPlaylist",
                        "@id": "${PRODUCTION_HOST}#chillpresetplaylistsample",
                        "name": "${pageProps?.jsonLdChillPresetSampleName}",
                        "numTracks": ${
                          pageProps?.jsonLdPresetPlaylistsTrackCounts
                        },
                        "genre": "${pageProps?.jsonLdChillPresetSampleGenres}",
                        "description": "${
                          pageProps?.jsonLdChillPresetSampleDescription
                        }",
                        "accessMode": "auditory",
                        "isAccessibleForFree": true,
                        "educationalUse": "${
                          pageProps?.jsonLdPresetPlaylistsEducationalUse
                        }",
                        "publisher": { "@id": "${PRODUCTION_HOST}#organization" },
                        "track": {
                          "@type": "ItemList",
                          "numberOfItems": ${
                            pageProps?.jsonLdPresetPlaylistsTrackCounts
                          },
                          "itemListElement": [
                            {
                              "@type": "ListItem",
                              "position": 1,
                              "item": {
                                  "@type": "MusicRecording",
                                  "name": "${
                                    pageProps?.jsonLdChillPreset_Song1_Name
                                  }",
                                  "byArtist": {
                                    "@type": "MusicGroup",
                                    "name": "${PRODUCT_NAME}" 
                                  },
                                  "duration": "${
                                    pageProps?.jsonLdChillPreset_Song1_Duration
                                  }",
                                  "genre": "${
                                    pageProps?.jsonLdChillPreset_Song1_Genres
                                  }",
                                  "audio": {
                                    "@type": "AudioObject",
                                    "contentUrl": "${
                                      pageProps?.jsonLdChillPreset_Song1_ContentUrl
                                    }",
                                    "encodingFormat": "audio/mpeg"
                                  }
                              }
                            },
                            {
                              "@type": "ListItem",
                              "position": 2,
                              "item": {
                                  "@type": "MusicRecording",
                                  "name": "${
                                    pageProps?.jsonLdChillPreset_Song2_Name
                                  }",
                                  "byArtist": {
                                    "@type": "MusicGroup",
                                    "name": "${PRODUCT_NAME}" 
                                  },
                                  "duration": "${
                                    pageProps?.jsonLdChillPreset_Song2_Duration
                                  }",
                                  "genre": "${
                                    pageProps?.jsonLdChillPreset_Song2_Genres
                                  }",
                                  "audio": {
                                    "@type": "AudioObject",
                                    "contentUrl": "${
                                      pageProps?.jsonLdChillPreset_Song2_ContentUrl
                                    }",
                                    "encodingFormat": "audio/mpeg"
                                  }
                              }
                            },
                            {
                              "@type": "ListItem",
                              "position": 3,
                              "item": {
                                  "@type": "MusicRecording",
                                  "name": "${
                                    pageProps?.jsonLdChillPreset_Song3_Name
                                  }",
                                  "byArtist": {
                                    "@type": "MusicGroup",
                                    "name": "${PRODUCT_NAME}" 
                                  },
                                  "duration": "${
                                    pageProps?.jsonLdChillPreset_Song3_Duration
                                  }",
                                  "genre": "${
                                    pageProps?.jsonLdChillPreset_Song3_Genres
                                  }",
                                  "audio": {
                                    "@type": "AudioObject",
                                    "contentUrl": "${
                                      pageProps?.jsonLdChillPreset_Song3_ContentUrl
                                    }",
                                    "encodingFormat": "audio/mpeg"
                                  }
                              }
                            },
                            {
                              "@type": "ListItem",
                              "position": 4,
                              "item": {
                                  "@type": "MusicRecording",
                                  "name": "${
                                    pageProps?.jsonLdChillPreset_Song4_Name
                                  }",
                                  "byArtist": {
                                    "@type": "MusicGroup",
                                    "name": "${PRODUCT_NAME}" 
                                  },
                                  "duration": "${
                                    pageProps?.jsonLdChillPreset_Song4_Duration
                                  }",
                                  "genre": "${
                                    pageProps?.jsonLdChillPreset_Song4_Genres
                                  }",
                                  "audio": {
                                    "@type": "AudioObject",
                                    "contentUrl": "${
                                      pageProps?.jsonLdChillPreset_Song4_ContentUrl
                                    }",
                                    "encodingFormat": "audio/mpeg"
                                  }
                              }
                            },
                            {
                              "@type": "ListItem",
                              "position": 5,
                              "item": {
                                  "@type": "MusicRecording",
                                  "name": "${
                                    pageProps?.jsonLdChillPreset_Song5_Name
                                  }",
                                  "byArtist": {
                                    "@type": "MusicGroup",
                                    "name": "${PRODUCT_NAME}" 
                                  },
                                  "duration": "${
                                    pageProps?.jsonLdChillPreset_Song5_Duration
                                  }",
                                  "genre": "${
                                    pageProps?.jsonLdChillPreset_Song5_Genres
                                  }",
                                  "audio": {
                                    "@type": "AudioObject",
                                    "contentUrl": "${
                                      pageProps?.jsonLdChillPreset_Song5_ContentUrl
                                    }",
                                    "encodingFormat": "audio/mpeg"
                                  }
                              }
                            }
                          ]
                        }
                      },
                      {
                        "@type": "MusicPlaylist",
                        "@id": "${PRODUCTION_HOST}#gympresetplaylistsample",
                        "name": "${pageProps?.jsonLdPumpPresetSampleName}",
                        "numTracks": ${
                          pageProps?.jsonLdPresetPlaylistsTrackCounts
                        },
                        "genre": "${pageProps?.jsonLdPumpPresetSampleGenres}",
                        "description": "${
                          pageProps?.jsonLdPumpPresetSampleDescription
                        }",
                        "accessMode": "auditory",
                        "isAccessibleForFree": true,
                        "educationalUse": "${
                          pageProps?.jsonLdPresetPlaylistsEducationalUse
                        }",
                        "publisher": { "@id": "${PRODUCTION_HOST}#organization" },
                        "track": {
                          "@type": "ItemList",
                          "numberOfItems": ${
                            pageProps?.jsonLdPresetPlaylistsTrackCounts
                          },
                          "itemListElement": [
                            {
                              "@type": "ListItem",
                              "position": 1,
                              "item": {
                                  "@type": "MusicRecording",
                                  "name": "${
                                    pageProps?.jsonLdPumpPreset_Song1_Name
                                  }",
                                  "byArtist": {
                                    "@type": "MusicGroup",
                                    "name": "${PRODUCT_NAME}" 
                                  },
                                  "duration": "${
                                    pageProps?.jsonLdPumpPreset_Song1_Duration
                                  }",
                                  "genre": "${
                                    pageProps?.jsonLdPumpPreset_Song1_Genres
                                  }",
                                  "audio": {
                                    "@type": "AudioObject",
                                    "contentUrl": "${
                                      pageProps?.jsonLdPumpPreset_Song1_ContentUrl
                                    }",
                                    "encodingFormat": "audio/mpeg"
                                  }
                              }
                            },
                            {
                              "@type": "ListItem",
                              "position": 2,
                              "item": {
                                  "@type": "MusicRecording",
                                  "name": "${
                                    pageProps?.jsonLdPumpPreset_Song2_Name
                                  }",
                                  "byArtist": {
                                    "@type": "MusicGroup",
                                    "name": "${PRODUCT_NAME}" 
                                  },
                                  "duration": "${
                                    pageProps?.jsonLdPumpPreset_Song2_Duration
                                  }",
                                  "genre": "${
                                    pageProps?.jsonLdPumpPreset_Song2_Genres
                                  }",
                                  "audio": {
                                    "@type": "AudioObject",
                                    "contentUrl": "${
                                      pageProps?.jsonLdPumpPreset_Song2_ContentUrl
                                    }",
                                    "encodingFormat": "audio/mpeg"
                                  }
                              }
                            },
                            {
                              "@type": "ListItem",
                              "position": 3,
                              "item": {
                                  "@type": "MusicRecording",
                                  "name": "${
                                    pageProps?.jsonLdPumpPreset_Song3_Name
                                  }",
                                  "byArtist": {
                                    "@type": "MusicGroup",
                                    "name": "${PRODUCT_NAME}" 
                                  },
                                  "duration": "${
                                    pageProps?.jsonLdPumpPreset_Song3_Duration
                                  }",
                                  "genre": "${
                                    pageProps?.jsonLdPumpPreset_Song3_Genres
                                  }",
                                  "audio": {
                                    "@type": "AudioObject",
                                    "contentUrl": "${
                                      pageProps?.jsonLdPumpPreset_Song3_ContentUrl
                                    }",
                                    "encodingFormat": "audio/mpeg"
                                  }
                              }
                            },
                            {
                              "@type": "ListItem",
                              "position": 4,
                              "item": {
                                  "@type": "MusicRecording",
                                  "name": "${
                                    pageProps?.jsonLdPumpPreset_Song4_Name
                                  }",
                                  "byArtist": {
                                    "@type": "MusicGroup",
                                    "name": "${PRODUCT_NAME}" 
                                  },
                                  "duration": "${
                                    pageProps?.jsonLdPumpPreset_Song4_Duration
                                  }",
                                  "genre": "${
                                    pageProps?.jsonLdPumpPreset_Song4_Genres
                                  }",
                                  "audio": {
                                    "@type": "AudioObject",
                                    "contentUrl": "${
                                      pageProps?.jsonLdPumpPreset_Song4_ContentUrl
                                    }",
                                    "encodingFormat": "audio/mpeg"
                                  }
                              }
                            },
                            {
                              "@type": "ListItem",
                              "position": 5,
                              "item": {
                                  "@type": "MusicRecording",
                                  "name": "${
                                    pageProps?.jsonLdPumpPreset_Song5_Name
                                  }",
                                  "byArtist": {
                                    "@type": "MusicGroup",
                                    "name": "${PRODUCT_NAME}" 
                                  },
                                  "duration": "${
                                    pageProps?.jsonLdPumpPreset_Song5_Duration
                                  }",
                                  "genre": "${
                                    pageProps?.jsonLdPumpPreset_Song5_Genres
                                  }",
                                  "audio": {
                                    "@type": "AudioObject",
                                    "contentUrl": "${
                                      pageProps?.jsonLdPumpPreset_Song5_ContentUrl
                                    }",
                                    "encodingFormat": "audio/mpeg"
                                  }
                              }
                            }
                          ]
                        }
                      },
                      {
                        "@type": "MusicPlaylist",
                        "@id": "${PRODUCTION_HOST}#spaspresetplaylistsample",
                        "name": "${pageProps?.jsonLdSpasPresetSampleName}",
                        "numTracks": ${
                          pageProps?.jsonLdPresetPlaylistsTrackCounts
                        },
                        "genre": "${pageProps?.jsonLdSpasPresetSampleGenres}",
                        "description": "${
                          pageProps?.jsonLdSpasPresetSampleDescription
                        }",
                        "accessMode": "auditory",
                        "isAccessibleForFree": true,
                        "educationalUse": "${
                          pageProps?.jsonLdPresetPlaylistsEducationalUse
                        }",
                        "publisher": { "@id": "${PRODUCTION_HOST}#organization" },
                        "track": {
                          "@type": "ItemList",
                          "numberOfItems": ${
                            pageProps?.jsonLdPresetPlaylistsTrackCounts
                          },
                          "itemListElement": [
                            {
                              "@type": "ListItem",
                              "position": 1,
                              "item": {
                                  "@type": "MusicRecording",
                                  "name": "${
                                    pageProps?.jsonLdSpaPreset_Song1_Name
                                  }",
                                  "byArtist": {
                                    "@type": "MusicGroup",
                                    "name": "${PRODUCT_NAME}" 
                                  },
                                  "duration": "${
                                    pageProps?.jsonLdSpaPreset_Song1_Duration
                                  }",
                                  "genre": "${
                                    pageProps?.jsonLdSpaPreset_Song1_Genres
                                  }",
                                  "audio": {
                                    "@type": "AudioObject",
                                    "contentUrl": "${
                                      pageProps?.jsonLdSpaPreset_Song1_ContentUrl
                                    }",
                                    "encodingFormat": "audio/mpeg"
                                  }
                              }
                            },
                            {
                              "@type": "ListItem",
                              "position": 2,
                              "item": {
                                  "@type": "MusicRecording",
                                  "name": "${
                                    pageProps?.jsonLdSpaPreset_Song2_Name
                                  }",
                                  "byArtist": {
                                    "@type": "MusicGroup",
                                    "name": "${PRODUCT_NAME}" 
                                  },
                                  "duration": "${
                                    pageProps?.jsonLdSpaPreset_Song2_Duration
                                  }",
                                  "genre": "${
                                    pageProps?.jsonLdSpaPreset_Song2_Genres
                                  }",
                                  "audio": {
                                    "@type": "AudioObject",
                                    "contentUrl": "${
                                      pageProps?.jsonLdSpaPreset_Song2_ContentUrl
                                    }",
                                    "encodingFormat": "audio/mpeg"
                                  }
                              }
                            },
                            {
                              "@type": "ListItem",
                              "position": 3,
                              "item": {
                                  "@type": "MusicRecording",
                                  "name": "${
                                    pageProps?.jsonLdSpaPreset_Song3_Name
                                  }",
                                  "byArtist": {
                                    "@type": "MusicGroup",
                                    "name": "${PRODUCT_NAME}" 
                                  },
                                  "duration": "${
                                    pageProps?.jsonLdSpaPreset_Song3_Duration
                                  }",
                                  "genre": "${
                                    pageProps?.jsonLdSpaPreset_Song3_Genres
                                  }",
                                  "audio": {
                                    "@type": "AudioObject",
                                    "contentUrl": "${
                                      pageProps?.jsonLdSpaPreset_Song3_ContentUrl
                                    }",
                                    "encodingFormat": "audio/mpeg"
                                  }
                              }
                            },
                            {
                              "@type": "ListItem",
                              "position": 4,
                              "item": {
                                  "@type": "MusicRecording",
                                  "name": "${
                                    pageProps?.jsonLdSpaPreset_Song4_Name
                                  }",
                                  "byArtist": {
                                    "@type": "MusicGroup",
                                    "name": "${PRODUCT_NAME}" 
                                  },
                                  "duration": "${
                                    pageProps?.jsonLdSpaPreset_Song4_Duration
                                  }",
                                  "genre": "${
                                    pageProps?.jsonLdSpaPreset_Song4_Genres
                                  }",
                                  "audio": {
                                    "@type": "AudioObject",
                                    "contentUrl": "${
                                      pageProps?.jsonLdSpaPreset_Song4_ContentUrl
                                    }",
                                    "encodingFormat": "audio/mpeg"
                                  }
                              }
                            },
                            {
                              "@type": "ListItem",
                              "position": 5,
                              "item": {
                                  "@type": "MusicRecording",
                                  "name": "${
                                    pageProps?.jsonLdSpaPreset_Song5_Name
                                  }",
                                  "byArtist": {
                                    "@type": "MusicGroup",
                                    "name": "${PRODUCT_NAME}" 
                                  },
                                  "duration": "${
                                    pageProps?.jsonLdSpaPreset_Song5_Duration
                                  }",
                                  "genre": "${
                                    pageProps?.jsonLdSpaPreset_Song5_Genres
                                  }",
                                  "audio": {
                                    "@type": "AudioObject",
                                    "contentUrl": "${
                                      pageProps?.jsonLdSpaPreset_Song5_ContentUrl
                                    }",
                                    "encodingFormat": "audio/mpeg"
                                  }
                              }
                            }
                          ]
                        }
                      },
                      {
                        "@type": "MusicPlaylist",
                        "@id": "${PRODUCTION_HOST}#partypresetplaylistsample",
                        "name": "${pageProps?.jsonLdPartyPresetSampleName}",
                        "numTracks": ${
                          pageProps?.jsonLdPresetPlaylistsTrackCounts
                        },
                        "genre": "${pageProps?.jsonLdPartyPresetSampleGenres}",
                        "description": "${
                          pageProps?.jsonLdPartyPresetSampleDescription
                        }",
                        "accessMode": "auditory",
                        "isAccessibleForFree": true,
                        "educationalUse": "${
                          pageProps?.jsonLdPresetPlaylistsEducationalUse
                        }",
                        "publisher": { "@id": "${PRODUCTION_HOST}#organization" },
                        "track": {
                          "@type": "ItemList",
                          "numberOfItems": ${
                            pageProps?.jsonLdPresetPlaylistsTrackCounts
                          },
                          "itemListElement": [
                            {
                              "@type": "ListItem",
                              "position": 1,
                              "item": {
                                  "@type": "MusicRecording",
                                  "name": "${
                                    pageProps?.jsonLdPartyPreset_Song1_Name
                                  }",
                                  "byArtist": {
                                    "@type": "MusicGroup",
                                    "name": "${PRODUCT_NAME}" 
                                  },
                                  "duration": "${
                                    pageProps?.jsonLdPartyPreset_Song1_Duration
                                  }",
                                  "genre": "${
                                    pageProps?.jsonLdPartyPreset_Song1_Genres
                                  }",
                                  "audio": {
                                    "@type": "AudioObject",
                                    "contentUrl": "${
                                      pageProps?.jsonLdPartyPreset_Song1_ContentUrl
                                    }",
                                    "encodingFormat": "audio/mpeg"
                                  }
                              }
                            },
                            {
                              "@type": "ListItem",
                              "position": 2,
                              "item": {
                                  "@type": "MusicRecording",
                                  "name": "${
                                    pageProps?.jsonLdPartyPreset_Song2_Name
                                  }",
                                  "byArtist": {
                                    "@type": "MusicGroup",
                                    "name": "${PRODUCT_NAME}" 
                                  },
                                  "duration": "${
                                    pageProps?.jsonLdPartyPreset_Song2_Duration
                                  }",
                                  "genre": "${
                                    pageProps?.jsonLdPartyPreset_Song2_Genres
                                  }",
                                  "audio": {
                                    "@type": "AudioObject",
                                    "contentUrl": "${
                                      pageProps?.jsonLdPartyPreset_Song2_ContentUrl
                                    }",
                                    "encodingFormat": "audio/mpeg"
                                  }
                              }
                            },
                            {
                              "@type": "ListItem",
                              "position": 3,
                              "item": {
                                  "@type": "MusicRecording",
                                  "name": "${
                                    pageProps?.jsonLdPartyPreset_Song3_Name
                                  }",
                                  "byArtist": {
                                    "@type": "MusicGroup",
                                    "name": "${PRODUCT_NAME}" 
                                  },
                                  "duration": "${
                                    pageProps?.jsonLdPartyPreset_Song3_Duration
                                  }",
                                  "genre": "${
                                    pageProps?.jsonLdPartyPreset_Song3_Genres
                                  }",
                                  "audio": {
                                    "@type": "AudioObject",
                                    "contentUrl": "${
                                      pageProps?.jsonLdPartyPreset_Song3_ContentUrl
                                    }",
                                    "encodingFormat": "audio/mpeg"
                                  }
                              }
                            },
                            {
                              "@type": "ListItem",
                              "position": 4,
                              "item": {
                                  "@type": "MusicRecording",
                                  "name": "${
                                    pageProps?.jsonLdPartyPreset_Song4_Name
                                  }",
                                  "byArtist": {
                                    "@type": "MusicGroup",
                                    "name": "${PRODUCT_NAME}" 
                                  },
                                  "duration": "${
                                    pageProps?.jsonLdPartyPreset_Song4_Duration
                                  }",
                                  "genre": "${
                                    pageProps?.jsonLdPartyPreset_Song4_Genres
                                  }",
                                  "audio": {
                                    "@type": "AudioObject",
                                    "contentUrl": "${
                                      pageProps?.jsonLdPartyPreset_Song4_ContentUrl
                                    }",
                                    "encodingFormat": "audio/mpeg"
                                  }
                              }
                            },
                            {
                              "@type": "ListItem",
                              "position": 5,
                              "item": {
                                  "@type": "MusicRecording",
                                  "name": "${
                                    pageProps?.jsonLdPartyPreset_Song5_Name
                                  }",
                                  "byArtist": {
                                    "@type": "MusicGroup",
                                    "name": "${PRODUCT_NAME}" 
                                  },
                                  "duration": "${
                                    pageProps?.jsonLdPartyPreset_Song5_Duration
                                  }",
                                  "genre": "${
                                    pageProps?.jsonLdPartyPreset_Song5_Genres
                                  }",
                                  "audio": {
                                    "@type": "AudioObject",
                                    "contentUrl": "${
                                      pageProps?.jsonLdPartyPreset_Song5_ContentUrl
                                    }",
                                    "encodingFormat": "audio/mpeg"
                                  }
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                  `
                    : toTrimmedString(pageProps?.jsonLdType) == 'favorites'
                      ? `{
                    "@context": "https://schema.org",
                    "@graph": [
                      {
                        "@type" : "WebSite",
                        "@id": "${PRODUCTION_HOST}#website",
                        "name" : "${JSON_LD_SITE_NAME}",
                        "url" : "${PRODUCTION_HOST}",
                        "publisher": {
                          "@id": "${PRODUCTION_HOST}#organization"
                        }
                      },
                      {
                        "@type": "WebPage",
                        "@id": "${translate(
                          PRODUCTION_HOST + '/favorites',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_SPANISH +
                            '/favoritos',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_FRENCH +
                            '/favoris',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_KOREAN +
                            '/좋아요',
                          selectedLanguage,
                        )}#webpage",
                        "url": "${translate(
                          PRODUCTION_HOST + '/favorites',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_SPANISH +
                            '/favoritos',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_FRENCH +
                            '/favoris',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_KOREAN +
                            '/좋아요',
                          selectedLanguage,
                        )}",
                        "inLanguage": "${toTrimmedString(selectedLanguage)}",
                        "name": "${translate(
                          `Your Favorites - ${PRODUCT_NAME}`,
                          `Tus Favoritos - ${PRODUCT_NAME}`,
                          `Vos Favoris - ${PRODUCT_NAME}`,
                          `즐겨찾기 - ${PRODUCT_NAME}`,
                          selectedLanguage,
                        )}",
                        "isPartOf": { "@id": "${PRODUCTION_HOST}#website" },
                        "breadcrumb": { "@id": "${translate(
                          PRODUCTION_HOST + '/favorites',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_SPANISH +
                            '/favoritos',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_FRENCH +
                            '/favoris',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_KOREAN +
                            '/좋아요',
                          selectedLanguage,
                        )}#breadcrumb" }
                      },
                      {
                        "@type": "BreadcrumbList",
                        "@id": "${translate(
                          PRODUCTION_HOST + '/favorites',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_SPANISH +
                            '/favoritos',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_FRENCH +
                            '/favoris',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_KOREAN +
                            '/좋아요',
                          selectedLanguage,
                        )}#breadcrumb",
                        "itemListElement": [
                          {
                            "@type": "ListItem",
                            "position": 1,
                            "name": "${translate(
                              'Home',
                              'Inicio',
                              'Accueil',
                              '홈페이지',
                              selectedLanguage,
                            )}",
                            "item": "${PRODUCTION_HOST}"
                          },
                          {
                            "@type": "ListItem",
                            "position": 2,
                            "name": "${translate(
                              'Favorites',
                              'Favoritos',
                              'Favoris',
                              '즐겨찾기',
                              selectedLanguage,
                            )}"
                          }
                        ]
                      }
                    ]
                  }
                  `
                      : toTrimmedString(pageProps?.jsonLdType) == 'studio'
                        ? `{
                    "@context": "https://schema.org",
                    "@graph": [
                      {
                        "@type" : "WebSite",
                        "@id": "${PRODUCTION_HOST}#website",
                        "name" : "${JSON_LD_SITE_NAME}",
                        "url" : "${PRODUCTION_HOST}",
                        "publisher": {
                          "@id": "${PRODUCTION_HOST}#organization"
                        }
                      },
                      {
                        "@type": "WebPage",
                        "@id": "${translate(
                          PRODUCTION_HOST + '/studio',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_SPANISH +
                            '/estudio',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_FRENCH +
                            '/studio',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_KOREAN +
                            '/스튜디오',
                          selectedLanguage,
                        )}#webpage",
                        "url": "${translate(
                          PRODUCTION_HOST + '/studio',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_SPANISH +
                            '/estudio',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_FRENCH +
                            '/studio',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_KOREAN +
                            '/스튜디오',
                          selectedLanguage,
                        )}",
                        "inLanguage": "${toTrimmedString(selectedLanguage)}",
                        "name": "${translate(
                          `Brand Studio - ${PRODUCT_NAME}`,
                          `Estudio - ${PRODUCT_NAME}`,
                          `Studio - ${PRODUCT_NAME}`,
                          `스튜디오 - ${PRODUCT_NAME}`,
                          selectedLanguage,
                        )}",
                        "isPartOf": { "@id": "${PRODUCTION_HOST}#website" },
                        "breadcrumb": { "@id": "${translate(
                          PRODUCTION_HOST + '/studio',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_SPANISH +
                            '/estudio',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_FRENCH +
                            '/studio',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_KOREAN +
                            '/스튜디오',
                          selectedLanguage,
                        )}#breadcrumb" }
                      },
                      {
                        "@type": "HowTo",
                        "name": "${translate('How to generate a branded music playlist', 'Cómo generar una lista de reproducción de música con la marca de tu empresa', "Comment générer une playlist musicale personnalisée à l'image de votre marque ?", '브랜드를 반영하는 맞춤형 음악 플레이리스트를 만드는 방법은 무엇일까요?', selectedLanguage)}",
                        "inLanguage": "${toTrimmedString(selectedLanguage)}",
                        "step": [
                          {
                            "@type": "HowToStep",
                            "name": "${translate("Enter your brand's website address, if applicable", 'Si procede, introduzca la dirección del sitio web de la marca.', "Le cas échéant, veuillez indiquer l'adresse du site web de la marque.", '해당하는 경우 브랜드 웹사이트 주소를 입력하세요.', selectedLanguage)}",
                            "text": "${getHowToStepDescriptor(1, selectedLanguage)}",
                            "url": "${translate(
                              PRODUCTION_HOST + '/studio',
                              PRODUCTION_HOST +
                                '/' +
                                LANGUAGE_CODE_SPANISH +
                                '/estudio',
                              PRODUCTION_HOST +
                                '/' +
                                LANGUAGE_CODE_FRENCH +
                                '/studio',
                              PRODUCTION_HOST +
                                '/' +
                                LANGUAGE_CODE_KOREAN +
                                '/스튜디오',
                              selectedLanguage,
                            )}#step1"
                          },
                          {
                            "@type": "HowToStep",
                            "name": "${translate('Enter a prompt that describes your brand', 'Introduce una descripción que defina tu marca', 'Saisissez une description qui représente votre marque.', '브랜드를 설명하는 문구를 입력하세요', selectedLanguage)}",
                            "text": "${getHowToStepDescriptor(2, selectedLanguage)}",
                            "url": "${translate(
                              PRODUCTION_HOST + '/studio',
                              PRODUCTION_HOST +
                                '/' +
                                LANGUAGE_CODE_SPANISH +
                                '/estudio',
                              PRODUCTION_HOST +
                                '/' +
                                LANGUAGE_CODE_FRENCH +
                                '/studio',
                              PRODUCTION_HOST +
                                '/' +
                                LANGUAGE_CODE_KOREAN +
                                '/스튜디오',
                              selectedLanguage,
                            )}#step2"
                          },
                          {
                            "@type": "HowToStep",
                            "name": "${translate('Generate and stream', 'Genera y reproduce la música', 'Générez et diffusez', '음악을 생성하고 스트리밍합니다', selectedLanguage)}",
                            "text": "${getHowToStepDescriptor(3, selectedLanguage)}",
                            "url": "${translate(
                              PRODUCTION_HOST + '/studio',
                              PRODUCTION_HOST +
                                '/' +
                                LANGUAGE_CODE_SPANISH +
                                '/estudio',
                              PRODUCTION_HOST +
                                '/' +
                                LANGUAGE_CODE_FRENCH +
                                '/studio',
                              PRODUCTION_HOST +
                                '/' +
                                LANGUAGE_CODE_KOREAN +
                                '/스튜디오',
                              selectedLanguage,
                            )}#step3"
                          }
                        ]
                      },
                      {
                        "@type": "BreadcrumbList",
                        "@id": "${translate(
                          PRODUCTION_HOST + '/studio',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_SPANISH +
                            '/estudio',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_FRENCH +
                            '/studio',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_KOREAN +
                            '/스튜디오',
                          selectedLanguage,
                        )}#breadcrumb",
                        "itemListElement": [
                          {
                            "@type": "ListItem",
                            "position": 1,
                            "name": "${translate(
                              'Home',
                              'Inicio',
                              'Accueil',
                              '홈페이지',
                              selectedLanguage,
                            )}",
                            "item": "${PRODUCTION_HOST}"
                          },
                          {
                            "@type": "ListItem",
                            "position": 2,
                            "name": "${translate(
                              'Studio',
                              'Estudio',
                              'Studio',
                              '스튜디오',
                              selectedLanguage,
                            )}"
                          }
                        ]
                      }
                    ]
                  }
                  `
                        : toTrimmedString(pageProps?.jsonLdType) == 'search'
                          ? `{
                    "@context": "https://schema.org",
                    "@graph": [
                      {
                        "@type" : "WebSite",
                        "@id": "${PRODUCTION_HOST}#website",
                        "name" : "${JSON_LD_SITE_NAME}",
                        "url" : "${PRODUCTION_HOST}",
                        "publisher": {
                          "@id": "${PRODUCTION_HOST}#organization"
                        }
                      },
                      {
                        "@type": "WebPage",
                        "@id": "${translate(
                          PRODUCTION_HOST + '/search',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_SPANISH +
                            '/buscar',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_FRENCH +
                            '/trouve',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_KOREAN +
                            '/찾기',
                          selectedLanguage,
                        )}#webpage",
                        "url": "${translate(
                          PRODUCTION_HOST + '/search',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_SPANISH +
                            '/buscar',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_FRENCH +
                            '/trouve',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_KOREAN +
                            '/찾기',
                          selectedLanguage,
                        )}",
                        "inLanguage": "${toTrimmedString(selectedLanguage)}",
                        "name": "${translate(
                          `Search Music - ${PRODUCT_NAME}`,
                          `Buscar Música - ${PRODUCT_NAME}`,
                          `Rechercher de la Musique - ${PRODUCT_NAME}`,
                          `음악 검색 - ${PRODUCT_NAME}`,
                          selectedLanguage,
                        )}",
                        "isPartOf": { "@id": "${PRODUCTION_HOST}#website" },
                        "breadcrumb": { "@id": "${translate(
                          PRODUCTION_HOST + '/search',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_SPANISH +
                            '/buscar',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_FRENCH +
                            '/trouve',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_KOREAN +
                            '/찾기',
                          selectedLanguage,
                        )}#breadcrumb" }
                      },
                      {
                        "@type": "BreadcrumbList",
                        "@id": "${translate(
                          PRODUCTION_HOST + '/search',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_SPANISH +
                            '/buscar',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_FRENCH +
                            '/trouve',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_KOREAN +
                            '/찾기',
                          selectedLanguage,
                        )}#breadcrumb",
                        "itemListElement": [
                          {
                            "@type": "ListItem",
                            "position": 1,
                            "name": "${translate(
                              'Home',
                              'Inicio',
                              'Accueil',
                              '홈페이지',
                              selectedLanguage,
                            )}",
                            "item": "${PRODUCTION_HOST}"
                          },
                          {
                            "@type": "ListItem",
                            "position": 2,
                            "name": "${translate(
                              'Search Music',
                              'Buscar Música',
                              'Rechercher de la Musique',
                              '음악 검색',
                              selectedLanguage,
                            )}"
                          }
                        ]
                      }
                    ]
                  }
                  `
                          : toTrimmedString(pageProps?.jsonLdType) == 'settings'
                            ? `{
                    "@context": "https://schema.org",
                    "@graph": [
                      {
                        "@type" : "WebSite",
                        "@id": "${PRODUCTION_HOST}#website",
                        "name" : "${JSON_LD_SITE_NAME}",
                        "url" : "${PRODUCTION_HOST}",
                        "publisher": {
                          "@id": "${PRODUCTION_HOST}#organization"
                        }
                      },
                      {
                        "@type": "WebPage",
                        "@id": "${translate(
                          PRODUCTION_HOST + '/settings',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_SPANISH +
                            '/settings',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_FRENCH +
                            '/settings',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_KOREAN +
                            '/settings',
                          selectedLanguage,
                        )}#webpage",
                        "url": "${translate(
                          PRODUCTION_HOST + '/settings',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_SPANISH +
                            '/settings',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_FRENCH +
                            '/settings',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_KOREAN +
                            '/settings',
                          selectedLanguage,
                        )}",
                        "inLanguage": "${toTrimmedString(selectedLanguage)}",
                        "name": "${translate(
                          `Account Settings - ${PRODUCT_NAME}`,
                          `Configuración de la Cuenta e Inicio de Sesión - ${PRODUCT_NAME}`,
                          `Configuration du Compte et Connexion - ${PRODUCT_NAME}`,
                          `계정 구성 및 로그인 - ${PRODUCT_NAME}`,
                          selectedLanguage,
                        )}",
                        "isPartOf": { "@id": "${PRODUCTION_HOST}#website" },
                        "breadcrumb": { "@id": "${translate(
                          PRODUCTION_HOST + '/settings',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_SPANISH +
                            '/settings',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_FRENCH +
                            '/settings',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_KOREAN +
                            '/settings',
                          selectedLanguage,
                        )}#breadcrumb" }
                      },
                      {
                        "@type": "BreadcrumbList",
                        "@id": "${translate(
                          PRODUCTION_HOST + '/settings',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_SPANISH +
                            '/settings',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_FRENCH +
                            '/settings',
                          PRODUCTION_HOST +
                            '/' +
                            LANGUAGE_CODE_KOREAN +
                            '/settings',
                          selectedLanguage,
                        )}#breadcrumb",
                        "itemListElement": [
                          {
                            "@type": "ListItem",
                            "position": 1,
                            "name": "${translate(
                              'Home',
                              'Inicio',
                              'Accueil',
                              '홈페이지',
                              selectedLanguage,
                            )}",
                            "item": "${PRODUCTION_HOST}"
                          },
                          {
                            "@type": "ListItem",
                            "position": 2,
                            "name": "${translate(
                              'Account Settings and Login',
                              'Configuración de la Cuenta e Inicio de Sesión',
                              'Paramètres du Compte et Connexion',
                              '계정 설정 및 로그인',
                              selectedLanguage,
                            )}"
                          }
                        ]
                      }
                    ]
                  }
                  `
                            : ``,
          }}
        />
        {DO_YANDEX_VERIFICATION && (
          <meta name="yandex-verification" content="0f4e6351ed60216c" />
        )}
        {(!IS_PRODUCTION ||
          (pageComponentDisplayName == STUDIO_PAGE_DISPLAY_NAME &&
            STUDIO_TESTING_MODE) ||
          pageComponentDisplayName == FOUR_O_FOUR_PAGE_DISPLAY_NAME ||
          pageComponentDisplayName == FIVE_HUNDRED_PAGE_DISPLAY_NAME ||
          pageComponentDisplayName == ERROR_PAGE_DISPLAY_NAME) && (
          <meta name="robots" content="noindex" />
        )}
        {IS_LOCAL ||
          ((pageComponentDisplayName == PLAYLIST_PAGE_DISPLAY_NAME ||
            pageComponentDisplayName == FAVORITES_PAGE_DISPLAY_NAME ||
            pageComponentDisplayName == ACCOUNT_SETTINGS_PAGE_DISPLAY_NAME) && (
            <meta httpEquiv="Cache-control" content="no-cache" />
          ))}
        {isPracticalPod ? (
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
          />
        ) : isMobileSafari ? (
          <meta
            name="viewport"
            content="initial-scale = 1.0,maximum-scale = 1.0"
          />
        ) : isMobileOnly && !isRunningPwa ? (
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1.0, maximum-scale=5.0, user-scalable=yes"
          />
        ) : (
          <meta
            name="viewport"
            content="minimum-scale=1, initial-scale=1, width=device-width"
          />
        )}
        {!isPracticalPod && (
          <>
            <meta
              name="description"
              content={toTrimmedString(pageProps?.metaDescription)}
            />
            <meta
              name="keywords"
              content={toTrimmedString(pageProps?.metaKeywords)}
            />
            <meta name="copyright" content={STREAMY3_OFFICIAL_BUSINESS_NAME} />
            <meta property="og:type" content={OG_TYPE} />
            <meta property="og:site_name" content={OG_SITE_NAME} />
            <meta
              property="og:url"
              content={toTrimmedString(pageProps?.ogUrl)}
            />
            <meta
              property="og:title"
              content={toTrimmedString(pageProps?.ogTitle)}
            />
            <meta
              property="og:description"
              content={toTrimmedString(pageProps?.ogDescription)}
            />
            <meta
              property="og:image"
              content={toTrimmedString(pageProps?.ogPageImage)}
            />
            <meta
              property="og:image:secure_url"
              content={toTrimmedString(pageProps?.ogPageImage)}
            />
            <meta property="og:image:width" content={OG_IMAGE_WIDTH} />
            <meta property="og:image:height" content={OG_IMAGE_HEIGHT} />
            <meta
              property="og:image:alt"
              content={toTrimmedString(pageProps?.ogDescription)}
            />
            <meta name="mobile-web-app-capable" content="yes" />
            {isApple && (
              <>
                <meta
                  name="apple-mobile-web-app-title"
                  content={PRODUCT_NAME}
                />
                <meta name="apple-mobile-web-app-capable" content="yes" />
                <meta
                  name="apple-mobile-web-app-status-bar-style"
                  content="black"
                />
              </>
            )}
          </>
        )}
        {!isPracticalPod && APPLE_STORE_LISTED && (
          <meta
            name="apple-itunes-app"
            content="app-id=APP_ID,affiliate-data=AFFILIATE_ID,app-argument=SOME_TEXT"
          />
        )}
        {USE_CDN && (
          <link rel="preconnect" href={PRACTICAL_STREAM_CDN_ADDRESS} />
        )}
        {IS_PRODUCTION && !isPracticalPod && (
          <>
            <link
              rel="alternate"
              type="text/plain"
              title="LLM-friendly Summary"
              href="/llms.txt"
            />
            {pageComponentDisplayName == HOME_PAGE_DISPLAY_NAME && (
              <link
                rel="alternate"
                type="text/plain"
                title="Full LLM Context"
                href="/llms-full.txt"
              />
            )}
          </>
        )}
        {!isPracticalPod &&
          pageComponentDisplayName == HOME_PAGE_DISPLAY_NAME && (
            <>
              <link
                rel="alternate"
                hrefLang={LANGUAGE_CODE_ENGLISH}
                href={PRODUCTION_HOST}
              />
              <link
                rel="alternate"
                hrefLang={LANGUAGE_CODE_SPANISH}
                href={PRODUCTION_HOST + '/' + LANGUAGE_CODE_SPANISH}
              />
              <link
                rel="alternate"
                hrefLang={LANGUAGE_CODE_FRENCH}
                href={PRODUCTION_HOST + '/' + LANGUAGE_CODE_FRENCH}
              />
              <link
                rel="alternate"
                hrefLang={LANGUAGE_CODE_KOREAN}
                href={PRODUCTION_HOST + '/' + LANGUAGE_CODE_KOREAN}
              />
              <link
                rel="alternate"
                hrefLang="x-default"
                href={PRODUCTION_HOST}
              />
            </>
          )}
        {!isPracticalPod &&
          pageComponentDisplayName == SEARCH_PAGE_DISPLAY_NAME && (
            <>
              <link
                rel="alternate"
                hrefLang={LANGUAGE_CODE_ENGLISH}
                href={PRODUCTION_HOST + '/search'}
              />
              <link
                rel="alternate"
                hrefLang={LANGUAGE_CODE_SPANISH}
                href={PRODUCTION_HOST + '/' + LANGUAGE_CODE_SPANISH + '/buscar'}
              />
              <link
                rel="alternate"
                hrefLang={LANGUAGE_CODE_FRENCH}
                href={PRODUCTION_HOST + '/' + LANGUAGE_CODE_FRENCH + '/trouve'}
              />
              <link
                rel="alternate"
                hrefLang={LANGUAGE_CODE_KOREAN}
                href={PRODUCTION_HOST + '/' + LANGUAGE_CODE_KOREAN + '/찾기'}
              />
              <link
                rel="alternate"
                hrefLang="x-default"
                href={PRODUCTION_HOST + '/search'}
              />
            </>
          )}
        {!isPracticalPod &&
          pageComponentDisplayName == STUDIO_PAGE_DISPLAY_NAME && (
            <>
              <link
                rel="alternate"
                hrefLang={LANGUAGE_CODE_ENGLISH}
                href={PRODUCTION_HOST + '/studio'}
              />
              <link
                rel="alternate"
                hrefLang={LANGUAGE_CODE_SPANISH}
                href={
                  PRODUCTION_HOST + '/' + LANGUAGE_CODE_SPANISH + '/estudio'
                }
              />
              <link
                rel="alternate"
                hrefLang={LANGUAGE_CODE_FRENCH}
                href={PRODUCTION_HOST + '/' + LANGUAGE_CODE_FRENCH + '/studio'}
              />
              <link
                rel="alternate"
                hrefLang={LANGUAGE_CODE_KOREAN}
                href={
                  PRODUCTION_HOST + '/' + LANGUAGE_CODE_KOREAN + '/스튜디오'
                }
              />
              <link
                rel="alternate"
                hrefLang="x-default"
                href={PRODUCTION_HOST + '/studio'}
              />
            </>
          )}
        {!isPracticalPod &&
          pageComponentDisplayName == PLAYLIST_PAGE_DISPLAY_NAME && (
            <>
              <link
                rel="alternate"
                hrefLang={LANGUAGE_CODE_ENGLISH}
                href={PRODUCTION_HOST + '/playlist'}
              />
              <link
                rel="alternate"
                hrefLang={LANGUAGE_CODE_SPANISH}
                href={
                  PRODUCTION_HOST + '/' + LANGUAGE_CODE_SPANISH + '/playlist'
                }
              />
              <link
                rel="alternate"
                hrefLang={LANGUAGE_CODE_FRENCH}
                href={
                  PRODUCTION_HOST + '/' + LANGUAGE_CODE_FRENCH + '/playlist'
                }
              />
              <link
                rel="alternate"
                hrefLang={LANGUAGE_CODE_KOREAN}
                href={
                  PRODUCTION_HOST + '/' + LANGUAGE_CODE_KOREAN + '/playlist'
                }
              />
              <link
                rel="alternate"
                hrefLang="x-default"
                href={PRODUCTION_HOST + '/playlist'}
              />
            </>
          )}
        {!isPracticalPod &&
          pageComponentDisplayName == FAVORITES_PAGE_DISPLAY_NAME && (
            <>
              <link
                rel="alternate"
                hrefLang={LANGUAGE_CODE_ENGLISH}
                href={PRODUCTION_HOST + '/favorites'}
              />
              <link
                rel="alternate"
                hrefLang={LANGUAGE_CODE_SPANISH}
                href={
                  PRODUCTION_HOST + '/' + LANGUAGE_CODE_SPANISH + '/favoritos'
                }
              />
              <link
                rel="alternate"
                hrefLang={LANGUAGE_CODE_FRENCH}
                href={PRODUCTION_HOST + '/' + LANGUAGE_CODE_FRENCH + '/favoris'}
              />
              <link
                rel="alternate"
                hrefLang={LANGUAGE_CODE_KOREAN}
                href={PRODUCTION_HOST + '/' + LANGUAGE_CODE_KOREAN + '/좋아요'}
              />
              <link
                rel="alternate"
                hrefLang="x-default"
                href={PRODUCTION_HOST + '/favorites'}
              />
            </>
          )}
        {!isPracticalPod &&
          pageComponentDisplayName == CONTACT_PAGE_DISPLAY_NAME && (
            <>
              <link
                rel="alternate"
                hrefLang={LANGUAGE_CODE_ENGLISH}
                href={PRODUCTION_HOST + '/contact'}
              />
              <link
                rel="alternate"
                hrefLang={LANGUAGE_CODE_SPANISH}
                href={
                  PRODUCTION_HOST + '/' + LANGUAGE_CODE_SPANISH + '/contact'
                }
              />
              <link
                rel="alternate"
                hrefLang={LANGUAGE_CODE_FRENCH}
                href={PRODUCTION_HOST + '/' + LANGUAGE_CODE_FRENCH + '/contact'}
              />
              <link
                rel="alternate"
                hrefLang={LANGUAGE_CODE_KOREAN}
                href={PRODUCTION_HOST + '/' + LANGUAGE_CODE_KOREAN + '/contact'}
              />
              <link
                rel="alternate"
                hrefLang="x-default"
                href={PRODUCTION_HOST + '/contact'}
              />
            </>
          )}
        {!isPracticalPod &&
          pageComponentDisplayName == ACCOUNT_SETTINGS_PAGE_DISPLAY_NAME && (
            <>
              <link
                rel="alternate"
                hrefLang={LANGUAGE_CODE_ENGLISH}
                href={PRODUCTION_HOST + '/settings'}
              />
              <link
                rel="alternate"
                hrefLang={LANGUAGE_CODE_SPANISH}
                href={
                  PRODUCTION_HOST + '/' + LANGUAGE_CODE_SPANISH + '/settings'
                }
              />
              <link
                rel="alternate"
                hrefLang={LANGUAGE_CODE_FRENCH}
                href={
                  PRODUCTION_HOST + '/' + LANGUAGE_CODE_FRENCH + '/settings'
                }
              />
              <link
                rel="alternate"
                hrefLang={LANGUAGE_CODE_KOREAN}
                href={
                  PRODUCTION_HOST + '/' + LANGUAGE_CODE_KOREAN + '/settings'
                }
              />
              <link
                rel="alternate"
                hrefLang="x-default"
                href={PRODUCTION_HOST + '/settings'}
              />
            </>
          )}
        {!isPracticalPod && (
          <>
            <meta name="pinterest-rich-pin" content="true" />
            <meta name="application-name" content={PRODUCT_NAME} />
            {!isMobileOnly && (
              <link
                rel="preload"
                as="image"
                href={'/images/64A9D722-26E1-414D-B662-58FF17AE57E9.avif'}
                media="(min-width: 548px)"
                {...({ fetchpriority: 'high' } as any)}
              />
            )}
            <link
              rel="preload"
              as="image"
              href="/images/F90CE7C0-9B76-40DC-9EE9-73FC16CF47C1.avif"
              media="(max-width: 547px)"
              {...({ fetchpriority: 'high' } as any)}
            />
            <link
              rel="preload"
              as="image"
              href={COUCH_HOMEPAGE_SMALL_SQUARE_AVIF}
              media="(max-width: 547px)"
              {...({ fetchpriority: 'high' } as any)}
            />
            <link
              rel="icon"
              type="image/png"
              sizes="512x512"
              href={ANDROID_CHROME_512_ICON}
            />
            <link
              rel="icon"
              type="image/png"
              sizes="384x384"
              href={ANDROID_CHROME_384_ICON}
            />
            <link
              rel="icon"
              type="image/png"
              sizes="256x256"
              href={ANDROID_CHROME_256_ICON}
            />
            <link
              rel="icon"
              type="image/png"
              sizes="192x192"
              href={ANDROID_CHROME_192_ICON}
            />
            <link
              rel="icon"
              type="image/png"
              sizes="144x144"
              href={ANDROID_CHROME_144_ICON}
            />
            <link
              rel="icon"
              type="image/png"
              sizes="128x128"
              href={ANDROID_CHROME_128_ICON}
            />
            <link
              rel="icon"
              type="image/png"
              sizes="72x72"
              href={ANDROID_CHROME_72_ICON}
            />
            <link
              rel="icon"
              type="image/png"
              sizes="48x48"
              href={ANDROID_CHROME_48_ICON}
            />
            <link
              rel="icon"
              type="image/png"
              sizes="36x36"
              href={ANDROID_CHROME_36_ICON}
            />
            {isApple && (
              <>
                <link
                  rel="apple-touch-icon"
                  sizes="180x180"
                  href={APPLE_TOUCH_180_ICON}
                />
                <link
                  rel="apple-touch-icon"
                  sizes="152x152"
                  href={APPLE_TOUCH_152_ICON}
                />
                <link
                  rel="apple-touch-icon"
                  sizes="144x144"
                  href={APPLE_TOUCH_144_ICON}
                />
                <link
                  rel="apple-touch-icon"
                  sizes="120x120"
                  href={APPLE_TOUCH_120_ICON}
                />
                <link
                  rel="apple-touch-icon"
                  sizes="114x114"
                  href={APPLE_TOUCH_114_ICON}
                />
                <link
                  rel="apple-touch-icon"
                  sizes="76x76"
                  href={APPLE_TOUCH_76_ICON}
                />
                <link
                  rel="apple-touch-icon"
                  sizes="72x72"
                  href={APPLE_TOUCH_72_ICON}
                />
                <link
                  rel="apple-touch-icon"
                  sizes="60x60"
                  href={APPLE_TOUCH_60_ICON}
                />
                <link
                  rel="apple-touch-icon"
                  sizes="57x57"
                  href={APPLE_TOUCH_57_ICON}
                />
                <link
                  rel="apple-touch-startup-image"
                  media="screen and (device-width: 430px) and (device-height: 932px) and (-webkit-device-pixel-ratio: 3) and (orientation: landscape)"
                  href={APPLE_TOUCH_STARTUP_IMAGE_1}
                />
                <link
                  rel="apple-touch-startup-image"
                  media="screen and (device-width: 393px) and (device-height: 852px) and (-webkit-device-pixel-ratio: 3) and (orientation: landscape)"
                  href={APPLE_TOUCH_STARTUP_IMAGE_2}
                />
                <link
                  rel="apple-touch-startup-image"
                  media="screen and (device-width: 428px) and (device-height: 926px) and (-webkit-device-pixel-ratio: 3) and (orientation: landscape)"
                  href={APPLE_TOUCH_STARTUP_IMAGE_3}
                />
                <link
                  rel="apple-touch-startup-image"
                  media="screen and (device-width: 390px) and (device-height: 844px) and (-webkit-device-pixel-ratio: 3) and (orientation: landscape)"
                  href={APPLE_TOUCH_STARTUP_IMAGE_4}
                />
                <link
                  rel="apple-touch-startup-image"
                  media="screen and (device-width: 375px) and (device-height: 812px) and (-webkit-device-pixel-ratio: 3) and (orientation: landscape)"
                  href={APPLE_TOUCH_STARTUP_IMAGE_5}
                />
                <link
                  rel="apple-touch-startup-image"
                  media="screen and (device-width: 414px) and (device-height: 896px) and (-webkit-device-pixel-ratio: 3) and (orientation: landscape)"
                  href={APPLE_TOUCH_STARTUP_IMAGE_6}
                />
                <link
                  rel="apple-touch-startup-image"
                  media="screen and (device-width: 414px) and (device-height: 896px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
                  href={APPLE_TOUCH_STARTUP_IMAGE_7}
                />
                <link
                  rel="apple-touch-startup-image"
                  media="screen and (device-width: 414px) and (device-height: 736px) and (-webkit-device-pixel-ratio: 3) and (orientation: landscape)"
                  href={APPLE_TOUCH_STARTUP_IMAGE_8}
                />
                <link
                  rel="apple-touch-startup-image"
                  media="screen and (device-width: 375px) and (device-height: 667px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
                  href={APPLE_TOUCH_STARTUP_IMAGE_9}
                />
                <link
                  rel="apple-touch-startup-image"
                  media="screen and (device-width: 320px) and (device-height: 568px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
                  href={APPLE_TOUCH_STARTUP_IMAGE_10}
                />
                <link
                  rel="apple-touch-startup-image"
                  media="screen and (device-width: 1024px) and (device-height: 1366px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
                  href={APPLE_TOUCH_STARTUP_IMAGE_11}
                />
                <link
                  rel="apple-touch-startup-image"
                  media="screen and (device-width: 834px) and (device-height: 1194px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
                  href={APPLE_TOUCH_STARTUP_IMAGE_12}
                />
                <link
                  rel="apple-touch-startup-image"
                  media="screen and (device-width: 820px) and (device-height: 1180px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
                  href={APPLE_TOUCH_STARTUP_IMAGE_13}
                />
                <link
                  rel="apple-touch-startup-image"
                  media="screen and (device-width: 834px) and (device-height: 1112px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
                  href={APPLE_TOUCH_STARTUP_IMAGE_14}
                />
                <link
                  rel="apple-touch-startup-image"
                  media="screen and (device-width: 810px) and (device-height: 1080px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
                  href={APPLE_TOUCH_STARTUP_IMAGE_15}
                />
                <link
                  rel="apple-touch-startup-image"
                  media="screen and (device-width: 768px) and (device-height: 1024px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
                  href={APPLE_TOUCH_STARTUP_IMAGE_16}
                />
                <link
                  rel="apple-touch-startup-image"
                  media="screen and (device-width: 744px) and (device-height: 1133px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
                  href={APPLE_TOUCH_STARTUP_IMAGE_17}
                />
                <link
                  rel="apple-touch-startup-image"
                  media="screen and (device-width: 430px) and (device-height: 932px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
                  href={APPLE_TOUCH_STARTUP_IMAGE_18}
                />
                <link
                  rel="apple-touch-startup-image"
                  media="screen and (device-width: 393px) and (device-height: 852px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
                  href={APPLE_TOUCH_STARTUP_IMAGE_19}
                />
                <link
                  rel="apple-touch-startup-image"
                  media="screen and (device-width: 428px) and (device-height: 926px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
                  href={APPLE_TOUCH_STARTUP_IMAGE_20}
                />
                <link
                  rel="apple-touch-startup-image"
                  media="screen and (device-width: 390px) and (device-height: 844px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
                  href={APPLE_TOUCH_STARTUP_IMAGE_21}
                />
                <link
                  rel="apple-touch-startup-image"
                  media="screen and (device-width: 375px) and (device-height: 812px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
                  href={APPLE_TOUCH_STARTUP_IMAGE_22}
                />
                <link
                  rel="apple-touch-startup-image"
                  media="screen and (device-width: 414px) and (device-height: 896px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
                  href={APPLE_TOUCH_STARTUP_IMAGE_23}
                />
                <link
                  rel="apple-touch-startup-image"
                  media="screen and (device-width: 414px) and (device-height: 896px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
                  href={APPLE_TOUCH_STARTUP_IMAGE_24}
                />
                <link
                  rel="apple-touch-startup-image"
                  media="screen and (device-width: 414px) and (device-height: 736px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
                  href={APPLE_TOUCH_STARTUP_IMAGE_25}
                />
                <link
                  rel="apple-touch-startup-image"
                  media="screen and (device-width: 375px) and (device-height: 667px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
                  href={APPLE_TOUCH_STARTUP_IMAGE_26}
                />
                <link
                  rel="apple-touch-startup-image"
                  media="screen and (device-width: 320px) and (device-height: 568px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
                  href={APPLE_TOUCH_STARTUP_IMAGE_27}
                />
                <link
                  rel="apple-touch-startup-image"
                  media="screen and (device-width: 1024px) and (device-height: 1366px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
                  href={APPLE_TOUCH_STARTUP_IMAGE_28}
                />
                <link
                  rel="apple-touch-startup-image"
                  media="screen and (device-width: 834px) and (device-height: 1194px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
                  href={APPLE_TOUCH_STARTUP_IMAGE_29}
                />
                <link
                  rel="apple-touch-startup-image"
                  media="screen and (device-width: 820px) and (device-height: 1180px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
                  href={APPLE_TOUCH_STARTUP_IMAGE_30}
                />
                <link
                  rel="apple-touch-startup-image"
                  media="screen and (device-width: 834px) and (device-height: 1112px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
                  href={APPLE_TOUCH_STARTUP_IMAGE_31}
                />
                <link
                  rel="apple-touch-startup-image"
                  media="screen and (device-width: 810px) and (device-height: 1080px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
                  href={APPLE_TOUCH_STARTUP_IMAGE_32}
                />
                <link
                  rel="apple-touch-startup-image"
                  media="screen and (device-width: 768px) and (device-height: 1024px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
                  href={APPLE_TOUCH_STARTUP_IMAGE_33}
                />
                <link
                  rel="apple-touch-startup-image"
                  media="screen and (device-width: 744px) and (device-height: 1133px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
                  href={APPLE_TOUCH_STARTUP_IMAGE_34}
                />
                <link
                  rel="mask-icon"
                  href={SAFARI_PINNED_TAB_CDN_OR_LOCAL}
                  color={SAFARI_PINNED_TAB_MASK_ICON_COLOR}
                />
              </>
            )}
            <meta name="msapplication-TileColor" content={MS_APP_TILE_COLOR} />
            <meta
              name="msapplication-TileImage"
              content={MS_TILE_144_CDN_OR_LOCAL}
            />
            <meta name="theme-color" content={THEME_COLOR} />
            <link rel="icon" href={FAV_ICON_CDN_OR_LOCAL} type="image/x-icon" />
            <link
              rel="icon"
              href={SVG_ICON_CDN_OR_LOCAL}
              type="image/svg+xml"
            />
            <link
              rel="icon"
              type="image/png"
              sizes="194x194"
              href={FAV_ICON_194}
            />
            <link
              rel="icon"
              type="image/png"
              sizes="96x96"
              href={FAV_ICON_96}
            />
            <link
              rel="icon"
              type="image/png"
              sizes="32x32"
              href={FAV_ICON_32}
            />
            <link
              rel="icon"
              type="image/png"
              sizes="16x16"
              href={FAV_ICON_16}
            />
          </>
        )}
        <link rel="canonical" href={toTrimmedString(pageProps?.canonicalUrl)} />
        {translate(
          <link rel="manifest" href="/manifest.json" />,
          <link rel="manifest" href="/manifest-es.json" />,
          <link rel="manifest" href="/manifest-fr.json" />,
          <link rel="manifest" href="/manifest-ko.json" />,
          selectedLanguage,
        )}
      </Head>
    )
  }, [
    dark,
    titleText,
    navigating,
    isMobileOnly,
    chatBotEmbed,
    isRunningPwa,
    isMobileSafari,
    isPracticalPod,
    timePickerScript,
    selectedLanguage,
    reactPlayerStyles,
  ])

  const colorsTuple = (color: any) => Array(10).fill(color)

  return (
    <>
      {!inIframe ? (
        <>
          {headTag}
          {!isMacOsSafariLessThanVersion15 && !isEmbedded && !TEST_EMBEDDED ? (
            <CookiesProvider>
              <SessionProvider
                session={pageProps?.session as any}
                refetchInterval={0}
              >
                <ColorSchemeProvider
                  colorScheme={colorScheme}
                  toggleColorScheme={toggleColorScheme}
                >
                  <MantineProvider
                    withGlobalStyles
                    withNormalizeCSS
                    theme={{
                      colorScheme: colorScheme,
                      colors: {
                        dark: darkColors,
                        //@ts-ignore
                        'dark-mode-action-color':
                          //@ts-ignore
                          colorsTuple(DARK_MODE_ACTION_COLOR),
                        //@ts-ignore
                        'light-mode-action-color': colorsTuple(
                          LIGHT_MODE_ACTION_COLOR,
                        ),
                      },
                    }}
                  >
                    <NotificationsProvider
                      zIndex={8439849343932328}
                      limit={isMobileOnly ? 3 : 5}
                    >
                      <main className={inter.variable}>
                        {!loading && (
                          <>
                            {showBufferRegion && (
                              <div
                                className={'inner'}
                                style={{
                                  backgroundColor: dark
                                    ? 'rgba(0,0,0,.7)'
                                    : 'rgba(255,255,255,.5)',
                                  height: '135px',
                                }}
                              >
                                &nbsp;
                              </div>
                            )}
                            <div
                              className={classes.audioAndLogoHeader}
                              style={{
                                height: '120px',
                                display:
                                  dontLoad || isViewportTooSmall
                                    ? 'none'
                                    : 'block',
                              }}
                            >
                              {showSubscribePitchModal && (
                                <SubscribePitchModal
                                  limitedSubscriptionPitchRender={
                                    limitedSubscriptionPitchRender
                                  }
                                  isSmallSubscriptionPitchRender={
                                    isSmallSubscriptionPitchRender
                                  }
                                  avifCompatible={avifCompatible}
                                  isAuthenticatedUserAndSubscribed={
                                    isAuthenticatedUserAndSubscribed
                                  }
                                  subscriptionButtonCurrencyText={
                                    subscriptionButtonCurrencyText
                                  }
                                  logSentryError={logSentryError}
                                  isAuthenticated={isAuthenticated}
                                  opened={showSubscribePitchModal}
                                  setOpened={setSubscribePitchModalOpened}
                                  navigateRoute={navigateRoute}
                                  scrollTop={scrollTop}
                                  previousRoute={previousRoute?.current}
                                  currentRoute={currentRoute?.current}
                                  stripeOrVercelHeaderBasedCurrency={
                                    stripeOrVercelHeaderBasedCurrency
                                  }
                                  selectedLanguage={selectedLanguage}
                                  dark={dark}
                                />
                              )}
                              {showCopyErrorModal && (
                                <CopyErrorModal
                                  logSentryError={logSentryError}
                                  copyErrorModalText={copyErrorModalText}
                                  opened={showCopyErrorModal}
                                  setOpened={setCopyErrorModalOpened}
                                  selectedLanguage={selectedLanguage}
                                  isIOS={isIOS}
                                  dark={dark}
                                  isPracticalPod={isPracticalPod}
                                />
                              )}
                              {showMaskModal && (
                                <>
                                  <MaskModal
                                    logSentryError={logSentryError}
                                    avifCompatible={avifCompatible}
                                    opened={showMaskModal}
                                    hideLoader={canShowAudioPlayer}
                                    isMacOsSafariLimitedRender={
                                      isMacOsSafariLimitedRender
                                    }
                                    web2UserIdLoggedIn={
                                      web2UserIdLoggedIn == true
                                    }
                                    dark={dark}
                                  />
                                </>
                              )}
                              {showSelectLanguageModal && (
                                <SelectLanguageModal
                                  logSentryError={logSentryError}
                                  setOpened={setSelectLanguageModalOpened}
                                  opened={selectLanguageModalOpened}
                                  selectedLanguage={selectedLanguage}
                                  toggleSelectedLanguage={
                                    toggleSelectedLanguage
                                  }
                                  isIOS={isIOS}
                                  isMobileOnly={isMobileOnly}
                                  isFirefox={isFirefox}
                                  dark={dark}
                                  isPracticalPod={isPracticalPod}
                                />
                              )}
                              {showAddToPlaylistModal && (
                                <>
                                  <AddToPlaylistModal
                                    imagePlaceholder={
                                      dark
                                        ? avifCompatible
                                          ? AVATAR_BACKGROUND_IMAGE_DARK_AVIF
                                          : AVATAR_BACKGROUND_IMAGE_DARK_PNG
                                        : avifCompatible
                                          ? AVATAR_BACKGROUND_IMAGE_LIGHT_AVIF
                                          : AVATAR_BACKGROUND_IMAGE_LIGHT_PNG
                                    }
                                    logSentryError={logSentryError}
                                    avifCompatible={avifCompatible}
                                    isPracticalPod={isPracticalPod}
                                    gotFavorites={gotFavorites}
                                    song_Master={song_Master}
                                    debouncedPlaying={debouncedPlaying}
                                    pauseSong={pauseSong}
                                    resumeSong={resumeSong}
                                    lastClickedPlaylistId={
                                      lastClickedPlaylistId
                                    }
                                    setLastClickedPlaylistId={
                                      setLastClickedPlaylistId
                                    }
                                    opened={showAddToPlaylistModal}
                                    userCanMakePlaylists={userCanMakePlaylists}
                                    setPlaydeck={setPlaydeck}
                                    setOpened={setAddToPlaylistModalOpened}
                                    debouncedPlayingLong={debouncedPlayingLong}
                                    getPlaylistEntries={getPlaylistEntries}
                                    song_Selected={song_Selected}
                                    songs_Master={songs_Master}
                                    wholeSongSetBound={wholeSongSetBound}
                                    navigateRoute={navigateRoute}
                                    addAndPlaySong={addAndPlaySong}
                                    copyErrorModalText={copyErrorModalText}
                                    copyErrorModalOpened={copyErrorModalOpened}
                                    setCopyErrorModalOpened={
                                      setCopyErrorModalOpened
                                    }
                                    setClipboardCopied={setClipboardCopied}
                                    clipboardCopied={clipboardCopied}
                                    setCopyErrorModalText={
                                      setCopyErrorModalText
                                    }
                                    copyKey={copyKey}
                                    setCopyKey={setCopyKey}
                                    user={user}
                                    previousRoute={previousRoute?.current}
                                    currentRoute={currentRoute?.current}
                                    songContractAddress={songContractAddress}
                                    songTokenId={songTokenId}
                                    reactToPlaylistsChange={
                                      reactToPlaylistsChange
                                    }
                                    isIOS={isIOS}
                                    isMacOs={isMacOs}
                                    isMobileOnly={isMobileOnly}
                                    selectedLanguage={selectedLanguage}
                                    toggleFavorite={toggleFavorite}
                                    dark={dark}
                                    web2UserSessionId={web2UserSessionId}
                                    web2UserSessionEmail={web2UserSessionEmail}
                                    web2UserIdLoggedIn={
                                      web2UserIdLoggedIn == true
                                    }
                                    signalRefreshMasterPlaylists={
                                      signalRefreshMasterPlaylists
                                    }
                                    setSignalRefreshMasterPlaylists={
                                      setSignalRefreshMasterPlaylists
                                    }
                                    masterPlaylists={masterPlaylists}
                                  />
                                </>
                              )}
                              {showUnsubscribeModal && (
                                <UnsubscribedModal
                                  logSentryError={logSentryError}
                                  opened={showUnsubscribeModal}
                                  setOpened={setUnsubscribedModalOpened}
                                  selectedLanguage={selectedLanguage}
                                  isIOS={isIOS}
                                  dark={dark}
                                />
                              )}
                              {showEnvironmentProtectionModal && (
                                <EnvironmentProtectionModal
                                  logSentryError={logSentryError}
                                  opened={showEnvironmentProtectionModal}
                                  toggleEnvironmentProtectionModal={
                                    toggleEnvironmentProtectionModal
                                  }
                                  selectedLanguage={selectedLanguage}
                                  dark={dark}
                                />
                              )}
                              {showApplePwaHelpModal && (
                                <ApplePwaHelpModal
                                  primaryImagePlaceholder={
                                    primaryImagePlaceholder
                                  }
                                  avifCompatible={avifCompatible}
                                  logSentryError={logSentryError}
                                  opened={showApplePwaHelpModal}
                                  setOpened={setApplePwaHelpModalOpened}
                                  selectedLanguage={selectedLanguage}
                                  isIOS={isIOS}
                                  dark={dark}
                                />
                              )}
                              {showAppleMacPwaHelpModal &&
                                !showApplePwaHelpModal && (
                                  <AppleMacPwaHelpModal
                                    logSentryError={logSentryError}
                                    opened={showAppleMacPwaHelpModal}
                                    setOpened={setAppleMacPwaHelpModalOpened}
                                    selectedLanguage={selectedLanguage}
                                    isIOS={isIOS}
                                    dark={dark}
                                    isPracticalPod={isPracticalPod}
                                  />
                                )}
                              {showStreamingInTwoOrMoreLocationsModal && (
                                <StreamingInTwoOrMoreLocationsModal
                                  logSentryError={logSentryError}
                                  opened={
                                    streamingInTwoOrMoreLocationsModalOpened
                                  }
                                  setOpened={
                                    setStreamingInTwoOrMoreLocationsModalOpened
                                  }
                                  signalCheckClearIntervals={
                                    signalCheckClearIntervals
                                  }
                                  setSignalCheckClearIntervals={
                                    setSignalCheckClearIntervals
                                  }
                                  dark={dark}
                                  isIOS={isIOS}
                                  isOnline={isOnline}
                                  environmentProtectionModalOpened={
                                    environmentProtectionModalOpened
                                  }
                                  selectedLanguage={selectedLanguage}
                                  web2UserIdLoggedIn={web2UserIdLoggedIn}
                                  web2UserSessionId={web2UserSessionId}
                                />
                              )}
                              {showScheduledPlaylistsEnabledModal && (
                                <ScheduledPlaylistsEnabledModal
                                  logSentryError={logSentryError}
                                  opened={scheduledPlaylistsEnabledModalOpened}
                                  setOpened={
                                    toggleScheduledPlaylistsEnabledModalOpened
                                  }
                                  masterSchedule={masterSchedule}
                                  dark={dark}
                                  isIOS={isIOS}
                                  selectedLanguage={selectedLanguage}
                                />
                              )}
                              {showInvitationWasCancelledModal && (
                                <InvitationWasCancelledModal
                                  dark={dark}
                                  isIOS={isIOS}
                                  setIsInvitationPass={setIsInvitationPass}
                                  selectedLanguage={selectedLanguage}
                                  logSentryError={logSentryError}
                                  opened={showInvitationWasCancelledModal}
                                  setOpened={
                                    setInvitationWasCancelledModalOpened
                                  }
                                />
                              )}
                              {showInvitationWasAlreadyAcceptedModal && (
                                <InvitationWasAlreadyAcceptedModal
                                  dark={dark}
                                  isIOS={isIOS}
                                  setIsInvitationPass={setIsInvitationPass}
                                  selectedLanguage={selectedLanguage}
                                  logSentryError={logSentryError}
                                  opened={showInvitationWasAlreadyAcceptedModal}
                                  setOpened={
                                    setInvitationWasAlreadyAcceptedModalOpened
                                  }
                                />
                              )}
                              {showMustAcceptInvitationWithSameEmailModal && (
                                <MustAcceptInvitationWithSameEmailModal
                                  dark={dark}
                                  isIOS={isIOS}
                                  setIsInvitationPass={setIsInvitationPass}
                                  selectedLanguage={selectedLanguage}
                                  logSentryError={logSentryError}
                                  opened={
                                    showMustAcceptInvitationWithSameEmailModal
                                  }
                                  setOpened={
                                    setMustAcceptInvitationWithSameEmailModalOpened
                                  }
                                />
                              )}
                              {showOnAnotherAccountCantAcceptInviteModal && (
                                <OnAnotherAccountCantAcceptInviteModal
                                  dark={dark}
                                  isIOS={isIOS}
                                  setIsInvitationPass={setIsInvitationPass}
                                  selectedLanguage={selectedLanguage}
                                  logSentryError={logSentryError}
                                  opened={
                                    showOnAnotherAccountCantAcceptInviteModal
                                  }
                                  setOpened={
                                    setOnAnotherAccountCantAcceptInviteModalOpened
                                  }
                                />
                              )}
                              {showOverrideExceptionDateModal && (
                                <OverrideExceptionDateModal
                                  dark={dark}
                                  isIOS={isIOS}
                                  navigateRoute={navigateRoute}
                                  canTraversetoPlaylist={
                                    pageComponentDisplayName !=
                                    PLAYLIST_PAGE_DISPLAY_NAME
                                  }
                                  setLastPlayedDate_Conditional={
                                    setLastPlayedDate_Conditional
                                  }
                                  setLastPlayedDate_Conditional_One={
                                    setLastPlayedDate_Conditional_One
                                  }
                                  setLastPlayedDate_Conditional_Two={
                                    setLastPlayedDate_Conditional_Two
                                  }
                                  triggerMonitorScheduleFn={
                                    triggerMonitorScheduleFn
                                  }
                                  playing={playing}
                                  resumeSong={resumeSong}
                                  selectedLanguage={selectedLanguage}
                                  logSentryError={logSentryError}
                                  setUserLastDateWhenOverrodeExceptionDate={
                                    setUserLastDateWhenOverrodeExceptionDate
                                  }
                                  setOpenToPlaylistScheduleTab={
                                    setOpenToPlaylistScheduleTab
                                  }
                                  opened={showOverrideExceptionDateModal}
                                  setOpened={
                                    setOverrideExceptionDateModalOpened
                                  }
                                  isHoliday={
                                    isPlayingPlaylistExceptionDateHoliday
                                  }
                                />
                              )}
                              {showSubscribedUserCantAcceptInviteModal && (
                                <SubscribedUserCantAcceptInviteModal
                                  dark={dark}
                                  isIOS={isIOS}
                                  setIsInvitationPass={setIsInvitationPass}
                                  selectedLanguage={selectedLanguage}
                                  logSentryError={logSentryError}
                                  opened={
                                    showSubscribedUserCantAcceptInviteModal
                                  }
                                  setOpened={
                                    setSubscribedUserCantAcceptInviteModalOpened
                                  }
                                />
                              )}
                              {showConfirmInvitationAcceptanceModal && (
                                <ConfirmInvitationAcceptanceModal
                                  dark={dark}
                                  isIOS={isIOS}
                                  user={user}
                                  selectedLanguage={selectedLanguage}
                                  logSentryError={logSentryError}
                                  setIsInvitationPass={setIsInvitationPass}
                                  opened={showConfirmInvitationAcceptanceModal}
                                  setOpened={
                                    setConfirmInvitationAcceptanceModalOpened
                                  }
                                  confirmInvitationAcceptanceCallback={
                                    confirmInvitationAcceptanceCallback
                                  }
                                  setConfirmInvitationAcceptanceModalJustClosed={
                                    setConfirmInvitationAcceptanceModalJustClosed
                                  }
                                />
                              )}
                              {showLoginToAcceptInvitationModal && (
                                <LoginToAcceptInvitationModal
                                  dark={dark}
                                  isIOS={isIOS}
                                  setIsInvitationPass={setIsInvitationPass}
                                  selectedLanguage={selectedLanguage}
                                  logSentryError={logSentryError}
                                  opened={showLoginToAcceptInvitationModal}
                                  setOpened={
                                    setLoginToAcceptInvitationModalOpened
                                  }
                                  loginToAcceptInvitationModalCallback={
                                    loginToAcceptInvitationModalCallback
                                  }
                                  pendingInvitationEmail={
                                    pendingInvitationEmail
                                  }
                                  signalReenableLoginFromInvitationControls={
                                    signalReenableLoginFromInvitationControls
                                  }
                                />
                              )}
                              {showDeviceIsAboutToRestartModal && (
                                <DeviceIsAboutToRestartModal
                                  logSentryError={logSentryError}
                                  opened={showDeviceIsAboutToRestartModal}
                                  setOpened={
                                    setDeviceIsAboutToRestartModalOpened
                                  }
                                  dark={dark}
                                  isIOS={isIOS}
                                  selectedLanguage={selectedLanguage}
                                />
                              )}
                              {showAppNeedsToReloadModal && (
                                <AppNeedsToReloadModal
                                  logSentryError={logSentryError}
                                  opened={appNeedsToReloadModalOpened}
                                  setOpened={setAppNeedsToReloadModalOpened}
                                  reloadAppForNewVersion={
                                    reloadAppForNewVersion
                                  }
                                  dark={dark}
                                  isIOS={isIOS}
                                  selectedLanguage={selectedLanguage}
                                  minutesRemainingToReload={
                                    minutesRemainingToReload
                                  }
                                />
                              )}
                              <OfflineModal
                                hasOfflineTracks={hasOfflineTracks}
                                logSentryError={logSentryError}
                                isOnline={isOnline}
                                opened={offlineModalOpened}
                                setOpened={setOfflineModalOpened}
                                selectedLanguage={selectedLanguage}
                                isPracticalPod={isPracticalPod}
                                resumeSong={resumeSong}
                                isIOS={isIOS}
                                dark={dark}
                                muted={muted}
                                wasPlayingBeforeWentOffline={
                                  wasPlayingBeforeWentOffline
                                }
                                isSubscribedUser={
                                  web2UserIdLoggedIn && userSubscribed == 'true'
                                }
                              />
                              {width > 640 && (
                                <Container
                                  className={classes.homepageLogoContainer}
                                  style={{
                                    zIndex: 56,
                                    top: largeDisplay ? 60 : 66.5,
                                    left: largeDisplay ? 0 : 20,
                                    position: 'fixed',
                                    backgroundColor: 'transparent',
                                    visibility: homepageLogoContainerVisibility,
                                  }}
                                >
                                  {!isViewportTooSmall &&
                                  !dontLoad &&
                                  pageComponentDisplayName ==
                                    HOME_PAGE_DISPLAY_NAME ? (
                                    <img
                                      decoding="async"
                                      alt={translate(
                                        'Home',
                                        'Inicio',
                                        'Accueil',
                                        '홈',
                                        selectedLanguage,
                                      )}
                                      draggable="false"
                                      style={{
                                        paddingTop: '8px',
                                        backgroundColor: 'transparent',
                                      }}
                                      className={classes.imgLogoNoHover}
                                      //@ts-ignore
                                      nopin="nopin"
                                      src={homepageLogoImageSource}
                                    />
                                  ) : (
                                    <Link
                                      href="/"
                                      title=""
                                      prefetch={false}
                                      style={{
                                        cursor: 'pointer',
                                        opacity: 'initial',
                                        backgroundColor: 'transparent',
                                      }}
                                    >
                                      <img
                                        decoding="async"
                                        alt={translate(
                                          'Home',
                                          'Inicio',
                                          'Accueil',
                                          '홈',
                                          selectedLanguage,
                                        )}
                                        draggable="false"
                                        style={{
                                          paddingTop: '8px',
                                        }}
                                        className={classes.imgLogo}
                                        //@ts-ignore
                                        nopin="nopin"
                                        src={homepageLogoImageSource}
                                      />
                                    </Link>
                                  )}
                                </Container>
                              )}
                              {!isViewportTooSmall &&
                                audioWrapperVisibility && (
                                  <>
                                    <ActionIcon
                                      aria-label={favoritesButtonAriaLabel}
                                      size={30}
                                      radius="xl"
                                      variant="transparent"
                                      onClick={() => {
                                        try {
                                          if (
                                            !canFavoriteAndPlaylist &&
                                            toggleSubscribePitchModalOpened
                                          )
                                            toggleSubscribePitchModalOpened(
                                              true,
                                              true,
                                              false,
                                            )
                                          else if (
                                            web2UserIdLoggedIn == true &&
                                            isNotNullOrEmptyString(
                                              user?.web2UserId,
                                            ) &&
                                            isNotNullOrEmptyString(
                                              song_Master?.contract_address,
                                            ) &&
                                            isNotNullOrEmptyString(
                                              song_Master?.token_id,
                                            )
                                          ) {
                                            if (!gotFavorites) {
                                              toast(
                                                translate(
                                                  "Your favorites haven't loaded yet.",
                                                  'Favoritos aún no están cargados.',
                                                  'Favoris ne sont pas encore chargés.',
                                                  '즐겨찾기가 아직 로드되지 않았습니다.',
                                                  selectedLanguage,
                                                ),
                                                'neutral',
                                              )
                                            } else {
                                              let cln =
                                                structuredClone(song_Master)
                                              cln.favorited =
                                                song_Master?.favorited != true
                                              setSong_Master(cln)
                                              React.startTransition(() => {
                                                try {
                                                  toggleFavorite(
                                                    toTrimmedString(
                                                      song_Master?.contract_address,
                                                    ),
                                                    toTrimmedString(
                                                      song_Master?.token_id,
                                                    ),
                                                    song_Master?.favorited !=
                                                      true,
                                                    user?.web2UserId,
                                                  )
                                                } catch (e) {
                                                  if (DEBUG_MODE) console.log(e)
                                                  logSentryError(
                                                    e,
                                                    SENTRY_COMPONENT_NAME,
                                                  )
                                                }
                                              })
                                            }
                                          }
                                        } catch (e) {
                                          if (DEBUG_MODE) console.log(e)
                                          logSentryError(
                                            e,
                                            SENTRY_COMPONENT_NAME,
                                          )
                                        }
                                      }}
                                      title={''}
                                      className={classes.buttonPlayerFavorite}
                                      style={{
                                        zIndex: 59,
                                        position: 'fixed',
                                        cursor: favoritesButtonCursor,
                                        visibility: favoritesButtonVisibility,
                                      }}
                                      sx={() => ({
                                        '&:focus': {
                                          outline: '0',
                                        },
                                        '&:hover': {
                                          opacity: dark
                                            ? ZERO_POINT_EIGHT_FIVE_OPACITY
                                            : ZERO_POINT_SIX_FIVE_OPACITY,
                                        },
                                      })}
                                    >
                                      {showFavoritesLoader ? (
                                        <Loader variant="dots" />
                                      ) : (
                                        <>
                                          <IconHeart
                                            fill={
                                              song_Master?.favorited
                                                ? SYSTEM_RED_COLOR
                                                : 'transparent'
                                            }
                                            color={
                                              song_Master?.favorited
                                                ? SYSTEM_RED_COLOR
                                                : dark
                                                  ? SYSTEM_WHITE_COLOR
                                                  : SYSTEM_BLACK_COLOR
                                            }
                                          />
                                        </>
                                      )}
                                    </ActionIcon>
                                    {(!isPracticalPod ||
                                      (isPracticalPod &&
                                        !FORCE_NO_PLAYLIST_EDITS_PRACTICAL_POD)) && (
                                      <ActionIcon
                                        aria-label={
                                          buttonPlayerPlaylistAriaLabel
                                        }
                                        size={30}
                                        radius="xl"
                                        variant="transparent"
                                        onClick={() => {
                                          React.startTransition(() => {
                                            try {
                                              if (
                                                !canFavoriteAndPlaylist &&
                                                toggleSubscribePitchModalOpened
                                              )
                                                toggleSubscribePitchModalOpened(
                                                  true,
                                                  true,
                                                  false,
                                                )
                                              else {
                                                if (
                                                  web2UserIdLoggedIn == true &&
                                                  isNotNullOrEmptyString(
                                                    song_Master?.contract_address,
                                                  ) &&
                                                  isNotNullOrEmptyString(
                                                    song_Master?.token_id,
                                                  )
                                                ) {
                                                  toggleAddToPlaylistModalOpened(
                                                    true,
                                                    song_Master,
                                                  )
                                                }
                                              }
                                            } catch (e) {
                                              if (DEBUG_MODE) console.log(e)
                                              logSentryError(
                                                e,
                                                SENTRY_COMPONENT_NAME,
                                              )
                                            }
                                          })
                                        }}
                                        title={''}
                                        className={classes.buttonPlayerPlaylist}
                                        style={{
                                          zIndex: 59,
                                          position: 'fixed',
                                          visibility:
                                            buttonPlayerPlaylistVisibility,
                                        }}
                                        sx={() => ({
                                          '&:focus': {
                                            outline: '0',
                                          },
                                          '&:hover': {
                                            opacity: dark
                                              ? ZERO_POINT_EIGHT_FIVE_OPACITY
                                              : ZERO_POINT_SIX_FIVE_OPACITY,
                                          },
                                        })}
                                      >
                                        <IconPlaylistAdd
                                          color={
                                            dark
                                              ? SYSTEM_WHITE_COLOR
                                              : SYSTEM_BLACK_COLOR
                                          }
                                          stroke={width > 500 ? 2 : 1.5}
                                          width={width > 500 ? 32 : 26}
                                          height={width > 500 ? 32 : 26}
                                        />
                                      </ActionIcon>
                                    )}
                                    <span
                                      style={{
                                        transform: marqueeTransform,
                                        zIndex: 59,
                                        position: 'fixed',
                                      }}
                                      className={marqueeClass}
                                    >
                                      <span
                                        aria-hidden="true"
                                        className={marqueeSpangClass}
                                        style={{
                                          position: 'absolute',
                                          zIndex: 4343,
                                          backgroundColor: dark
                                            ? 'rgba(0,0,0,.7) !important'
                                            : 'rgba(255,255,255,.5) !important',
                                        }}
                                      >
                                        <span
                                          aria-hidden="true"
                                          className={
                                            !isPracticalPod
                                              ? 'animate-character'
                                              : 'static-character'
                                          }
                                          style={{
                                            visibility:
                                              animateCharacterVisibility,
                                            cursor: 'default',
                                          }}
                                        >
                                          {isEdge && isMobileOnly
                                            ? renderedSongNameMasterSmall
                                            : renderedSongNameMaster}
                                        </span>
                                      </span>
                                    </span>
                                  </>
                                )}
                              <div
                                className={classes.audioWrapper}
                                style={{
                                  width: audioWrapperWidth,
                                  backgroundColor: audioWrapperBackgroundColor,
                                  left: audioWrapperLeft,
                                }}
                              >
                                <div>
                                  <div>
                                    {!isViewportTooSmall &&
                                      audioWrapperVisibility && (
                                        <>
                                          <AudioPlayer //@ts-ignore
                                            ref={player}
                                            showSkipControls={true}
                                            autoPlayAfterSrcChange={false}
                                            defaultCurrentTime={'00:00'}
                                            src={AUDIO_SILENT_SRC}
                                            defaultDuration={
                                              preloadAudioMetadata
                                                ? ''
                                                : formatDuration(
                                                    song_Master?.duration,
                                                    true,
                                                  )
                                            }
                                            progressJumpSteps={{
                                              backward: 5000,
                                              forward: 5000,
                                            }}
                                            preload={
                                              preloadAudioMetadata
                                                ? 'metadata'
                                                : 'none'
                                            }
                                            autoPlay={false}
                                            muted={muted}
                                            showDownloadProgress={true}
                                            showFilledVolume={false}
                                            showJumpControls={true}
                                            showFilledProgress={false}
                                            progressUpdateInterval={1}
                                            listenInterval={1350}
                                            crossOrigin={undefined}
                                            loop={loop}
                                            i18nAriaLabels={
                                              Player_i18nAriaLabels
                                            }
                                            customIcons={{
                                              play: (
                                                <>
                                                  {!debouncedPlaying ? (
                                                    <svg
                                                      xmlns="http://www.w3.org/2000/svg"
                                                      aria-hidden="true"
                                                      role="img"
                                                      width="1em"
                                                      height="1em"
                                                      preserveAspectRatio="xMidYMid meet"
                                                      viewBox="0 0 24 24"
                                                    >
                                                      <path
                                                        d="M10 16.5v-9l6 4.5M12 2A10 10 0 0 0 2 12a10 10 0 0 0 10 10a10 10 0 0 0 10-10A10 10 0 0 0 12 2z"
                                                        fill="currentColor"
                                                      ></path>
                                                    </svg>
                                                  ) : (
                                                    <svg
                                                      xmlns="http://www.w3.org/2000/svg"
                                                      aria-hidden="true"
                                                      role="img"
                                                      width="1em"
                                                      height="1em"
                                                      preserveAspectRatio="xMidYMid meet"
                                                      viewBox="0 0 24 24"
                                                    >
                                                      <path
                                                        d="M15 16h-2V8h2m-4 8H9V8h2m1-6A10 10 0 0 0 2 12a10 10 0 0 0 10 10a10 10 0 0 0 10-10A10 10 0 0 0 12 2z"
                                                        fill="currentColor"
                                                      ></path>
                                                    </svg>
                                                  )}
                                                </>
                                              ),
                                              pause: (
                                                <>
                                                  {!debouncedPlaying ? (
                                                    <svg
                                                      xmlns="http://www.w3.org/2000/svg"
                                                      aria-hidden="true"
                                                      role="img"
                                                      width="1em"
                                                      height="1em"
                                                      preserveAspectRatio="xMidYMid meet"
                                                      viewBox="0 0 24 24"
                                                    >
                                                      <path
                                                        d="M10 16.5v-9l6 4.5M12 2A10 10 0 0 0 2 12a10 10 0 0 0 10 10a10 10 0 0 0 10-10A10 10 0 0 0 12 2z"
                                                        fill="currentColor"
                                                      ></path>
                                                    </svg>
                                                  ) : (
                                                    <svg
                                                      xmlns="http://www.w3.org/2000/svg"
                                                      aria-hidden="true"
                                                      role="img"
                                                      width="1em"
                                                      height="1em"
                                                      preserveAspectRatio="xMidYMid meet"
                                                      viewBox="0 0 24 24"
                                                    >
                                                      <path
                                                        d="M15 16h-2V8h2m-4 8H9V8h2m1-6A10 10 0 0 0 2 12a10 10 0 0 0 10 10a10 10 0 0 0 10-10A10 10 0 0 0 12 2z"
                                                        fill="currentColor"
                                                      ></path>
                                                    </svg>
                                                  )}
                                                </>
                                              ),
                                            }}
                                            style={{
                                              pointerEvents: 'initial',
                                              paddingLeft:
                                                width < 641 ? '20px' : '30px',
                                              border: 'none',
                                              boxShadow: 'none',
                                              width: audioPlayerWidth,
                                              float: 'left',
                                              display: 'block',
                                              marginTop: '50px',
                                              marginLeft:
                                                width > 640 && !largeDisplay
                                                  ? '110px'
                                                  : '0px',
                                              zIndex: 1000,
                                              top: 0,
                                              left: largeDisplay
                                                ? 0
                                                : width > 640
                                                  ? 100
                                                  : 0,
                                            }}
                                            onListen={(e: any) => {
                                              try {
                                                setLoadedData(true)
                                                if (e?.target) {
                                                  let newStreamCount: any =
                                                    song_StreamCount_Master +
                                                    1350
                                                  setSong_StreamCount_Master(
                                                    newStreamCount,
                                                  )
                                                  if (
                                                    newStreamCount > 30000 &&
                                                    newStreamCount < 31550
                                                  ) {
                                                    let streamEnd: Date =
                                                      new Date()
                                                    let timeoutId: NodeJS.Timeout | null =
                                                      setTimeout(async () => {
                                                        await processStream(
                                                          newStreamCount,
                                                          song_StreamStart_Master,
                                                          streamEnd,
                                                          song_Master?.contract_address,
                                                          song_Master?.token_id,
                                                        )
                                                          .then((r: any) => {
                                                            if (DEBUG_MODE)
                                                              console.log(r)
                                                          })
                                                          .catch((e: any) => {
                                                            if (DEBUG_MODE)
                                                              console.log(e)
                                                            logSentryError(
                                                              e,
                                                              SENTRY_COMPONENT_NAME,
                                                            )
                                                          })
                                                      }, 10)
                                                    setProcessStreamTimeoutId_2(
                                                      timeoutId,
                                                    )
                                                  }
                                                }
                                              } catch (e) {
                                                if (DEBUG_MODE) console.log(e)
                                                logSentryError(
                                                  e,
                                                  SENTRY_COMPONENT_NAME,
                                                )
                                              }
                                            }}
                                            onError={async (e: any) => {
                                              if (DEBUG_MODE) console.log(e)
                                              logSentryError(
                                                e,
                                                SENTRY_COMPONENT_NAME,
                                              )
                                              audioControlsTempDisabled.current = false
                                              if (isOnline) await nextSong(null)
                                              setLoadedData(true)
                                            }}
                                            onPlayError={async (e: any) => {
                                              if (DEBUG_MODE) console.log(e)
                                              logSentryError(
                                                e,
                                                SENTRY_COMPONENT_NAME,
                                              )
                                            }}
                                            onStalled={async (e: any) => {
                                              if (DEBUG_MODE) console.log(e)
                                            }}
                                            onCanPlay={(e: any) => {
                                              try {
                                                if (!canShowAudioPlayer) {
                                                  setCanShowAudioPlayer(true)
                                                }
                                                if (DEBUG_MODE) console.log(e)
                                                setLoadedData(true)
                                              } catch (er) {
                                                if (DEBUG_MODE) console.log(er)
                                              }
                                            }}
                                            onChangeCurrentTimeError={() => {
                                              try {
                                                setLoadedData(true)
                                              } catch (er) {
                                                if (DEBUG_MODE) console.log(er)
                                                logSentryError(
                                                  er,
                                                  SENTRY_COMPONENT_NAME,
                                                )
                                              }
                                            }}
                                            onEmptied={() => {
                                              try {
                                                setLoadedData(true)
                                              } catch (er) {
                                                if (DEBUG_MODE) console.log(er)
                                                logSentryError(
                                                  er,
                                                  SENTRY_COMPONENT_NAME,
                                                )
                                              }
                                            }}
                                            onLoadedData={() => {
                                              try {
                                                setLoadedData(true)
                                              } catch (e) {
                                                if (DEBUG_MODE) console.log(e)
                                                logSentryError(
                                                  e,
                                                  SENTRY_COMPONENT_NAME,
                                                )
                                              }
                                            }}
                                            onPause={() => {
                                              try {
                                                setLoadedData(true)
                                                setPlaying(false)
                                              } catch (er) {
                                                if (DEBUG_MODE) console.log(er)
                                                logSentryError(
                                                  er,
                                                  SENTRY_COMPONENT_NAME,
                                                )
                                              }
                                            }}
                                            onPlaying={async () => {
                                              try {
                                                if (
                                                  //@ts-ignore
                                                  player?.current?.audio
                                                    ?.current?.src ==
                                                  AUDIO_SILENT_SRC
                                                )
                                                  return
                                                else {
                                                  setLoadedData(true)
                                                  upsertMediaSessionState(
                                                    'playing',
                                                  )
                                                  setPlaying(true)
                                                }
                                              } catch (er) {
                                                if (DEBUG_MODE) console.log(er)
                                                logSentryError(
                                                  er,
                                                  SENTRY_COMPONENT_NAME,
                                                )
                                              }
                                            }}
                                            onSeeked={() => {
                                              try {
                                                setLoadedData(true)
                                                setSong_StreamCount_Master(0)
                                                setSong_StreamStart_Master(null)
                                              } catch (er) {
                                                if (DEBUG_MODE) console.log(er)
                                                logSentryError(
                                                  er,
                                                  SENTRY_COMPONENT_NAME,
                                                )
                                              }
                                            }}
                                            onCanPlayThrough={async () => {
                                              try {
                                                setLoadedData(true)
                                              } catch (er) {
                                                if (DEBUG_MODE) console.log(er)
                                                logSentryError(
                                                  er,
                                                  SENTRY_COMPONENT_NAME,
                                                )
                                              }
                                            }}
                                            onSuspend={async () => {
                                              try {
                                              } catch (er) {
                                                if (DEBUG_MODE) console.log(er)
                                                logSentryError(
                                                  er,
                                                  SENTRY_COMPONENT_NAME,
                                                )
                                              }
                                            }}
                                            onLoadStart={() => {
                                              try {
                                                setLoadedData(false)
                                              } catch (er) {
                                                if (DEBUG_MODE) console.log(er)
                                                logSentryError(
                                                  er,
                                                  SENTRY_COMPONENT_NAME,
                                                )
                                              }
                                            }}
                                            onWaiting={() => {
                                              try {
                                                setLoadedData(true)
                                              } catch (er) {
                                                if (DEBUG_MODE) console.log(er)
                                                logSentryError(
                                                  er,
                                                  SENTRY_COMPONENT_NAME,
                                                )
                                              }
                                            }}
                                            onLoadedMetaData={() => {
                                              try {
                                              } catch (er) {
                                                if (DEBUG_MODE) console.log(er)
                                                logSentryError(
                                                  er,
                                                  SENTRY_COMPONENT_NAME,
                                                )
                                              }
                                            }}
                                            onAbort={() => {
                                              try {
                                                setLoadedData(true)
                                              } catch (er) {
                                                if (DEBUG_MODE) console.log(er)
                                                logSentryError(
                                                  er,
                                                  SENTRY_COMPONENT_NAME,
                                                )
                                              }
                                            }}
                                            onSeeking={() => {
                                              try {
                                                if (
                                                  //@ts-ignore
                                                  player?.current?.audio
                                                    ?.current?.src ==
                                                  AUDIO_SILENT_SRC
                                                )
                                                  notifySongStillLoading()
                                                else setLoadedData(true)
                                              } catch (er) {
                                                if (DEBUG_MODE) console.log(er)
                                                logSentryError(
                                                  er,
                                                  SENTRY_COMPONENT_NAME,
                                                )
                                              }
                                            }}
                                            onPlay={async () => {
                                              try {
                                                if (
                                                  //@ts-ignore
                                                  player?.current?.audio
                                                    ?.current?.src ==
                                                  AUDIO_SILENT_SRC
                                                )
                                                  notifySongStillLoading()
                                                else {
                                                  setLoadedData(true)
                                                  upsertMediaSessionState(
                                                    'playing',
                                                  )
                                                }
                                              } catch (er) {
                                                if (DEBUG_MODE) console.log(er)
                                                logSentryError(
                                                  er,
                                                  SENTRY_COMPONENT_NAME,
                                                )
                                              } finally {
                                                setSong_StreamCount_Master(0)
                                                setSong_StreamStart_Master(
                                                  new Date(),
                                                )
                                              }
                                            }}
                                            onClickNext={() => {
                                              const loaded: any = loadedData
                                              setPlaying(loaded)
                                              React.startTransition(() => {
                                                try {
                                                  if (
                                                    //@ts-ignore
                                                    player?.current?.audio
                                                      ?.current?.src ==
                                                    AUDIO_SILENT_SRC
                                                  )
                                                    notifySongStillLoading()
                                                  else if (
                                                    !audioControlsTempDisabled?.current
                                                  ) {
                                                    setLoadedData(true)
                                                    if (loaded) {
                                                      setSong_StreamCount_Master(
                                                        0,
                                                      )
                                                      setSong_StreamStart_Master(
                                                        null,
                                                      )
                                                      if (
                                                        !audioControlsTempDisabled?.current
                                                      ) {
                                                        nextSong(null)
                                                        upsertMediaSessionState(
                                                          'playing',
                                                        )
                                                      }
                                                    } else {
                                                      let tid: NodeJS.Timeout | null =
                                                        setTimeout(async () => {
                                                          try {
                                                            setSong_StreamCount_Master(
                                                              0,
                                                            )
                                                            setSong_StreamStart_Master(
                                                              null,
                                                            )
                                                            if (
                                                              !audioControlsTempDisabled?.current
                                                            ) {
                                                              await nextSong(
                                                                null,
                                                              )
                                                              await upsertMediaSessionState(
                                                                'playing',
                                                              )
                                                            }
                                                          } catch (e) {
                                                            if (DEBUG_MODE)
                                                              console.log(e)
                                                            logSentryError(
                                                              e,
                                                              SENTRY_COMPONENT_NAME,
                                                            )
                                                          } finally {
                                                            try {
                                                              if (tid != null) {
                                                                clearTimeout(
                                                                  tid,
                                                                )
                                                                tid = null
                                                              }
                                                            } catch {}
                                                          }
                                                        }, 1000)
                                                    }
                                                  }
                                                } catch (er) {
                                                  if (DEBUG_MODE)
                                                    console.log(er)
                                                  logSentryError(
                                                    er,
                                                    SENTRY_COMPONENT_NAME,
                                                  )
                                                }
                                              })
                                            }}
                                            onClickPrevious={async () => {
                                              const loaded: any = loadedData
                                              setPlaying(loaded)
                                              React.startTransition(() => {
                                                try {
                                                  if (
                                                    //@ts-ignore
                                                    player?.current?.audio
                                                      ?.current?.src ==
                                                    AUDIO_SILENT_SRC
                                                  )
                                                    notifySongStillLoading()
                                                  else if (
                                                    !audioControlsTempDisabled?.current
                                                  ) {
                                                    setLoadedData(true)
                                                    if (loaded) {
                                                      setSong_StreamCount_Master(
                                                        0,
                                                      )
                                                      setSong_StreamStart_Master(
                                                        null,
                                                      )
                                                      if (
                                                        !audioControlsTempDisabled?.current
                                                      ) {
                                                        prevSong(null)
                                                        upsertMediaSessionState(
                                                          'playing',
                                                        )
                                                      }
                                                    } else {
                                                      let tid: NodeJS.Timeout | null =
                                                        setTimeout(async () => {
                                                          try {
                                                            setSong_StreamCount_Master(
                                                              0,
                                                            )
                                                            setSong_StreamStart_Master(
                                                              null,
                                                            )
                                                            if (
                                                              !audioControlsTempDisabled?.current
                                                            ) {
                                                              await prevSong(
                                                                null,
                                                              )
                                                              await upsertMediaSessionState(
                                                                'playing',
                                                              )
                                                            }
                                                          } catch (e) {
                                                            if (DEBUG_MODE)
                                                              console.log(e)
                                                            logSentryError(
                                                              e,
                                                              SENTRY_COMPONENT_NAME,
                                                            )
                                                          } finally {
                                                            try {
                                                              if (tid != null) {
                                                                clearTimeout(
                                                                  tid,
                                                                )
                                                                tid = null
                                                              }
                                                            } catch {}
                                                          }
                                                        }, 1000)
                                                    }
                                                  }
                                                } catch (er) {
                                                  if (DEBUG_MODE)
                                                    console.log(er)
                                                  logSentryError(
                                                    er,
                                                    SENTRY_COMPONENT_NAME,
                                                  )
                                                }
                                              })
                                            }}
                                            onEnded={() => {
                                              React.startTransition(() => {
                                                try {
                                                  setLoadedData(true)
                                                  setSong_StreamCount_Master(0)
                                                  setSong_StreamStart_Master(
                                                    null,
                                                  )
                                                  if (
                                                    !audioControlsTempDisabled?.current &&
                                                    !hasUsedNonSubscribedPlayLimit &&
                                                    isOnline
                                                  ) {
                                                    nextSong(null)
                                                    upsertMediaSessionState(
                                                      'playing',
                                                    )
                                                  }
                                                } catch (er) {
                                                  if (DEBUG_MODE)
                                                    console.log(er)
                                                  logSentryError(
                                                    er,
                                                    SENTRY_COMPONENT_NAME,
                                                  )
                                                }
                                              })
                                            }}
                                            onVolumeChange={async () => {
                                              try {
                                                setLoadedData(true)
                                              } catch (er) {
                                                if (DEBUG_MODE) console.log(er)
                                                logSentryError(
                                                  er,
                                                  SENTRY_COMPONENT_NAME,
                                                )
                                              }
                                            }}
                                          />
                                        </>
                                      )}
                                  </div>
                                </div>
                                {canShowCoverArtInPlayer && (
                                  <>
                                    <CoverArtImage
                                      boxShadowCoverArtGenreThumb={
                                        boxShadowCoverArtGenreThumb
                                      }
                                      primaryImagePlaceholder={
                                        primaryImagePlaceholder
                                      }
                                      setHoverImage={setHoverImage}
                                      hoverImage={hoverImage}
                                      songCoverImageLocalOrExternalURL_Master={
                                        songCoverImageLocalOrExternalURL_Master
                                      }
                                      selectedLanguage={selectedLanguage}
                                      logSentryError={logSentryError}
                                      resumeSong={resumeSong}
                                      pauseSong={pauseSong}
                                      debouncedPlaying={debouncedPlaying}
                                      playing={playing}
                                      dark={dark}
                                    />
                                  </>
                                )}
                              </div>
                            </div>
                            {USE_VERCEL_ANALYTICS && !isPracticalPod && (
                              <Analytics debug={!IS_PRODUCTION} />
                            )}
                            {!isViewportTooSmall ? (
                              <>
                                <Layout
                                  allSongsArtistsGenreContractsLoaded={
                                    allSongsArtistsGenreContractsLoaded
                                  }
                                  hasInvitationQs={hasInvitationQs}
                                  gotFavoritesDebounced={gotFavoritesDebounced}
                                  isPwaInstallableApple={isPwaInstallableApple}
                                  openedReturnsModal={openedReturnsModal}
                                  setOpenedReturnsModal={setOpenedReturnsModal}
                                  isSubscribePassOrSubscribePassReloaded={
                                    isSubscribePassOrSubscribePassReloaded
                                  }
                                  showAppNeedsToReloadModal={
                                    showAppNeedsToReloadModal
                                  }
                                  isInvitationPass={isInvitationPass}
                                  setLastClickedPlaylistId={
                                    setLastClickedPlaylistId
                                  }
                                  wholeSongSetBound={wholeSongSetBound}
                                  isRunningPwa={isRunningPwa}
                                  activeRoute={pageProps?.initialRoute}
                                  currentRouterPath={currentRouterPath}
                                  firstNavigatedComponent={
                                    firstNavigatedComponent
                                  }
                                  debouncedPlayingLong={debouncedPlayingLong}
                                  openFooterItem={openFooterItem}
                                  setOpenFooterItem={setOpenFooterItem}
                                  hasShownOpenFooterItem={
                                    hasShownOpenFooterItem
                                  }
                                  setHasShownOpenFooterItem={
                                    setHasShownOpenFooterItem
                                  }
                                  primaryImagePlaceholder={
                                    primaryImagePlaceholder
                                  }
                                  avifCompatible={avifCompatible}
                                  canShowAudioPlayer={canShowAudioPlayer}
                                  userCanMakePlaylists={userCanMakePlaylists}
                                  logSentryError={logSentryError}
                                  isPracticalPod={isPracticalPod}
                                  gotFavorites={gotFavorites}
                                  navigateRoute={navigateRoute}
                                  navigating={navigating}
                                  songs_Master={songs_Master}
                                  isErrorPage={dontLoad}
                                  song_Master={song_Master}
                                  share={share}
                                  pwaInstallShown={pwaInstallShown}
                                  pwaDeferredInstallEvent={
                                    pwaDeferredInstallEvent
                                  }
                                  acceptedPwaInstallation={
                                    acceptedPwaInstallation
                                  }
                                  setAcceptedPwaInstallation={
                                    setAcceptedPwaInstallation
                                  }
                                  setQuickSearchOpened={setQuickSearchOpened}
                                  quickSearchOpened={quickSearchOpened}
                                  copyErrorModalText={copyErrorModalText}
                                  copyErrorModalOpened={copyErrorModalOpened}
                                  setCopyErrorModalOpened={
                                    setCopyErrorModalOpened
                                  }
                                  setClipboardCopied={setClipboardCopied}
                                  clipboardCopied={clipboardCopied}
                                  setCopyErrorModalText={setCopyErrorModalText}
                                  setCopyKey={setCopyKey}
                                  copyKey={copyKey}
                                  scrollTop={scrollTop}
                                  user={user}
                                  dark={dark}
                                  setEnvironmentProtectionModalOpened={
                                    setEnvironmentProtectionModalOpened
                                  }
                                  environmentProtectionModalOpened={
                                    environmentProtectionModalOpened
                                  }
                                  setHasResolvedEnvironmentProtectionModal={
                                    setHasResolvedEnvironmentProtectionModal
                                  }
                                  isOnline={isOnline}
                                  pageComponentDisplayName={
                                    pageComponentDisplayName
                                  }
                                  setApplePwaHelpModalOpened={
                                    setApplePwaHelpModalOpened
                                  }
                                  applePwaHelpModalOpened={
                                    applePwaHelpModalOpened
                                  }
                                  setAppleMacPwaHelpModalOpened={
                                    setAppleMacPwaHelpModalOpened
                                  }
                                  appleMacPwaHelpModalOpened={
                                    appleMacPwaHelpModalOpened
                                  }
                                  notifications={notifications}
                                  selectedLanguage={selectedLanguage}
                                  setSelectLanguageModalOpened={
                                    setSelectLanguageModalOpened
                                  }
                                  afterClearAllNotificationsClicked={
                                    afterClearAllNotificationsClicked
                                  }
                                  notifyIsAuthenticatedChanged={
                                    notifyIsAuthenticatedChanged
                                  }
                                  songContractAddress={songContractAddress}
                                  songTokenId={songTokenId}
                                  isIOS={isIOS}
                                  isMacOs={isMacOs}
                                  isApple={isApple}
                                  isDesktop={isDesktop}
                                  isEdge={isEdge}
                                  isChrome={isChrome}
                                  isSafari={isSafari}
                                  isTablet={isTablet}
                                  isFirefox={isFirefox}
                                  isMobile={isMobile}
                                  isMobileOnly={isMobileOnly}
                                  isMobileSafari={isMobileSafari}
                                  isEdgeChromium={isEdgeChromium}
                                  isSamsungBrowser={isSamsungBrowser}
                                  resumeSong={resumeSong}
                                  pauseSong={pauseSong}
                                  addAndPlaySong={addAndPlaySong}
                                  playing={playing}
                                  setPlaying={setPlaying}
                                  toggleSubscribePitchModalOpened={
                                    toggleSubscribePitchModalOpened
                                  }
                                  toggleAddToPlaylistModalOpened={
                                    toggleAddToPlaylistModalOpened
                                  }
                                  toggleFavorite={toggleFavorite}
                                  userSubscribed={userSubscribed}
                                  setNavigating={setNavigating}
                                >
                                  {height > 500 && navigating ? (
                                    <FadeIn delay={0} transitionDuration={50}>
                                      <div
                                        className={
                                          isMobileOnly &&
                                          isIOS &&
                                          height > width
                                            ? !isOldIOS
                                              ? classes.loaderIphonePortrait
                                              : classes.loaderOldIphonePortrait
                                            : classes.loader
                                        }
                                      >
                                        <div className={'wrap'}>
                                          <div className={'boombox'}>
                                            <div className={'handle'}></div>
                                            <div className={'body'}>
                                              <div className={'controls'}>
                                                <div
                                                  className={
                                                    'control-section-left'
                                                  }
                                                ></div>
                                                <div
                                                  className={
                                                    'control-section-right'
                                                  }
                                                ></div>
                                              </div>
                                              <div className={'left-speaker'}>
                                                <div className={'circle'}></div>
                                              </div>
                                              <div className={'right-speaker'}>
                                                <div className={'circle'}></div>
                                              </div>
                                              <div className={'display'}></div>
                                              <div className={'front-markings'}>
                                                <div className={'shape1'}></div>
                                                <div className={'shape2'}></div>
                                                <div className={'shape3'}></div>
                                              </div>
                                            </div>
                                          </div>
                                          <div className={'platform'}></div>
                                        </div>
                                      </div>
                                    </FadeIn>
                                  ) : isNotNullOrUndefined(width) &&
                                    navigating ? (
                                    <div
                                      style={{
                                        transform: smallLoaderOffset,
                                        marginTop: '50px',
                                        marginLeft: '15px',
                                      }}
                                    >
                                      <div className={'wrap'}>
                                        <div className={'boombox'}>
                                          <div className={'handle'}></div>
                                          <div className={'body'}>
                                            <div className={'controls'}>
                                              <div
                                                className={
                                                  'control-section-left'
                                                }
                                              ></div>
                                              <div
                                                className={
                                                  'control-section-right'
                                                }
                                              ></div>
                                            </div>
                                            <div className={'left-speaker'}>
                                              <div className={'circle'}></div>
                                            </div>
                                            <div className={'right-speaker'}>
                                              <div className={'circle'}></div>
                                            </div>
                                            <div className={'display'}></div>
                                            <div className={'front-markings'}>
                                              <div className={'shape1'}></div>
                                              <div className={'shape2'}></div>
                                              <div className={'shape3'}></div>
                                            </div>
                                          </div>
                                        </div>
                                        <div className={'platform'}></div>
                                      </div>
                                    </div>
                                  ) : (
                                    <></>
                                  )}
                                  <div
                                    style={{
                                      minHeight:
                                        width < 768
                                          ? 'var(--safe-viewport-calc-max-alt-7)'
                                          : 'var(--safe-viewport-calc-max-alt-3)',
                                      opacity: !anInvitationModalsOpened
                                        ? 1
                                        : 0,
                                    }}
                                  >
                                    {!softReloading && (
                                      <Component
                                        primaryImagePlaceholder={
                                          primaryImagePlaceholder
                                        }
                                        isEU={pageProps?.isEU}
                                        setStripeSubscriptions={
                                          setStripeSubscriptions
                                        }
                                        modifySubscriptionOriginalQuantity={
                                          modifySubscriptionOriginalQuantity
                                        }
                                        modifySubscriptionIsHardware={
                                          modifySubscriptionIsHardware
                                        }
                                        modifyStripeSubscriptionId={
                                          modifyStripeSubscriptionId
                                        }
                                        setModifySubscriptionOriginalQuantity={
                                          setModifySubscriptionOriginalQuantity
                                        }
                                        setModifySubscriptionIsHardware={
                                          setModifySubscriptionIsHardware
                                        }
                                        setModifyStripeSubscriptionId={
                                          setModifyStripeSubscriptionId
                                        }
                                        openToPlaylistScheduleTab={
                                          openToPlaylistScheduleTab
                                        }
                                        setOpenToPlaylistScheduleTab={
                                          setOpenToPlaylistScheduleTab
                                        }
                                        userLastDateWhenOverrodeExceptionDate={
                                          userLastDateWhenOverrodeExceptionDate
                                        }
                                        setTriggerPlaylistOverlayRefresh={
                                          setTriggerPlaylistOverlayRefresh
                                        }
                                        triggerPlaylistOverlayRefresh={
                                          triggerPlaylistOverlayRefresh
                                        }
                                        setUserLastDateWhenOverrodeExceptionDate={
                                          setUserLastDateWhenOverrodeExceptionDate
                                        }
                                        triggerMonitorScheduleFn={
                                          triggerMonitorScheduleFn
                                        }
                                        setLastPlayedDate_Conditional={
                                          setLastPlayedDate_Conditional
                                        }
                                        setLastPlayedDate_Conditional_One={
                                          setLastPlayedDate_Conditional_One
                                        }
                                        setLastPlayedDate_Conditional_Two={
                                          setLastPlayedDate_Conditional_Two
                                        }
                                        updateUserCredits={updateUserCredits}
                                        userCreditsBalance={userCreditsBalance}
                                        setSubscribePitchModalOpened={
                                          setSubscribePitchModalOpened
                                        }
                                        setMoveToPlaylistEditId={
                                          setMoveToPlaylistEditId
                                        }
                                        firstNavigatedComponent={
                                          firstNavigatedComponent
                                        }
                                        triggerRebindSchedule={
                                          triggerRebindSchedule
                                        }
                                        playing={playing}
                                        setPlaying={setPlaying}
                                        setTriggerRebindSchedule={
                                          setTriggerRebindSchedule
                                        }
                                        isBusinessUserSubscribed={
                                          isBusinessUserSubscribed
                                        }
                                        confirmedAcceptance={
                                          pageProps?.confirmedAcceptance
                                        }
                                        errorBuyCredits={
                                          pageProps?.errorBuyCredits
                                        }
                                        signalCheckClearIntervals={
                                          signalCheckClearIntervals
                                        }
                                        signalRefreshUser={signalRefreshUser}
                                        setSignalCheckClearIntervals={
                                          setSignalCheckClearIntervals
                                        }
                                        triggerUpdateAppStartupDate={
                                          triggerUpdateAppStartupDate
                                        }
                                        setTriggerUpdateAppStartupDate={
                                          setTriggerUpdateAppStartupDate
                                        }
                                        hideTimepickers={hideTimepickers}
                                        lastClickedPlaylistId={
                                          lastClickedPlaylistId
                                        }
                                        masterPlaylistsAndPresets={
                                          masterPlaylistsAndPresets
                                        }
                                        songsMasterOriginalLength={
                                          songs_Master_Original?.length
                                        }
                                        isAuthenticatedUserAndSubscribed={
                                          isAuthenticatedUserAndSubscribed
                                        }
                                        hasInvitationToken={hasInvitationToken}
                                        isSubscribePassOrSubscribePassReloaded={
                                          isSubscribePassOrSubscribePassReloaded
                                        }
                                        setTriggerRefreshSettingsPageUsersAndInvitations={
                                          setTriggerRefreshSettingsPageUsersAndInvitations
                                        }
                                        setOpenedReturnsModal={
                                          setOpenedReturnsModal
                                        }
                                        isInvitationPass={isInvitationPass}
                                        gotFavoritesDebounced={
                                          gotFavoritesDebounced
                                        }
                                        wholeSongSetBound={wholeSongSetBound}
                                        refreshSubscriptionsAndCharges={
                                          refreshSubscriptionsAndCharges
                                        }
                                        subscriptionButtonCurrencyText={
                                          subscriptionButtonCurrencyText
                                        }
                                        showAppNeedsToReloadModal={
                                          showAppNeedsToReloadModal
                                        }
                                        subscriptionButtonCurrencyText_Hardware_No_Suffix_Zeroes={
                                          subscriptionButtonCurrencyText_Hardware_No_Suffix_Zeroes
                                        }
                                        triggerRefreshSettingsPageUsersAndInvitations={
                                          triggerRefreshSettingsPageUsersAndInvitations
                                        }
                                        hasUsedNonSubscribedPlayLimit={
                                          hasUsedNonSubscribedPlayLimit
                                        }
                                        songs_MasterLength={
                                          songs_Master?.length
                                        }
                                        changingSongs_Master_PlaylistDelay={
                                          changingSongs_Master_PlaylistDelay
                                        }
                                        reactToPlaylistsChange={
                                          reactToPlaylistsChange
                                        }
                                        synchronizePlaydeckWithPlaylistChange={
                                          synchronizePlaydeckWithPlaylistChange
                                        }
                                        forceRedrawSearch={
                                          forceRedrawSearch?.current
                                        }
                                        presetSamples={presetSamples}
                                        masterArtists={masterArtists}
                                        logSentryError={logSentryError}
                                        avifCompatible={avifCompatible}
                                        playlistToRefreshWithoutReloading={
                                          playlistToRefreshWithoutReloading
                                        }
                                        setPlaylistToRefreshWithoutReloading={
                                          setPlaylistToRefreshWithoutReloading
                                        }
                                        canShowAudioPlayer={canShowAudioPlayer}
                                        userCanMakePlaylists={
                                          userCanMakePlaylists
                                        }
                                        userCanSetSchedule={userCanSetSchedule}
                                        userCanPlayPresets={userCanPlayPresets}
                                        userCanPlayStations={
                                          userCanPlayStations
                                        }
                                        ipAddressCountryCode={
                                          ipAddressCountryCode
                                        }
                                        triggerSignIn={triggerSignIn}
                                        signalReenableLoginFromInvitationControls={
                                          signalReenableLoginFromInvitationControls
                                        }
                                        setSignalReenableLoginFromInvitationControls={
                                          setSignalReenableLoginFromInvitationControls
                                        }
                                        signalRefreshNotifications={
                                          signalRefreshNotifications
                                        }
                                        setSignalRefreshNotifications={
                                          setSignalRefreshNotifications
                                        }
                                        loginEmail={loginEmail}
                                        setLoginEmail={setLoginEmail}
                                        isPracticalPod={isPracticalPod}
                                        subscriptionExpiring={
                                          subscriptionExpiring
                                        }
                                        setSubscriptionExpiring={
                                          setSubscriptionExpiring
                                        }
                                        setSignalRefreshUser={
                                          setSignalRefreshUser
                                        }
                                        cancelStripeSubscriptionId={
                                          cancelStripeSubscriptionId
                                        }
                                        setCancelStripeSubscriptionId={
                                          setCancelStripeSubscriptionId
                                        }
                                        cancelSubscriptionQuantity={
                                          cancelSubscriptionQuantity
                                        }
                                        setCancelSubscriptionQuantity={
                                          setCancelSubscriptionQuantity
                                        }
                                        cancelSubscriptionIsHardware={
                                          cancelSubscriptionIsHardware
                                        }
                                        setCancelSubscriptionIsHardware={
                                          setCancelSubscriptionIsHardware
                                        }
                                        stripeCustomerId={stripeCustomerId}
                                        setStripeCustomerId={
                                          setStripeCustomerId
                                        }
                                        stripeCharges={stripeCharges}
                                        setStripeCharges={setStripeCharges}
                                        stripeSubscriptions={
                                          stripeSubscriptions
                                        }
                                        stripeActiveSeats={stripeActiveSeats}
                                        stripeActiveHardwareSeats={
                                          stripeActiveHardwareSeats
                                        }
                                        toggleFavorite={toggleFavorite}
                                        gotFavorites={gotFavorites}
                                        triggerGetMasterSchedule={
                                          triggerGetMasterSchedule
                                        }
                                        setTriggerGetMasterSchedule={
                                          setTriggerGetMasterSchedule
                                        }
                                        masterSchedule={masterSchedule}
                                        removeSongFromPlaylist={
                                          removeSongFromPlaylist
                                        }
                                        favoritesPageFavorites={
                                          favoritesPageFavorites
                                        }
                                        postPlaylistSongEntries={
                                          postPlaylistSongEntries
                                        }
                                        setTriggerGetFavorites={
                                          setTriggerGetFavorites
                                        }
                                        triggerGetFavorites={
                                          triggerGetFavorites
                                        }
                                        resumeSong={resumeSong}
                                        pauseSong={pauseSong}
                                        reverseDebouncedPlaying={
                                          reverseDebouncedPlaying
                                        }
                                        debouncedPlaying={debouncedPlaying}
                                        debouncedPlayingLong={
                                          debouncedPlayingLong
                                        }
                                        addAndPlaySong={addAndPlaySong}
                                        setPlaydeck={setPlaydeck}
                                        navigating={navigating}
                                        navigateRoute={navigateRoute}
                                        previousRoute={previousRoute?.current}
                                        currentRoute={currentRoute?.current}
                                        broadcastDimensions={
                                          broadcastDimensions
                                        }
                                        toggleColorScheme={toggleColorScheme}
                                        changingSongs_Master={
                                          changingSongs_Master
                                        }
                                        scrollTop={scrollTop}
                                        songGenres_Master={songGenres_Master}
                                        songs_Master={songs_Master}
                                        song_Master={song_Master}
                                        songUrl_Master={songUrl_Master}
                                        songName_Master={songName_Master}
                                        songContractAddress={
                                          songContractAddress
                                        }
                                        songTokenId={songTokenId}
                                        contractGenres={contractGenres}
                                        user={user}
                                        toggleAddToPlaylistModalOpened={
                                          toggleAddToPlaylistModalOpened
                                        }
                                        moveToPlaylistEditId={
                                          moveToPlaylistEditId
                                        }
                                        toggleSubscribePitchModalOpened={
                                          toggleSubscribePitchModalOpened
                                        }
                                        pwaInstallShown={pwaInstallShown}
                                        pwaDeferredInstallEvent={
                                          pwaDeferredInstallEvent
                                        }
                                        acceptedPwaInstallation={
                                          acceptedPwaInstallation
                                        }
                                        setAcceptedPwaInstallation={
                                          setAcceptedPwaInstallation
                                        }
                                        subscriptionPrice={subscriptionPrice}
                                        stripeOrVercelHeaderBasedCurrency={
                                          stripeOrVercelHeaderBasedCurrency
                                        }
                                        pageComponentDisplayName={
                                          pageComponentDisplayName
                                        }
                                        isOnline={isOnline}
                                        environmentProtectionModalOpened={
                                          environmentProtectionModalOpened &&
                                          USE_ENV_PROTECTION_MODAL
                                        }
                                        copyErrorModalText={copyErrorModalText}
                                        copyErrorModalOpened={
                                          copyErrorModalOpened
                                        }
                                        setCopyErrorModalOpened={
                                          setCopyErrorModalOpened
                                        }
                                        hideComponentContentForInvitationModal={
                                          confirmInvitationAcceptanceModalJustClosed ||
                                          hideComponentContentForInvitationModal
                                        }
                                        setClipboardCopied={setClipboardCopied}
                                        clipboardCopied={clipboardCopied}
                                        setCopyErrorModalText={
                                          setCopyErrorModalText
                                        }
                                        setCopyKey={setCopyKey}
                                        copyKey={copyKey}
                                        playlistsChanged={playlistsChanged}
                                        isIOS={isIOS}
                                        isEdge={isEdge}
                                        isApple={isApple}
                                        isMacOs={isMacOs}
                                        isOpera={isOpera}
                                        isTablet={isTablet}
                                        isMobile={isMobile}
                                        isChrome={isChrome}
                                        isSafari={isSafari}
                                        isAndroid={isAndroid}
                                        isFirefox={isFirefox}
                                        isDesktop={isDesktop}
                                        isWindows={isWindows}
                                        isMobileOnly={isMobileOnly}
                                        isMobileSafari={isMobileSafari}
                                        isEdgeChromium={isEdgeChromium}
                                        isSamsungBrowser={isSamsungBrowser}
                                        notifications={notifications}
                                        selectedLanguage={selectedLanguage}
                                        toggleSelectedLanguage={
                                          toggleSelectedLanguage
                                        }
                                        clearAllNotificationsClicked={
                                          clearAllNotificationsClicked
                                        }
                                        setLastClickedPlaylistId={
                                          setLastClickedPlaylistId
                                        }
                                        subscribed={subscribed}
                                        setSubscribed={setSubscribed}
                                        subscriptionEnd={subscriptionEnd}
                                        setSubscriptionEnd={setSubscriptionEnd}
                                        subscriptionElapsed={
                                          subscriptionElapsed
                                        }
                                        dark={dark}
                                        signalRefreshMasterPlaylists={
                                          signalRefreshMasterPlaylists
                                        }
                                        setSignalRefreshMasterPlaylists={
                                          setSignalRefreshMasterPlaylists
                                        }
                                        hasResolvedStripeActiveSoftwareOrHardwareSeats={
                                          hasResolvedStripeActiveSoftwareOrHardwareSeats
                                        }
                                        {...pageProps}
                                      />
                                    )}
                                  </div>
                                </Layout>
                                {web2UserIdLoggedIn == true &&
                                  !softReloading && (
                                    <>
                                      <_App_UserLoggedIn
                                        setCreditsStripeConfirmationCode={
                                          setCreditsStripeConfirmationCode
                                        }
                                        // triggerResumePlayOnPracticalPodPowerButtonClick={
                                        //   triggerResumePlayOnPracticalPodPowerButtonClick_State
                                        // }
                                        // resumeSong={resumeSong}
                                        // playing={playing}
                                        // wasPlayingLessThanFiveSecondsAgo={
                                        //   wasPlayingLessThanFiveSecondsAgo
                                        // }
                                        creditsReady={creditsReady}
                                        setHasOfflineTracks={
                                          setHasOfflineTracks
                                        }
                                        setCreditsReady={setCreditsReady}
                                        userCreditsBalance={userCreditsBalance}
                                        setUserCreditsBalance={
                                          setUserCreditsBalance
                                        }
                                        creditsStripeConfirmationCode={
                                          creditsStripeConfirmationCode
                                        }
                                        cleanRouterUrl={cleanRouterUrl}
                                        userSubscribed={userSubscribed}
                                        gotFavorites={gotFavorites}
                                        user={user}
                                        setSong_Master_Unfavorited={
                                          setSong_Master_Unfavorited
                                        }
                                        setIsPlayingPlaylistScheduled={
                                          setIsPlayingPlaylistScheduled
                                        }
                                        setIsPlayingPlaylistExceptionDate={
                                          setIsPlayingPlaylistExceptionDate
                                        }
                                        setIsPlayingPlaylistExceptionDateHoliday={
                                          setIsPlayingPlaylistExceptionDateHoliday
                                        }
                                        setScheduledPlaylistScheduled_End={
                                          setScheduledPlaylistScheduled_End
                                        }
                                        setScheduledPlaylistScheduled_Begin={
                                          setScheduledPlaylistScheduled_Begin
                                        }
                                        song_Master={song_Master}
                                        songs_Master={songs_Master}
                                        setSong_Master={setSong_Master}
                                        setSongs_Master={setSongs_Master}
                                        setStripeCustomerId={
                                          setStripeCustomerId
                                        }
                                        setScheduledPlaylistId={
                                          setScheduledPlaylistId
                                        }
                                        setCopyErrorModalOpened={
                                          setCopyErrorModalOpened
                                        }
                                        setFavoritesPageFavorites={
                                          setFavoritesPageFavorites
                                        }
                                        toggleAddToPlaylistModalOpened={
                                          toggleAddToPlaylistModalOpened
                                        }
                                        toggleSubscribePitchModalOpened={
                                          toggleAddToPlaylistModalOpened
                                        }
                                        scrollTop={scrollTop}
                                        lastLogin={lastLogin}
                                        setTriggerGetMasterSchedule={
                                          setTriggerGetMasterSchedule
                                        }
                                        triggerGetMasterSchedule={
                                          triggerGetMasterSchedule
                                        }
                                        triggerGetMasterScheduleRef={
                                          triggerGetMasterScheduleRef
                                        }
                                        signalCheckClearIntervals={
                                          signalCheckClearIntervals
                                        }
                                        setSignalCheckClearIntervals={
                                          setSignalCheckClearIntervals
                                        }
                                        checkNotificationsIntervalId={
                                          checkNotificationsIntervalId?.current
                                        }
                                        setCheckNotificationsIntervalId={
                                          setCheckNotificationsIntervalId
                                        }
                                        logSentryError={logSentryError}
                                        web2UserSessionId={web2UserSessionId}
                                        web2UserSessionEmail={
                                          web2UserSessionEmail
                                        }
                                        web2UserIdLoggedIn={web2UserIdLoggedIn}
                                        hasDeterminedIsPracticalPod={
                                          hasDeterminedIsPracticalPod
                                        }
                                        isPracticalPod={isPracticalPod}
                                        refreshUser={refreshUser}
                                        notifications={notifications}
                                        setNotifications={setNotifications}
                                        refreshSubscriptionsAndCharges={
                                          refreshSubscriptionsAndCharges
                                        }
                                        signalRefreshUser={signalRefreshUser}
                                        setSignalRefreshUser={
                                          setSignalRefreshUser
                                        }
                                        signalRefreshNotifications={
                                          signalRefreshNotifications
                                        }
                                        web2UserIdLoggedInDebounced={
                                          web2UserIdLoggedInDebounced
                                        }
                                        stripeCustomerId={
                                          user?.customer?.stripeCustomerId
                                        }
                                        setUserCanMakePlaylists={
                                          setUserCanMakePlaylists
                                        }
                                        setUserCanSetSchedule={
                                          setUserCanSetSchedule
                                        }
                                        setUserCanPlayPresets={
                                          setUserCanPlayPresets
                                        }
                                        setUserCanPlayStations={
                                          setUserCanPlayStations
                                        }
                                        canSetSchedule={user?.canSetSchedule}
                                        canPlayPresets={user?.canPlayPresets}
                                        canPlayStations={user?.canPlayStations}
                                        adminWeb2UserId={user?.adminWeb2UserId}
                                        canMakePlaylists={
                                          user?.canMakePlaylists
                                        }
                                        pageComponentDisplayName={
                                          pageComponentDisplayName
                                        }
                                        toggleColorScheme={toggleColorScheme}
                                        darkModeDefault={user?.darkModeDefault}
                                        setAddedWakeLockHandler={
                                          setAddedWakeLockHandler
                                        }
                                        addedWakeLockHandler={
                                          addedWakeLockHandler
                                        }
                                        masterScheduleEnabled={
                                          masterSchedule?.enabled
                                        }
                                        upsertMediaSession={upsertMediaSession}
                                        selectedLanguage={selectedLanguage}
                                      />
                                      {(hasInteracted || isPracticalPod) &&
                                        isOnline && (
                                          <ScheduleMonitor
                                            softReload={softReload}
                                            web2UserId={web2UserSessionId}
                                            isOnline={isOnline}
                                            triggerMonitorSchedule={
                                              triggerMonitorSchedule_State
                                            }
                                            showStreamingInTwoOrMoreLocationsModal={
                                              showStreamingInTwoOrMoreLocationsModal
                                            }
                                            userLastDateWhenOverrodeExceptionDate={
                                              userLastDateWhenOverrodeExceptionDate
                                            }
                                            setUserLastDateWhenOverrodeExceptionDate={
                                              setUserLastDateWhenOverrodeExceptionDate
                                            }
                                            turnOnHasPausedForCurrentTimeWithinOneMinuteAfterEndOfSchedule={
                                              turnOnHasPausedForCurrentTimeWithinOneMinuteAfterEndOfSchedule
                                            }
                                            turnOnHasPausedForCurrentTimeWithinOneMinuteAfterEndOfSchedule_One={
                                              turnOnHasPausedForCurrentTimeWithinOneMinuteAfterEndOfSchedule_One
                                            }
                                            turnOnHasPausedForCurrentTimeWithinOneMinuteAfterEndOfSchedule_Two={
                                              turnOnHasPausedForCurrentTimeWithinOneMinuteAfterEndOfSchedule_Two
                                            }
                                            isBusinessUserSubscribed={
                                              isBusinessUserSubscribed
                                            }
                                            masterSchedule={masterSchedule}
                                            masterPlaylistsAndPresets={
                                              masterPlaylistsAndPresets
                                            }
                                            deviceIsAboutToRestartModalOpened={
                                              deviceIsAboutToRestartModalOpened
                                            }
                                            hardwareUserNotLoggedIntoPracticalPod={
                                              hardwareUserNotLoggedIntoPracticalPod
                                            }
                                            hasPausedForCurrentTimeWithinOneMinuteAfterEndOfSchedule={
                                              hasPausedForCurrentTimeWithinOneMinuteAfterEndOfSchedule
                                            }
                                            hasPausedForCurrentTimeWithinOneMinuteAfterEndOfSchedule_One={
                                              hasPausedForCurrentTimeWithinOneMinuteAfterEndOfSchedule_One
                                            }
                                            hasPausedForCurrentTimeWithinOneMinuteAfterEndOfSchedule_Two={
                                              hasPausedForCurrentTimeWithinOneMinuteAfterEndOfSchedule_Two
                                            }
                                            setScheduledPlaylistScheduled_Begin={
                                              setScheduledPlaylistScheduled_Begin
                                            }
                                            setScheduledPlaylistScheduled_End={
                                              setScheduledPlaylistScheduled_End
                                            }
                                            setIsPlayingPlaylistScheduled={
                                              setIsPlayingPlaylistScheduled
                                            }
                                            setIsPlayingPlaylistExceptionDate={
                                              setIsPlayingPlaylistExceptionDate
                                            }
                                            setIsPlayingPlaylistExceptionDateHoliday={
                                              setIsPlayingPlaylistExceptionDateHoliday
                                            }
                                            setScheduledPlaylistId={
                                              setScheduledPlaylistId
                                            }
                                            refreshPlaylistPlayingOverlay={
                                              refreshPlaylistPlayingOverlay
                                            }
                                            appStartupDate={appStartupDate}
                                            userDeviceRestartTime={
                                              userDeviceRestartTime
                                            }
                                            userAppReloadTime={
                                              userAppReloadTime
                                            }
                                            songs_Master={songs_Master}
                                            lastClickedPlaylistId={
                                              lastClickedPlaylistId
                                            }
                                            debouncedPlaying={debouncedPlaying}
                                            lastTeedUpDate={lastTeedUpDate}
                                            lastPlayedDate={lastPlayedDate}
                                            lastTeedUpDate_One={
                                              lastTeedUpDate_One
                                            }
                                            lastPlayedDate_One={
                                              lastPlayedDate_One
                                            }
                                            lastTeedUpDate_Two={
                                              lastTeedUpDate_Two
                                            }
                                            lastPlayedDate_Two={
                                              lastPlayedDate_Two
                                            }
                                            isPracticalPod={isPracticalPod}
                                            logSentryError={logSentryError}
                                            performRestart={performRestart}
                                            setLastTeedUpDate_Conditional={
                                              setLastTeedUpDate_Conditional
                                            }
                                            setLastPlayedDate_Conditional={
                                              setLastPlayedDate_Conditional
                                            }
                                            setLastTeedUpDate_Conditional_One={
                                              setLastTeedUpDate_Conditional_One
                                            }
                                            setLastPlayedDate_Conditional_One={
                                              setLastPlayedDate_Conditional_One
                                            }
                                            setLastTeedUpDate_Conditional_Two={
                                              setLastTeedUpDate_Conditional_Two
                                            }
                                            setLastPlayedDate_Conditional_Two={
                                              setLastPlayedDate_Conditional_Two
                                            }
                                            playScheduledPlaylist={
                                              playScheduledPlaylist
                                            }
                                            wasPausedLessThanOneMinuteAgo={
                                              wasPausedLessThanOneMinuteAgo
                                            }
                                            wasScheduledPlaylistPlayedLessThanOneMinuteAgo={
                                              wasScheduledPlaylistPlayedLessThanOneMinuteAgo
                                            }
                                            wasScheduledPlaylistPlayedLessThanOneMinuteAgo_One={
                                              wasScheduledPlaylistPlayedLessThanOneMinuteAgo_One
                                            }
                                            wasScheduledPlaylistPlayedLessThanOneMinuteAgo_Two={
                                              wasScheduledPlaylistPlayedLessThanOneMinuteAgo_Two
                                            }
                                            pauseSong={pauseSong}
                                            setPlayingPlayingOverlayOpened={
                                              setPlayingPlayingOverlayOpened
                                            }
                                            setHasPausedForCurrentTimeWithinOneMinuteAfterEndOfSchedule={
                                              setHasPausedForCurrentTimeWithinOneMinuteAfterEndOfSchedule
                                            }
                                            setHasPausedForCurrentTimeWithinOneMinuteAfterEndOfSchedule_One={
                                              setHasPausedForCurrentTimeWithinOneMinuteAfterEndOfSchedule_One
                                            }
                                            setHasPausedForCurrentTimeWithinOneMinuteAfterEndOfSchedule_Two={
                                              setHasPausedForCurrentTimeWithinOneMinuteAfterEndOfSchedule_Two
                                            }
                                          />
                                        )}
                                    </>
                                  )}
                              </>
                            ) : (
                              <div
                                style={{
                                  width: '100vw',
                                  height: 'var(--overlay-height)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <p style={{ padding: '7px' }}>
                                  {translate(
                                    <>Your viewport is too small.</>,
                                    <>
                                      Su ventana gráfica es demasiado pequeña.
                                    </>,
                                    <>
                                      Votre fenêtre d'affichage est trop petite.
                                    </>,
                                    <>
                                      <span aria-label="뷰포트가 너무 작습니다">
                                        <span
                                          className={classes.asianTextBlock}
                                          aria-hidden={true}
                                        >
                                          뷰포트가
                                        </span>{' '}
                                        <span
                                          className={classes.asianTextBlock}
                                          aria-hidden={true}
                                        >
                                          너무
                                        </span>{' '}
                                        <span
                                          className={classes.asianTextBlock}
                                          aria-hidden={true}
                                        >
                                          작습니다
                                        </span>
                                      </span>
                                    </>,
                                    selectedLanguage,
                                  )}
                                </p>
                              </div>
                            )}
                          </>
                        )}
                        {canShowPlaylistPlayingOverlay ? (
                          <PlaylistPlayingOverlay
                            muted={muted}
                            unmute={unmute}
                            setOverrideExceptionDateModalOpened={
                              setOverrideExceptionDateModalOpened
                            }
                            triggerPlaylistOverlayRefresh={
                              triggerPlaylistOverlayRefresh
                            }
                            lastClickedPlaylistId={lastClickedPlaylistId}
                            triggerMonitorScheduleFn={triggerMonitorScheduleFn}
                            userLastDateWhenOverrodeExceptionDate={
                              userLastDateWhenOverrodeExceptionDate
                            }
                            setUserLastDateWhenOverrodeExceptionDate={
                              setUserLastDateWhenOverrodeExceptionDate
                            }
                            logSentryError={logSentryError}
                            setMoveToPlaylistEditId={setMoveToPlaylistEditId}
                            songs_Master={songs_Master}
                            playlistSongs={lastClickedPlaylistSongs}
                            playlistName={lastClickedPlaylistName}
                            playlistId={lastClickedPlaylistId}
                            isPlayingPlaylistScheduled={
                              isPlayingPlaylistScheduled
                            }
                            isPlayingPlaylistExceptionDate={
                              isPlayingPlaylistExceptionDate
                            }
                            isPlayingPlaylistExceptionDateHoliday={
                              isPlayingPlaylistExceptionDateHoliday
                            }
                            scheduledPlaylistScheduled_Begin={
                              scheduledPlaylistScheduled_Begin
                            }
                            scheduledPlaylistScheduled_End={
                              scheduledPlaylistScheduled_End
                            }
                            scheduledPlaylistId={scheduledPlaylistId}
                            pageComponentDisplayName={pageComponentDisplayName}
                            navigating={navigating}
                            navigateRoute={navigateRoute}
                            debouncedPlaying={debouncedPlaying}
                            debouncedPlayingLong={debouncedPlayingLong}
                            changingSongs_Master_PlaylistDelay={
                              changingSongs_Master_PlaylistDelay
                            }
                            dark={dark}
                            isFirefox={isFirefox}
                            isPracticalPod={isPracticalPod}
                            isMobileOnly={isMobileOnly}
                            isIOS={isIOS}
                            isMacOs={isMacOs}
                            isApple={isApple}
                            selectedLanguage={selectedLanguage}
                            masterPlaylistsAndPresets={
                              masterPlaylistsAndPresets
                            }
                            setLastClickedPlaylistId={setLastClickedPlaylistId}
                            setPlaydeck={setPlaydeck}
                            stopSong={stopSong}
                          />
                        ) : (
                          canShowInstallPwaOverlay && (
                            <InstallPwaOverlay
                              setFadingInPwaInstallOverlay={
                                setFadingInPwaInstallOverlay
                              }
                              setCookie={setCookie}
                              setDismissedInstallPwaOverlay={
                                setDismissedInstallPwaOverlay
                              }
                              primaryImagePlaceholder={primaryImagePlaceholder}
                              logSentryError={logSentryError}
                              avifCompatible={avifCompatible}
                              pwaDeferredInstallEvent={pwaDeferredInstallEvent}
                              setAcceptedPwaInstallation={
                                setAcceptedPwaInstallation
                              }
                              fadingInPwaInstallOverlay={
                                fadingInPwaInstallOverlay
                              }
                              dark={dark}
                              isMobileOnly={isMobileOnly}
                              isIOS={isIOS}
                              isMacOs={isMacOs}
                              isApple={isApple}
                              setApplePwaHelpModalOpened={
                                setApplePwaHelpModalOpened
                              }
                              applePwaHelpModalOpened={applePwaHelpModalOpened}
                              setAppleMacPwaHelpModalOpened={
                                setAppleMacPwaHelpModalOpened
                              }
                              appleMacPwaHelpModalOpened={
                                appleMacPwaHelpModalOpened
                              }
                              selectedLanguage={selectedLanguage}
                            />
                          )
                        )}
                        <p aria-hidden="true">
                          {keepTabAwakeDOMManipulationSwitch ? (
                            <>&nbsp;</>
                          ) : (
                            <>&nbsp;&nbsp;</>
                          )}
                        </p>
                      </main>
                    </NotificationsProvider>
                  </MantineProvider>
                </ColorSchemeProvider>
              </SessionProvider>
            </CookiesProvider>
          ) : (
            <UnsupportedDeviceOrBrowser
              selectedLanguage={selectedLanguage}
              TEST_EMBEDDED={TEST_EMBEDDED}
              isEmbedded={isEmbedded}
              logSentryError={logSentryError}
            />
          )}
        </>
      ) : (
        <>
          {headTag}
          <p>&nbsp;</p>
        </>
      )}
    </>
  )
}
